// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../../../node_modules/css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../../../node_modules/css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/* ============================================================
   DESIGN SYSTEM — READ ME FIRST
   ============================================================
   For NEW screens, build with these reusable pieces only:

     Buttons  → <CustomButton> (src/components/CustomButton.js), CSS in
                SECTION 11a (.az-btn / .az-btn--<variant> / .az-btn--<size>)
     Tables   → <CustomTable> (src/components/CustomTable.js), CSS in
                SECTION 12 (.az-table). 98% of datatables already use it;
                the only exceptions are nested expandable sub-tables that
                need antd's <Table.Summary>, which CustomTable doesn't wrap.
     Cards    → .page-card-header / .page-card-title / .page-card-subtitle
                / .page-card-actions / .page-card-body / .page-card-form
                (SECTION "PAGE CARD") for whole-page layout, OR the generic
                .az-card (SECTION 11c) for boxed content that isn't a full
                page, or .sd-kpi / .sd-card (SECTION "SMART DASHBOARD")
                for dashboard KPI tiles and chart cards.
     Search   → .az-search-input (SECTION 11a, next to the button system).
     Status   → <StatusCard>/<StatusConnector> (src/components/StatusCard.js)
                for the horizontal stat-pipeline strip used on listing pages.

   All of the above pull their colors/radii/shadows from the :root tokens
   directly below — change a token once, every component using it updates.
   --colorDark/--colorLight/--colorDark-rgb are the exception: they're set
   at runtime per-org by src/utils/themeManager.js, so the :root values here
   are just the pre-JS fallback, not something to hand-edit per theme.

   Classes named customCard, customButtonOutlinedBoard/Cancel, customSearch,
   searchBtn, themeCard etc. below are PRE-EXISTING and still load-bearing
   across ~200+ older pages — do not delete them. They've been pointed at
   the same tokens so they stay visually identical to the new system, but
   new code should reach for the components above instead of these classes.
   ============================================================ */

:root {
  /* Brand accent — runtime default before themeManager.js applies the
     org's theme; also the fallback in every var(--colorDark, ...) usage. */
  --colorDark: #007aff;
  --colorLight: rgba(0, 122, 255, 0.08);
  --colorDark-rgb: 0, 122, 255;

  /* Neutral surfaces — NOT theme-dependent, same for every org. */
  --az-gray-50: #f9f9f9;
  --az-gray-300: #e5e7eb;
  --az-gray-600: #4f4f4f;

  /* Text — opacity-based scale matches the Figma design system exactly. */
  --az-text-primary: rgba(0, 0, 0, 0.9);
  --az-text-secondary: rgba(0, 0, 0, 0.6);
  --az-text-tertiary: rgba(0, 0, 0, 0.5);
  --az-border-subtle: rgba(0, 0, 0, 0.08);

  /* Radius scale — use --az-radius-pill instead of hardcoding 1000px /
     9999px; both render identically as "fully round" but having two
     spellings scattered across the file makes future edits error-prone. */
  --az-radius-sm: 8px;
  --az-radius-md: 16px;
  --az-radius-lg: 16px;
  --az-radius-pill: 9999px;

  /* Shadows */
  --az-shadow-card: 0 1px 4px rgba(0, 0, 0, 0.08);
  --az-shadow-card-lg: 0 8px 16px rgba(38, 88, 174, 0.08);

  /* Spacing scale (4px base) */
  --az-space-1: 4px;
  --az-space-2: 8px;
  --az-space-3: 12px;
  --az-space-4: 16px;
  --az-space-5: 20px;

  /* ── Base colors ── */
  --az-white: #ffffff;
  --az-white-rgb: 255, 255, 255;
  --az-black: #000000;
  --az-black-rgb: 0, 0, 0;
  --az-black-e0: #000000e0; /* disabled-text alpha-black; kept as its own
     8-digit hex rather than converted to rgba(var(--az-black-rgb), 0.88) —
     0xe0 isn't an exact 0.88, so this preserves the exact original pixel. */

  /* ── Extended gray scale — every exact gray value found hardcoded across
     this file, given its own slot (not rounded to a neighbor) so this
     extraction never shifts a single pixel of existing color. Slots between
     the original 25/50/100/150/200/300/600 anchors are numbered to preserve
     lightest-to-darkest order. ── */
  --az-gray-135: #f3f4f6;
  --az-gray-175: #f0f0f0;
  --az-gray-310: #e4e4e4;
  --az-gray-350: #dcdcdc;
  --az-gray-400: #d1d5db;
  --az-gray-420: #cac8c7;
  --az-gray-450: #bcbcbc;
  --az-gray-480: #aaaaaa;
  --az-gray-500: #9ca3af;
  --az-gray-550: #888888;
  --az-gray-570: #6b7280;
  --az-gray-580: #707070;
  --az-gray-50-rgb: 249, 249, 249;
  --az-gray-630: #595959;
  --az-gray-650: #444444;
  --az-gray-680: #3d3d3d;
  --az-gray-700: #374151;
  --az-gray-750: #333333;
  --az-gray-910: #1a1a1a;
  --az-gray-950: #111111;
  --az-gray-970: #050202;

  /* ── Slate (blue-tinted gray) — distinct family, not on the neutral scale ── */
  --az-slate-100: #e2e8f0;
  --az-slate-300: #a8bcc1;
  --az-slate-500: #64748b;
  --az-slate-900: #111827;

  /* ── Blue (non-theme — legacy/antd-adjacent accents, distinct from the
     per-org --colorDark accent) ── */
  --az-blue-500: #1890ff;
  --az-blue-600: #0666d1;
  --az-blue-900: #06377e;
  /* The .az-login page's deliberately fixed brand blue (see that section's
     own comment) — same numeric value as colorDark's default fallback, but
     a DIFFERENT token on purpose: this one must never change with the org
     theme, colorDark is meant to. Do not point .az-login at colorDark. */
  --az-login-fixed-blue: #007aff;

  /* ── Red / danger ── */
  --az-red-100: #fef2f2;
  --az-red-400: #f44336;
  --az-red-500: #d74b4b;
  --az-red-500-rgb: 215, 75, 75;
  --az-red-600: #ff3b30;
  --az-red-600-rgb: 255, 59, 48;
  --az-red-900-rgb: 105, 9, 9;

  /* ── Green / success ── */
  --az-green-600: #438f04;
  --az-green-700: #02872e;

  /* ── Orange — one semantic status color (the .orange utility class),
     not theme-related. Legacy hover/active shades that used to live here
     were removed — those states now track the org's live theme accent
     (var(--colorDark)) directly, see .custom-checkbox above. ── */
  --az-orange-400: #ea6e03;

  /* ── One-off shadow/overlay tints (RGB-only — always used via rgba() at
     a specific opacity, never as a solid fill) ── */
  --az-shadow-tint-rgb: 38, 88, 174;
  --az-navy-shadow-rgb: 9, 30, 66;
  --az-blush-rgb: 234, 229, 229;
}

/* SECTION 1: Base & global styles (font, scrollbar, placeholder colors, utility classes) */

body {
  /* App-wide typeface — matches the Figma design system (Plus Jakarta Sans).
     Every component below should inherit this rather than hardcoding its
     own font-family, so a typeface change only ever happens here. */
  font-family: "Plus Jakarta Sans", "Noto Sans", sans-serif !important;
  font-weight: 500;
  color: var(--az-black);
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}

/* a:hover {
  color: var(--primaryDark) !important;
} */

::-webkit-scrollbar {
  width: 2px;
  height: 10px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: var(--primaryDark);
}

::placeholder,
.ant-select-selection-placeholder {
  color: var(--az-gray-580) !important;
  opacity: 1;
}

.ant-select-selection-placeholder {
  text-transform: capitalize;
}

:-ms-input-placeholder {
  color: var(--az-gray-580) !important;
}

::-ms-input-placeholder {
  color: var(--az-gray-580) !important;
}

.mb-30 {
  margin-bottom: 30px;
}

.brdrChecked {
  border: 1px solid var(--primaryDark) !important;
}

textarea::-webkit-input-placeholder {
  color: var(--az-gray-350) !important;
}

textarea:-moz-placeholder {
  color: var(--az-gray-350) !important;
}

textarea::-moz-placeholder {
  color: var(--az-gray-350) !important;
}

textarea:-ms-input-placeholder {
  color: var(--az-gray-350) !important;
}

.w-70 {
  flex: 0 0 70%;
  max-width: 70%;
}

.w-30 {
  flex: 0 0 28%;
  max-width: 28%;
  width: 28%;
  height: 100%;
  transition: all 0.5s ease;
}

.mv20 {
  margin: 20px 0 20px 0;
}

.mt10 {
  margin-top: 10px;
}

.brandText {
  font-size: 18px;
  font-style: italic;
  color: var(--primaryDark);
  font-weight: 500;
}

#navbarNav {
  flex-grow: inherit !important;
}

/* SECTION 2: Layout — sidebar open/close transitions */

/* .sidebar.open ~ .w-70 {
  flex: 0 0 60%;
  max-width: 60%;
  width: calc(100vw - 40%);
} */
.sidebar.open .w-30 {
  flex: 0 0 25%;
  max-width: 30%;
  transition: all 0.5s ease;
}

/* /////////////////////////////// */

/* SECTION 3: Button styles — legacy system applied as a className directly on antd's <Button>; still load-bearing across ~200+ pages so nothing here gets deleted/renamed. customButtonOutlinedBoard/Cancel point at the same --az-* tokens as SECTION 11a so they stay visually identical, and are height/font-size-matched to the same 44px/16px standard as .az-btn--xl so they don't look mismatched sitting next to a CustomButton in the same toolbar row. For any NEW button use <CustomButton> instead. */

.customButtonOutlinedDraft {
  width: 150px !important;
  color: var(--primaryTextDark) !important;
  background-color: transparent !important;
  border-color: var(--primaryDark) !important;
  border-radius: 22px !important;
  height: 43px !important;
  transition: all 0.3s ease-in-out;
  font-size: 14px !important;
}

.customButtonOutlinedDraft:hover {
  width: 150px !important;
  color: var(--primaryTextDark);
  background-color: var(--primaryDark) !important;
  box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.01);
}

/* color was #fff !important — invisible on the light .customTabCard
   background this is actually used on (a white icon on a near-white
   card). Switched to a visible dark-gray that still picks up the
   theme accent on hover below. */
.customReload {
  width: auto !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  color: rgba(var(--az-black-rgb), 0.55) !important;
  background-color: transparent !important;
  border-color: rgba(var(--az-black-rgb), 0.12) !important;
  border-radius: 6px !important;
  /* Was 38px + a 5px margin all round (~48px total) for what's just a
     small icon on the DEPOSITS APPROVED tile — that oversized footprint
     is what pushed the title/stats down and left an odd gap above them. */
  height: 24px !important;
  transition: all 0.5s ease-in-out;
  text-shadow: none !important;
  margin: 0;
  padding: 2px 8px !important;
  border-width: 0;
  max-width: 100%;
}
.customReload:hover {
  width: auto !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  color: var(--primaryTextDark) !important;

  background-color: var(--primaryDark) !important;
  border-color: var(--az-white) !important;
  border-radius: 6px !important;
  /* Kept the same box (height/margin/padding) as the resting state —
     growing them on hover reflowed the button past the now fixed-height
     DEPOSITS APPROVED card, spilling outside its border. */
  height: 24px !important;
  transition: all 0.5s ease-in-out;
  text-shadow: none !important;
  margin: 0;
  padding: 2px 8px !important;
  border-width: 0;
  max-width: 100%;
}
/* Cancel button */
.profileCropModal .ant-modal-footer .ant-btn-default {
  width: auto !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  color: black !important;
  background-color: transparent !important;
  border-color: var(--primaryLight) !important;
  border-radius: 6px !important;
  height: 30px !important;
  transition: all 0.5s ease-in-out;
  text-shadow: none !important;
  margin: 5px;
}
.profileCropModal .ant-modal-footer .ant-btn-default:hover {
  width: auto !important;
  color: var(--primaryTextDark) !important;
  background-color: var(--primaryLight) !important;
  border-color: var(--primaryDark) !important;
  box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.01);
}

/* OK button */
.profileCropModal .ant-modal-footer .ant-btn-primary {
  width: auto !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  color: black !important;
  background-color: transparent !important;
  border-color: var(--primaryDark) !important;
  border-radius: 6px !important;
  height: 30px !important;
  transition: all 0.5s ease-in-out;
  text-shadow: none !important;
  margin: 5px;
  max-width: 100%;
}
.profileCropModal .ant-modal-footer .ant-btn-primary:hover {
  width: auto !important;
  color: var(--primaryTextDark) !important;
  background-color: var(--primaryDark) !important;
  border-color: var(--primaryDark) !important;
  box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.01);
}

.customButtonOutlinedBoard {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 6px !important;
  height: 44px !important;
  padding: 0 16px !important;
  border: none !important;
  border-radius: var(--az-radius-pill) !important;
  background: var(--colorLight, rgba(0, 122, 255, 0.07)) !important;
  color: var(--colorDark, #007aff) !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  line-height: 1 !important;
  cursor: pointer !important;
  white-space: nowrap !important;
  transition: background 0.15s !important;
  text-shadow: none !important;
  margin: 0 !important;
  max-width: 100%;
  position: relative;
}

/* Make <Link>/<a> inside button fill the entire clickable area */
.customButtonOutlinedBoard > span > a,
.customButtonOutlinedBoard > a {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 0 11px;
  color: inherit !important;
  text-decoration: none;
}
.customButtonOutlinedCancel {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 6px !important;
  width: auto !important;
  height: 44px !important;
  padding: 0 16px !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  line-height: 1 !important;
  color: var(--az-gray-600) !important;
  background-color: var(--az-gray-135) !important;
  border: none !important;
  border-radius: var(--az-radius-pill) !important;
  transition:
    background-color 0.15s ease-in-out,
    color 0.15s ease-in-out;
  text-shadow: none !important;
  box-shadow: none !important;
  margin: 0 !important;
  max-width: 100%;
}

.customButtonOutlinedCancel:hover {
  color: var(--az-gray-600) !important;
  background-color: var(--az-gray-300) !important;
}
.customRadioButtonOutlinedBoard .ant-radio-button-wrapper {
  color: black !important;
  background-color: transparent !important;
  border-color: var(--primaryDark) !important;
  height: 36px !important;
  line-height: 34px !important;
  padding: 0 16px !important;
  font-size: 14px !important;
}
/* Segmented-control corners (antd's own first/last-child radius comes
   from the global borderRadius:6 token — bump to 8px per spec here). */
.customRadioButtonOutlinedBoard .ant-radio-button-wrapper:first-child {
  border-radius: 8px 0 0 8px !important;
}
.customRadioButtonOutlinedBoard .ant-radio-button-wrapper:last-child {
  border-radius: 0 8px 8px 0 !important;
}
.customRadioButtonOutlinedBoard .ant-radio-button-wrapper:hover {
  color: var(--primaryTextDark) !important;
  background-color: var(--primaryDark) !important;
  border-color: var(--primaryDark) !important;
}
.customRadioButtonOutlinedBoard .ant-radio-button-wrapper-checked {
  color: var(--primaryTextDark) !important;
  background-color: var(--primaryDark) !important;
  border-color: var(--primaryDark) !important;
}

.ant-table-column-sorter .ant-table-column-sorter-full {
  color: var(--primaryTextLight);
}

.customButtonOutlinedBoardDisabled {
  width: auto !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  color: var(--az-gray-580) !important;
  background-color: transparent !important;
  border-color: var(--az-gray-580) !important;
  border-radius: 6px !important;
  height: 30px !important;
  transition: all 0.5s ease-in-out;
  text-shadow: none !important;
  margin: 5px;
  /* padding: 2px 4px !important; */
  border-width: 0;
  max-width: 100% !important;
}
.cancelBtn {
  display: flex;
  justify-content: end;
  align-items: flex-start;
  float: right;
}

.custom-popconfirm .ant-popover-inner {
  background-color: var(--az-red-100); /* light red/pink tone */
  border: 1px solid var(--az-green-600);
  border-radius: 8px;
}

.customButtonOutlinedBoard:hover {
  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.13) !important;
  color: var(--colorDark, #007aff) !important;
  border: none !important;
  box-shadow: none !important;
}

.customButtonBoard {
  width: auto !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  color: var(--primaryTextLight) !important;
  background-color: var(--primaryLight) !important;
  border-color: var(--primaryDark) !important;
  border-radius: 6px !important;
  height: 25px !important;
  transition: all 0.5s ease-in-out;
  text-shadow: none !important;
}

.customButtonBoard:hover {
  width: auto !important;
  color: var(--primaryTextDark) !important;
  background-color: var(--primaryDark) !important;
  box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.01);
}
.resetLink {
  color: gray !important;
}
.resetLink:hover {
  color: gray !important;
}

.green {
  color: var(--az-green-700);
  font-weight: 600;
}
.orange {
  color: var(--az-orange-400);
  font-weight: 600;
}

/* commission box */
.reviewCommissionBox {
  border: 1px solid gray;
  padding: 10px;
  margin-right: 10px;
  border-radius: 10px;
}

/* SECTION 4: Filter section (search/filter bars, block/pill styles) */

/***** Filter Section *****/
.customInput .ant-select-selector,
.customInput .ant-input-affix-wrapper {
  border: 0 !important;
  height: 32px;
  font-size: 12px !important;
  border-radius: 25px !important;
  border: 1px solid gray;
}

.ant-select-dropdown {
  padding: 0 !important;
}

.ant-form-item:has(.ant-select) .ant-form-item-explain-error {
  margin-top: 14px;
}

.anticon .customStep {
  position: relative;
  top: -3px;
  color: var(--az-blue-500);
  line-height: 1;
}

.custom-steps .ant-steps-item-title {
  text-align: center;
  /* Example */
  font-size: large;
}

.ant-select-dropdown .ant-select-item {
  font-size: 12px;
  padding: 8px !important;
  border-bottom: 1px solid var(--az-gray-135);
}

.filterSection {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
}

.filterSection1 {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 5px;
}

#fcComboBtn1 {
  margin: 5px;
  border: 1px solid var(--primaryDark);
  border-radius: 5px;
  color: white;
}

#fcComboBtn2 {
  border: 1px solid var(--primaryDark);
  border-radius: 5px;
  color: white;
}

#forwordToAZ {
  margin: 5px;
  border: 1px solid var(--primaryDark);
  border-radius: 5px;
  color: white;
}

#reverseFromAZ {
  border: 1px solid var(--primaryDark);
  border-radius: 5px;
  color: white;
}

.depositDistrictFilter {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: -60px;
  margin-bottom: 60px;
}

.remittanceFilterSection {
  display: flex;
  flex-direction: column;
  padding-right: 4px;

  justify-content: space-between;
  align-items: flex-end;
  margin-top: -45px;
  margin-bottom: 20px;
}

.row-filter-section {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
}

.districtSelect {
  margin-top: 10px !important;
}

.customSearch .ant-input-group .ant-input-group-addon {
  box-shadow: none !important;
  /* border-top-right-radius: 25px; */
  /* border-bottom-right-radius: 25px; */
}

.customSearch .ant-input-group .ant-input-group-addon .ant-input-search-button {
  box-shadow: none !important;
  padding: 5px 10px;
  background-color: red;
  /* border-top-right-radius: 25px !important; */
  /* border-bottom-right-radius: 25px !important; */
}
.ant-input-search-button {
  background-color: var(--primaryLight);
}
.ant-input-search-button:hover {
  background-color: var(--primaryDark) !important;
}

.customInput .ant-select-selector,
.customInput .ant-input-affix-wrapper {
  border: 0 !important;
  height: 32px;
  font-size: 12px !important;
  border-radius: 25px !important;
  font-weight: 500;
}
/* Stat chip ("Total Customer : 389" etc.) — same pill treatment as
   .customButtonOutlinedBoard so these read as part of the same design
   system instead of an older plain-bordered-box style. Used widely
   across Future Fulfillment / Finance ledger pages. */
.blockStyle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 16px;
  background: var(--az-gray-50);
  border: 1px solid var(--az-border-subtle);
  color: var(--az-text-primary);
  border-radius: var(--az-radius-pill);
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.blockStyle1 {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 16px;
  background: var(--az-white);
  border: 1px solid var(--az-border-subtle);
  color: var(--az-text-primary);
  border-radius: var(--az-radius-pill);
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  margin-top: 10px;
  margin-left: 10px;
}

/* Active filter tag (e.g. "Creation Date: 2026-03-26 to 2026-06-26 ✕") —
   used by the shared CustomTag component across ~50 list pages. Same pill
   treatment as .blockStyle/.customButtonOutlinedBoard for consistency. */
.custom-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 14px;
  /* background: var(--az-gray-50); */
  background: var(--az-white);
  border: 1px solid var(--az-border-subtle);
  border-radius: var(--az-radius-pill);
  color: var(--az-text-primary);
  font-size: 13px;
  white-space: nowrap;
}
.custom-tag-label {
  font-weight: 600;
}
/* Phones: a long filter tag (e.g. a date range) must wrap inside the
   content column instead of sticking out past the screen edge. */
@media (max-width: 767px) {
  .custom-tag {
    max-width: 100%;
    height: auto;
    min-height: 32px;
    flex-wrap: wrap;
    white-space: normal;
  }
}
.custom-tag-value {
  color: var(--az-text-secondary);
}
.custom-tag-close {
  cursor: pointer;
  font-size: 11px;
  color: var(--az-text-tertiary, rgba(0, 0, 0, 0.45));
  margin-left: 2px;
}
.custom-tag-close:hover {
  color: var(--az-text-primary);
}
.filterStyle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: -5px;
}

/* ── Grid columns sized only for bigger screens ───────────────────────
   AntD gives a <Col md={12}> (no xs / span) NO width below md — it shrinks
   to its content, so form fields render as narrow as their placeholder on
   phones. ~330 Cols across the app are written that way, so instead of
   editing each one: below each breakpoint, a Col that only has sizes for
   larger breakpoints (and no base span) takes the full row. Cols that do
   set xs/sm/span keep their own widths. Base span = class "ant-col-<n>";
   [class*="ant-col-1"] can't match "ant-col-md-1…", so the :not() list
   below safely detects it. */
@media (max-width: 1199.98px) {
  .ant-col[class*="ant-col-xl-"]:not([class*="ant-col-xs-"]):not([class*="ant-col-sm-"]):not([class*="ant-col-md-"]):not([class*="ant-col-lg-"]):not([class*="ant-col-1"]):not([class*="ant-col-2"]):not([class*="ant-col-3"]):not([class*="ant-col-4"]):not([class*="ant-col-5"]):not([class*="ant-col-6"]):not([class*="ant-col-7"]):not([class*="ant-col-8"]):not([class*="ant-col-9"]) {
    flex: 0 0 100%;
    max-width: 100%;
  }
}
@media (max-width: 991.98px) {
  .ant-col[class*="ant-col-lg-"]:not([class*="ant-col-xs-"]):not([class*="ant-col-sm-"]):not([class*="ant-col-md-"]):not([class*="ant-col-1"]):not([class*="ant-col-2"]):not([class*="ant-col-3"]):not([class*="ant-col-4"]):not([class*="ant-col-5"]):not([class*="ant-col-6"]):not([class*="ant-col-7"]):not([class*="ant-col-8"]):not([class*="ant-col-9"]) {
    flex: 0 0 100%;
    max-width: 100%;
  }
}
@media (max-width: 767.98px) {
  .ant-col[class*="ant-col-md-"]:not([class*="ant-col-xs-"]):not([class*="ant-col-sm-"]):not([class*="ant-col-1"]):not([class*="ant-col-2"]):not([class*="ant-col-3"]):not([class*="ant-col-4"]):not([class*="ant-col-5"]):not([class*="ant-col-6"]):not([class*="ant-col-7"]):not([class*="ant-col-8"]):not([class*="ant-col-9"]) {
    flex: 0 0 100%;
    max-width: 100%;
  }
}
@media (max-width: 575.98px) {
  .ant-col[class*="ant-col-sm-"]:not([class*="ant-col-xs-"]):not([class*="ant-col-1"]):not([class*="ant-col-2"]):not([class*="ant-col-3"]):not([class*="ant-col-4"]):not([class*="ant-col-5"]):not([class*="ant-col-6"]):not([class*="ant-col-7"]):not([class*="ant-col-8"]):not([class*="ant-col-9"]) {
    flex: 0 0 100%;
    max-width: 100%;
  }
}

/* Phones: a gutter <Row> placed straight in the page canvas (no padded
   wrapper) bleeds 8px past both screen edges via its negative margins and
   scrolls the page sideways — keep it inside; its Cols' gutter padding
   still gives an even inset. */
@media (max-width: 767px) {
  .page-content-canvas > .ant-row {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
}

/* Tablets: horizontal Steps (e.g. Create Manual Order) keep each title
   on one line and cut it off ("Custom…") — let titles wrap instead. AntD
   already switches Steps to vertical on phones. */
@media (min-width: 532px) and (max-width: 991.98px) {
  .ant-steps-horizontal .ant-steps-item-title {
    white-space: normal;
    line-height: 1.3;
  }
  .ant-steps-horizontal .ant-steps-item-title::after {
    top: 16px;
  }
  /* equal share for every step — otherwise the long last title takes
     its full natural width and squeezes the others */
  .ant-steps-horizontal .ant-steps-item,
  .ant-steps-horizontal .ant-steps-item:last-child {
    flex: 1 1;
    min-width: 0;
  }
  /* icon + title side by side as flex, so the title wraps within the
     space left after the number circle instead of being clipped */
  .ant-steps-horizontal .ant-steps-item-container {
    display: flex;
    align-items: flex-start;
  }
  .ant-steps-horizontal .ant-steps-item-content {
    flex: 1 1 auto;
    min-width: 0;
  }
  .ant-steps-horizontal .ant-steps-item-icon {
    flex-shrink: 0;
  }
}

/* Phones/small tablets: the header action row (Download / Filters / Add)
   is wider than the content column — let it wrap instead of pushing the
   Add button off-screen and scrolling the whole page sideways. */
@media (max-width: 767px) {
  .page-card-header > div {
    min-width: 0;
    max-width: 100%;
  }
  .filterStyle {
    flex-wrap: wrap;
    justify-content: flex-start;
  }
  /* Same for pages that lay out their totals chips (.blockStyle1) or
     header buttons (.az-btn) in an inline-styled flex row of their own */
  div:has(> .blockStyle1),
  div:has(> .az-btn) {
    flex-wrap: wrap;
    row-gap: 8px;
  }
  /* Long breadcrumb labels truncate instead of widening the top bar */
  .app-bc,
  .app-bc-list {
    min-width: 0;
  }
  .app-bc-label {
    flex-shrink: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

/* .font-md .ant-select-selector, .font-md .ant-input-affix-wrapper {
  font-size: 14px !important;
  font-weight: 500;
} */
.ant-select-dropdown {
  padding: 0 !important;
}

.ant-select-dropdown .ant-select-item {
  font-size: 12px;
  padding: 8px !important;
  border-bottom: 1px solid var(--az-gray-135);
}

/* SECTION 5: Sign-in page */

/***** SignIn Page Style *****/
.signInPage {
  width: 100%;
  height: 100vh;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background-position: right 35% bottom 45% !important;
}

/* .signInPage .assetsSection img {
  width: 293px;
} */
.signInPage .SignInFormSection .customCard {
  border-radius: 14px;
  width: 401px;
  margin: 0 auto;
  cursor: pointer;
  box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.1);
}

.signInPage .SignInFormSection .customCard .ant-card-body {
  padding: 55px 66px;
}

.signInPage .SignInFormSection .customCard .customFormControl {
  border-radius: 6px !important;
  height: 50px !important;
  padding: 10px 20px !important;
}

.customFormControl {
  border: 1px solid var(--az-gray-310);
  border-radius: 6px !important;
  height: 40px !important;
  padding: 6px 16px !important;
  width: 100%;
}

.tableInputFields {
  border: 1px solid var(--az-gray-310);
  border-radius: 4px !important;
  width: 45px;
  padding: 4px !important;
}

.tableInputErrors {
  color: var(--az-red-400) !important;
}

.variantTable .editFormControl {
  width: 100% !important;
  padding: 10px !important;
}

.signInPage .SignInFormSection .customCard .ant-input-prefix {
  margin-right: 20px;
}

/* ── New split-screen login (SignIn/index.js) — client-neutral, no
   background image; brand panel colored purely from --colorDark so it
   already matches whatever theme (blue by default) the platform is on,
   with no hardcoded client name/logo. Kept separate from the classes
   above (.signInPage etc.) since resetPassword.js/ChangePassword still
   use those for their own, unchanged pages. */
.az-login {
  display: flex;
  min-height: 100vh;
  width: 100%;
  background: var(--az-white);
}
.az-login__brand {
  position: relative;
  flex: 1 1 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px;
  /* Fixed blue, not var(--colorDark): that variable reflects whichever
     org theme was last active in this browser (see themeManager.js) and
     defaults to orange (THEMES[0]), not blue — visible proof at
     /Users/admin/Desktop/Agrozone/admin-portal/src/utils/themeManager.js:24
     ("blue" preset is #007AFF). Pre-login there's no signed-in org to
     react to anyway, so this page always shows the platform's own blue
     rather than leaking whatever theme a previous session left behind. */
  background: linear-gradient(
    135deg,
    var(--az-login-fixed-blue) 0%,
    var(--az-blue-900) 100%
  );
}
.az-login__brandGlow {
  position: absolute;
  border-radius: 50%;
  background: rgba(var(--az-white-rgb), 0.12);
  pointer-events: none;
}
.az-login__brandGlow--one {
  width: 420px;
  height: 420px;
  top: -140px;
  right: -120px;
}
.az-login__brandGlow--two {
  width: 320px;
  height: 320px;
  bottom: -110px;
  left: -90px;
  background: rgba(var(--az-white-rgb), 0.08);
}
.az-login__brandContent {
  position: relative;
  z-index: 1;
  max-width: 420px;
  color: var(--az-white);
}
.az-login__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 18px;
  border-radius: 14px;
  background: rgba(var(--az-white-rgb), 0.92);
  margin-bottom: 32px;
}
.az-login__markLogo {
  height: 28px;
  width: auto;
  display: block;
}
/* "Powered by Traidster" pill pinned to the bottom-right corner of the
   login page (over the white form panel, hence the hairline border) —
   same pill + dark wordmark as the sidebar footer. */
.az-login {
  position: relative;
}
.az-login__poweredBy {
  position: absolute;
  right: 24px;
  bottom: 20px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  border-radius: 9999px;
  background: #ffffff;
  border: 1px solid rgba(23, 26, 33, 0.1);
  box-shadow: 0 1px 3px rgba(23, 26, 33, 0.06);
  white-space: nowrap;
}
.az-login__poweredByLabel {
  font-size: 11px;
  font-weight: 600;
  color: var(--traidster-charcoal, #171a21);
}
.az-login__poweredByLogo {
  height: 14px;
  width: auto;
  display: block;
}
.az-login__brandTitle {
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 16px;
  color: var(--az-white);
}
.az-login__brandTagline {
  font-size: 16px;
  line-height: 1.6;
  color: rgba(var(--az-white-rgb), 0.85);
  margin: 0;
}
.az-login__panel {
  flex: 1 1 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  background: var(--az-white);
}
.az-login__formWrap {
  width: 100%;
  max-width: 360px;
}
.az-login__header {
  margin-bottom: 32px;
}
.az-login__title {
  font-size: 26px;
  font-weight: 700;
  color: var(--az-text-primary, rgba(0, 0, 0, 0.9));
  margin: 0 0 8px;
}
.az-login__subtitle {
  font-size: 14px;
  color: var(--az-text-secondary, rgba(0, 0, 0, 0.6));
  margin: 0;
}
.az-login__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.az-login__link {
  color: var(--az-login-fixed-blue);
  font-weight: 500;
  text-decoration: none;
}
.az-login__link:hover {
  text-decoration: underline;
}
.az-login__footerText {
  text-align: center;
  color: var(--az-text-secondary, rgba(0, 0, 0, 0.6));
  font-size: 14px;
  margin-top: 16px;
}
/* Solid fixed-blue submit button — deliberately not .customButton (which
   uses var(--primaryDark)/var(--primaryLight) and would otherwise render
   in whatever org theme is cached, e.g. orange, with low-contrast white
   text on a near-white background). */
.az-login__submit.ant-btn {
  width: 100%;
  height: 44px;
  background-color: var(--az-login-fixed-blue);
  border-color: var(--az-login-fixed-blue);
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  color: var(--az-white);
}
.az-login__submit.ant-btn:hover,
.az-login__submit.ant-btn:focus {
  background-color: var(--az-blue-600);
  border-color: var(--az-blue-600);
  color: var(--az-white);
}
/* Same reasoning as the button — keep the checkbox fixed-blue rather than
   inheriting the global .ant-checkbox-checked rule's var(--secondaryDark). */
.az-login .ant-checkbox-checked .ant-checkbox-inner {
  background-color: var(--az-login-fixed-blue) !important;
  border-color: var(--az-login-fixed-blue) !important;
}
@media (max-width: 767px) {
  .az-login__panel {
    padding: 24px 20px;
  }
}

.login-form-forgot {
  color: var(--az-gray-580);
  font-weight: 500;
  text-decoration: underline;
}
.primaryColor {
  color: var(--az-gray-580) !important;
}
.orText {
  text-align: center;
  margin-bottom: 0;
  color: var(--az-gray-580);
}
.formHeading p {
  color: var(--az-gray-580);
  /* line-height: 30px; */
}

.defaultColor {
  color: var(--az-gray-580) !important;
}

.signInForm .ant-form-item {
  margin-bottom: 10px;
}

/* .downloadCsv {
  margin-right: 5px;
  border-radius: 50% solid black;
  font-weight: bolder;
} */

.customButton {
  width: 100% !important;
  background-color: var(--primaryDark) !important;
  border-color: var(--primaryDark) !important;
  border-radius: 6px !important;
  height: 43px !important;
  line-height: 30px;
  padding-top: 0px !important;
  font-size: 16px !important;
}

.customButton:disabled {
  background-color: lightgray !important;
  border-color: lightgray !important;
  color: gray !important;
}

.customButtonLink {
  color: var(--primaryDark) !important;
  border-color: var(--primaryDark) !important;
  border-radius: 6px !important;
}

.customButtonLink:hover {
  color: white !important;
  background-color: var(--primaryDark) !important;
  border-radius: 6px !important;
}

.customButton1 {
  width: 100% !important;
  border: 1px solid var(--primaryDark) !important;
  border-radius: 6px !important;
  height: 25px !important;
  padding-top: 0px !important;
  line-height: 30px;
  font-size: 14px !important;
}

.customButton2 {
  width: 100% !important;
  background-color: var(--az-white) !important;
  border-color: var(--primaryDark) !important;
  border-radius: 6px !important;
  height: 43px !important;
  line-height: 30px;
  font-size: 14px !important;
}

.nextBtn {
  display: flex;
  align-content: flex-end;
  justify-items: end;
  /* left: 1100px; */
  width: 150px !important;
}

.addComboProduct {
  width: 150px !important;
  color: white !important;
}

.login-form-button {
  width: 100% !important;
  background-color: var(--primaryLight) !important;
  border-color: var(--primaryDark) !important;
  border-radius: 50px !important;
  transition: all 0.3s ease-in-out;
}

.login-form-button:hover {
  color: var(--primaryDark) !important;
  background-color: var(--primaryTextDark) !important;
  box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.1);
}

.allocateBtn {
  height: 25px !important;
  padding-top: 0px !important;
}

.customButtonAction {
  width: auto !important;
  background-color: var(--primaryDark) !important;
  border-color: var(--primaryDark) !important;
  border-radius: 6px !important;
  color: var(--az-white) !important;
  height: 43px !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  font-size: 14px !important;
  padding: 4px 10px !important;
  border-width: 0;
  width: 100%;
  cursor: pointer;
}

a {
  text-decoration: none;
}

.customButtonOutlinedDisable {
  width: auto !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  color: var(--az-gray-970) !important;
  background-color: var(--az-gray-420) !important;
  border-color: var(--primaryDark) !important;
  border-radius: 6px !important;
  height: 25px !important;
  transition: all 0.5s ease-in-out;
  text-shadow: none !important;
  margin: 5px;
  padding: 4px 10px !important;
  border-width: 0;
  max-width: 100%;
}

.disableBtn {
  width: auto !important;
  background-color: var(--az-gray-420) !important;
  border-color: var(--primaryDark) !important;
  border-radius: 6px !important;
  color: var(--az-gray-970) !important;
  height: 43px !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  font-size: 14px !important;
  padding: 4px 10px !important;
  border-width: 0;
  width: 100%;
  cursor: pointer;
}

.submitDeposit {
  font-size: 15px !important;
  width: 200% !important;
  display: flex !important;
  align-items: center !important;
}

.ant-checkbox-checked .ant-checkbox-inner {
  background-color: var(--secondaryDark) !important;
  border-color: var(--secondaryLight) !important;
  border-radius: 4px !important;
  border-color: var(--az-white) !important;
}
/* Make tick white */
.ant-checkbox-checked.ant-checkbox-disabled .ant-checkbox-inner::after {
  border-color: var(--az-white) !important;
}
.ant-checkbox-disabled + span {
  color: var(--az-black-e0) !important;
}

.customButtonOutlined {
  width: 100% !important;
  color: var(--primaryTextLight) !important;
  background-color: var(--primaryLight) !important;
  border-color: var(--primaryDark) !important;
  border-radius: 22px !important;
  height: 43px !important;
}

/* .action .ant-input-number {
  width: 60px;
} */

/* SECTION 6: Sidebar — collapsed 96px icon-only, expanded 212px icon+label; submenu panel is a fixed dark overlay via portal, doesn't shift content */

.sidebar {
  position: fixed;
  left: 0;
  /* Starts at the topbar's bottom border — the brand lives on the topbar's
     white band above it (.sb-topbrand) */
  top: var(--sb-topbar-height);
  height: calc(100vh - var(--sb-topbar-height));
  /* Collapsed default: icon-only rail */
  width: var(--sb-rail-width);
  /* Figma fill: #1B1B1B */
  background: var(--sb-rail-bg);
  /* Figma auto-layout: vertical flow */
  display: flex;
  flex-direction: column;
  /* Figma alignment: center */
  align-items: center;
  /* Figma gap: 8px — applied between brand, nav, profile */
  gap: var(--sb-gap);
  /* Figma padding: 16px all sides — only vertical when collapsed */
  padding: var(--sb-padding) 0;
  /* Figma stroke: #FFFFFF 10% → right border */
  border-right: 1px solid var(--sb-stroke);
  /* Figma corner radius: 0 */
  border-radius: 0;
  z-index: 100;
  overflow: hidden;
  transition:
    width 0.22s cubic-bezier(0.4, 0, 0.2, 1),
    padding 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Expanded: Figma W 212px, padding 16px all sides */
.sidebar.expanded {
  width: var(--sb-expanded-width); /* Figma W: 212px */
  padding: var(--sb-padding); /* Figma padding: 16px all sides */
  align-items: stretch; /* children fill full width */
}

/* ── Brand band (top-left) ─────────────────────────────────────
   Fixed over the topbar's left end: same height, white background and
   bottom border as .page-topbar, so logo + breadcrumb/search read as one
   continuous header and the dark rail starts underneath. Width follows the
   rail (collapsed/expanded) so the logo stays aligned over it. */
.sb-topbrand {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 102;
  width: var(--sb-rail-width);
  height: var(--sb-topbar-height);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 16px;
  background: var(--az-white);
  border-bottom: 1px solid rgba(var(--az-black-rgb), 0.06);
  transition: width 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}
.sb-topbrand.expanded {
  width: var(--sb-expanded-width);
}
.sb-topbrand .sb-brand-box {
  background: transparent;
  height: 100%;
  padding: 0;
}
.sb-topbrand:not(.expanded) .sb-brand-box {
  width: 64px;
}

/* Burger — small screens only (rendered by Sidebar.js when mobile) */
.sb-burger-btn {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid rgba(var(--az-black-rgb), 0.1);
  background: var(--az-white);
  color: var(--az-gray-910);
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.sb-burger-btn:hover {
  background: var(--az-gray-175);
}

/* ── Brand row ─────────────────────────────────────────────────
   Figma: padding comes from parent (sidebar), not brand row itself
   Row is full-width, 72px tall, bottom border stroke                */
.sb-brand-row {
  width: 100%;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 0;
}

/* Single unified brand box — Figma node 43:6090/42:3418: one rounded-12
   tile, light accent tint, holds either the org initial (collapsed) or
   the full org name (expanded) in the SAME accent color. Not a separate
   avatar + label pair. */
.sb-brand-box {
  width: 100%;
  height: 64px;
  border-radius: 12px;
  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  padding: 0 12px;
}
/* An uploaded org logo is almost always a flat image with its own opaque
   (usually white) background baked in, not a transparent PNG — on the
   translucent theme-tint box above, that embedded rectangle shows up as a
   visibly mismatched card. Solid white here absorbs it regardless of what
   any given org's logo file looks like, without needing to touch or
   inspect the uploaded image itself. */
.sb-brand-box--logo {
  background: var(--az-white, #fff);
}

/* Collapsed: 64×64 square, centered by .sb-brand-row — same inset
   pattern as .sb-rail-item above, so the logo box sits flush with the
   nav icons below it (Figma node 44:7820: 64×64, not edge-to-edge) */
.sidebar:not(.expanded) .sb-brand-box {
  width: 64px;
  padding: 0;
}

.sb-brand-initial {
  font-size: 34px;
  font-weight: 800;
  color: var(--colorDark, #007aff);
  text-transform: uppercase;
  letter-spacing: 0;
  line-height: 1;
}

.sb-brand-fullname {
  font-size: 20px;
  font-weight: 500;
  color: var(--colorDark, #007aff);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  text-align: center;
}
/* Client org logos vary in shape (wide wordmark, square mark, tall lockup —
   every client can upload something different in a SaaS app), so scale
   dynamically to fit rather than assume one aspect ratio: max-height caps
   it within .sb-brand-box's 64px row, max-width keeps it inside the box's
   own available width (accounting for the box's 12px side padding), and
   object-fit preserves the logo's real proportions at whatever size it
   ends up scaled to — never stretched, never overflowing either axis. */
.sb-brand-logo {
  max-height: 45px;
  max-width: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
}

/* Traidster wordmark image in the top brand box (platform login only) —
   wider aspect ratio than an org logo, so cap width instead of just height. */
.sb-brand-logo--traidster {
  max-width: 100%;
  max-height: 32px;
  object-fit: contain;
}

/* Platform login, collapsed rail — same cobalt→cyan gradient as the
   wordmark's "ai" (Traidster Internal Brand Guide, 26 Sep 2026), applied
   to the "T" initial in place of the per-org --colorDark used elsewhere,
   since this represents the platform itself, not a client's theme. */
.sb-brand-initial--gradient {
  background: linear-gradient(
    135deg,
    var(--traidster-cobalt) 0%,
    var(--traidster-midpoint) 50%,
    var(--traidster-cyan) 100%
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  color: var(--traidster-cobalt); /* fallback if background-clip unsupported */
}

/* Only one of the two text variants is mounted at a time, swapped in JS
   (Sidebar.js) by collapsed state — no CSS-only opacity dance needed. */

/* ── Collapse toggle — Figma: circle at sidebar right edge, vertically centered */
.sb-collapse-btn {
  position: fixed;
  left: calc(var(--sb-rail-width) - 14px);
  /* vertical centre of the rail, which starts below the topbar */
  top: calc(50% + var(--sb-topbar-height) / 2);
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--az-white);
  border: 1px solid rgba(var(--az-black-rgb), 0.12);
  box-shadow: 0 2px 8px rgba(var(--az-black-rgb), 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 101;
  color: var(--az-gray-630);
  font-size: 11px;
  padding: 0;
  transition:
    left 0.22s cubic-bezier(0.4, 0, 0.2, 1),
    background 0.15s ease,
    color 0.15s ease;
}

.sb-collapse-btn.expanded {
  left: calc(var(--sb-expanded-width) - 14px);
}

.sb-collapse-btn:hover {
  background: var(--az-gray-175);
  color: var(--az-gray-910);
  border-color: rgba(var(--az-black-rgb), 0.2);
}

.sb-collapse-btn:focus-visible {
  outline: 2px solid var(--sb-accent);
  outline-offset: 2px;
}

/* ── Nav rail ─────────────────────────────────────────────────
   Figma: flex-grow fills space between brand and profile
   Figma gap 8px is inherited from parent sidebar gap            */
.sb-rail-nav {
  flex: 1 1;
  min-height: 0; /* allows flex child to shrink + scroll on short screens */
  width: 100%;
  padding: 0;
  display: flex;
  flex-direction: column;
  /* Figma gap: 8px between nav items */
  gap: var(--sb-gap);
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  align-items: stretch;
}

.sidebar:not(.expanded) .sb-rail-nav {
  align-items: center;
}

.sb-rail-nav::-webkit-scrollbar {
  display: none;
}

/* ── Module item ───────────────────────────────────────────────
   Expanded : full-width row, icon + label + optional chevron
   Collapsed: 44×44 centred icon square                         */
.sb-rail-item {
  display: flex;
  align-items: center;
  gap: 8px;
  /* Expanded: full width, left-to-right — Figma node 42:3428 uses
     padding-driven height (px-8 py-12), not a fixed box height */
  width: 100%;
  height: auto;
  padding: 12px 8px;
  border-radius: 12px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-decoration: none;
  color: rgba(var(--az-white-rgb), 0.65);
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  box-sizing: border-box;
  transition:
    background 0.13s,
    color 0.13s;
}

/* Collapsed: 64×64 square, icon centred — Figma node 43:6425 (64×64,
   12px padding around a 40×40 icon) */
.sidebar:not(.expanded) .sb-rail-item {
  width: 64px;
  height: 64px;
  padding: 0;
  justify-content: center;
  border-radius: 12px;
  gap: 0;
  flex-shrink: 0; /* never compress icons on short screens */
}

.sb-rail-item:hover {
  background: var(--sb-item-hover);
  color: var(--sb-text);
}

/* Active: solid blue pill — Figma accent */
.sb-rail-item.active {
  background: var(--sb-accent);
  color: var(--az-white);
}

.sidebar:not(.expanded) .sb-rail-item.active {
  background: var(--sb-accent-collapsed);
  color: var(--az-white);
}

.sb-rail-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 19px;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
}

/* Collapsed rail — Figma node 43:6427 etc: 40×40 icon glyph inside the
   64×64 tap target above */
.sidebar:not(.expanded) .sb-rail-icon {
  font-size: 26px;
  width: 40px;
  height: 40px;
}

/* Label — fades out when collapsed */
.sb-rail-label {
  flex: 1 1;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  font-size: 16px;
  font-weight: 500;
  opacity: 1;
  max-width: 140px;
  transition:
    opacity 0.12s ease 0.07s,
    max-width 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar:not(.expanded) .sb-rail-label {
  opacity: 0;
  max-width: 0;
}

/* Right-side chevron — hidden when collapsed */
.sb-rail-chevron {
  font-size: 16px;
  line-height: 1;
  color: rgba(var(--az-white-rgb), 0.3);
  flex-shrink: 0;
  opacity: 1;
  max-width: 20px;
  transition:
    opacity 0.12s ease 0.07s,
    max-width 0.22s cubic-bezier(0.4, 0, 0.2, 1),
    color 0.13s,
    transform 0.18s ease;
}

.sidebar:not(.expanded) .sb-rail-chevron {
  opacity: 0;
  max-width: 0;
  overflow: hidden;
}

.sb-rail-item:hover .sb-rail-chevron {
  color: rgba(var(--az-white-rgb), 0.6);
}

.sb-rail-item.active .sb-rail-chevron {
  color: rgba(var(--az-white-rgb), 0.8);
  transform: rotate(90deg);
}

/* ── Settings section — lives inside sb-rail-nav, pushed to bottom ── */
.sb-settings-section {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: var(--sb-gap);
  width: 100%;
  flex-shrink: 0; /* never compress on short screens */
}

.sidebar:not(.expanded) .sb-settings-section {
  align-items: center;
}

/* ── Profile section — pinned to sidebar bottom ────────────────
   Figma: same padding as sidebar (handled by parent),
   top stroke border separates from nav                         */
.sb-profile-section {
  width: 100%;
  flex-shrink: 0;
  padding-top: 8px;
}

.sidebar:not(.expanded) .sb-profile-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: auto;
  padding-top: 8px;
}

.sb-profile-inner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.13s;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
}

/* Collapsed: shrink to fit just the avatar circle — otherwise the gap
   before .sb-profile-info still renders even though that element has
   collapsed to max-width: 0, pushing the avatar left of center. */
.sidebar:not(.expanded) .sb-profile-inner {
  gap: 0;
  width: auto;
}

.sb-profile-inner:hover {
  background: var(--sb-item-hover);
}

.sb-avatar-circle {
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 50%;
  background: rgba(var(--az-white-rgb), 0.14);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  color: var(--az-white);
  flex-shrink: 0;
}

.sb-profile-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1 1;
  opacity: 1;
  max-width: 160px;
  overflow: hidden;
  transition:
    opacity 0.12s ease 0.08s,
    max-width 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar:not(.expanded) .sb-profile-info {
  opacity: 0;
  max-width: 0;
}

.sb-profile-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--sb-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
}

/* ── "Powered by" footer — a full-bleed white strip across the very
   bottom of the rail: negative margins cancel the sidebar's own padding
   (16px sides when expanded, 16px bottom always) so it runs edge to edge
   with no radius or gap. Holds the Traidster wordmark (dark/charcoal
   variant, since it sits on white) — Traidster Internal Brand Guide,
   26 Sep 2026. */
.sb-powered-by {
  margin: 8px calc(-1 * var(--sb-padding)) calc(-1 * var(--sb-padding));
  width: calc(100% + 2 * var(--sb-padding));
  height: 40px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  background: var(--az-white);
  border-radius: 0;
  padding: 0 14px;
  white-space: nowrap;
}

.sb-powered-by-label {
  font-size: 10px;
  font-weight: 600;
  color: var(--traidster-charcoal, #171a21);
}

.sb-powered-by-logo {
  height: 12px;
  width: auto;
  display: block;
}

/* Collapsed: same full-width white strip, wordmark only ("Powered by" is
   shown in the expanded rail only). The collapsed rail has no side
   padding, so only the bottom padding needs cancelling. */
.sidebar:not(.expanded) .sb-profile-section {
  width: 100%;
}
.sidebar:not(.expanded) .sb-powered-by {
  width: 100%;
  margin: 8px 0 calc(-1 * var(--sb-padding));
  padding: 0 12px;
}
.sidebar:not(.expanded) .sb-powered-by-label {
  display: none;
}
.sidebar:not(.expanded) .sb-powered-by-logo {
  height: auto;
  width: 100%;
  max-width: 72px;
}

/* ── Submenu panel — dark theme (matches Figma) ────────────── */
.sb-backdrop {
  position: fixed;
  inset: var(--sb-topbar-height) 0 0 0;
  z-index: 98;
  background: transparent;
}

.sb-submenu {
  position: fixed;
  top: var(--sb-topbar-height);
  height: calc(100% - var(--sb-topbar-height));
  width: var(--sb-panel-width);
  background: var(--sb-panel-bg);
  border-right: 1px solid rgba(var(--az-white-rgb), 0.05);
  box-shadow: 4px 0 24px rgba(var(--az-black-rgb), 0.5);
  z-index: 99;
  display: flex;
  flex-direction: column;
  animation: sbSubmenuIn 0.18s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes sbSubmenuIn {
  from {
    opacity: 0.5;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* ── Submenu panel close button — Figma: same circle as sidebar toggle ── */
.sb-panel-close-btn {
  position: absolute;
  right: -14px;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--az-white);
  border: 1px solid rgba(var(--az-black-rgb), 0.12);
  box-shadow: 0 2px 8px rgba(var(--az-black-rgb), 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 102;
  color: var(--az-gray-630);
  font-size: 11px;
  padding: 0;
  flex-shrink: 0;
}

.sb-panel-close-btn:hover {
  background: var(--az-gray-175);
  color: var(--az-gray-910);
  border-color: rgba(var(--az-black-rgb), 0.2);
}

/* Panel body — no header, content starts from top.
   Figma node 42:3368: 16px padding, 16px gap between groups
   (the gap is applied per-group via .sb-submodule margin-top below). */
.sb-panel-body {
  flex: 1 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  scrollbar-width: thin;
  scrollbar-color: rgba(var(--az-white-rgb), 0.1) transparent;
}

.sb-panel-body::-webkit-scrollbar {
  width: 4px;
}
.sb-panel-body::-webkit-scrollbar-track {
  background: transparent;
}
.sb-panel-body::-webkit-scrollbar-thumb {
  background: rgba(var(--az-white-rgb), 0.1);
  border-radius: 4px;
}

/* Page links inside the panel — white text on dark bg.
   Figma node 42:3397/42:3375: rounded-12 box, 8px padding all around,
   not a flat full-bleed strip. */
.sb-panel-item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 400;
  color: var(--az-gray-450);
  text-decoration: none;
  transition:
    background 0.11s,
    color 0.11s;
  white-space: nowrap;
  overflow: hidden;
}

.sb-panel-item-label {
  flex: 1 1;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}

.sb-panel-item:hover {
  background: rgba(var(--az-white-rgb), 0.07);
  color: var(--az-white);
}

.sb-panel-item.active {
  background: rgba(var(--az-white-rgb), 0.1);
  color: var(--az-white);
  font-weight: 600;
}

/* Nested — Figma indents pages only by the width of the group header's
   icon+gap (≈20px), not a full 32px tab-stop */
.sb-panel-item.nested {
  padding-left: 20px;
}

/* Dot indicator */
.sb-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  flex-shrink: 0;
  background: rgba(var(--az-white-rgb), 0.2);
  transition: background 0.12s;
}

.sb-panel-item:hover .sb-dot,
.sb-panel-item.active .sb-dot {
  background: var(--sb-accent);
}

/* Submodule group — Figma node 42:3368 gap: 16px between groups */
.sb-submodule {
  margin-top: 12px;
}

/* Submodule header — chevron on LEFT, white text, medium weight —
   Figma node 42:3369/42:3391: 16px text, font-weight 500, solid white */
.sb-subhead {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 500;
  color: var(--az-white);
  text-transform: none;
  letter-spacing: 0;
  border: none;
  background: none;
  cursor: pointer;
  width: 100%;
  font-family: inherit;
  transition: color 0.12s;
}

.sb-subhead-label {
  flex: 1 1;
  text-align: left;
  white-space: normal;
  word-break: break-word;
  line-height: 1.3;
}

.sb-subhead:hover {
  color: var(--az-white);
}
.sb-subhead.active {
  color: var(--az-white);
}

/* Chevron — now on the LEFT in JSX */
.sb-chevron {
  font-size: 15px;
  display: inline-block;
  transition: transform 0.18s ease;
  line-height: 1;
  color: rgba(var(--az-white-rgb), 0.4);
  flex-shrink: 0;
}

.sb-subhead:hover .sb-chevron,
.sb-subhead.active .sb-chevron {
  color: rgba(var(--az-white-rgb), 0.7);
}

/* Pages inside an open submodule */
.sb-sub-pages {
  display: flex;
  flex-direction: column;
}

/* ── Settings sidebar (SettingsSidebar.js — light background) ── */
.sb-nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 0;
  width: 100%;
}

.sb-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--az-gray-700);
  text-align: left;
  background: none;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  text-decoration: none;
  font-family: inherit;
  transition:
    background 0.12s,
    color 0.12s;
  white-space: nowrap;
  overflow: hidden;
  box-sizing: border-box;
}

.sb-item:hover {
  background: rgba(var(--az-black-rgb), 0.04);
  color: var(--az-slate-900);
}

.sb-item-active {
  background: var(--primaryLight, #ede9fe);
  color: var(--primaryDark);
  font-weight: 600;
}

.sb-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
}

.sb-label {
  flex: 1 1;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}

.sb-children {
  display: flex;
  flex-direction: column;
}

.sb-child {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px 8px 36px;
  font-size: 13px;
  font-weight: 400;
  color: var(--az-gray-570);
  text-decoration: none;
  border-radius: 6px;
  transition:
    background 0.1s,
    color 0.1s;
  white-space: nowrap;
  overflow: hidden;
}

.sb-child:hover {
  background: rgba(var(--az-black-rgb), 0.04);
  color: var(--az-gray-700);
}

.sb-child-active {
  color: var(--primaryDark);
  font-weight: 600;
  background: rgba(var(--az-black-rgb), 0.03);
}

.sb-child-nested {
  padding-left: 48px;
}

.sb-sub-children {
  display: flex;
  flex-direction: column;
}

/* Settings sidebar subhead — override the dark-bg version for light context */
.sb-nav .sb-subhead {
  padding: 8px 14px 8px 24px;
  color: var(--az-gray-700);
  font-size: 13px;
  font-weight: 600;
}

.sb-nav .sb-subhead:hover {
  color: var(--az-slate-900);
}

.sb-nav .sb-subhead.sb-subhead-active {
  color: var(--primaryDark);
}

.sb-nav .sb-chevron {
  color: var(--az-gray-500);
}

.sb-nav .sb-subhead:hover .sb-chevron,
.sb-nav .sb-subhead.sb-subhead-active .sb-chevron {
  color: var(--primaryDark);
}

.sb-nav .sb-dot {
  background: var(--az-gray-400);
}

.sb-nav .sb-child-active .sb-dot {
  background: var(--primaryDark);
}

/* ── Layout: home-section shifts with sidebar ── */
.welcomeScreen {
  position: relative;
  text-align: center;
  margin-top: 20%;
}

.sidebar.panel-open ~ .home-section .contentSection .timlineSection .w-30 {
  left: 66rem;
  transition: all 0.5s ease;
}

.home-section {
  position: relative;
  background: var(--az-white);
  min-height: 100vh;
  top: 0;
  left: var(--sb-rail-width);
  width: calc(100% - var(--sb-rail-width));
  transition:
    left 0.22s cubic-bezier(0.4, 0, 0.2, 1),
    width 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Off-white canvas below the topbar — Figma's "Rectangle 17" (#f9f9f9),
   a separate layer from the topbar's white so the topbar reads as its
   own panel rather than blending into the page background. */
.page-content-canvas {
  background: var(--az-gray-50);
  min-height: calc(100vh - 80px);
  /* Pages that put a gutter <Row> straight in the canvas bleed its -8px
     margins past the right edge, adding a sideways scrollbar at every
     width. clip (not hidden) trims that empty strip without creating a
     scroll container, so sticky headers keep working and nothing shifts. */
  overflow-x: clip;
}

/* Content shifts when sidebar is expanded */
.sidebar.expanded ~ .home-section {
  left: var(--sb-expanded-width);
  width: calc(100% - var(--sb-expanded-width));
}
/* Submenu panel is a fixed overlay — does NOT shift content */

/* Small screens (keep in sync with MOBILE_QUERY in Sidebar.js): the rail
   becomes an off-canvas drawer opened by the burger in .sb-topbrand, and
   content takes the full width. */
@media (max-width: 767px) {
  .home-section,
  .sidebar.expanded ~ .home-section {
    left: 0;
    width: 100%;
  }
  .sidebar {
    transform: translateX(-100%);
    transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: none;
  }
  .sidebar.mobile-open {
    transform: translateX(0);
    box-shadow: 4px 0 24px rgba(var(--az-black-rgb), 0.35);
  }
  .sb-mobile-backdrop {
    position: fixed;
    inset: var(--sb-topbar-height) 0 0 0;
    z-index: 99;
    background: rgba(var(--az-black-rgb), 0.35);
  }
  /* Brand band: burger + logo, sized to its content */
  .sb-topbrand,
  .sb-topbrand.expanded {
    width: auto;
    max-width: 60vw;
    justify-content: flex-start;
    padding: 0 12px;
  }
  .sb-topbrand .sb-brand-box {
    width: auto;
    justify-content: flex-start;
  }
  .sb-topbrand .sb-brand-logo {
    max-height: 36px;
    max-width: 120px;
  }
  /* Topbar content starts after the brand band (burger + logo). Scoped
     under .home-section so it outranks the base .page-topbar rule that
     comes later in this file. The breadcrumb is dropped — the page title
     right below already names the page — so search + help fit. */
  .home-section .page-topbar {
    padding-left: 196px;
    padding-right: 12px;
    flex-wrap: nowrap;
  }
  .home-section .page-topbar .app-bc {
    display: none;
  }
  .home-section .page-topbar .client-chip__label {
    display: none;
  }
  .home-section .page-topbar .client-chip__name {
    max-width: 90px;
  }
  .home-section .page-topbar .page-topbar-right {
    flex-wrap: nowrap;
    gap: 8px;
  }
  .home-section .page-topbar .page-topbar-search {
    min-width: 0;
  }
  /* Submenu drills in over the drawer (rail + panel side by side don't
     fit a phone); its close button steps back to the drawer */
  .sb-submenu {
    left: 0 !important;
    width: min(280px, 85vw);
    z-index: 101;
  }
}

/* SECTION 7: Navbar (sticky top bar, header logo, badges, contentSection padding, pageHeader) */

/* Logo in the header navbar — visible when sidebar is collapsed */
.header-collapsed-logo {
  height: 32px;
  width: auto;
  object-fit: contain;
  margin-right: 8px;
  flex-shrink: 0;
  transition: opacity 0.2s ease;
}

/* Hide the header logo when sidebar is expanded (it shows in the brand row) */
.sidebar.expanded ~ .home-section .header-collapsed-logo {
  display: none;
}

/* ── Home page settings sidebar (in-flow, not fixed) ── */
.home-settings-sidebar {
  position: relative !important;
  left: auto !important;
  height: calc(100vh - 62px);
  flex-shrink: 0;
  overflow-y: auto;
  overflow-x: hidden;
  border-right: 1px solid rgba(var(--az-black-rgb), 0.08);
  z-index: auto !important;
}

.home-settings-sidebar__header {
  height: 44px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  font-size: 12px;
  font-weight: 600;
  color: var(--az-gray-550);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-bottom: 1px solid rgba(var(--az-black-rgb), 0.06);
  background: var(--az-white);
  flex-shrink: 0;
}

.home-settings-sidebar .home-settings-menu.customSideMenu {
  height: calc(100% - 44px) !important;
}

/* ── Mobile Drawer (mobSider) ─────────────────────────────────
   Shared rules for the AntD Drawer used in Header & HomeHeader.
   Width is set via the \`width\` prop on the Drawer component (280px).
──────────────────────────────────────────────────────────── */

/* White bg — replaces old orange (#d18146) */
.mobSider.ant-drawer .ant-drawer-content {
  background: var(--az-white) !important;
}

/* Drawer title bar */
.mobSider .ant-drawer-header {
  padding: 12px 16px !important;
  border-bottom: 1px solid rgba(var(--az-black-rgb), 0.08) !important;
  background: var(--az-white) !important;
}

/* Body: zero padding — menu handles its own spacing */
.mobSider .ant-drawer-body {
  padding: 0 !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Sidebar wrapper inside drawer: in-flow (not fixed) */
.mobSider .sidebar,
.mobSider .sidebar.open {
  display: flex !important;
  flex-direction: column;
  position: relative !important;
  width: 100% !important;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Menu fills the entire drawer body */
.mobSider .customSideMenu {
  height: 100% !important;
  flex: 1 1;
}

/* Icon container — same sizing as .sidebar.open */
.mobSider .ant-menu-item-icon,
.mobSider .ant-menu-submenu-title .ant-menu-item-icon {
  min-width: 20px !important;
  width: 20px !important;
  margin-right: 10px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0 !important;
}

/* menuImg — same as .sidebar.open .menuImg */
.mobSider .menuImg {
  color: var(--primaryDark);
  height: 18px !important;
  width: 18px !important;
  min-width: 18px !important;
  min-height: 18px !important;
  max-width: 18px !important;
  max-height: 18px !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  margin: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  flex-shrink: 0 !important;
}

/* Settings sidebar inside drawer: fill height, no border */
.mobSider .home-settings-sidebar {
  height: 100% !important;
  border-right: none !important;
}

/* Hide the "Platform Settings" label — drawer title already shows it */
.mobSider .home-settings-sidebar__header {
  display: none !important;
}

.mobSider .home-settings-sidebar .home-settings-menu.customSideMenu {
  height: 100% !important;
}

/* Hide Bootstrap's navbar-toggler — sidebar has its own toggle button */
.navbar-toggler {
  display: none !important;
}

.custom-navbar {
  width: 100%;
  height: 62px;
  position: sticky;
  top: 0;
  right: 0;
  padding-top: 0;
  padding-bottom: 0;
  background-color: var(--az-white) !important;
  z-index: 1099;
}

.custom-navbar .navbar-collapse {
  justify-content: end;
}

.badgeSec {
  width: 37px;
  height: 37px;
  background: var(--secondaryLight);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
}

.notBadge .ant-img {
  display: block !important;
}

/* ── App Breadcrumb ────────────────────────────────────────────────────────── */
/* ── Top bar: breadcrumb row + global search ── */
.page-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  row-gap: 10px;
  gap: 16px;
  height: 80px;
  padding: 10px 28px;
  background: var(--az-white);
  border-bottom: 1px solid rgba(var(--az-black-rgb), 0.06);
  /* Stays put while the page scrolls. */
  position: sticky;
  top: 0;
  z-index: 40;
}

/* Client onboarding steps (Organization create → first admin) */
/* Client edit → step 2 while the existing admin is looked up */
.org-admin-loading {
  display: flex;
  justify-content: center;
  padding: 48px 0 !important;
}

/* One line: ① Organization details ── ② Organization admin. Titles never
   wrap (overrides the tablet Steps wrap rule); width fits the content. */
.onboarding-steps.ant-steps {
  width: auto;
  max-width: 100%;
  margin: 0 0 12px;
}
.onboarding-steps.ant-steps .ant-steps-item {
  flex: 0 1 auto !important;
}
.onboarding-steps.ant-steps .ant-steps-item:not(:last-child) {
  min-width: 300px;
}
.onboarding-steps.ant-steps .ant-steps-item-title {
  white-space: nowrap !important;
}

/* Left side of the topbar: client chip (platform CLIENT_VIEW) + breadcrumb */
.page-topbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

/* "Viewing client: <name>" — platform user inside a client. Theme tint so
   it reads as context, not an alert; × returns to Manage Clients. */
.client-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 6px 0 12px;
  border-radius: var(--az-radius-pill, 999px);
  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.1);
  border: 1px solid rgba(var(--colorDark-rgb, 0, 122, 255), 0.25);
  color: var(--colorDark, #007aff);
  white-space: nowrap;
  min-width: 0;
  flex-shrink: 1;
}
.client-chip__icon {
  font-size: 14px;
}
.client-chip__label {
  font-size: 12px;
  color: var(--az-text-secondary);
}
.client-chip__name {
  font-size: 14px;
  font-weight: 600;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.client-chip__exit {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  font-size: 11px;
}
.client-chip__exit:hover {
  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.15);
}

/* flex:1 1 auto (not just margin-left:auto) — without this, the search
   input's flex:1 1 200px below has no spare width to grow into (this
   wrapper just shrink-wraps its content), so it settles near its 200px
   basis instead of growing up to its 372px max-width like Figma. */
.page-topbar-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  flex: 1 1 auto;
  min-width: 0;
}

/* ── Reusable search input — Figma node 42:2958. .page-topbar-search is
   kept as an alias (it's the name already used by AppLayout's top bar);
   use .az-search-input for any new page-level search/filter input so
   there's one visual definition for "search" across the whole app. ── */
.az-search-input,
.page-topbar-search {
  width: 100%;
  max-width: 372px;
  min-width: 160px;
  height: 44px;
  border-radius: var(--az-radius-pill) !important;
  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.08) !important;
  border: none !important;
  box-shadow: none !important;
  flex: 1 1 200px;
}

.az-search-input .ant-input,
.page-topbar-search .ant-input {
  background: transparent !important;
  font-size: 16px !important;
  letter-spacing: -0.32px;
  color: var(--az-text-primary);
}

/* !important here too — antd's own injected placeholder styling otherwise
   wins over the plain font-size set above and the placeholder silently
   renders at antd's default size instead of inheriting this 16px. */
.az-search-input .ant-input::placeholder,
.page-topbar-search .ant-input::placeholder {
  font-size: 16px !important;
  letter-spacing: -0.32px;
  color: var(--az-text-secondary);
}

.az-search-input .ant-input-prefix,
.page-topbar-search .ant-input-prefix {
  margin-inline-end: 8px;
}

.az-search-input .ant-input-prefix .anticon,
.page-topbar-search .ant-input-prefix .anticon {
  font-size: 22px;
  color: var(--az-text-secondary);
}

.need-help-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 44px;
  padding: 0 20px 0 14px;
  border: none;
  border-radius: 70px;
  background: var(--colorDark, #007aff);
  color: var(--az-white);
  font-family: inherit;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: -0.32px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: opacity 0.15s;
}

.need-help-btn .anticon {
  font-size: 24px;
}

.need-help-btn:hover {
  opacity: 0.9;
}

.need-help-popover {
  max-width: 220px;
  font-size: 13px;
  color: rgba(var(--az-black-rgb), 0.7);
}

@media (max-width: 768px) {
  .page-topbar-search {
    min-width: 120px;
    flex: 1 1 120px;
  }

  .need-help-btn .need-help-label {
    display: none;
  }

  .need-help-btn {
    padding: 0 14px;
  }
}

/* ── Shared row-action edit (Pen) button ── */
.az-edit-pen-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px; /* Figma node 42:3067: size-[40px] */
  height: 40px;
  flex-shrink: 0;
  border: none;
  border-radius: var(--az-radius-pill);
  background: var(--colorDark, #007aff);
  cursor: pointer;
  transition: opacity 0.15s;
}
.az-edit-pen-btn:hover {
  opacity: 0.85;
}
.az-edit-pen-btn:disabled {
  background: var(--az-gray-350);
  cursor: not-allowed;
}

/* ── Pill-shaped Select — for filter dropdowns that should match az-btn radius ── */
.az-pill-select .ant-select-selector {
  border-radius: var(--az-radius-pill) !important;
}

/* ── Row view (Eye) button — same pill shape as edit, blue */
.az-eye-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border: none;
  border-radius: var(--az-radius-pill);
  background: var(--colorDark, #007aff);
  color: var(--az-white);
  font-size: 18px;
  cursor: pointer;
  transition: opacity 0.15s;
}
.az-eye-btn:hover {
  opacity: 0.85;
}

/* ── Row delete button — same pill shape as edit, danger red */
.az-delete-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border: none;
  border-radius: var(--az-radius-pill);
  background: var(--az-red-500);
  color: var(--az-white);
  font-size: 18px;
  cursor: pointer;
  transition: opacity 0.15s;
}
.az-delete-btn:hover {
  opacity: 0.85;
}

/* ── Action column button group — aligns edit/delete/dots buttons in a row ── */
.action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
}

/* ── Row "more actions" dots button — Figma node 42:3069: size-[40px] bg-[#ededed] ── */
.az-dots-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border: none;
  border-radius: var(--az-radius-pill);
  background: var(--az-gray-50);
  color: rgba(var(--az-black-rgb), 0.65);
  font-size: 24px;
  cursor: pointer;
  transition: background 0.15s;
}
.az-dots-btn:hover {
  background: var(--az-gray-175);
}

/* ── Row "Hold" toggle button — Figma node 42:3066/43:6118: h-[40px] pill, HandPalm icon ── */
.az-hold-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 40px;
  padding: 0 16px;
  flex-shrink: 0;
  border: none;
  border-radius: var(--az-radius-pill);
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.15s;
}
.az-hold-btn:hover {
  opacity: 0.85;
}
.az-hold-btn--filled {
  background: var(--colorDark, #007aff);
  color: var(--az-white);
}
.az-hold-btn--ghost {
  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.08);
  color: var(--colorDark, #007aff);
}

/* ── Row "more actions" dropdown — Figma node 45:336: white card,
   rounded-16, soft shadow, plain text rows, danger row in red ── */
.az-dots-menu .ant-dropdown-menu {
  padding: 8px;
  border-radius: 16px;
  border: 1px solid var(--az-gray-50);
  box-shadow: 0px 24px 48px -12px rgba(var(--az-black-rgb), 0.18);
  overflow: hidden;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
/* Each item rendered as a ghost pill button — visible before hover */
.az-dots-menu .ant-dropdown-menu-item {
  padding: 9px 18px;
  font-size: 14px;
  font-weight: 500;
  border-radius: var(--az-radius-pill) !important;
  color: var(--colorDark, #007aff);
  background: var(--colorLight, rgba(0, 122, 255, 0.07));
}
.az-dots-menu
  .ant-dropdown-menu-item:not(.ant-dropdown-menu-item-disabled):hover {
  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.13) !important;
  color: var(--colorDark, #007aff);
}
/* Disabled — matches az-btn:disabled (opacity 0.4, cursor not-allowed) */
.az-dots-menu .ant-dropdown-menu-item-disabled,
.az-dots-menu .ant-dropdown-menu-item-disabled:hover {
  opacity: 0.4;
  cursor: not-allowed;
  background: var(--colorLight, rgba(0, 122, 255, 0.07)) !important;
  color: var(--colorDark, #007aff) !important;
}
/* Danger items (Deactivate etc.) */
.az-dots-menu .ant-dropdown-menu-item-danger {
  color: var(--az-red-500) !important;
  background: rgba(var(--az-red-500-rgb), 0.07) !important;
}
.az-dots-menu
  .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover {
  background: rgba(var(--az-red-500-rgb), 0.14) !important;
  color: var(--az-red-500) !important;
}

/* ── Sidebar profile dropdown (Customise Profile / Change Password / Logout) —
   same themed-pill treatment as .az-dots-menu so it follows the org theme
   instead of AntD's untouched default popup styling ── */
.sb-profile-menu .ant-dropdown-menu {
  padding: 8px;
  border-radius: 16px;
  border: 1px solid var(--az-gray-50);
  box-shadow: 0px 24px 48px -12px rgba(var(--az-black-rgb), 0.18);
  overflow: hidden;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.sb-profile-menu .ant-dropdown-menu-item {
  padding: 9px 18px;
  font-size: 14px;
  font-weight: 500;
  border-radius: var(--az-radius-pill) !important;
  color: var(--colorDark, #007aff);
  background: var(--colorLight, rgba(0, 122, 255, 0.07));
}
.sb-profile-menu
  .ant-dropdown-menu-item:not(.ant-dropdown-menu-item-disabled):hover {
  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.13) !important;
  color: var(--colorDark, #007aff);
}
.sb-profile-menu .ant-dropdown-menu-item-danger {
  color: var(--az-red-500) !important;
  background: rgba(var(--az-red-500-rgb), 0.07) !important;
}
.sb-profile-menu
  .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover {
  background: rgba(var(--az-red-500-rgb), 0.14) !important;
  color: var(--az-red-500) !important;
}

/* ── Breadcrumb nav ── */
.app-bc {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 26px;
}

.app-bc-list {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 0;
  overflow: hidden;
}

.app-bc-item {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  /* Smooth expand/collapse via max-width + opacity */
  max-width: 400px;
  opacity: 1;
  overflow: hidden;
  transition:
    max-width 0.28s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.2s ease,
    margin 0.25s ease;
}

.app-bc-item--hidden {
  max-width: 0;
  opacity: 0;
  pointer-events: none;
  margin: 0;
}

.app-bc-sep {
  font-size: 14px;
  color: rgba(var(--az-black-rgb), 0.7);
  letter-spacing: -0.32px;
  -webkit-user-select: none;
          user-select: none;
  flex-shrink: 0;
}

/* Figma node 44:7327 specified 16px — standardized down to 14px, matching
   the rest of the app's text sizing. Every crumb is the same 70% black,
   Regular weight; only the current (last) crumb is SemiBold — no separate
   "link" color. */
.app-bc-label {
  font-size: 16px;
  font-weight: 400;
  color: rgba(var(--az-black-rgb), 0.7);
  letter-spacing: -0.32px;
  cursor: pointer;
  transition: color 0.15s;
  flex-shrink: 0;
}

.app-bc-label:hover {
  color: rgba(var(--az-black-rgb), 0.9);
  text-decoration: underline;
}

.app-bc-label--active {
  color: rgba(var(--az-black-rgb), 0.7);
  cursor: default;
  font-weight: 600;
}

.app-bc-label--active:hover {
  color: rgba(var(--az-black-rgb), 0.7);
  text-decoration: none;
}

/* Toggle chevron button — only visible on mobile */
.app-bc-toggle {
  display: none;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: 1px solid var(--az-gray-300);
  border-radius: 5px;
  background: var(--az-gray-50);
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
  transition: background 0.15s;
}

.app-bc-toggle:hover {
  background: var(--az-gray-135);
  border-color: var(--az-gray-400);
}

.app-bc-chevron {
  font-size: 16px;
  color: var(--az-gray-570);
  line-height: 1;
  display: inline-block;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

@media (max-width: 991px) {
  .app-bc-toggle {
    display: flex;
  }

  .app-bc-list {
    flex-wrap: wrap;
    row-gap: 2px;
  }

  .app-bc-label {
    font-size: 16px;
  }
}

.contentSection {
  padding: 8px 24px;
}

.contentSection .ant-page-header {
  padding: 10px;
  border-radius: 10px;
  margin-bottom: 16px;
}

.pageHeader h2 {
  font-size: 18px !important;
  font-weight: 500 !important;
  margin-bottom: 0 !important;
}

/* Prevent title from shrinking character-by-character in flex pageHeaders */
.pageHeader h1,
.pageHeader h2,
.pageHeader h3,
.pageHeader h4,
.pageHeader h5,
.pageHeader .ant-typography {
  flex-shrink: 0;
  white-space: nowrap;
  margin-bottom: 0;
  min-width: 0;
}

/* ── Page card — white card used on all listing & form pages ── */
.page-card {
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  overflow: visible;
}

/* ── Page card header — title + actions sit in the gray body (outside card) ── */
.page-card-header {
  padding: 0 0 2px 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-bottom: none;
  gap: 12px;
  flex-wrap: wrap;
  min-height: 48px;
}

/* ── Page title + subtitle group (left side of card header) ── */
.page-card-title-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

/* ── Page title inside the header — Figma node 44:6511/42:2999 specified
   34px, standardized down to 28px app-wide. --hero kept as an alias now
   that the base class matches it exactly, so existing usages of either
   class render the same. ── */
.page-card-title,
.page-card-title--hero {
  font-size: 28px;
  font-weight: 500;
  color: rgba(var(--az-black-rgb), 0.9);
  margin: 0;
  line-height: 1.52;
  white-space: normal;
}

.page-title-count {
  font-weight: 400;
  font-size: inherit;
}

/* ── Page subtitle below the title — Figma node 42:3158: 14px Regular, 60% black ── */
.page-card-subtitle,
.page-card-subtitle--hero {
  font-size: 14px;
  font-weight: 400;
  color: rgba(var(--az-black-rgb), 0.6);
  margin: 0;
  line-height: 1.5;
  letter-spacing: -0.28px;
}

/* ── Actions area (search + buttons) ── */
.page-card-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  flex-shrink: 0;
}

/* ── Body content — white card containing only the table ── */
.page-card-body {
  background: var(--az-white);
  border-radius: var(--az-radius-sm);
  box-shadow: var(--az-shadow-card);
  overflow: visible;
  padding: 0;
}

/* ── "Hero" body card — Figma node 42:3006 "Latest Orders": one white
   rounded card wrapping the status cards + filter tags + table together,
   floating on the page's light-gray background. Opt-in modifier so the
   plain .page-card-body used elsewhere is unaffected. ── */
.page-card-body--hero {
  background: var(--az-white);
  border-radius: var(--az-radius-lg);
  box-shadow: var(--az-shadow-card-lg);
  /* padding: var(--az-space-4); */
}

/* Manage Clients (Home/index.js): the org cards (.az-card) already carry
   their own white card background — this page doesn't also want the
   --hero wrapper's white card-behind-the-cards frame, just the light-gray
   page background showing through around them. Opt-in modifier, scoped to
   this one page only — --hero's own base look stays unchanged everywhere
   else it's used (Dashboard, Order Dashboard, etc.). */
.page-card-body--hero-transparent {
  background: none;
  box-shadow: none;
}

/* ── Table variant of the hero card: same 16px radius + shadow as
   --hero, but no padding. A table's own toolbar/cells already provide
   their own spacing, and the edit-columns button is specifically
   designed to sit flush against this card's real border — --hero's
   16px stat-tile padding is what pushed it away from that border.
   Use this (not --hero) when the card wraps a table rather than a
   row of padded stat tiles. ── */
.page-card-body--hero-flush {
  background: var(--az-white);
  border-radius: var(--az-radius-lg);
  box-shadow: var(--az-shadow-card-lg);
  padding: 0;
}

/* ── Form page card: white card for create/edit forms ── */
.page-card-form {
  background: var(--az-white);
  border-radius: var(--az-radius-sm);
  box-shadow: var(--az-shadow-card);
  padding: 24px 28px;
}

/* ── Page-level title rendered outside the card (above form/table cards) ── */
.page-card-section-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--az-slate-900);
  margin: 0 0 14px 0;
  padding-bottom: 0;
  border-bottom: none;
  line-height: 1.3;
}

/* SECTION 8: Accordion / collapse sections */

/* Was background: var(--primaryLight) [10%-opacity accent] + white text —
   nearly unreadable ("blurry") since white-on-10%-tint has almost no
   contrast. Use a solid light surface + dark text, same pairing as
   .page-card-header / .status-card use everywhere else in the system. */
.accordionSection .ant-collapse-header {
  background-color: var(--az-gray-50);
  padding: 13px 20px !important;
  border-top-right-radius: var(--az-radius-md) !important;
  border-top-left-radius: var(--az-radius-md) !important;
  color: var(--az-text-primary) !important;
  font-size: 16px !important;
  font-weight: 600 !important;
}

.accordionSection .ant-collapse-header:hover {
  background-color: var(--az-gray-50);
}

.accordionSection
  .ant-collapse
  > .ant-collapse-item
  > .ant-collapse-header
  .ant-collapse-arrow,
.accordionSection .ant-collapse-header .ant-collapse-expand-icon {
  font-size: 16px !important;
  font-weight: 500 !important;
  float: right;
  margin-top: 8px;
  color: var(--colorDark, #007aff) !important;
}

.accordionSection .ant-collapse-content-box {
  padding: 13px 20px !important;
}

.accordionSection .ant-collapse-borderless > .ant-collapse-item:last-child,
.accordionSection .ant-collapse {
  border-bottom-right-radius: 14px !important;
  border-bottom-left-radius: 14px !important;
  border-color: transparent;
}

.accordionSection
  .ant-col
  .ant-select:not(.ant-select-customize-input)
  .ant-select-selector {
  border-radius: 6px;
  height: 50px;
  padding: 10px 20px;
}

.accordionSection .ant-form-item {
  display: block !important;
}

.accordionSection .ant-form-item-label > label::after {
  display: none !important;
}

/* Form cards (new design — replaces accordionSection wrapper) */
.contentSection
  .ant-form-vertical
  .ant-select:not(.ant-select-customize-input)
  .ant-select-selector {
  border-radius: 6px !important;
  height: 40px !important;
  padding: 5px 16px !important;
}

.contentSection
  .ant-form-vertical
  .ant-select-single
  .ant-select-selector
  .ant-select-selection-item,
.contentSection
  .ant-form-vertical
  .ant-select-single
  .ant-select-selector
  .ant-select-selection-placeholder {
  line-height: 28px !important;
}

.infoAccordion .panelHeader .ant-collapse-content-box {
  padding: 0 !important;
}

/* Was color: var(--primaryTextLight) [white] with no background set —
   invisible white-on-white column-header text once the panel expanded. */
.infoAccordion .panelHeader .ant-collapse-content-box .infoSection .infoHeader {
  padding: 8px 20px !important;
  background: var(--az-gray-50);
  color: var(--az-text-secondary);
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 0.3px;
}

.infoAccordion
  .panelHeader
  .ant-collapse-content-box
  .infoSection
  .infoDetails {
  padding: 20px 20px;
}

.pdng {
  padding: 10px 20px !important;
}

.infoSection .infoHeader .infoTh p,
.infoSection .infoDetails .infoTd p {
  margin-bottom: 0;
  font-weight: 600;
  word-break: break-all;
  padding: 0 5px;
}

.infoTd {
  word-break: break-all;
}

.infoSection .infoDetails .infoTd p {
  padding: 0 10px !important;
}

/* Was color: var(--primaryTextLight) [white] on an unset (white) background
   — invisible text, same bug as .infoHeader above. */
.infoSection .pymntHdr {
  padding: 15px 20px !important;
  background: var(--colorLight, rgba(0, 122, 255, 0.08));
  border-radius: var(--az-radius-sm) var(--az-radius-sm) 0 0;
}

.infoSection .pymntHdr p {
  margin-bottom: 0;
  color: var(--colorDark, #007aff);
  font-weight: 600;
}

.infoTd .ant-switch {
  background-color: var(--secondaryLight) !important;
}

.infoTd .ant-switch-checked {
  background-color: var(--secondaryDark) !important;
}

.even {
  background-color: var(--az-gray-50);
}

.even:last-child {
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
}

.ant-collapse-borderless {
  background-color: var(--az-white) !important;
}

/* SECTION 9: Images & media */

.valueImage {
  width: 60px;
  height: 60px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  margin: 0 auto;
}

.combo {
  padding-top: 3px;
  width: 100px;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  color: white;
  justify-content: center;

  border: 1px solid rgb(var(--az-blush-rgb));
  border-radius: 10px;
}

.comboImg {
  padding: 2px;
}

.valueImage {
  width: 40px;
  height: 40px;
  overflow: hidden;
  display: flex;
  border: 1px solid rgb(var(--az-blush-rgb));
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  margin: 0 auto;
}

.infoDetails .valueImage {
  margin: inherit !important;
}

.editImage {
  width: 80px;
  height: 80px;
  border: 1px solid var(--az-gray-310);
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 15px;
  border-radius: 5px;
  background: var(--az-white);
}

.varImageSec {
  position: relative;
}

.removeIcon {
  all: unset !important;
  position: absolute !important;
  background-color: var(--az-white) !important;
  width: 20px !important;
  height: 20px !important;
  border-radius: 50px !important;
  top: -0.5rem !important;
  right: 0.5rem !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  text-align: center !important;
  box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.1) !important;
  cursor: pointer !important;
}

/* SECTION 10: Action/feedback buttons (Approve, Reject, Hold, Return) */

.btnSection {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.btnSection .feedbackBtn {
  height: 50px;
  color: var(--az-white);
  border: 0;
  border-radius: 6px;
}

.btnSection .feedbackBtn .reject {
  display: flex;
  height: 50px;
  color: var(--primaryDark);
  border: 0;
  border-radius: 6px;
}

.btnSection .feedbackBtn:hover {
  color: var(--primaryTextLight);
  opacity: 0.8;
}

.return {
  background-color: var(--secondaryLight) !important;
}

.hold {
  background-color: var(--primaryLight) !important;
}

.approve {
  background-color: var(--secondaryDark) !important;
}

.reject {
  background-color: var(--primaryDark) !important;
}

/* SECTION 11: Custom card (legacy card system) */

/***** Custom Card Style *****/
.customCard,
.ant-collapse {
  border-radius: 14px !important;
  cursor: pointer;
  box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.1);
}

.customCard {
  border-radius: 8px !important;
  height: 100%;
}
.themeCard {
  transform: translateY(-3px);
  box-shadow:
    rgba(var(--az-navy-shadow-rgb), 0.25) 0px 4px 8px -2px,
    rgba(var(--az-navy-shadow-rgb), 0.08) 0px 0px 0px 1px;
}
.themeCard:hover {
  transform: translateY(-3px);
  /* box-shadow:
    rgba(0, 0, 0, 0.4) 0px 2px 4px,
    rgba(0, 0, 0, 0.3) 0px 7px 13px -3px,
    rgba(0, 0, 0, 0.2) 0px -3px 0px inset; */
  box-shadow: 0 6px 16px rgba(var(--az-black-rgb), 0.08);
}

.customCard .ant-card-body {
  padding: 14px 20px;
  height: 100%;
}

.counterText h4 {
  margin-bottom: 0 !important;
}

.searchBtn {
  display: flex;
  align-items: center;
  background: var(--primaryDark);
  border-color: var(--primaryDark);
  transition: all 0.2s ease-in-out;
}

.searchBtn:hover {
  background: var(--primaryLight) !important;
  border-color: var(--primaryDark) !important;
  box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.1) !important;
}

.dateRange {
  display: flex;
  justify-content: end;
  margin-top: -2.2rem;
  position: absolute !important;
  right: 2.2%;
}

.satrtDate {
  width: 100%;
  border-radius: 5px;
}

.endDate {
  width: 100%;
  border-radius: 5px;
}

.uploadCsv {
  display: flex;
  /* justify-content: end; */
  margin-top: -5rem;
  position: absolute !important;
  right: 50%;
  border-radius: 50px;
  height: 43px !important;
}

.modalCloseBtn {
  background: var(--primaryLight) !important;
  border-color: var(--primaryDark) !important;
  color: var(--primaryDark) !important;
  border-radius: 50px !important;
  margin-right: 15px;
  transition: all 0.3s ease-in-out;
}

.modalCloseBtn:hover {
  background: var(--primaryDark) !important;
  border-color: var(--primaryDark) !important;
  color: var(--primaryTextLight) !important;
}

.modalCloseBtnFilled {
  background: var(--primaryDark) !important;
  border-color: var(--primaryDark) !important;
  color: var(--primaryTextLight) !important;
  border-radius: 50px !important;
  transition: all 0.3s ease-in-out;
}

.modalCloseBtnFilled:hover {
  background: var(--primaryLight) !important;
  border-color: var(--primaryDark) !important;
  color: var(--primaryDark) !important;
}

.innerCon {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.innerSecCard {
  display: flex;
  justify-content: center;
  align-items: center;
}

.innerSecCard .cardHeading h5 {
  font-size: 13px;
  margin-bottom: 0;
}

.innerSecCard .cardImg {
  display: flex;
  align-items: center;
  margin-right: 15px;
}

.pd .ant-card-body {
  padding: 10px 15px;
  display: flex;
  align-items: center;
}

.imgSec {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.imgCir {
  width: 45px;
  height: 45px;
  border-radius: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--secondaryDark);
}

/* .card1Bg {
  background: var(--primaryDark);
}
.card2Bg {
  background: #e6a827;
}
.card3Bg {
  background: #a8bcc1;
}
.card4Bg {
  background: #f44336;
}
.card5Bg {
  background: #5baf25;
} */
.innerCon .textSec .cardHeading h5 {
  font-size: 13px;
}

.innerCon .textSec .counterText h4 {
  font-size: 44px;
  font-weight: 300;
  margin-bottom: 0;
}

.cardActive {
  border: 2px solid var(--primaryDark);
}

.uploadBtn label {
  font-size: 12px !important;
  font-weight: 600;
  color: var(--az-gray-580) !important;
  margin-right: 5px;
}

.uploadBtn label::after {
  display: none;
}

/* .uploadBtn .ant-upload-list-text {
  position: absolute;
  right: 6.3rem;
  top: 2.5rem;
} */
.docUploadBtn {
  background-color: var(--primaryLight) !important;
  border-color: var(--primaryDark) !important;
  border-radius: 32px !important;
  color: var(--primaryTextLight) !important;
  height: 25px !important;
  padding: 2px !important;
  font-size: 11px !important;
  font-weight: 500 !important;
  width: 90px !important;
  display: flex !important;
  /* justify-content: space-between; */
  /* align-items: center; */
}

.upImg {
  background: var(--az-white);
  border-radius: 50%;
  width: 19px;
  height: 19px;
  line-height: 19px;
  margin-right: 15px;
}

.infoText {
  font-size: 10px;
  font-weight: 600;
  color: var(--az-gray-580);
  display: flex;
  align-items: center;
}

/* SECTION 11a: AZ button system — matches Figma; variants primary/ghost/soft/secondary/danger, sizes lg/md(default)/sm */

/* ── Base ─────────────────────────────────────────────── */
.az-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 16px;
  border: none;
  border-radius: var(--az-radius-pill);
  font-size: 13px;
  font-weight: 500; /* Figma node 42:3001/42:3004: font-['Plus_Jakarta_Sans:Medium'] */
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 0.15s,
    opacity 0.15s;
  text-decoration: none;
  outline: none;
  font-family: inherit;
}
.az-btn:focus-visible {
  outline: 2px solid rgba(var(--colorDark-rgb, 0, 122, 255), 0.45);
  outline-offset: 2px;
}

/* ── Sizes ────────────────────────────────────────────── */
/* Figma node 42:3000/42:3003 specified h-[54px]/20px — standardized down to
   44px/16px app-wide (matches .need-help-btn and .az-search-input, which
   were already at this size) since this is the size used by the Filter and
   Download trigger buttons throughout the app. */
.az-btn--xl {
  height: 44px;
  padding: 0 24px;
  font-size: 16px;
  gap: 8px;
}
.az-btn--lg {
  height: 44px;
  padding: 0 24px;
  font-size: 15px;
  gap: 8px;
}
.az-btn--sm {
  /* Figma's table row-action pill (Inspect/List, node 298:244/298:247): h-[40px] */
  height: 40px;
  padding: 0 16px;
  font-size: 14px;
  gap: 4px;
}
.az-btn--icon-only {
  padding: 0;
  width: 36px;
}
.az-btn--icon-only.az-btn--xl {
  width: 44px;
}
.az-btn--icon-only.az-btn--lg {
  width: 44px;
}
.az-btn--icon-only.az-btn--sm {
  width: 40px;
}

/* ── Variants ─────────────────────────────────────────── */
.az-btn--primary {
  background: var(--colorDark, #007aff);
  color: var(--az-white);
}
.az-btn--primary:hover:not(:disabled) {
  background: var(--colorDark, #007aff);
  filter: brightness(0.88);
  color: var(--az-white);
}
.az-btn--primary:not(:hover):not(:disabled) {
  filter: none;
}

.az-btn--ghost {
  background: var(--colorLight, rgba(0, 122, 255, 0.07));
  color: var(--colorDark, #007aff);
}
.az-btn--ghost:hover:not(:disabled) {
  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.13);
  color: var(--colorDark, #007aff);
}

/* White pill button — Figma node 42:3000/42:3003 (Download / Sort & Filters) */
.az-btn--white {
  background: var(--az-white);
  border: 1px solid var(--az-gray-135);
  color: var(--colorDark, #007aff);
}
.az-btn--white:hover:not(:disabled) {
  background: var(--az-gray-50);
  color: var(--colorDark, #007aff);
}

.az-btn--soft {
  background: var(--colorLight, rgba(0, 122, 255, 0.07));
  color: var(--colorDark, #007aff);
}
.az-btn--soft:hover:not(:disabled) {
  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.13);
  color: var(--colorDark, #007aff);
}

.az-btn--secondary {
  background: var(--az-gray-135);
  color: var(--az-gray-600);
}
.az-btn--secondary:hover:not(:disabled) {
  background: var(--az-gray-300);
  color: var(--az-gray-600);
}

.az-btn--danger {
  background: rgba(var(--az-red-600-rgb), 0.08);
  color: var(--az-red-600);
}
.az-btn--danger:hover:not(:disabled) {
  background: rgba(var(--az-red-600-rgb), 0.14);
  color: var(--az-red-600);
}

/* ── Disabled ─────────────────────────────────────────── */
.az-btn:disabled,
.az-btn[disabled] {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Loading is a temporary busy state, not a permanent disabled state —
   keep the button (and its label) fully visible, just swap in the spinner */
.az-btn--loading:disabled,
.az-btn--loading[disabled] {
  opacity: 1;
  cursor: wait;
}

/* ── Loading spinner ──────────────────────────────────── */
.az-btn__spinner {
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: az-btn-spin 0.6s linear infinite;
}
@keyframes az-btn-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Icon wrapper ─────────────────────────────────────── */
.az-btn__icon {
  display: flex;
  align-items: center;
  font-size: 1em;
  line-height: 0;
}

.ant-notification {
  z-index: 9999 !important;
}

/* ── Responsive ───────────────────────────────────────── */
@media (max-width: 768px) {
  .az-btn--lg {
    height: 40px;
    padding: 0 20px;
    font-size: 14px;
  }
}

/* SECTION 11a-2: Pill tabs — reusable segmented tab group (Figma node 42:4182/42:4184), replaces inline-styled tab buttons duplicated across several pages */
.az-pill-tabs {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding-top: 10px;
  margin-left: 10px;
}
.az-pill-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 176px;
  border-radius: 12px;
  padding: 20px 24px;
  border: 1px solid rgba(var(--az-black-rgb), 0.08);
  background: var(--az-gray-50);
  color: rgba(var(--az-black-rgb), 0.6);
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s;
}
.az-pill-tab--active {
  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.1);
  border-color: rgba(var(--colorDark-rgb, 0, 122, 255), 0.4);
  color: rgba(var(--az-black-rgb), 0.9);
}

/* ── Status variant (PillTabs variant="status") — Enquiry/Order Dashboards,
   most Reports pages: these pills are a STATUS filter, not sequential
   stages, so they read as our standard button pill (.az-btn family) rather
   than the Leads-Dashboard stage-card look above. Every pill is sized to
   match the single widest one via JS (see PillTabs.js) instead of each
   auto-sizing to its own label. ── */
.az-pill-tabs--status {
  row-gap: 8px;
}
.az-pill-tab--status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 16px;
  border: none;
  border-radius: var(--az-radius-pill);
  background: var(--az-gray-135);
  color: var(--az-gray-600);
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;
}
.az-pill-tab--status:hover {
  background: var(--az-gray-300);
}
.az-pill-tab--status-active {
  background: var(--colorLight, rgba(0, 122, 255, 0.07));
  color: var(--colorDark, #007aff);
}
.az-pill-tab--status-active:hover {
  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.13);
  color: var(--colorDark, #007aff);
}

/* ── Pill Select — height/border matched to Figma "Select State/District" ── */
.az-pill-select .ant-select-selector {
  border-radius: var(--az-radius-pill) !important;
  height: 37px !important;
  padding: 0 20px !important;
  border-color: var(--az-gray-175) !important;
}
.az-pill-select .ant-select-selection-item,
.az-pill-select .ant-select-selection-placeholder {
  line-height: 35px !important;
  font-size: 14px;
  font-weight: 500;
  color: rgba(var(--az-black-rgb), 0.9);
}

/* SECTION 11b: Status card — reusable stat card for listing pages */

/* Scrollable strip container */
.status-card-strip {
  display: flex;
  gap: 16px; /* Figma: gap-[16px] */
  overflow-x: auto;
  padding-bottom: 6px;
  margin-bottom: 16px;
  position: relative;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.status-card-strip::-webkit-scrollbar {
  display: none;
}

/* Dot connector between cards — Figma node 44:6486 — overlaps the strip's
   16px gap on both sides so it sits centered between the two cards.
   margin-top = step-label-area(26px) + (card-height(108px) - connector-height(9px))/2
   = 26 + 49.5 ≈ 75px — aligns the dot at the card's vertical centre. */
.status-connector {
  flex-shrink: 0;
  align-self: flex-start;
  margin-top: 75px;
  margin-left: -16px;
  margin-right: -16px;
  z-index: 1;
}

/* Edge fades — only rendered when StatusCardStrip's scroll-position check
   says there's actually more content in that direction (--fade-left /
   --fade-right). Without that gating, a static always-on fade visually
   dims the last real card even when it's fully visible and there's
   nothing left to scroll to. */
.status-card-strip-wrap {
  position: relative;
}
.status-card-strip-wrap--fade-right::after,
.status-card-strip-wrap--fade-left::before {
  content: "";
  pointer-events: none;
  position: absolute;
  top: 0;
  width: 48px;
  height: 100%;
  z-index: 2;
}
.status-card-strip-wrap--fade-right::after {
  right: 0;
  background: linear-gradient(
    to right,
    rgba(var(--az-gray-50-rgb), 0),
    var(--az-gray-50)
  );
}
.status-card-strip-wrap--fade-left::before {
  left: 0;
  background: linear-gradient(
    to left,
    rgba(var(--az-gray-50-rgb), 0),
    var(--az-gray-50)
  );
}
/* Inside the white "hero" card the strip fades to white, not page-gray */
.page-card-body--hero .status-card-strip-wrap--fade-right::after {
  background: linear-gradient(
    to right,
    rgba(var(--az-white-rgb), 0),
    var(--az-white)
  );
}
.page-card-body--hero .status-card-strip-wrap--fade-left::before {
  background: linear-gradient(
    to left,
    rgba(var(--az-white-rgb), 0),
    var(--az-white)
  );
}

/* Individual card — Figma: p-[20px] gap-[4px] rounded-[12px] shrink-0 w-[160px] */
.status-card {
  flex-shrink: 0;
  width: 160px; /* Figma: exact fixed width (120px content + 20px pad × 2) */
  overflow: hidden;
  background: var(--az-gray-50);
  border: 1px solid var(--az-border-subtle);
  border-radius: var(--az-radius-md);
  padding: var(--az-space-5);
  display: flex;
  flex-direction: column;
  gap: var(--az-space-1); /* Figma: gap-[4px] */
  cursor: pointer;
  transition: border-color 0.15s;
  -webkit-user-select: none;
          user-select: none;
}
.status-card:hover {
  border-color: rgba(var(--colorDark-rgb, 0, 122, 255), 0.25);
}
.status-card--active {
  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.08);
  border-color: rgba(var(--colorDark-rgb, 0, 122, 255), 0.4);
}

/* Column wrapper — holds the step label ABOVE the card box (outside its
   border/background), matching Figma node 44:6774 where step labels are a
   separate row above the card strip. All wrappers render the step div (even
   empty) so every column is the same height → connector dot aligns exactly
   at card vertical centre. */
.status-card-col {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  margin-left: 10px;
  margin-top: 10px;
}

/* Step label — Figma node 44:6774: 12px Medium, 60% black, always 22px
   tall (min-height) so wrappers stay equal height even without a step. */
.status-card__step {
  min-height: 22px;
  line-height: 22px;
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 500;
  color: rgba(var(--az-black-rgb), 0.6);
  white-space: nowrap;
}
.status-card-col:has(.status-card--active) .status-card__step {
  color: rgba(var(--az-black-rgb), 0.9);
}

/* Label — Figma: h-[34px] flex-col justify-center text-[14px] font-medium text-[rgba(0,0,0,0.6)] leading-[1.2] w-[120px] */
.status-card__label {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 34px; /* Figma: h-[34px] — fixed height holds up to 2 lines */
  overflow: hidden;
  font-size: 14px; /* Figma: text-[14px] */
  font-weight: 500;
  color: var(--az-text-secondary);
  line-height: 1.2; /* Figma: leading-[1.2] */
  overflow-wrap: break-word;
}

/* Count — Figma: text-[20px] font-bold text-[rgba(0,0,0,0.8)] leading-[1.5] */
.status-card__count {
  font-size: 20px;
  font-weight: 700;
  color: rgba(var(--az-black-rgb), 0.8);
  line-height: 1.5; /* Figma: leading-[1.5] */
}
.status-card__count--zero {
  color: var(
    --az-text-tertiary
  ); /* Figma: text-[rgba(0,0,0,0.5)] for zero values */
}
.status-card--active .status-card__count {
  color: var(--az-text-primary);
}
.status-card--active .status-card__label {
  color: rgba(var(--az-black-rgb), 0.7);
}

/* Responsive: on small screens let cards shrink a bit */
@media (max-width: 768px) {
  .status-card {
    width: 130px;
    padding: 14px 16px;
  }
  .status-card__label {
    font-size: 12px;
  }
  .status-card__count {
    font-size: 17px;
  }
}

/* SECTION 11c: Generic card — reusable tile for boxed content that isn't a full page or a stat strip; used for Manage Clients org cards */
/* Sized to match the Product Movement stage tiles (.customTabCard /
   MovementTile — Figma's fixed 203x150 card, see SECTION 11d below) so the
   platform's Manage Clients cards read as the same design-system card
   size, not a bespoke bigger one. */
.az-card {
  position: relative;
  width: 203px;
  height: 150px;
  background: var(--az-white);
  border-radius: var(--az-radius-md);
  border: 1px solid var(--az-gray-300);
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}
.az-card__edit-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: none;
  background: rgba(var(--az-black-rgb), 0.06);
  color: var(--az-gray-630);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  cursor: pointer;
  opacity: 0;
  transition:
    opacity 0.15s,
    background 0.15s,
    color 0.15s;
}
.az-card:hover .az-card__edit-btn {
  opacity: 1;
}
.az-card__edit-btn:hover {
  background: var(--az-card-accent, rgba(0, 0, 0, 0.1));
  color: var(--colorDark, #007aff);
}
/* Set --az-card-accent inline per-instance (theme color) to tint the
   border on hover; falls back to the default gray border if unset. */
.az-card:hover {
  border-color: var(--az-card-accent, var(--az-gray-300));
  box-shadow: 0 4px 16px rgba(var(--az-black-rgb), 0.1);
}
.az-card__accent-bar {
  height: 5px;
  flex-shrink: 0;
}
.az-card__body {
  flex: 1 1;
  min-height: 0;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
}
.az-card__eyebrow {
  font-size: 11px;
  color: var(--az-gray-480);
  margin-bottom: 10px;
  letter-spacing: 0.3px;
}
.az-card__header {
  display: flex;
  align-items: center;
  gap: var(--az-space-3);
}
.az-card__avatar {
  width: 38px;
  height: 38px;
  border-radius: var(--az-radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
}
.az-card__title {
  font-weight: 700;
  font-size: 14px;
  color: var(--az-gray-950);
  line-height: 1.3;
  flex: 1 1;
  min-width: 0;
}
.az-card__meta {
  font-size: 12px;
  color: var(--az-gray-630);
  line-height: 1.8;
}

/* SECTION 11d: Movement group — reusable stage-group tile used on Material/Money Movement, replaces old ad-hoc inline-bordered boxes */
/* Grouping container — a light gray background + border box around a
   set of stage tiles (e.g. "INBOUND", "CURRENTLY WITH DPP"), shared by
   Material Movement and Money Movement. Not a clickable tile itself
   (no cursor/hover) — just a visual section boundary; the individual
   .customTabCard tiles inside carry their own border on top of this. */
.az-movement-group {
  height: 100%;
  background: var(--az-gray-50);
  border: 1px solid var(--az-border-subtle);
  border-radius: var(--az-radius-md);
  padding: var(--az-space-3);
}
.az-movement-group__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--az-space-2);
  padding: var(--az-space-2) var(--az-space-1);
  margin-bottom: var(--az-space-2);
}
.az-movement-group__title {
  display: flex;
  align-items: center;
  gap: var(--az-space-2);
  font-size: 15px;
  font-weight: 700;
  color: var(--az-text-primary);
  text-transform: uppercase;
  letter-spacing: 0.2px;
}
.az-movement-group__totals {
  display: flex;
  flex-wrap: wrap;
  gap: var(--az-space-4);
  font-size: 13px;
  color: var(--az-text-secondary);
}
.az-movement-group__totals b {
  color: var(--az-text-primary);
  font-weight: 700;
}
/* Centered variant for groups whose tiles stack vertically with a plain
   centered title (e.g. "CURRENTLY WITH DPP") instead of a title+totals bar */
.az-movement-group__title--center {
  display: block;
  text-align: center;
  margin-bottom: var(--az-space-2);
}
.az-movement-group__tiles {
  display: flex;
  flex-direction: column;
  /* Matches Material Movement's measured Figma stacked-tile gap (18px, see
     .material-movement-grid/colStack) so both pages read as one design. */
  gap: 18px;
  align-items: stretch;
}
.az-movement-group__tiles--row {
  flex-direction: row;
  justify-content: center;
  flex-wrap: wrap;
}

/* Plays every time the tile grid panel mounts — i.e. every time you go
   "back" from the compact bar — mirroring the bar's own enter animation
   so the whole toggle reads as one continuous expand/collapse motion.
   Scoped to its own class (not added to .page-card-body--hero directly)
   since that class is shared with many unrelated pages. */
@keyframes az-dashboard-panel-enter {
  from {
    opacity: 0;
    transform: translateY(-12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.az-dashboard-panel-enter {
  animation: az-dashboard-panel-enter 0.28s ease-out;
}

/* SECTION 11d-2: Dashboard grid — shared by Material/Money Movement; fixed-width tracks (not 1fr) keep every card-to-card gap pixel-identical to Figma at any screen width — a bigger screen just centers the same layout with more margin instead of stretching columns around cards that stay a fixed 203px */
.az-movement-dashboard-grid {
  display: grid;
  /* auto-fit + a fixed 203px track (not minmax(...,1fr)) keeps every
     column at exactly its Figma width — never smaller (so cards don't get
     squeezed) and never bigger either. A 1fr track would grow to fill
     whatever extra width the panel has, but the 203px-capped tile inside
     it wouldn't grow with it, leaving uneven dead space around whichever
     cards happened to sit in a stretched column. Fixed tracks avoid that
     entirely: the whole row is always the same size, on any screen. */
  grid-template-columns: repeat(auto-fit, 203px);
  gap: 60px;
  /* stretch (not start): the divider between groups (::before below) is
     anchored to top:0/bottom:0 of its own grid item, so that item needs to
     span the full row height — with align-items:start each item only sizes
     to its own content, and a shorter neighboring column's divider stops
     short of the taller column beside it instead of reaching the bottom. */
  align-items: stretch;
  /* On a wide/monitor screen the fixed-width row no longer reaches the
     panel's edges — center it so the leftover space becomes balanced
     margin on both sides instead of one lopsided gap on the right. */
  justify-content: center;
}
.az-movement-dashboard-grid__group {
  position: relative;
  min-width: 203px;
  grid-column: span 1;
  /* Material Movement always gives this exactly one child (its own
     internal colStack/gap wrapper), so this has no effect there — it
     exists so Money Movement/FC Collection Ledger's two direct children
     (plain header tile, then the .az-movement-group__tiles stack) get the
     same 18px base gap as Material Movement's tile columns, instead of
     touching with 0px. */
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.az-movement-dashboard-grid__group--wide {
  grid-column: span 2;
}
/* Money Movement's "CURRENTLY WITH FC" group (COLLECTED AT FC + PENDING
   REVIEW) — on a bigger-than-current screen this widens to a 2-column
   group and lays its two tiles side by side, the same way Material
   Movement's "STOCK AT FC" group already does. Scoped to just this one
   group/class pair (not a blanket rule on every group) because that's
   what the design calls for here — e.g. "CURRENTLY WITH DPP"'s pair stays
   stacked at any width. Below the breakpoint both classes are no-ops, so
   the group renders exactly as it does everywhere else. */
@media (min-width: 1600px) {
  .az-movement-dashboard-grid__group--wide-lg {
    grid-column: span 2;
  }
  .az-movement-group__tiles--wide-lg {
    flex-direction: row;
    flex-wrap: wrap;
  }
}
/* Divider line between groups — Figma's rotated Line/Line8 nodes */
.az-movement-dashboard-grid__group:not(:first-child)::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: -31px;
  width: 1px;
  background: var(--az-border-subtle);
}
/* Below this width even two 203px columns plus the 60px gutter no longer
   fit alongside the divider's own offset, so once auto-fit is down to a
   single column, collapse the "wide" span and drop the divider (it would
   otherwise draw a line above every wrapped row instead of just between
   side-by-side groups). */
@media (max-width: 640px) {
  .az-movement-dashboard-grid__group--wide {
    grid-column: span 1;
  }
  .az-movement-dashboard-grid__group:not(:first-child)::before {
    display: none;
  }
}

/* Deposit Approval always has exactly 2 "--wide" (span 2) groups. A fixed
   grid-template-columns track (e.g. repeat(4, 203px)) fixes the auto-fit
   column-count ambiguity (see below) but doesn't shrink — at any effective
   viewport narrower than ~1200px (a smaller window, or browser zoom above
   ~125%) the fixed 992px-wide track overflows the panel instead of
   reflowing. Flex does what grid can't here: flex-wrap lets the Online
   group drop below Cash once there's no room for both, and
   justify-content:center (inherited from .az-movement-dashboard-grid)
   applies per wrapped line, so each group re-centers on its own row
   instead of leaving the whole pair pinned to one side — exactly the
   graceful degradation a fixed column count can't provide. Each group's
   own width comes from its content (2×203px cards + 18px gap = 424px via
   .az-movement-group__tiles--row's existing centering), so this needs no
   width math of its own, unlike the grid-column-span approach. */
.az-movement-dashboard-grid--fixed-4 {
  display: flex;
  flex-wrap: wrap;
}
/* Each section grows to claim an equal half of the available width (not
   just its own 424px content width) so its 2 cards center within that
   whole half — Cash centered in the left half, Online centered in the
   right half — instead of the pair clustering together in the middle of
   a much wider panel with dead space on both outer edges. min-width keeps
   a section from being squeezed smaller than its 2 cards actually need;
   below that, flex-wrap (above) drops it to its own full-width row instead,
   where it then centers across the whole row exactly the same way. */
.az-movement-dashboard-grid--fixed-4 .az-movement-dashboard-grid__group--wide {
  flex: 1 1 424px;
  min-width: 424px;
}
/* Phones: the content column is narrower than 2 cards (424px) — let the
   group shrink so its tiles row (already flex-wrap) stacks the cards
   instead of overflowing the page sideways. */
@media (max-width: 640px) {
  .az-movement-dashboard-grid--fixed-4 .az-movement-dashboard-grid__group--wide {
    min-width: 0;
    flex-basis: 100%;
  }
}
/* Container query (not a viewport media query) because the actual wrap
   point depends on this element's own rendered width — which shifts with
   sidebar collapsed/expanded state and isn't reliably tied to viewport
   width alone. Threshold matches the two groups' combined natural width
   (2 × 424px + 60px gap = 908px): below it Online has already wrapped
   under Cash, so the vertical divider (meant to sit between side-by-side
   groups) would draw a stray line to the left of the wrapped row instead. */
.az-movement-dashboard-grid--fixed-4 {
  container-type: inline-size;
}
@container (max-width: 907px) {
  .az-movement-dashboard-grid--fixed-4
    .az-movement-dashboard-grid__group:not(:first-child)::before {
    display: none;
  }
}

/* SECTION 11d-3: Flow toggle — "Forward Flow"/"Reverse Flow" segmented pill switch (Figma node 298:328) */
.az-flow-toggle {
  display: inline-flex;
  align-items: center;
  background: var(--az-white);
  border: 1px solid var(--az-gray-135);
  border-radius: var(--az-radius-pill);
  height: 44px;
  padding: 6px;
  gap: 0;
}
.az-flow-toggle__option {
  width: 155px;
  height: 36px;
  border: none;
  border-radius: var(--az-radius-pill);
  background: transparent;
  color: var(--colorDark, #007aff);
  font-family: inherit;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;
}
.az-flow-toggle__option--active {
  background: var(--colorDark, #007aff);
  color: var(--az-white);
}
@media (max-width: 480px) {
  .az-flow-toggle__option {
    width: auto;
    padding: 0 16px;
  }
}

/* SECTION 11e: Flow summary bar — collapsible totals bar for Material Movement (Figma node 298:161/298:177) */
/* Simple fade-in for bars that don't have a FLIP origin to animate from
   (the COMPLETE FLOW bar). The per-stage compact bar skips this — its
   caller (material-movement/index.js) drives its entrance via a JS FLIP
   transform instead, so it needs a bare mount with no competing CSS
   animation on the same \`transform\` property. */
@keyframes az-flow-summary-bar-enter {
  from {
    opacity: 0;
    transform: translateY(-10px) scaleY(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scaleY(1);
  }
}
.az-flow-summary-bar--fade-in {
  animation: az-flow-summary-bar-enter 0.25s ease-out;
}
.az-flow-summary-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--az-space-4);
  background: var(--az-white);
  border-radius: 16px;
  box-shadow: 0 0 5px rgba(var(--az-shadow-tint-rgb), 0.03);
  padding: 20px;
  margin-bottom: 5px;
  cursor: pointer;
  transition: box-shadow 0.15s;
  transform-origin: top center;
}
.az-flow-summary-bar:hover {
  box-shadow: 0 0 8px rgba(var(--az-shadow-tint-rgb), 0.1);
}
.az-flow-summary-bar:focus-visible {
  outline: 2px solid rgba(var(--colorDark-rgb, 0, 122, 255), 0.45);
  outline-offset: 2px;
}
/* Static/non-interactive bar (COMPLETE FLOW) — no click affordance */
.az-flow-summary-bar--static {
  cursor: default;
}
.az-flow-summary-bar--static:hover {
  box-shadow: 0 0 5px rgba(var(--az-shadow-tint-rgb), 0.03);
}
.az-flow-summary-bar__title {
  display: flex;
  align-items: center;
  gap: var(--az-space-2);
  font-size: 16px;
  font-weight: 700;
  color: var(--az-text-secondary);
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  /* Fixed (not min-) so the stats column starts at the same x-position in
     every bar regardless of title length — otherwise "Total SKUs"/"Value"
     visibly jumps left/right between the summary bar and the compact
     per-stage bar stacked right below it. 350px comfortably fits the
     longest title actually in use ("PENDING ONLINE DEPOSIT CONFIRMATION",
     ~338px at this weight/size) with headroom; overflow+ellipsis is just a
     safety net so a future longer title truncates instead of breaking the
     shared alignment. */
  width: 350px;
  flex-shrink: 0;
}
.az-flow-summary-bar__stats {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-left: 16px;
  margin-right: auto;
}
.az-flow-summary-bar__stat-group {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 16px;
  white-space: nowrap;
}
.az-flow-summary-bar__stat-label {
  color: var(--az-text-secondary);
  font-weight: 500;
}
.az-flow-summary-bar__stat-value {
  color: var(--az-text-primary);
  font-weight: 700;
}
.az-flow-summary-bar__dot {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: var(--az-border-subtle);
  flex-shrink: 0;
}
.az-flow-summary-bar__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  color: var(--az-text-primary);
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
}
/* Below ~991px the fixed 350px title left too little room for the stats
   group, forcing each stat onto its own line inside that leftover sliver
   instead of sitting in a row. Let the bar wrap onto two rows instead —
   title on top, stats filling their own full-width row underneath, still
   wrapping internally if the viewport is narrower still. */
@media (max-width: 991px) {
  .az-flow-summary-bar {
    flex-wrap: wrap;
    row-gap: 8px;
  }
  .az-flow-summary-bar__title {
    width: auto;
    flex-shrink: 1;
  }
  .az-flow-summary-bar__stats {
    width: 100%;
    margin-left: 0;
    margin-right: 0;
  }
}

/* SECTION 12: Tables. Legacy raw-<Table> systems (stateCustomTable, deltaTable, lotCustomTable, nastedTable, punch-order-table, custom-table) were removed — every table now renders through CustomTable/.az-table (SECTION 11a); keeping them risked a page re-attaching one and fighting .az-table's styling via matching-specificity !important rules, which happened before. */

.ant-notification {
  z-index: 9999 !important; /* Ensure the z-index is high enough */
}

.wareHouseCard:nth-child(even) {
  background-color: var(--az-gray-175) !important;
  border-bottom: 1px solid var(--primaryDark);
}

.wareHouseCard:nth-child(odd) {
  border-bottom: 1px solid var(--primaryDark);
}

.wareHouseCard:last-child {
  border-bottom: 0 !important;
}

.textTransform table > tbody > tr td:first-child {
  text-transform: capitalize;
}

.textTransform table > tbody > tr td:nth-child(2) {
  text-transform: uppercase;
}

/* SECTION 13: Custom side menu (AntD Menu) */

/***** Custom Side Menu *****/
.customSideMenu {
  background: transparent !important;
  overflow-y: auto;
  height: calc(100% - 70px);
  padding-right: 0 !important;
  margin-right: 0 !important;
  width: 100% !important;
}

.customSideMenu.ant-menu-vertical {
  border-right: 0;
}

.customSubMenu {
  border-bottom: 1px solid rgba(var(--az-black-rgb), 0.08);
  color: var(--az-gray-950);
}

.ant-menu-item {
  border-bottom: 1px solid rgba(var(--az-black-rgb), 0.08);
  color: var(--az-gray-950);
}

/* .ant-menu-vertical > .ant-menu-item .ant-menu-title-content a:hover {
  color: #954c0a !important;
} */
.customSideMenu .ant-menu-sub.ant-menu-inline {
  background: transparent !important;
  padding-right: 0 !important;
  margin-right: 0 !important;
  width: 100% !important;
}

.customSideMenu .ant-menu-sub.ant-menu-inline .ant-menu-item {
  height: 38px !important;
  line-height: 38px !important;
  width: 100% !important;
  padding-right: 12px !important;
  margin: 0 !important;
  display: flex !important;
  align-items: center !important;
  border-bottom: none !important;
}

/* Depth-2 pages: align text with 2nd letter of submodule title (submodule at 52px + ~8px) */
.customSideMenu
  .ant-menu-sub.ant-menu-inline
  .ant-menu-sub.ant-menu-inline
  .ant-menu-item {
  padding-left: 60px !important;
}

/* Sub-item title content — block so overflow clips text cleanly */
.customSideMenu
  .ant-menu-sub.ant-menu-inline
  .ant-menu-item
  .ant-menu-title-content {
  display: block !important;
  width: 100% !important;
  max-width: 100% !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  padding-left: 7px !important;
}

/* Sub-item links */
.customSideMenu .ant-menu-sub.ant-menu-inline .ant-menu-item a {
  color: var(--az-gray-650) !important;
  font-size: 12px !important;
  font-weight: 400;
  display: block !important; /* block so text-overflow works */
  width: 100%;
  max-width: 100%;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  line-height: 38px;
}

/* Span inside sub-item link also needs overflow control */
.customSideMenu .ant-menu-sub.ant-menu-inline .ant-menu-item a span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

/* Inactive sub-item text — force #444 to prevent orange inheritance */
.customSideMenu .ant-menu-sub .ant-menu-item:not(.ant-menu-item-selected) a,
.customSideMenu
  .ant-menu-sub
  .ant-menu-item:not(.ant-menu-item-selected)
  a
  span {
  color: var(--az-gray-650) !important;
}

/* ── Depth-1 submodule titles: align text with 2nd letter of module title
   Module text starts at ~44px (14px pad + 20px icon + 10px gap); +8px = 52px  */
.customSideMenu
  .ant-menu-sub.ant-menu-inline
  > .ant-menu-submenu
  > .ant-menu-submenu-title {
  padding-left: 52px !important;
}

/* ── Depth-1 direct pages (Finance, Reports — directPages with no submodule) ──
   Same indent as submodule titles so they align with module text's 2nd letter. */
.customSideMenu .ant-menu-sub.ant-menu-inline > .ant-menu-item {
  height: 42px !important;
  line-height: 42px !important;
  padding-left: 52px !important;
}

.customSideMenu .ant-menu-sub.ant-menu-inline > .ant-menu-item a {
  color: var(--az-gray-750) !important;
  font-size: 13px !important;
  font-weight: 500;
  line-height: 42px;
}

.customSideMenu .ant-menu-sub.ant-menu-inline > .ant-menu-item a span {
  font-size: 13px !important;
  font-weight: 500;
}

/* Inactive depth-1 direct page — keep #333 (overrides the #444 rule above) */
.customSideMenu
  .ant-menu-sub.ant-menu-inline
  > .ant-menu-item:not(.ant-menu-item-selected)
  a,
.customSideMenu
  .ant-menu-sub.ant-menu-inline
  > .ant-menu-item:not(.ant-menu-item-selected)
  a
  span {
  /* color: black !important; */
}

.customSideMenu .ant-menu-sub.ant-menu-inline .ant-menu-item a:hover,
.customSideMenu .ant-menu-sub .ant-menu-item:hover a,
.customSideMenu .ant-menu-sub .ant-menu-item:hover a span {
  color: var(--primaryDark) !important;
}

.customSideMenu .ant-menu-title-content,
.customSideMenu .ant-menu-title-content a {
  /* color: #bla !important; */
  font-size: 13px !important;
  font-weight: 500;
  text-align: left !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  line-height: 1.3 !important;
  flex: 1 1;
  min-width: 0;
  padding: 0 !important;
}

.customSideMenu > .ant-menu-submenu {
  padding-bottom: 0px !important;
  /* padding-left: -10px !important; */
}

.customSideMenu > .ant-menu-submenu > .ant-menu-submenu-title,
.customSubMenu > .ant-menu-submenu > .ant-menu-submenu-title,
.customSideMenu > .ant-menu-item {
  height: 46px !important;
  line-height: 46px !important;
  padding: 0 12px 0 14px !important;
  display: flex !important;
  align-items: center !important;
  width: 100% !important;
  margin: 0 !important;
  border-radius: 0 !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  box-sizing: border-box !important;
}
/* .customSubMenu > .ant-menu-submenu-title {
  display: flex;
  align-items: start;
  justify-content: space-between;
  margin: 0 !important;
  width: 100% !important;
  padding-right: 0 !important;
} */

.customSubMenu > .ant-menu-submenu-title .ant-menu-item-icon + span {
  margin-left: 0px !important;
}

.customSideMenu > .ant-menu-submenu > .ant-menu-submenu-title:hover,
.customSideMenu > .ant-menu-item:hover {
  border-radius: 0 !important;
  transition: background 0.2s ease;
  width: 100% !important;
  margin: 0 !important;
  padding: 0 12px 0 14px !important;
  height: 46px !important;
  display: flex !important;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
}

/* ================= POPUP SUBMENU FIX ================= */

/* Popup submenu title layout */
.ant-menu-submenu-popup .ant-menu-submenu-title {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: space-between !important;
  text-align: left !important;
}

/* Popup submenu text */
.ant-menu-submenu-popup .ant-menu-title-content {
  text-align: left !important;
  width: auto !important;
  display: inline-flex !important;
  margin-right: 10px;
  padding-right: 30px;
}

/* Gap between icon and label is handled by icon margin-right in .sidebar.open .ant-menu-item-icon */
.ant-menu-item .ant-menu-item-icon + span,
.ant-menu-submenu-title .ant-menu-item-icon + span {
  margin-left: 0 !important;
}

.customSideMenu.ant-menu-inline {
  border-right: 0 !important;
  padding-right: 0 !important;
  margin-right: 0 !important;
}

.customSideMenu.ant-menu-inline .ant-menu-item {
  width: 100% !important;
}

.sidebar .customSideMenu > li > div > .ant-menu-submenu-arrow {
  opacity: 0;
  color: rgba(var(--az-black-rgb), 0.4);
}

.sidebar.open .customSideMenu > li > div > .ant-menu-submenu-arrow {
  opacity: 1;
  color: rgba(var(--az-black-rgb), 0.45);
  transition: all 0.1s;
}

/* sub sub menu selected*/
.ant-menu:not(.ant-menu-horizontal) .ant-menu-item-selected {
  background-color: var(--primaryDark) !important;
  border-radius: 0px !important;
  width: 100% !important;
  margin-right: 0 !important;
  margin-bottom: 0 !important;
}

/* Sub-page selected overrides the broad rule above — warm-white bg + dark text */
.customSideMenu .ant-menu-sub .ant-menu-item-selected {
  background-color: transparent !important;
  border-left: 3px solid var(--primaryDark) !important;
}

.ant-menu-inline .ant-menu-item::after {
  display: none !important;
}

.ant-menu-inline {
  height: calc(100% - 80px);
  overflow-y: auto;
  overflow-x: hidden;
}
/* ant all sub menu */
.ant-menu-submenu-popup .ant-menu-sub {
  background-color: var(--primaryLight) !important;
  padding-right: 10px;
  gap: 10px;
}

.ant-menu-submenu-popup .ant-menu-sub .ant-menu-item {
  border-bottom: 1px solid var(--az-gray-50);
  margin: 0 !important;
  width: 100% !important;
  padding-right: 0 !important;
  text-align: start !important;
}

.ant-menu-submenu-popup .ant-menu-sub .ant-menu-item:last-child {
  border-bottom: 0;
}

.ant-menu-submenu-popup .ant-menu-sub .ant-menu-item .ant-menu-title-content a {
  color: var(--az-white);
  display: flex !important;
  justify-content: start !important;
  align-items: center !important;
  text-align: start !important;
  width: 100% !important;
  padding-right: 10px;
}
.ant-menu-item,
.ant-menu-submenu-title,
.ant-menu-submenu-popup .ant-menu-item {
  color: var(--az-white) !important;
}

/* Hover */
.ant-menu-item:hover,
.ant-menu-submenu-title:hover,
.ant-menu-submenu-popup .ant-menu-item:hover {
  color: var(--az-white) !important;
}

/* Selected menu item background */
.ant-menu-item-selected {
  background-color: var(--primaryDark) !important;
  color: var(--az-white) !important;
}

/* Selected submenu item */
.ant-menu-submenu-popup .ant-menu-item-selected {
  background-color: var(--primaryDark) !important;
  color: var(--az-white) !important;
}

/* Remove AntD default blue indicator */
.ant-menu-item-selected::after {
  border-right: none !important;
}

/* SECTION 14: Timeline section */

.timlineSection {
  width: 100%;
  height: 100%;
  position: relative;
  transition: all 0.5s ease;
}

.timlineSection .timelineSectionInner {
  position: fixed;
  right: 0;
  height: 100%;
  transition: all 0.5s ease;
}

.timlineSection .timelineHeader {
  background: var(--az-gray-350);
  padding: 13px 31px;
  border-top-left-radius: 14px;
}

.timlineSection .timelineHeader h5 {
  margin-bottom: 0 !important;
}

.timlineSection .timelineBody {
  padding: 30px;
  height: calc(90% - 100px);
  overflow-y: scroll;
}

.timlineSection
  .timelineBody
  .ant-timeline
  .ant-timeline-item
  .ant-timeline-item-head-blue {
  color: var(--az-slate-300) !important;
  border-color: var(--az-slate-300) !important;
  background: var(--az-slate-300) !important;
}

.timlineSection
  .timelineBody
  .ant-timeline
  .ant-timeline-item
  .ant-timeline-item-content
  span {
  background: var(--az-gray-135);
  width: auto;
  display: inline-block;
  color: var(--az-black);
  padding: 5px;
  border-top-right-radius: 16px;
  border-bottom-right-radius: 16px;
}

.subTimeline {
  position: relative;
  top: 1rem;
  right: 1.5rem;
  padding-bottom: 0;
}

.subTimeline .ant-timeline-item .ant-timeline-item-head-blue {
  width: 6px !important;
  height: 6px !important;
}

.subTimeline .ant-timeline-item-last .ant-timeline-item-content {
  min-height: 0 !important;
}

.subTimeline .ant-timeline-item .ant-timeline-item-tail {
  display: none !important;
}

.subTimeline .ant-timeline-item .ant-timeline-item-content span {
  background: transparent !important;
  opacity: 0.2;
  font-style: italic;
}

.ant-timeline-item-last > .ant-timeline-item-tail {
  display: block !important;
}

/* SECTION 15: Variant / product page */

/***** Varint page Style *****/
.greenText {
  color: var(--secondaryDark) !important;
}

.uploadText {
  color: var(--az-gray-630) !important;
  font-weight: 500;
}

.h-100 {
  height: auto !important;
}

.addProductForm .ant-form-item {
  margin-bottom: 10px;
}

.borderRadius textarea,
.borderRadius Select {
  border-radius: 6px !important;
  border: 1px solid var(--az-gray-310) !important;
}

.dragBox .ant-upload-drag {
  border: 2px dashed var(--az-gray-450) !important;

  border-radius: 6px !important;

  padding: 10px;
}

.dragBox .ant-form-item-control-input-content > p + span {
  display: flex;

  justify-content: space-between;
}

/* .dragBox .ant-form-item-control-input-content > p + span .ant-upload-drag,

.dragBox .ant-form-item-control-input-content > p + span .ant-upload-list-text {

  width: 50%;

  padding: 0 15px;

} */

.dragBox .ant-upload.ant-upload-drag p.ant-upload-text {
  font-size: 14px;
}

.infoTexts {
  display: flex;
  align-items: center;
  padding: 10px 0;
}

.infoTexts .anticon {
  font-size: 24px;
  color: var(--az-gray-450);
  margin-right: 10px;
}

.addBtnMore {
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
  color: var(--az-gray-580) !important;
}

.addMoreBtn {
  font-size: 18px;
  color: var(--secondaryDark);
  margin-right: 10px;
}

.addProductForm
  .ant-col
  .ant-select:not(.ant-select-customize-input)
  .ant-select-selector {
  border: 1px solid var(--az-gray-310);
  border-radius: 6px;
  height: 50px;
  padding: 10px 20px;
}

span.srch-icon {
  padding: 5px 8px;
  border: 1px solid var(--az-gray-350);
  border-left: 0 !important;
  background: var(--az-white);
  border-radius: 2px;
}

.ant-table-filter-trigger {
  color: var(--az-white) !important;
  font-size: 16px !important;
}

.underLogoText {
  /* margin-top: 18px; */
  font-size: 20px;
  font-weight: 500;
  color: var(--az-black);
}

.site-form-item-icon .ant-input-prefix {
  color: var(--az-gray-580) !important;
}

.ant-input-affix-wrapper:not(.ant-input-affix-wrapper-disabled):hover {
  border-color: var(--primaryDark) !important;
}

.ant-input-affix-wrapper:focus,
.ant-input-affix-wrapper-focused {
  box-shadow: none !important;
}

/* Open (expanded) alone — NO highlight, just layout */
.ant-menu-submenu-open .ant-menu-submenu-title {
  border-radius: 0 !important;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
}

/* Open + selected — depth-0 module: darkest shade */
.customSideMenu
  > .ant-menu-submenu.ant-menu-submenu-open.ant-menu-submenu-selected
  > .ant-menu-submenu-title {
  background-color: var(--primaryDark) !important;
  color: var(--az-white) !important;
}
/* Open + selected — depth-1 submodule: mid-shade for visual hierarchy */
.customSideMenu
  .ant-menu-sub
  > .ant-menu-submenu.ant-menu-submenu-open.ant-menu-submenu-selected
  > .ant-menu-submenu-title {
  background-color: var(--primaryLight) !important;
  color: var(--az-white) !important;
}

/* .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn,
.ant-tabs-tab:hover {
  color: #5baf24 !important;
  text-shadow: none !important;
} */

/* AntD v5 menu active + hover fix */

/* DEFAULT menu text — sidebar inline items use dark text (white bg) */
.customSideMenu .ant-menu-item,
.customSideMenu .ant-menu-submenu-title {
  color: var(--az-gray-950) !important;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
}

/* Popup (collapsed sidebar flyout) stays white text on dark bg */
.ant-menu-submenu-popup .ant-menu-item {
  color: var(--az-white) !important;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
}
.sidebar-menu-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
}

/* HOVER (non-selected only) — module headers use dark bg + white text */
.ant-menu-item:not(.ant-menu-item-selected):hover,
.ant-menu-submenu-title:hover,
.ant-menu-submenu-popup .ant-menu-item:not(.ant-menu-item-selected):hover {
  background-color: var(--primaryDark) !important;
  color: var(--az-white) !important;
}

/* Sub-page hover overrides above — light tint + primaryDark text */
.customSideMenu
  .ant-menu-sub
  .ant-menu-item:not(.ant-menu-item-selected):hover {
  background-color: var(--primaryLight) !important;
  color: var(--primaryDark) !important;
}
.customSideMenu
  .ant-menu-sub
  .ant-menu-item:not(.ant-menu-item-selected):hover
  a,
.customSideMenu
  .ant-menu-sub
  .ant-menu-item:not(.ant-menu-item-selected):hover
  a
  span {
  color: var(--primaryDark) !important;
}

/* Open + selected submenu title (both depths) → white text */
.customSideMenu
  > .ant-menu-submenu.ant-menu-submenu-open.ant-menu-submenu-selected
  > .ant-menu-submenu-title,
.customSideMenu
  .ant-menu-sub
  > .ant-menu-submenu.ant-menu-submenu-open.ant-menu-submenu-selected
  > .ant-menu-submenu-title {
  color: var(--az-white) !important;
}

/* SELECTED item (ALWAYS visible) */
.ant-menu-item-selected {
  color: var(--az-white) !important;
  background-color: var(--primaryDark) !important;
  width: 100% !important;
  margin-right: 0 !important;
  margin-bottom: 0 !important;
}

/* Sub-page selected overrides above — warm-white bg + dark bold text */
.customSideMenu .ant-menu-sub .ant-menu-item-selected {
  background-color: transparent !important;
  color: var(--primaryDark) !important;
}

/* PARENT submenu when child selected — depth-0 module: darkest shade */
.customSideMenu
  > .ant-menu-submenu.ant-menu-submenu-selected
  > .ant-menu-submenu-title {
  color: var(--az-white) !important;
  background-color: var(--primaryDark) !important;
}
/* PARENT submenu when child selected — depth-1 submodule: mid shade */
.customSideMenu
  .ant-menu-sub
  > .ant-menu-submenu.ant-menu-submenu-selected
  > .ant-menu-submenu-title {
  color: var(--az-white) !important;
  background-color: var(--primaryLight) !important;
}

/* Remove AntD default blue indicator */
.ant-menu-item-selected::after {
  border-right: none !important;
}

.ant-table-filter-column {
  justify-content: center !important;
}

.cancelButton {
  width: 100% !important;
  color: var(--az-black) !important;
  background-color: var(--az-gray-300) !important;
  border-color: var(--az-gray-300) !important;
  border-radius: 6px !important;
  height: 43px !important;
  line-height: 43px !important;
}

/* SECTION 16: Profile dropdown */

/**** Profile Dropdown *****/
.profileDropdownMenu {
  padding: 0 !important;
}

.profileDropdownMenu .ant-dropdown-menu-item {
  padding: 10px 12px;
  border-bottom: 1px solid var(--az-gray-300);
  color: var(--az-black) !important;
  font-weight: 500;
}

.profileDropdownMenu .ant-dropdown-menu-item .dropMenuIcon {
  margin-right: 10px;
  color: var(--primaryDark);
  font-size: 16px;
  vertical-align: text-top;
}

.customerDetails {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.detailsList {
  width: 100%;
  padding: 10px 0;
  border-bottom: 1px solid var(--az-gray-300);
  margin: 0 15px;
}

.detailsList h6 {
  margin-bottom: 0;
}

.cardData {
  color: var(--az-gray-580);
  font-size: 14px;
  margin-left: 10px;
}

.ant-select-single .ant-select-selector .ant-select-selection-search {
  top: 5px !important;
  left: 20px !important;
}

.ant-select-single
  .ant-select-selector
  .ant-select-selection-search
  .selectSearch {
  top: 2px !important;
  left: 20px !important;
}

/* Same tokens as .status-card (AZ Sales Agent's stat-card strip) so
   every stage tile in the system — Material Movement, Money Movement,
   Sales Commission Voucher, deposit/pending-payment cards, etc. — shares
   one literal source of color/background/radius instead of separately
   hardcoded values that happened to match by coincidence. */
.customTabCard {
  background: var(--az-gray-50) !important;
  border-radius: var(--az-radius-md) !important;
  border: 1px solid var(--az-border-subtle) !important;
  cursor: pointer;
  transition: border-color 0.15s;
}

/* Material/Money Movement stage tiles use a 12px radius (Figma node
   298:448), narrower than the 16px shared .customTabCard default other
   dashboards use — scoped via a second class so those other callers
   (Dashcard.js, Fulfillment/index.js, Reports/depositeApproval.js) are
   unaffected. */
.customTabCard.movement-tile-card {
  border-radius: 12px !important;
}

.customTabCard:hover {
  border-color: rgba(var(--colorDark-rgb, 0, 122, 255), 0.25) !important;
  box-shadow: none;
}

/* .is-active: self-targeting modifier for components that toggle the
   highlight directly on the tile (most callers) — kept alongside the
   .customTabCardActive ancestor-class pattern (used by Fulfillment/
   index.js) so both styles of caller keep working. Previously several
   pages tried to drive this via an inline style={{backgroundColor:...}}
   on the same element, which can never win against the !important
   above — that's why "active" tiles never visibly highlighted. */
.customTabCardActive .customTabCard,
.customTabCard.is-active {
  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.08) !important;
  /* Solid, full-opacity theme color (not translucent) — Figma node
     298:460 specifies a solid border, and it must stay theme-aware
     (var(--colorDark)) so it renders in the org's own accent color,
     not a hardcoded blue. */
  border: 1px solid var(--colorDark, #007aff) !important;
}

.customTabCard .ant-card-body {
  height: 100%;
}

/* Stage-tile title + stat rows — matches Figma's stage-card layout
   (gap-[20px] title→stats, gap-[4px] between stat rows) */
.movement-tile__title {
  line-height: 1.2;
}
.movement-tile__stats {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 14px;
  /* Without this, these rows inherit antd's default 1.5715 line-height —
     over 21px per row — which alone is taller than the card's fixed
     150px height can spare once a 2-line title is added on top.
     Tightened to fit the actual card, not antd's generic body spacing. */
  line-height: 1.3;
}
.movement-tile__stat-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.movement-tile__stat-label {
  color: rgba(var(--az-black-rgb), 0.6);
}
.movement-tile__stat-value {
  color: rgba(var(--az-black-rgb), 0.8);
  font-weight: 600;
}

.cardRow .cardHeading h5 {
  font-size: 14px;
  font-weight: 500;
  color: rgba(var(--az-black-rgb), 0.6);
}

.customTabCardActive .customTabCard .cardHeading h5 {
  color: rgba(var(--az-black-rgb), 0.7);
}

.customTabCard .innerCon .cardHeading h5 {
  margin-bottom: 0;
}

.customTabCard .innerCon .counterText h4 {
  font-size: 20px;
  font-weight: 700;
  color: rgba(var(--az-black-rgb), 0.8);
}

.customTabCardActive .customTabCard .counterText h4 {
  color: rgba(var(--az-black-rgb), 0.9);
}

.customTabCard .ant-card-body .innerCon .textSec {
  height: 100%;
  display: flex;
  align-items: center;
}

/* SECTION 17: Delta table header */

.deltaTableHeader {
  width: 100%;
  padding: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--primaryDark);
  border-top-left-radius: 16px !important;
  border-top-right-radius: 16px !important;
}

.deltaTableHeader .innerGreenBox {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--az-gray-450);
  border-radius: 10px;
  margin-right: 10px;
  padding: 10px 15px;
  cursor: pointer;
  border: 1px solid var(--az-white);
}

.innerGreenBox.activeBox {
  /* border: 3px solid #5baf25; */
  background-color: var(--secondaryDark);
  color: var(--az-white) !important;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-right: 10px;
  padding: 10px 15px;
  cursor: pointer;
}

.deltaTableHeader .innerGreenBox .boxHeading {
  margin-right: 15px;
}

.deltaTableHeader .innerGreenBox .boxHeading,
.deltaTableHeader .innerGreenBox .boxDesc {
  font-size: 16px;
  color: var(--az-white);
  margin-bottom: 0px;
  text-transform: uppercase;
}

/* SECTION 18: Dropdown menu (action dropdown) */

.dropdownAct .ant-dropdown-link {
  color: var(--az-black);
  vertical-align: super;
  font-size: 24px;
}

.dropMenu {
  padding: 0 !important;
  min-width: 10rem;
}

.dropMenu li {
  padding: 10px 15px;
}

.dropBtn {
  font-size: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--az-gray-580) !important;
  font-weight: 500;
}

.action_btn {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.action_btn button {
  width: auto !important;
  min-width: 100%;
  margin: 5px;
}

/* Row-action pill pair (Inspect/List, View/Ship, etc.) — Figma's action
   cells sit side by side, centered in the row, not stacked like the
   older .action_btn (which many other pages still rely on, so this is
   a separate class rather than a change to that one). */
.az-table-row-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: nowrap;
}

.table-responsive {
  position: relative;
  border-radius: 16px !important;
  overflow-x: scroll !important;
  width: 100%;
  scrollbar-width: thin;
  scrollbar-color: var(--az-gray-400) var(--az-gray-135);
}

/* Some datatables (e.g. SalesData.js, LotsTable.js) wrap their <CustomTable>
   usage in an extra, redundant outer .table-responsive div, ABOVE the
   table's own sticky wrapper (.az-table-wrap for the AntD-fallback engine,
   .az-ts-wrap for the TanStack engine — both covered here). Any non-visible
   overflow on an ancestor between a position:sticky element and the real
   page viewport becomes ITS containing block instead — and since this div
   is never actually what scrolls (the page is), that stickiness would bind
   to this permanently-static box and never correctly engage. :has() targets
   exactly this ancestor-wrapping case. :not(.az-table-scroll-inner) excludes
   CustomTable's OWN internal .table-responsive (CustomTable.js) — without
   it, a nested CustomTable rendered inside an expandable row (e.g.
   BasketTableV3.js) would match this rule via ITS OWN .az-table-wrap/
   .az-ts-wrap appearing as a deep descendant, incorrectly disabling the
   OUTER table's real horizontal-scroll boundary and breaking its layout.
   Higher specificity than the plain .table-responsive rule above (two
   selectors vs one), so it wins regardless of source order. */
.table-responsive:not(.az-table-scroll-inner):has(.az-table-wrap),
.table-responsive:not(.az-table-scroll-inner):has(.az-ts-wrap) {
  overflow-x: visible !important;
}

/* ═══════════════════════════════════════════════════════════════════════════
   TABLE SCROLL & ALIGNMENT
   ───────────────────────────────────────────────────────────────────────────
   AntD's own scroll.y (passed from CustomTable.js, bounded to the remaining
   viewport) makes the row area scroll internally with its header pinned at
   the top of that scrollport — a native, long-standing AntD feature, no
   \`sticky\` prop needed. Page-level stickiness for the whole table block
   (header + bounded rows + pagination) is handled once, on .az-table-wrap.
   ═══════════════════════════════════════════════════════════════════════════ */

/* Horizontal-only scroll context, for tables wider than their card */
.az-table .ant-table-container {
  overflow-x: auto !important;
  scrollbar-width: thin;
  scrollbar-color: var(--az-gray-400) var(--az-gray-135);
}

/* Webkit scrollbar styling on the container */
.az-table .ant-table-container::-webkit-scrollbar {
  height: 6px;
  width: 6px;
}
.az-table .ant-table-container::-webkit-scrollbar-corner {
  background: var(--az-gray-135);
}
.az-table .ant-table-container::-webkit-scrollbar-track {
  background: var(--az-gray-135);
  border-radius: 4px;
}
.az-table .ant-table-container::-webkit-scrollbar-thumb {
  background: var(--az-gray-400);
  border-radius: 4px;
}
.az-table .ant-table-container::-webkit-scrollbar-thumb:hover {
  background: var(--az-gray-500);
}

.productImage {
  width: 60px;
  height: 60px;
}

.goBackBtn {
  background: none !important;
  border: 0 !important;
  box-shadow: none !important;
  display: flex !important;
  align-items: center !important;
  font-weight: 500 !important;
}

.goBackBtn:hover {
  color: var(--az-gray-680) !important;
}

.goBackBtn:focus {
  outline: 0 !important;
  box-shadow: 0 !important;
  border: 0 !important;
}

/* SECTION 19: Modal styles (content/header/title/close button, width variants, cartModal, punch-order-container) */

/***** Warehouse Modal *****/
/* Global modal design */

.ant-modal-content {
  border-radius: 20px;
  padding: 0 !important; /* OK */
  height: auto !important; /* IMPORTANT */
}

.ant-modal-header {
  background: var(--primaryDark) !important;
  padding: 0 !important; /* 👈 KEY FIX */
  min-height: 64px;
  display: flex;
  align-items: center;
}

.ant-modal-title {
  padding: 0 24px; /* 👈 move padding here */
  color: var(--az-white) !important;
  font-weight: 600;
  line-height: 64px;
}

.ant-modal-close {
  top: 50%;
  right: 10px;
}

.ant-modal-close-x {
  background: var(--az-white);
  width: 30px;
  height: 30px;
  border-radius: 10px;
  line-height: 30px;
}

/* Scroll body safely. Scoped under .ant-modal-root .ant-modal so it beats
   antd's own ".ant-modal .ant-modal-body { padding: 0 }" — the old bare
   .ant-modal-body rule lost to it, leaving only an uneven margin (content
   flush with the right and bottom edges). The body scrolls instead of the
   popup running off-screen (.ant-modal-wrap doesn't scroll). */
.ant-modal-root .ant-modal .ant-modal-body {
  margin: 0;
  padding: 20px 24px 24px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

/* antd's own footer (OK / Cancel) */
.ant-modal-root .ant-modal .ant-modal-footer {
  margin: 0;
  padding: 0 24px 20px;
}

.ant-modal-root .ant-modal .ant-modal-body:has(+ .ant-modal-footer) {
  padding-bottom: 16px;
}

/* Modal.confirm has no header bar — even padding all round */
.ant-modal-root .ant-modal.ant-modal-confirm .ant-modal-body {
  padding: 24px;
}

/* Phones: fixed-width variants (widthModal2, widthModalPunchOrder, …) fit
   the screen */
@media (max-width: 767px) {
  .ant-modal-root .ant-modal {
    max-width: calc(100vw - 32px) !important;
    min-width: 0 !important;
    margin: 0 auto;
  }
}

/* Customer form popups (confirm details, shipping address) */
.customer-modal__subtitle {
  margin: 0 0 12px;
  color: var(--az-text-secondary);
  font-weight: 500;
}

.customer-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.customer-modal .infoSection .infoDetails .infoTd,
.customer-modal .infoSection .infoDetails .infoTd p {
  word-break: normal;
  overflow-wrap: anywhere;
}

.customer-modal .infoSection .infoDetails ol {
  padding-left: 18px !important;
}

.widthModal {
  width: 900px !important;
  /* height: 900px !important; */
}

.widthModalInv {
  max-width: 800px !important;
  min-width: 730px !important;
  max-height: 0 auto !important;
}

.widthModal1 {
  width: "70%" !important;
}
.widthModalInv {
  /* height: 10% !important; */
  max-width: 800px !important;
  min-width: 730px !important;
  max-height: 100% !important;
}
.widthModal2 {
  /* width: 1300px !important; */
  height: 10% !important;
  max-width: 1000px !important;
  min-width: 900px !important;
}

.widthModalPunchOrder {
  height: 10% !important;
  max-width: 820px !important;
  min-width: 820px !important;
  margin-left: 0px !important;
}
.widthModalLarge {
  width: 95vw !important;
  max-width: 100vw;
  height: 80vh !important;
  max-height: 50vh;
  /* overflow-y: auto; */
}

.widthModalLarge .ant-table-footer {
  padding: 0 !important; /* Removes all padding */
}

/* .widthModalLarge {
  width: 90vw !important;
  height: 40vh;
  max-height: 40vh;
} */
/* Antd v5 form fixes */
.ant-form-item {
  margin-bottom: 16px;
}

.ant-picker,
.ant-select {
  width: 100%;
}

/* Bootstrap conflict fix */
label {
  margin-bottom: 0 !important;
}

@media (min-width: 768px) {
  .widthModalLarge {
    width: 750px !important;
  }
}
@media (min-width: 850px) {
  .widthModalLarge {
    width: 820px !important; /* Customize this value as needed */
  }
}

@media (min-width: 1024px) {
  .widthModalLarge {
    width: 970px !important;
  }
}
@media (min-width: 1200px) {
  .widthModalLarge {
    width: 1150px !important;
  }
}

@media (min-width: 1300px) {
  .widthModalLarge {
    width: 1250px !important;
  }
}

.ant-modal-mask {
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

.ant-modal-root .ant-modal {
  pointer-events: auto !important;
  /* Ensure modal can be clicked */
}

.punch-order-container {
  width: 50% !important;
  padding: 0% !important;
}

.cartModal {
  width: 900px !important;
  height: 400px !important;
}

/* SECTION 20: Grid & card layout helpers */

.card-container {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-template-rows: 1fr auto;
  gap: 10px;
}

.cl {
  grid-column: auto / span 12;
}

@media screen and (min-width: 600px) {
  .cl-3-lg {
    grid-column: auto / span 12;
  }
}

@media screen and (min-width: 1000px) {
  .cl-3-lg {
    grid-column: auto / span 6;
  }
}

@media screen and (min-width: 1600px) {
  .cl-3-lg {
    grid-column: auto / span 4;
  }
}

.place-order-button {
  position: fixed;
  bottom: 0;
}

.ant-modal-wrap {
  overflow-y: hidden;
}

.shipperData:last-child {
  margin-bottom: 0;
}

.customSearch {
  position: absolute !important;
  width: 250px !important;
  left: 22rem;
  top: 2.8rem;

  /* border: 1px solid var(--primaryDark); */
}

.comboListItem {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 50px;
}

.rangePicker {
  position: absolute !important;
  width: auto !important;
  left: 52rem;
  top: 5.3rem;
  border-radius: 20px;
}

/* SECTION 21: Absolute-positioned page-level action buttons */

.absltBtn {
  position: absolute !important;
  width: auto !important;
  /* color: #954c0a !important; */
  top: 1.5rem;
}

.manageBtn {
  padding: 0px 10px 0px 10px !important;
}

.absltBtn span {
  font-size: 12px;
}

.bankBtn {
  right: 8.2rem;
}

.stateBtn,
.blockBtn {
  right: 8.5rem;
}

.districtBtn,
.vlgBtn,
.marketBtn {
  right: 9rem;
}

.branchBtn {
  right: 9.2rem;
}

.subDstBtn {
  right: 11rem;
}

.pinBtn {
  right: 10rem;
}

.ctgryBtn {
  right: 10.2rem;
}

.sbCtgryBtn {
  right: 12rem;
}

.orgBtn {
  right: 14.1rem;
}

.fcBtn {
  right: 13.9rem;
}

.azPartnerBtn {
  top: 5rem;
  right: 3rem;
}

.sellerBtn,
.cstmrBtn {
  top: 5rem;
  right: 2rem;
}

.productBtn {
  top: 2.4rem;
  right: 10rem;
}

.variantTable .productBtn {
  top: 1.5rem;
  right: 2.2rem;
}

.sllrPrdctBtn {
  top: 1.2rem;
  right: 19rem;
}

.comboBtn {
  top: 4rem;
  right: 19rem;
}

/* SECTION 22: Miscellaneous UI */

.borderBottom {
  text-align: center;
  margin-top: 50px;
  /* bottom: 80px; */
  border-bottom: 5px solid rgb(var(--az-red-900-rgb));
}

.loader {
  width: 48px;
  height: 48px;
  border: 5px solid var(--az-white);
  border-bottom-color: var(--primaryDark);
  border-radius: 50%;
  display: inline-block;
  box-sizing: border-box;
  animation: rotation 1s linear infinite;
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* SECTION 23: Responsive / media queries */

/***** Responsive Style *****/
@media (max-width: 280px) {
  .navbar-brand {
    width: 40%;
  }
}

@media (max-width: 320px) {
  .navbar-brand {
    margin-right: 0;
  }

  .navbar-toggler {
    padding: 0.25rem 0.5rem;
    font-size: 1rem;
  }
}

@media only screen and (min-width: 1024px) and (max-width: 1200px) {
  .sidebar.panel-open ~ .home-section .contentSection .timlineSection .w-30 {
    left: 51rem;
    transition: all 0.5s ease;
  }
}

@media only screen and (max-width: 991px) {
  .bodySection {
    display: block !important;
  }

  .w-70,
  .w-30 {
    flex: 1 1;
    max-width: 100%;
  }

  .timlineSection .timelineSectionInner {
    position: relative;
    left: 0 !important;
  }

  .infoSection .infoHeader .ant-row,
  .infoDetails .ant-row {
    display: -webkit-box !important;
    flex-wrap: nowrap;
    overflow-x: auto;
  }

  ::-webkit-scrollbar {
    height: 0px;
  }
}

@media (max-width: 767px) {
  .mobHide {
    display: none !important;
  }

  @media only screen and (max-width: 601px) {
    .filterSection {
      display: flex !important;
      flex-direction: column !important;
      justify-content: space-between !important;
      /* align-items: center !important; */
    }
  }

  .signInPage .SignInFormSection .customCard {
    width: 100%;
  }

  /* home-section shifts with sidebar on all screen sizes — desktop CSS handles it */

  .table-responsive .ant-table-content {
    overflow-x: auto;
  }

  .mobFLex {
    flex-direction: column;
    align-items: start !important;
  }
}

@media only screen and (min-width: 768px) and (max-width: 991px) {
  .table-responsive .ant-table-content {
    overflow-x: auto;
  }
}

/* .tblData{
  border: 1px solid black;
  border-collapse: collapse;
  text-align: center;
  margin: 10px 0px 10px 0px;
} */
.tblMain {
  /* border-collapse: collapse; */
  border-radius: 1em;
  overflow: hidden;
  text-align: center;
  font-size: 12px;
  width: 100%;
}

/* th, td */

.tblHead {
  background: var(--primaryLight);
  text-align: center;
  font-weight: 500;
  font-size: 12px;
  color: var(--primaryTextLight);
  height: 50px;
}

.tblData {
  /* border: 0.5px solid white; */
  padding: 0.8em;
  background: hsl(0, 3%, 92%);
  border-bottom: 2px solid white;
  color: var(--az-black);
}

.input {
  border: none;
  text-align: center;
  width: 100%;
}

.expandBtn {
  border: 1px solid var(--primaryDark);
  border-radius: 5px;
  background-color: var(--primaryTextDark);
}

/* .css-mine {
  margin-top: 2em;
  clear: both;
} */

.tblBody {
  margin: 1.5em;
}

.customSelect {
  width: 200px !important;
}

@media only screen and (min-width: 993px) {
  .mobSider {
    display: none;
  }
}

@media only screen and (min-width: 90.063em) and (max-width: 120em) {
  .sidebar.panel-open ~ .home-section .contentSection .timlineSection .w-30 {
    left: 76%;
    transition: all 0.5s ease;
  }
}

@media (max-width: 750px) {
  .contentSection {
    display: flex;
    flex-direction: column !important;
    /* align-items: start; */
  }
}

@media only screen and (min-width: 120.063em) {
  .sidebar.panel-open ~ .home-section .contentSection .timlineSection .w-30 {
    left: 75%;
    transition: all 0.5s ease;
  }
}

@media (min-width: 1200px) and (max-width: 1920px) {
  .w-40 {
    width: 60%;
  }
}

.btn-div {
  margin: 2rem !important;
}

.ant-result {
  align-self: center;
}

body,
#root {
  height: 100%;
}

.errorPageBody {
  height: 100%;
  display: flex;
  justify-content: center;
}

.customButtonOutlinedPunch:hover {
  color: var(--az-white) !important;
  background-color: var(--primaryDark) !important;
  box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.01);
}

.customButtonOutlinedPunch {
  width: auto !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  color: var(--primaryDark) !important;
  background-color: transparent !important;
  border-color: var(--primaryDark) !important;
  border-radius: 22px !important;
  transition: all 0.5s ease-in-out;
  text-shadow: none !important;
}

/* AZ table — new Figma design (borderless, white header, row dividers, theme-colored pagination); used by default in CustomTable.js */

/* Spinner */
.az-table .ant-spin-dot-item {
  background-color: var(--colorDark) !important;
}

/* Remove outer table border / radius override */
.az-table .ant-table {
  border-radius: 0 !important;
  border: none !important;
}
.az-table .ant-table-container {
  border-inline-start: none !important;
  border-top: none !important;
}
.az-table .ant-table-container::before,
.az-table .ant-table-container::after {
  display: none !important;
}

/* ── Header — Figma node 42:3030: text-[14px] font-medium text-[rgba(0,0,0,0.6)] h-[52px] ── */
.az-table .ant-table-thead > tr > th {
  background-color: var(--az-white) !important;
  color: var(--az-text-secondary) !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  text-align: left !important;
  letter-spacing: 0;
  border-bottom: 1px solid var(--az-gray-300) !important;
  border-top: none !important;
  padding: 15px 16px !important;
  box-shadow: none !important;
  white-space: nowrap !important;
  word-break: keep-all !important;
  overflow: visible !important;
  text-overflow: unset !important;
}

/* Inner header wrappers: no wrapping, no clipping, no ellipsis. */
.az-table .ant-table-column-sorters,
.az-table .ant-table-column-title,
.az-table-col-title {
  white-space: nowrap !important;
  word-break: keep-all !important;
  overflow: visible !important;
  text-overflow: unset !important;
}

/* Remove separator pseudo-element between header cells */
.az-table .ant-table-thead > tr > th::before {
  display: none !important;
}

/* First / last header radius */
.az-table .ant-table-thead > tr > th:first-child {
  border-start-start-radius: 0 !important;
}
.az-table .ant-table-thead > tr > th:last-child {
  border-start-end-radius: 0 !important;
}

/* ── Body cells — Figma node 42:3059 specified text-[16px], but every table in
   the app (including this AntD-fallback branch) is standardized on 14px row
   text for consistency — see .az-ts-td below, the TanStack branch's equivalent.
   Padding is 26px so a row containing a 36px .customButtonOutlinedBoard action
   button totals exactly 88px: 26 + 36 + 26. ── */
.az-table .ant-table-tbody > tr > td {
  border-bottom: 1px solid var(--az-gray-135) !important;
  padding: 26px 16px !important;
  font-size: 14px !important;
  color: var(--az-text-primary) !important;
  background: var(--az-white) !important;
  text-align: left;
  transition: background 0.15s;
  white-space: nowrap !important;
  overflow: hidden !important;
}

/* Columns with ellipsis:true — AntD wraps content and adds this class;
   text-overflow:ellipsis is scoped here so it only fires where intended. */
.az-table .ant-table-cell-ellipsis,
.az-table .ant-table-cell-ellipsis .ant-table-cell-content {
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

/* No zebra striping */
.az-table .ant-table-tbody > tr:nth-child(even) > td {
  background-color: var(--az-white) !important;
}

/* Hover */
.az-table .ant-table-tbody > tr:hover > td {
  background-color: var(--az-gray-50) !important;
}

/* Remove last row bottom border */
.az-table .ant-table-tbody > tr:last-child > td {
  border-bottom: none !important;
}

/* ── Sorter ── */
.az-table .ant-table-column-sorters {
  justify-content: flex-start !important;
  gap: 4px;
  text-align: left !important;
}
.az-table .ant-table-column-title {
  flex: initial !important;
  text-align: left !important;
}
.az-table .ant-table-column-sorter {
  color: var(--az-gray-400) !important;
  margin-left: 4px;
}
.az-table .ant-table-column-sorter-up.active,
.az-table .ant-table-column-sorter-down.active {
  color: var(--colorDark) !important;
}

/* ── Column title + hide-column icon ── */
.az-table-col-title {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.az-table-hide-col {
  cursor: pointer;
  color: rgba(var(--az-black-rgb), 0.45);
  flex-shrink: 0;
  transition: color 0.15s;
}
.az-table-hide-col:hover {
  color: var(--colorDark) !important;
}

/* ── Edit column toolbar — sits directly above the table. Most pages also
   render their own header row (stat chips / Download button) right above
   CustomTable, and with margin-bottom:0 here those two rows had no
   breathing room between them, making "Edit column" look like a stray
   button floating just outside the table rather than part of the page's
   layout. margin-top gives it consistent separation from whatever the
   page renders above, on every page that uses CustomTable. ── */
.az-table-toolbar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 10px 0 8px;
}
.az-table-toolbar-extra {
  display: flex;
  align-items: center;
  gap: 8px;
}
/* Page-specific toolbar content (e.g. Download) passed via CustomTable's
   \`extra\` prop — right-aligned so it sits at the end of the row, matching
   the edit-columns button below it. */
.az-table-toolbar-extra-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 8px;
}
/* Figma node 298:193 spec: Hug (44px) x Fixed (40px), padding 6/12/6/12,
   gap spacing-1 (4px), fill #F4F4F4 (== --az-gray-150), radius/max on the
   left corners only — right corners stay square so that edge reads as
   part of the card border once flush against it, not a floating pill.
   No margin-right set here: pages wrap CustomTable in very different
   amounts of card padding (.page-card-body's 0 vs .page-card-body--hero's
   16px, or none at all), so a hardcoded offset can only ever be right for
   one of them. The actual offset is measured against whichever card
   really encloses this button and applied inline — see CustomTable.js. */
.az-edit-column-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--az-space-1);
  height: 40px;
  flex-shrink: 0;
  padding: 6px 12px;
  background: var(--primaryDark) !important;
  border-color: var(--primaryDark) !important;
  /* background: var(--az-gray-135); */
  border: none;
  border-radius: var(--az-radius-pill) 0 0 var(--az-radius-pill);
  color: var(--az-white);
  cursor: pointer;
  transition: background 0.15s;
}
.az-edit-column-btn:hover {
  background: var(--az-gray-300);
}
/* AntDFallback's outer wrapper (CustomTable.js) — see .az-ts-wrap for the
   TanStack engine's identical rule and the full rationale: position:sticky
   here (not just on the header) so the WHOLE table block — header, rows
   bounded to the remaining viewport via scroll.y, pagination — moves and
   settles together, guaranteeing pagination is never scrolled out of view. */
.az-table-wrap {
  position: sticky;
  top: 80px;
  z-index: 2;
  background: var(--az-white);
}
/* Non-scrolling wrapper around the table's scroll container (CustomTable.js).
   border-radius: 16px, rounding is provided by the enclosing .page-card-body /
   .page-card-body--hero-flush card. */
.az-table-header-overlay {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
}
/* Zero-height wrapper for the edit-columns button, so the button overlays
   the header row instead of adding its own height above it. Base rule has
   NO position:sticky — SalesData.js reuses this same class for its own,
   differently-structured column picker (not nested in CustomTable's
   .az-table-header-overlay, no bounded scroll box under it), where sticky
   positioning would bubble all the way to the page and pin the button
   under the topbar once scrolled. The scoped rule below opts CustomTable's
   own usage into sticky at top:0 — the same offset as .az-ts-thead / AntD's
   own sticky header, both independent sticky elements sharing the same
   bounded scroll container (.az-ts-scroll), which need to match or they
   visually drift apart as rows scroll past underneath. */
.az-edit-column-sticky {
  position: relative;
  height: 0;
  z-index: 21;
}
.az-table-header-overlay .az-edit-column-sticky {
  position: sticky;
  top: 0;
}
.az-edit-column-sticky .az-edit-column-btn {
  position: absolute;
  top: 6px;
  right: 0;
}
.az-edit-column-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 280px;
  min-width: 180px;
  overflow-y: auto;
}
.az-edit-column-list .ant-checkbox-checked .ant-checkbox-inner {
  background-color: var(--colorDark) !important;
  border-color: var(--colorDark) !important;
}

/* ── Expand icon ── */
.az-table .ant-table-row-expand-icon {
  color: var(--colorDark) !important;
  border-color: var(--colorDark) !important;
  border-radius: 4px !important;
}
.az-table .ant-table-row-expand-icon:hover {
  color: var(--colorDark) !important;
  border-color: var(--colorDark) !important;
}
.az-table .ant-table-row-expand-icon-cell {
  background: var(--az-white) !important;
}

/* ── Expanded / nested row ── */
.az-table .ant-table-expanded-row > .ant-table-cell {
  background: var(--az-gray-50) !important;
  padding: 0 0 0 48px !important;
  border-bottom: 1px solid var(--az-gray-300) !important;
}

/* Nested table header inside expanded row */
.az-table .ant-table-expanded-row .ant-table-thead > tr > th {
  background-color: var(--az-gray-50) !important;
  color: var(--az-slate-500) !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: 0;
  border-bottom: 1px solid var(--az-slate-100) !important;
  padding: 8px 14px !important;
}

/* Nested table body cells */
.az-table .ant-table-expanded-row .ant-table-tbody > tr > td {
  background: var(--az-gray-50) !important;
  border-bottom: 1px solid var(--az-gray-135) !important;
  font-size: 12px !important;
  color: var(--az-gray-700) !important;
  padding: 10px 14px !important;
}
.az-table .ant-table-expanded-row .ant-table-tbody > tr:hover > td {
  background: var(--az-gray-135) !important;
}

/* A nested CustomTable rendered inside an expanded row (e.g.
   BasketTableV3.js's expandedRowRender) reuses the same wrapper classes as
   a top-level page table, none of which are appropriate this deep:
   - .az-ts-wrap's position:sticky/top:80px (see that rule's own comment)
     assumes it's pinning below .page-topbar; nested here it instead sticks
     to the page viewport from inside the row.
   - .az-table-sticky-region's min-height:100vh-ish (see that rule's own
     comment) is sized for a full top-level table; nested here it would
     force the expanded row to reserve almost a full screen of blank space.
   - AntD/rc-table wraps expandable-row content in its own
     .ant-table-expanded-row-fixed div (rc-table's ExpandedRow.js) with an
     INLINE style — width: <outer table's own visible width>px, sticky,
     overflow:hidden — sized to whatever the OUTER table's own (narrower)
     columns add up to. Confirmed via DevTools: that inline width+overflow
     is what clips the nested table's last column and its edit-columns
     button, regardless of how the nested table's own wrapper chain is
     sized underneath it. Since it's an inline style, it needs !important
     to override. We take over as the sole horizontal-scroll owner here —
     .az-ts-scroll below still has its own overflow-x:auto, now free to
     size itself off the real containing <td> (bounded by table-layout via
     colSpan) instead of this narrower inline width. */
.az-table .ant-table-expanded-row .ant-table-expanded-row-fixed {
  width: 100% !important;
  max-width: 100% !important;
  position: static !important;
  left: auto !important;
  overflow: visible !important;
}
.az-table .ant-table-expanded-row .az-table-sticky-region {
  min-height: 0;
}
.az-table .ant-table-expanded-row .az-ts-wrap {
  position: static;
  top: auto;
  z-index: auto;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}
.az-table .ant-table-expanded-row .az-table-header-overlay,
.az-table .ant-table-expanded-row .az-ts-scroll {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

/* ── Pagination ── */

/* Wrapper row that holds the pagination inside the table.
   position:sticky + bottom:0, opaque background, so on a table tall enough
   to scroll the page, pagination settles at the bottom of the viewport
   instead of requiring a scroll all the way past the last row to reach it. */
.az-table .ant-table-pagination,
.az-table .ant-table-pagination.ant-pagination {
  position: sticky !important;
  bottom: 0 !important;
  z-index: 4 !important;
  background: var(--az-gray-50) !important;
  margin: 0 !important;
  padding: 10px 20px !important;
  border-top: 1px solid var(--az-gray-300) !important;
  justify-content: flex-end !important;
  align-items: center !important;
  display: flex !important;
  flex-wrap: wrap !important;
  row-gap: 6px !important;
}

/* Page number buttons */
.az-table .ant-pagination-item {
  border: 1px solid var(--az-gray-300) !important;
  border-radius: 6px !important;
  min-width: 32px !important;
  height: 32px !important;
  line-height: 30px !important;
  background: var(--az-white) !important;
  margin-inline-end: 4px !important;
}
.az-table .ant-pagination-item a {
  color: var(--az-gray-700) !important;
  font-size: 13px !important;
  padding: 0 !important;
}
.az-table .ant-pagination-item:hover {
  border-color: var(--colorDark) !important;
}
.az-table .ant-pagination-item:hover a {
  color: var(--colorDark) !important;
}

/* Active page */
.az-table .ant-pagination-item-active {
  border-color: var(--colorDark) !important;
  background-color: var(--colorDark) !important;
}
.az-table .ant-pagination-item-active a {
  color: var(--az-white) !important;
}
.az-table .ant-pagination-item-active:hover a {
  color: var(--az-white) !important;
}

/* Prev / Next buttons */
.az-table .ant-pagination-prev,
.az-table .ant-pagination-next {
  min-width: 32px !important;
  height: 32px !important;
  line-height: 30px !important;
  margin-inline-end: 4px !important;
}
.az-table .ant-pagination-prev .ant-pagination-item-link,
.az-table .ant-pagination-next .ant-pagination-item-link {
  border: 1px solid var(--az-gray-300) !important;
  border-radius: 6px !important;
  background: var(--az-white) !important;
  color: var(--az-gray-570) !important;
  font-size: 13px !important;
  width: 32px !important;
  height: 32px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}
.az-table
  .ant-pagination-prev:not(.ant-pagination-disabled):hover
  .ant-pagination-item-link,
.az-table
  .ant-pagination-next:not(.ant-pagination-disabled):hover
  .ant-pagination-item-link {
  border-color: var(--colorDark) !important;
  color: var(--colorDark) !important;
}

/* Disabled prev/next */
.az-table .ant-pagination-disabled .ant-pagination-item-link {
  color: var(--az-gray-400) !important;
  border-color: var(--az-gray-135) !important;
  background: var(--az-gray-50) !important;
}

/* Jump prev / next (…) */
.az-table
  .ant-pagination-jump-prev
  .ant-pagination-item-container
  .ant-pagination-item-link-icon,
.az-table
  .ant-pagination-jump-next
  .ant-pagination-item-container
  .ant-pagination-item-link-icon {
  color: var(--colorDark) !important;
}

/* Page size changer */
.az-table .ant-pagination-options {
  margin-inline-start: 8px !important;
}
.az-table .ant-pagination-options .ant-select-selector {
  border: 1px solid var(--az-gray-300) !important;
  border-radius: 6px !important;
  height: 32px !important;
  font-size: 13px !important;
  background: var(--az-white) !important;
}
.az-table .ant-pagination-options .ant-select-selector:hover {
  border-color: var(--colorDark) !important;
}

/* Total count text */
.az-table .ant-pagination-total-text {
  color: var(--az-gray-570) !important;
  font-size: 12px !important;
  margin-inline-end: auto !important;
  line-height: 32px !important;
}

/* ── Empty state ── */
.az-table .ant-empty-description {
  color: var(--az-gray-500) !important;
  font-size: 13px !important;
}

.custom-button3 {
  border-color: var(--primaryDark) !important;
  background: var(--primaryDark) !important;
  color: var(--az-white) !important;
  border-radius: 2px !important;
  transition: all 0.5s ease-in-out;
}

.custom-button3:hover {
  color: var(--az-white) !important;
  background-color: var(--primaryLight) !important;
  box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.01);
}

.btn-primary {
  /* width: 100% !important; */
  background-color: var(--primaryDark) !important;
  border-color: var(--primaryDark) !important;
  border-radius: 6px !important;
  /* height: 36px !important; */
  /* line-height: 30px; */
  font-size: 14px !important;
}

.btn-primary:hover {
  background-color: var(--primaryDark) !important;
  border-color: var(--primaryDark) !important;
}

.btn-primary:disabled {
  background-color: lightgray !important;
  border-color: lightgray !important;
  color: gray !important;
}

.btn-secondary {
  width: 100% !important;
  border: 1px solid var(--primaryDark) !important;
  border-radius: 6px !important;
  height: 36px !important;
  line-height: 30px;
  font-size: 14px !important;
}

.btn-secondary:hover {
  color: var(--primaryDark) !important;
  border-color: var(--primaryDark) !important;
}

.ant-radio-group-solid
  .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
  color: var(--az-white);
  background: var(--primaryDark) !important;
  border-color: var(--primaryDark) !important;
}

.ant-radio-button-wrapper:hover {
  position: relative;
  color: var(--primaryDark) !important;
}

.ant-btn-link {
  color: var(--primaryDark) !important;
}

.ant-btn-link:hover {
  color: var(--primaryDark) !important;
  opacity: 0.75;
}

.ant-btn-link:disabled {
  color: gray !important;
}

/* Unlike .az-login's checkbox (deliberately fixed-blue, see that rule's own
   comment — there's no signed-in org yet at login time), this checkbox is
   used inside the authenticated app, where a real per-org theme is always
   active — so its checked/hover states should track that theme's accent
   (var(--colorDark)), not a fixed legacy color. */
.custom-checkbox .ant-checkbox-checked .ant-checkbox-inner::after {
  border-color: var(--colorDark, #007aff);
}

.custom-checkbox .ant-checkbox:hover .ant-checkbox-inner {
  border-color: var(--colorDark, #007aff) !important;
}

.material-movement-grid {
  display: grid;
  /* Fixed 203px tracks, same reasoning as .az-movement-dashboard-grid: the
     outer wide (span-2) column can end up wider than these two sub-columns
     actually need, and a 1fr track would stretch to fill that instead of
     staying at the card's real size — leaving an oversized, uneven gap
     between LOT INSPECTION and BASKET CREATION instead of Figma's fixed
     20px. Fixed tracks keep that gap pixel-stable; auto-fit still wraps
     them to stacked rows if the column is too narrow for both. */
  grid-template-columns: repeat(auto-fit, 203px);
  /* Figma's real gap between two cards inside the same group (e.g.
     LOT INSPECTION / BASKET CREATION side by side) — narrower than
     the 60px gap between separate groups in .az-movement-dashboard-grid. */
  gap: 20px;
  /* Center rather than left-align in case the outer wide column is a
     little bigger than these two fixed tracks + gap need. */
  justify-content: center;
}
.tabBox {
  border: 1px solid var(--az-gray-310);
  padding: 8px;
  margin: 8px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(var(--az-black-rgb), 0.1);
  cursor: pointer;
  transition: transform 0.2s;
  background-color: white;
}

@media screen and (min-width: 800px) {
  .material-movement-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
}

.tabBox:hover {
  transform: scale(1.05);
  background-color: var(--primaryLight);
}

.tabHeader {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
}

.tabHeader h3 {
  margin: 0;
}

.tabInfo {
  display: flex;
  gap: 16px;
}

.tabContent {
  margin-top: 0;
}

.ant-select-single .ant-select-selector .ant-select-selection-search {
  top: 0 !important;
}

/* SECTION 24: Menu icon overrides (AntD icon visibility fixes) */

/* AntD menu icon container — collapsed: 36px circle; expanded: 20px inline */
.sidebar:not(.open) .ant-menu-item-icon,
.sidebar:not(.open) .ant-menu-submenu-title .ant-menu-item-icon {
  min-width: 36px !important;
  width: 36px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  margin-right: 0 !important;
  visibility: visible !important;
  opacity: 1 !important;
  flex-shrink: 0 !important;
}

.sidebar.open .ant-menu-item-icon,
.sidebar.open .ant-menu-submenu-title .ant-menu-item-icon {
  min-width: 20px !important;
  width: 20px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  margin-right: 10px !important;
  visibility: visible !important;
  opacity: 1 !important;
  flex-shrink: 0 !important;
}

/* Actual image inside menu */
.menuIconImg {
  width: 24px !important;
  height: 24px !important;
  object-fit: contain;
  display: block !important;
  margin: 0 auto !important;
  flex-shrink: 0 !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.customSideMenu .ant-menu-item-icon .menuImg .menuIconImg {
  display: block !important;
  margin: 0 auto !important;
  width: 24px !important;
  height: 24px !important;
  visibility: visible !important;
  opacity: 1 !important;
}

/* Remove bootstrap interference */
.menuImg img {
  max-width: none !important;
  visibility: visible !important;
  opacity: 1 !important;
  display: block !important;
}

/* Ensure icons are always visible in sidebar */
.sidebar .ant-menu-item-icon,
.sidebar .ant-menu-submenu-title .ant-menu-item-icon {
  visibility: visible !important;
  opacity: 1 !important;
  display: flex !important;
}

.sidebar .ant-menu-item-icon img,
.sidebar .ant-menu-submenu-title .ant-menu-item-icon img {
  visibility: visible !important;
  opacity: 1 !important;
  display: block !important;
}

/* Fix: Link wrapping Button — <a><button> is invalid HTML so clicks on button
   padding/border don't reach the anchor. Disable pointer events on the button
   so all clicks hit the parent <a> instead. Hover visuals are preserved via
   the <a>:hover rule below. */
a > .ant-btn {
  pointer-events: none;
}
a:hover > .ant-btn:not([disabled]) {
  color: var(--ant-color-primary, #ffffff);
  border-color: var(--ant-color-primary);
}
a:hover > .ant-btn-primary:not([disabled]) {
  color: var(--az-white);
  background-color: var(--ant-color-primary-hover, #4096ff);
  border-color: var(--ant-color-primary-hover, #4096ff);
}

/* SECTION 25: Responsive card sections (Material/Money Movement) */
.cardSectionn {
  overflow-x: auto;
  min-width: 0;
}

@media (max-width: 767px) {
  .cardSectionn .ant-row {
    flex-wrap: wrap;
  }
  .customTabCard {
    width: 100% !important;
    min-width: 0 !important;
  }
}

/* SECTION 26: AZ TanStack enterprise table (TanStack Table v8 + Virtual v3). AntD's scroll.y renders two separate <table> elements and syncs scrollLeft via JS, which can drift and misalign columns — fixed here with one <table> + <colgroup> as the single source of column widths inside one scroll container; thead sticks via position:sticky, pinned columns use position:sticky with TanStack-calculated offsets, no custom JS syncing. */

/* ── Sticky-slack wrapper ──
   position:sticky only actually engages while its own immediate CONTAINING
   BLOCK has "slack" left below the sticky element — room within that same
   parent for the element's reserved layout box to keep extending past the
   sticky point. Without it (confirmed empirically: even a single plain
   position:relative ancestor wrapping just a spacer + a sticky box, with
   nothing after the sticky box inside that same wrapper, is enough to
   silently defeat stickiness — no clamping at all, indistinguishable from
   position:static), the browser just never sticks the element, no matter
   how clean the rest of the ancestor chain is. .az-ts-wrap (the actual
   sticky element, below) is normally the last substantial thing on a page
   — nothing trails it inside whatever page-specific div happens to wrap
   <CustomTable> — so that natural parent almost never has slack on its
   own. This wrapper, rendered unconditionally by CustomTable.js itself
   (so it doesn't depend on the surrounding page's markup), guarantees at
   least one remaining-viewport's worth of it. The +120px on top of that is
   deliberate, not rounding — CustomTable.js sizes the table's own content
   to come out at exactly 100vh-80px (so the stuck table fills the screen
   with no gap below pagination), which means content height and this
   min-height would otherwise be mathematically identical — i.e. zero
   slack by construction — and confirmed empirically, that's not enough
   for the browser to reliably engage stickiness at all (unlike a visibly
   large margin, which works cleanly). The extra 120px is spare capacity in
   THIS wrapper only, never in the table's own visible content, so it
   doesn't reintroduce a gap under pagination — it can only ever show as
   blank space below the table if a user keeps scrolling past the point
   where pagination is already fully on-screen. */
/* .az-table-sticky-region {
  min-height: calc(100vh - 80px + 120px);
} */

/* ── Outer wrapper ──
   position:sticky here (not just on the header row) so the WHOLE table —
   header, bounded/scrollable rows, pagination — moves together as one
   block once scrolled into place: the page scrolls normally past whatever
   sits above the table, then this wrapper pins at top:80 (right below the
   sticky .page-topbar) and everything inside it (including pagination, a
   later sibling of .az-table-header-overlay — see CustomTable.js) fits
   within the remaining viewport, so pagination never ends up scrolled out
   of view below the fold. The row area inside (.az-ts-scroll) is bounded
   with its own internal scrollbar and sticky header — see .az-ts-thead —
   which is what makes the header stick correctly in the first place
   (position:sticky on <thead>/<th> needs a genuinely scrolling ancestor;
   confirmed via an isolated repro outside this app). */
.az-ts-wrap {
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 80px;
  z-index: 2;
  background: var(--az-white);
  border-radius: 16px;
}

/* ── Scroll container (both axes) ──
   Bounded height (see CustomTable.js's scrollMaxHeight, applied inline) so
   rows beyond what fits scroll internally instead of growing the table —
   and therefore the page — past one screen's worth of height. This
   genuinely-scrolling container is also what the sticky header depends on
   — see .az-ts-thead. */
.az-ts-scroll {
  position: relative;
  overflow-x: auto;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--az-gray-400) var(--az-gray-135);
}
.az-ts-scroll::-webkit-scrollbar {
  height: 6px;
  width: 6px;
}
.az-ts-scroll::-webkit-scrollbar-corner {
  background: var(--az-gray-135);
}
.az-ts-scroll::-webkit-scrollbar-track {
  background: var(--az-gray-135);
  border-radius: 4px;
}
.az-ts-scroll::-webkit-scrollbar-thumb {
  background: var(--az-gray-400);
  border-radius: 4px;
}
.az-ts-scroll::-webkit-scrollbar-thumb:hover {
  background: var(--az-gray-500);
}

/* ── Loading overlay ── */
/* z-index below .az-edit-column-btn's 20 (see SECTION 11a-ish comment above
   it) — both sit in the same stacking context (.az-table-header-overlay,
   position:relative with no z-index of its own), and this overlay comes
   later in the DOM (nested inside .az-ts-scroll, a sibling rendered after
   the button). Equal z-index would let DOM order decide, which paints this
   overlay on top and hides the button behind it for as long as the table
   is loading — exactly the "outside the scroll area" independence the
   button's positioning comment says it's supposed to have. */
.az-ts-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 15;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--az-white-rgb), 0.72);
}
.az-ts-loading-overlay .ant-spin-dot-item {
  background-color: var(--colorDark) !important;
}

/* ── Table element ──
   table-layout:auto — browser grows each column to fit its widest cell.
   <col minWidth> sets the floor (from header text); data drives the final width.
   border-collapse:separate allows position:sticky on pinned cells. */
.az-ts-table {
  width: 100%;
  table-layout: auto;
  border-collapse: separate;
  border-spacing: 0;
  font-family: inherit;
}

/* ── Header row ──
   Sticky relative to .az-ts-scroll, its own real (bounded, overflow-y:auto)
   scrolling ancestor — rows scroll internally beneath it, unaffected by
   the outer page. This MUST stay in lockstep with .az-edit-column-sticky
   (same top:0 offset) — the two are independent sticky elements, and if
   one pins while the other stays in normal flow they visually drift apart.
   (position:sticky on <thead>/<th> only renders correctly with a genuinely
   scrolling ancestor like this one — tried it against an unbounded,
   ever-growing table and confirmed, via an isolated repro outside this
   app, that rows silently overlap the header there. Bounded + internal
   scroll is a requirement, not a stylistic choice.) */
.az-ts-thead {
  position: sticky;
  top: 0;
  z-index: 2;
}

/* ── Header cells — Figma: 14px / Medium(500) / rgba(0,0,0,0.6) / always left-aligned ── */
.az-ts-th {
  background-color: var(--az-white);
  color: rgba(var(--az-black-rgb), 0.6);
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  border-bottom: 1px solid var(--az-gray-300);
  height: 52px;
  padding: 0 16px;
  vertical-align: middle;
  box-sizing: border-box;
  -webkit-user-select: none;
          user-select: none;
}

/* Pinned header cells — left-pinned columns use the Figma highlight token */
.az-ts-th--pinned-left {
  background-color: var(--colorLight);
  z-index: 3 !important;
}
.az-ts-th--pinned-right {
  background-color: var(--az-white);
  z-index: 3 !important;
}

/* Shadow lines at the pinning edge to signal hidden content */
.az-ts-th--last-left {
  box-shadow: 4px 0 8px -4px rgba(var(--az-black-rgb), 0.1);
}
.az-ts-th--first-right {
  box-shadow: -4px 0 8px -4px rgba(var(--az-black-rgb), 0.1);
}

/* Column-separator pseudo-element between header cells: hidden */
.az-ts-th::before {
  display: none;
}

/* ── Sortable header button ──
   white-space:nowrap keeps the entire label on one line.
   Column widths control visibility — set the width prop wide enough and the
   full label is always shown, no clipping, no dots. */
.az-ts-sort-btn,
.az-ts-th-inner {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 7px;
  width: 100%;
  white-space: nowrap;
}

/* Right/center-aligned columns (numeric values, via inferAlign()) — the
   header's label + hide/sort icons follow the same alignment as the body
   cells below them, instead of always packing to the left regardless of
   where the column's actual values sit. */
.az-ts-sort-btn.az-ts-th-inner--right,
.az-ts-th-inner.az-ts-th-inner--right {
  justify-content: flex-end;
}
.az-ts-sort-btn.az-ts-th-inner--center,
.az-ts-th-inner.az-ts-th-inner--center {
  justify-content: center;
}

.az-ts-sort-btn {
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: rgba(var(--az-black-rgb), 0.6);
  font-family: inherit;
}

/* Label: never shrinks, never wraps, always shows full text */
.az-ts-th-label {
  flex-shrink: 0;
  white-space: nowrap;
  text-align: left;
}

/* Eye icon and sort arrows also never shrink */
.az-table-hide-col,
.az-ts-sort-btn > svg {
  flex-shrink: 0;
}

.az-ts-sort-btn:focus-visible {
  outline: 2px solid var(--colorDark);
  outline-offset: 2px;
  border-radius: 2px;
}
.az-ts-sort-btn:hover {
  color: var(--az-text-primary);
}

/* ── Body cells — Figma specified 16px, but standardized to 14px across every
   table in the app (matches .az-ts-th's header size and the AntD-fallback
   branch's .ant-table-tbody > tr > td above) ── Regular(400) / rgba(0,0,0,0.9) / 88px row.
   Padding is 24px so a row containing the 40px .az-btn--sm action pill
   (Figma node 298:244/298:247) totals exactly 88px: 24 + 40 + 24. ── */
.az-ts-td {
  background: var(--az-white);
  color: rgba(var(--az-black-rgb), 0.9);
  font-size: 14px;
  font-weight: 400;
  padding: 24px 20px;
  border-bottom: 1px solid var(--az-gray-135);
  box-sizing: border-box;
  transition: background 0.12s;
  white-space: nowrap;
  overflow: hidden;
}

/* Small-size rows — same 14px row text as the default size now, just tighter padding */
.az-ts-wrap--small .az-ts-td {
  padding: 10px 16px;
}

/* Pinned body cells — left-pinned columns carry the Figma highlight + Medium weight */
.az-ts-td--pinned-left {
  background: var(--colorLight);
  z-index: 1;
  font-weight: 500;
}
.az-ts-td--pinned-right {
  background: var(--az-white);
  z-index: 1;
}

/* Keep the highlight visible on row hover for pinned-left cells */
.az-ts-tr:hover .az-ts-td--pinned-left {
  background-color: rgba(var(--colorDark-rgb), 0.13) !important;
}

/* Shadow lines on pinned body cells match the header shadow */
.az-ts-td--last-left {
  box-shadow: 4px 0 8px -4px rgba(var(--az-black-rgb), 0.1);
}
.az-ts-td--first-right {
  box-shadow: -4px 0 8px -4px rgba(var(--az-black-rgb), 0.1);
}

/* Ellipsis: only shows … when text genuinely overflows the column.
   Short text that fits shows completely — no dots. Hover title shows full value. */
.az-ts-td--ellipsis {
  text-overflow: ellipsis;
}

/* ── Row hover — entire row, NOT individual cells ──
   The !important overrides the background on pinned cells too,
   so the full row highlights uniformly during hover. */
.az-ts-tr:hover .az-ts-td {
  background-color: var(--az-gray-50) !important;
}

/* Remove last row's bottom border */
.az-ts-tbody .az-ts-tr:last-child .az-ts-td {
  border-bottom: none !important;
}

/* Virtual spacer rows must be invisible */
.az-ts-tr--spacer td {
  padding: 0 !important;
  border: none !important;
  background: transparent !important;
}

/* ── Empty state ── */
.az-ts-empty {
  text-align: center;
  padding: 48px 16px !important;
  color: var(--az-gray-500) !important;
  font-size: 13px !important;
  background: var(--az-white) !important;
  white-space: normal !important;
  border-bottom: none !important;
}

/* ── Footer ── */
.az-ts-footer {
  background: var(--az-white);
  border-top: 1px solid var(--az-gray-300);
  padding: 12px 16px;
}

/* ── Pagination ──
   CustomTable wraps TanStackTable with class "az-table" so the
   existing .az-table .ant-table-pagination rules apply to the
   AntD <Pagination className="ant-table-pagination"> element
   that sits directly inside. No duplicated CSS needed. */
.az-ts-wrap .ant-table-pagination.ant-pagination {
  border-radius: 0 0 var(--az-radius-md) var(--az-radius-md);
}

/* ── Mobile — card layout ─────────────────────────────────────
   Below 480 px the horizontal table becomes impossible to use.
   Switch to a vertical card where each cell shows its column
   label in a ::before pseudo-element.
   ─────────────────────────────────────────────────────────────*/
@media (max-width: 480px) {
  /* Card-layout rows stack to full natural height below — a sticky,
     viewport-bounded table box doesn't fit that model on small screens. */
  .az-ts-wrap,
  .az-table-wrap {
    position: static;
  }
  .az-table-sticky-region {
    min-height: 0;
  }
  /* The edit-columns button's sticky lock-step with the (now hidden, see
     .az-ts-thead below) header row doesn't fit the card-stack layout below.
     Pagination stays sticky to the screen bottom — with 100 tall cards per
     page it's the only way to reach it without scrolling the whole list. */
  .az-edit-column-sticky {
    position: static !important;
  }
  .az-ts-scroll {
    overflow-x: visible;
    overflow-y: visible;
    max-height: none !important;
  }
  .az-ts-table,
  .az-ts-thead,
  .az-ts-tbody,
  .az-ts-header-row,
  .az-ts-tr,
  .az-ts-th,
  .az-ts-td {
    display: block;
  }
  /* React sets an inline minWidth (sum of every column's width, easily
     700-1000px+) on .az-ts-table for the horizontal-scroll layout — block
     display alone doesn't clear it, so the card stack below would still
     sit inside that oversized box instead of the viewport's width. */
  .az-ts-table {
    min-width: 0 !important;
    width: 100% !important;
  }
  /* colgroup has no visual role in block layout */
  .az-ts-table colgroup,
  .az-ts-table col {
    display: none;
  }

  /* Hide the header row — labels come from data-label */
  .az-ts-thead {
    display: none;
  }

  .az-ts-tr {
    margin-bottom: 12px;
    border: 1px solid var(--az-gray-300);
    border-radius: var(--az-radius-md);
    overflow: hidden;
  }

  .az-ts-td {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 16px !important;
    white-space: normal !important;
    text-overflow: unset !important;
    overflow: visible !important;
    /* cancel sticky positioning — not meaningful in card layout */
    position: static !important;
    left: auto !important;
    right: auto !important;
    box-shadow: none !important;
  }
  /* Each cell carries an inline width/maxWidth = its desktop column width
     (CustomTable) — in the card it must span the card instead, and the
     value side must be allowed to shrink and wrap, otherwise long
     emails/names/descriptions get clipped by the card. */
  .az-ts-td {
    width: auto !important;
    max-width: none !important;
    text-align: left !important;
    overflow-wrap: break-word;
    word-break: break-word;
  }
  .az-ts-td > * {
    min-width: 0;
  }
  .az-ts-td::before {
    content: attr(data-label);
    font-weight: 500;
    color: var(--az-text-secondary);
    font-size: 13px;
    flex-shrink: 0;
    min-width: 110px;
  }
  /* The sidebar rail stays visible on phones, so the card is narrow —
     stack label above value so the value gets the card's full width. */
  .az-ts-td {
    flex-direction: column;
    gap: 2px;
  }
  .az-ts-td::before {
    min-width: 0;
    font-size: 12px;
  }
  /* Reclaim side padding for content next to the rail */
  .contentSection {
    padding: 8px 12px;
  }
  .az-ts-td:last-child {
    border-bottom: none;
  }

  /* Utility columns (Sr.No., checkboxes) are noise on mobile */
  .az-ts-td[data-col-utility="true"] {
    display: none;
  }
}

/* ══ Phones (≤767px) — compact type, controls and spacing ══════════════
   Desktop/tablet sizing is untouched: everything below only applies under
   this breakpoint. Values are the desktop ones scaled down (titles 28→20,
   controls 44/40→36, 16→14px button text, tighter form padding/gaps).
   !important only where the desktop rule it overrides already uses it. */
@media (max-width: 767px) {
  /* Page titles + subtitles */
  .page-card-title,
  h2.page-card-title {
    font-size: 20px !important;
    line-height: 1.3;
  }
  .page-card-title .page-title-count {
    font-size: inherit;
  }
  .page-card-subtitle,
  .page-card-subtitle--hero {
    font-size: 12px !important;
    line-height: 1.45;
    margin-top: 2px;
  }
  .page-card-header {
    gap: 10px;
    min-height: 0;
  }

  /* Buttons: page actions, form Submit/Cancel, Download/Filters */
  .customButtonOutlinedBoard,
  .customButtonOutlinedCancel,
  .az-btn,
  .az-btn--xl {
    height: 36px !important;
    font-size: 14px !important;
    padding: 0 14px !important;
  }
  .az-btn__icon {
    font-size: 12px;
  }
  .filterStyle {
    gap: 6px;
  }

  /* Topbar search + help */
  .page-topbar-search,
  .az-search-input {
    height: 36px !important;
    font-size: 13px;
  }
  .need-help-btn {
    height: 36px !important;
    min-width: 36px;
    padding: 0 10px !important;
  }

  /* Tabs */
  .ant-tabs-tab {
    font-size: 13px;
    padding: 8px 0 !important;
  }
  .ant-tabs-tab + .ant-tabs-tab {
    margin-left: 20px !important;
  }

  /* Forms */
  .page-card-form {
    padding: 16px 14px !important;
  }
  .ant-form-item {
    margin-bottom: 12px !important;
  }
  .ant-form-item-label > label {
    font-size: 13px !important;
  }
  .ant-form-vertical .ant-form-item-label {
    padding-bottom: 4px !important;
  }
  .ant-form-item .ant-input-affix-wrapper,
  .ant-form-item input.ant-input,
  .ant-form-item .ant-picker,
  .ant-form-item .ant-input-number,
  .ant-form-item .customFormControl {
    min-height: 36px;
    height: 36px !important;
    font-size: 14px;
  }
  .ant-form-item .ant-input-affix-wrapper input.ant-input {
    height: auto !important;
    min-height: 0;
  }
  .ant-form-item textarea.ant-input {
    height: auto !important;
    font-size: 14px;
  }
  .ant-form-item .ant-select-single .ant-select-selector,
  .contentSection .ant-form-vertical .ant-select:not(.ant-select-customize-input) .ant-select-selector {
    height: 36px !important;
    padding: 2px 12px !important;
  }
  .ant-form-item .ant-select-multiple .ant-select-selector {
    min-height: 36px !important;
    height: auto !important;
  }
  .ant-form-item .ant-select-single .ant-select-selection-item,
  .ant-form-item .ant-select-single .ant-select-selection-placeholder,
  .contentSection .ant-form-vertical .ant-select-single .ant-select-selector .ant-select-selection-item,
  .contentSection .ant-form-vertical .ant-select-single .ant-select-selector .ant-select-selection-placeholder {
    line-height: 30px !important;
    font-size: 14px;
  }

  /* Table cards */
  .az-ts-td {
    font-size: 13px;
  }

  /* Compact pager (CustomTable switches to AntD's simple mode on phones):
     one centred row, pinned to the screen bottom */
  .az-table .ant-table-pagination.ant-pagination,
  .az-table .ant-table-pagination {
    justify-content: center !important;
    padding: 8px !important;
    font-size: 13px;
  }
  .az-table .ant-pagination-simple .ant-pagination-simple-pager {
    margin-right: 4px !important;
  }
  .az-table .ant-pagination-simple .ant-pagination-simple-pager input {
    width: 36px;
    height: 30px;
    margin-right: 4px !important;
    text-align: center;
  }
  .az-table .ant-pagination-total-text {
    margin-right: auto;
    font-size: 12px;
  }
  /* AntD hides the rows-per-page changer below 576px — keep it */
  .az-table .ant-pagination .ant-pagination-options {
    display: inline-flex !important;
    margin-left: 4px !important;
  }
  .az-table .ant-pagination-simple .ant-pagination-prev,
  .az-table .ant-pagination-simple .ant-pagination-next {
    margin: 0 !important;
  }
  .az-table .ant-pagination-options .ant-select-selector {
    height: 30px !important;
    font-size: 13px;
  }
}

/* SECTION SMART DASHBOARD — src/pages/Dashboard (.sd-*)
   One screen on desktop: header · KPI row · main area (70% graphs | 30%
   pies) filling exactly the viewport below the 80px topbar — charts and
   donuts flex to the space left, no page scroll. Narrow or short screens
   fall back to a normal scrolling stack. One accent (--colorDark) for
   single-series data; text in text tokens; solid hairline grids. */
.sd-page {
  padding: 14px 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
@media (min-width: 992px) and (min-height: 640px) {
  .sd-page {
    height: calc(100vh - 80px);
    height: calc(100dvh - 80px);
    overflow: hidden;
  }
}

/* Header — one row */
.sd-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.sd-head__text {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;
}
.sd-head__title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--az-text-primary);
  line-height: 1.25;
}
.sd-head__sub {
  font-size: 13px;
  color: var(--az-text-secondary);
}
.sd-head__actions {
  display: flex;
  gap: 8px;
}
.sd-head__actions .ant-btn {
  height: 36px !important;
  font-size: 14px !important;
}

/* KPI tiles — compact */
.sd-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  flex-shrink: 0;
}
.sd-kpi {
  background: var(--az-white);
  border: 1px solid var(--az-border-subtle);
  border-radius: 12px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.sd-kpi__top {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.sd-kpi__icon {
  width: 24px;
  height: 24px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--colorDark, #007aff);
  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.1);
  font-size: 13px;
}
.sd-kpi__label {
  font-size: 13px;
  font-weight: 500;
  color: var(--az-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sd-kpi__main {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
}
.sd-kpi__value {
  font-size: 22px;
  font-weight: 600;
  line-height: 1.15;
  color: var(--az-text-primary);
  white-space: nowrap;
}
.sd-kpi__meta {
  margin-top: 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  align-items: center;
}
.sd-kpi__spark {
  width: 96px;
  height: 38px;
  flex-shrink: 0;
}
.sd-kpi__delta {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
/* Theme colours only: up in the theme colour, down in neutral ink — the
   arrow carries the direction, so meaning never rests on colour alone */
.sd-kpi__delta--good {
  color: var(--colorDark, #007aff);
}
.sd-kpi__delta--bad {
  color: var(--az-text-secondary, #6b7280);
}
.sd-kpi__delta--flat {
  color: var(--az-text-tertiary, #9ca3af);
  font-weight: 500;
}
.sd-kpi__period {
  font-weight: 400;
  color: var(--az-text-secondary);
}

/* Sample-data tag */
.sd-sample-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 8px;
  border-radius: var(--az-radius-pill, 999px);
  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.1);
  color: var(--colorDark, #007aff);
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  cursor: help;
}

/* Main area: 70% graphs | 30% pies, filling the remaining height */
.sd-main {
  flex: 1 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 7fr) minmax(0, 3fr);
  gap: 12px;
}
.sd-col {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
  min-width: 0;
}
.sd-col--graphs .sd-card--revenue {
  flex: 3 1;
}
.sd-col--graphs .sd-card--bars {
  flex: 2 1;
}
.sd-col--pies .sd-card {
  flex: 1 1;
}

/* Card shell */
.sd-card {
  background: var(--az-white);
  border: 1px solid var(--az-border-subtle);
  border-radius: 12px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  min-height: 0;
}
.sd-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  flex-shrink: 0;
}
.sd-card__titles {
  min-width: 0;
}
.sd-card__title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--az-text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.sd-card__subtitle {
  margin: 2px 0 0;
  font-size: 12px;
  color: var(--az-text-secondary);
}
.sd-card__actions {
  display: flex;
  align-items: center;
  gap: 6px;
}
.sd-card__body {
  flex: 1 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.sd-card__state {
  flex: 1 1;
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--az-text-secondary);
  font-size: 13px;
}
.sd-inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.sd-inline strong {
  color: var(--az-text-primary);
  font-weight: 600;
}

/* Compact pill tabs + icon button in card headers */
.sd-card .sd-range .az-btn,
.sd-card .sd-range button {
  height: 28px !important;
  min-width: 0 !important;
  padding: 0 10px !important;
  font-size: 12px !important;
}
.sd-icon-btn {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid var(--az-border-subtle);
  background: var(--az-white);
  color: var(--az-text-secondary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.sd-icon-btn:hover {
  color: var(--az-text-primary);
  background: var(--az-gray-50);
}

/* Charts fill their card */
.sd-chart {
  flex: 1 1;
  min-height: 0;
  min-width: 0;
}

/* Tooltip */
.sd-tooltip {
  background: var(--az-white);
  border: 1px solid var(--az-border-subtle);
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(var(--az-black-rgb, 0, 0, 0), 0.1);
  padding: 8px 10px;
  min-width: 130px;
}
.sd-tooltip__label {
  font-size: 12px;
  color: var(--az-text-secondary);
  margin-bottom: 4px;
}
.sd-tooltip__row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.sd-tooltip__key {
  width: 12px;
  height: 2px;
  border-radius: 1px;
}
.sd-tooltip__value {
  font-size: 14px;
  font-weight: 600;
  color: var(--az-text-primary);
}
.sd-tooltip__name {
  font-size: 12px;
  color: var(--az-text-secondary);
}

/* Donut cards: donut fills the free height, legend below */
.sd-donut {
  flex: 1 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sd-donut__chart {
  position: relative;
  flex: 1 1;
  min-height: 110px;
}
.sd-donut__center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
.sd-donut__total {
  font-size: 17px;
  font-weight: 600;
  color: var(--az-text-primary);
}
.sd-donut__label {
  font-size: 11px;
  color: var(--az-text-secondary);
}

/* Shared legend */
.sd-legend {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
}
.sd-legend li {
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr) auto 46px;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}
.sd-swatch {
  width: 10px;
  height: 10px;
  border-radius: 3px;
}
.sd-legend__name {
  color: var(--az-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sd-legend__value {
  font-weight: 600;
  color: var(--az-text-primary);
  font-variant-numeric: tabular-nums;
}
.sd-legend__pct {
  text-align: right;
  color: var(--az-text-secondary);
  font-variant-numeric: tabular-nums;
}

/* Revenue table view (scrolls inside the card) */
.sd-table-wrap {
  flex: 1 1;
  min-height: 0;
  overflow: auto;
}
.sd-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.sd-table th {
  position: sticky;
  top: 0;
  background: var(--az-white);
  text-align: left;
  font-size: 12px;
  font-weight: 500;
  color: var(--az-text-secondary);
  padding: 6px 10px;
  border-bottom: 1px solid var(--az-border-subtle);
}
.sd-table td {
  padding: 7px 10px;
  border-bottom: 1px solid var(--az-border-subtle);
  color: var(--az-text-primary);
}
.sd-num {
  text-align: right !important;
  font-variant-numeric: tabular-nums;
}

/* Tablet / small laptop: graphs full width, pies side by side below */
@media (max-width: 991.98px) {
  .sd-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .sd-main {
    grid-template-columns: minmax(0, 1fr);
  }
  .sd-col--graphs .sd-card--revenue {
    height: 320px;
    flex: none;
  }
  .sd-col--graphs .sd-card--bars {
    height: 240px;
    flex: none;
  }
  .sd-col--pies {
    flex-direction: row;
  }
  .sd-col--pies .sd-card {
    height: 300px;
  }
}

/* Phones */
@media (max-width: 575.98px) {
  .sd-page {
    padding: 12px;
  }
  .sd-head__title {
    font-size: 18px;
  }
  .sd-head__actions {
    width: 100%;
  }
  .sd-head__actions > a {
    flex: 1 1;
  }
  .sd-head__actions .ant-btn {
    width: 100%;
  }
  .sd-kpi__value {
    font-size: 20px;
  }
  .sd-kpi__spark,
  .sd-kpi__period {
    display: none;
  }
  .sd-col--pies {
    flex-direction: column;
  }
  .sd-col--graphs .sd-card--revenue {
    height: 280px;
  }
  .sd-col--graphs .sd-card--bars {
    height: 220px;
  }
}

/* ── Image upload field (components/ImageUploadField) ── */
.az-image-upload {
  display: flex;
  align-items: center;
  gap: var(--az-space-3);
  flex-wrap: wrap;
}
.az-image-upload__preview {
  width: 96px;
  height: 96px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px dashed var(--az-gray-300);
  border-radius: var(--az-radius-sm);
  background: var(--az-gray-50);
}
.az-image-upload__preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.az-image-upload__placeholder {
  font-size: 28px;
  color: var(--az-text-secondary);
}
.az-image-upload__actions {
  display: flex;
  align-items: center;
  gap: var(--az-space-2);
  flex-wrap: wrap;
  min-width: 0;
}
.az-image-upload__name {
  color: var(--az-text-secondary);
  font-size: 12px;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* The error message turns the frame red, like other invalid fields */
.ant-form-item-has-error .az-image-upload__preview {
  border-color: var(--az-red-500);
}

/* ── Add Sales Agent wizard (pages/AffiliateTemplate/CreateAffiliate) ── */
/* 5 steps: drop the 2-step min width so they fit on one line */
.onboarding-steps--multi.ant-steps {
  width: 100%;
}
.onboarding-steps--multi.ant-steps .ant-steps-item:not(:last-child) {
  min-width: 0;
  flex: 1 1 auto !important;
}
.az-wizard-actions {
  margin-top: var(--az-space-2);
}
.az-wizard-loading {
  display: flex;
  justify-content: center;
  padding: 48px 0;
}
.az-wizard-verified {
  display: flex;
  align-items: center;
  gap: var(--az-space-2);
  color: var(--az-text-primary);
  margin-bottom: var(--az-space-3);
}
.az-wizard-verified .anticon {
  color: var(--az-green-600);
  font-size: 18px;
}
.az-wizard-link {
  display: inline-block;
  margin: -12px 0 var(--az-space-3);
  font-size: 13px;
}
.az-wizard-hint {
  color: var(--az-text-secondary);
  font-size: 13px;
  margin: 0 0 var(--az-space-3);
}
.az-kyc-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--az-space-3);
  margin-bottom: var(--az-space-3);
}
.az-kyc-card {
  border: 1px solid var(--az-border-subtle);
  border-radius: var(--az-radius-sm);
  padding: var(--az-space-3) 16px 0;
  background: var(--az-white);
  min-width: 0;
}
.az-kyc-card__title {
  font-size: 15px;
  font-weight: 600;
  color: var(--az-text-primary);
  margin: 0 0 var(--az-space-3);
}
.az-kyc-card__sides {
  display: flex;
  flex-wrap: wrap;
  column-gap: 24px;
}
@media (max-width: 767px) {
  .az-kyc-grid {
    grid-template-columns: 1fr;
  }
}
.az-image-upload__pdf {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  color: var(--az-red-500);
  font-size: 28px;
}
.az-image-upload__pdf span {
  font-size: 11px;
  font-weight: 600;
}

/* Repeatable sections in registration wizards (licences, markets, warehouses) */
.az-repeat-card {
  border: 1px solid var(--az-border-subtle);
  border-radius: var(--az-radius-sm);
  padding: var(--az-space-3) 16px 0;
  margin-bottom: var(--az-space-3);
  background: var(--az-white);
}
.az-repeat-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--az-space-2);
}
.az-repeat-card__head .az-kyc-card__title {
  margin: 0 0 var(--az-space-3);
}
.az-repeat-add {
  display: flex;
  margin-bottom: var(--az-space-3);
}
.az-wizard-check {
  display: flex;
  width: fit-content;
  margin-bottom: var(--az-space-3);
}
.az-progress-cell {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}
.az-progress-cell small {
  color: var(--az-text-secondary);
}

/* Sales Agent wizard — app sub-screens (KYC docs, agreements) */
.az-kyc-substeps.ant-steps {
  max-width: 720px;
  margin-bottom: var(--az-space-3);
  padding: var(--az-space-2) var(--az-space-3);
  border-radius: var(--az-radius-sm);
  background: var(--az-gray-50);
}
.az-agreement-frame {
  display: block;
  width: 100%;
  height: 420px;
  border: 1px solid var(--az-border-subtle);
  border-radius: var(--az-radius-sm);
  background: var(--az-white);
  margin-bottom: var(--az-space-3);
}
.az-language-options.ant-radio-group {
  display: flex;
  flex-direction: column;
  gap: var(--az-space-2);
}
@media (max-width: 767px) {
  .az-agreement-frame {
    height: 60vh;
  }
}

/* T-Ops product logo (replaces the "Admin Portal" wordmark) */
.az-login__productLogo {
  display: block;
  height: 56px;
  width: auto;
  max-width: 100%;
}
.brandProductLogo {
  display: block;
  height: 26px;
  width: auto;
}
@media (max-width: 767px) {
  .brandProductLogo {
    height: 22px;
  }
}

/* ── Portal name (T-Ops) in the sidebar brand band ── */
.sb-brand-logo--tops {
  max-height: 34px;
  max-width: 100%;
  object-fit: contain;
}
/* Collapsed platform rail: the T mark painted in the Traidster gradient
   (same as the old "T" initial) via a mask of the logo's T */
.sb-brand-tmark {
  display: block;
  width: 36px;
  height: 32px;
  background: linear-gradient(
    135deg,
    var(--traidster-cobalt) 0%,
    var(--traidster-midpoint) 50%,
    var(--traidster-cyan) 100%
  );
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;
}

/* Portal name in the top bar, before the breadcrumb — client users (platform
   users see T-Ops in the sidebar brand band) */
.page-topbar-portal {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding-right: 12px;
  border-right: 1px solid var(--az-border-subtle);
}
.page-topbar-portal img {
  display: block;
  height: 20px;
  width: auto;
}
@media (max-width: 767px) {
  .home-section .page-topbar .page-topbar-portal {
    display: none;
  }
}
`, "",{"version":3,"sources":["webpack://./src/assets/css/style.css"],"names":[],"mappings":"AAAA;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;iEAgCiE;;AAEjE;EACE;2EACyE;EACzE,oBAAoB;EACpB,qCAAqC;EACrC,4BAA4B;;EAE5B,gEAAgE;EAChE,qBAAqB;EACrB,sBAAsB;EACtB,sBAAsB;;EAEtB,wEAAwE;EACxE,qCAAqC;EACrC,uCAAuC;EACvC,sCAAsC;EACtC,uCAAuC;;EAEvC;;0EAEwE;EACxE,mBAAmB;EACnB,oBAAoB;EACpB,oBAAoB;EACpB,wBAAwB;;EAExB,YAAY;EACZ,+CAA+C;EAC/C,uDAAuD;;EAEvD,6BAA6B;EAC7B,iBAAiB;EACjB,iBAAiB;EACjB,kBAAkB;EAClB,kBAAkB;EAClB,kBAAkB;;EAElB,sBAAsB;EACtB,mBAAmB;EACnB,6BAA6B;EAC7B,mBAAmB;EACnB,uBAAuB;EACvB,wBAAwB,EAAE;;4EAEgD;;EAE1E;;;;oCAIkC;EAClC,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,+BAA+B;EAC/B,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;;EAEtB,+EAA+E;EAC/E,uBAAuB;EACvB,uBAAuB;EACvB,uBAAuB;EACvB,uBAAuB;;EAEvB;qCACmC;EACnC,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB;;;yEAGuE;EACvE,8BAA8B;;EAE9B,uBAAuB;EACvB,qBAAqB;EACrB,qBAAqB;EACrB,qBAAqB;EACrB,6BAA6B;EAC7B,qBAAqB;EACrB,6BAA6B;EAC7B,2BAA2B;;EAE3B,0BAA0B;EAC1B,uBAAuB;EACvB,uBAAuB;;EAEvB;;;kEAGgE;EAChE,wBAAwB;;EAExB;oDACkD;EAClD,iCAAiC;EACjC,+BAA+B;EAC/B,6BAA6B;AAC/B;;AAEA,2FAA2F;;AAE3F;EACE;;oEAEkE;EAClE,oEAAoE;EACpE,gBAAgB;EAChB,sBAAsB;AACxB;;AAEA,gCAAgC;AAChC;;EAEE,wBAAwB;EACxB,SAAS;AACX;;AAEA,YAAY;AACZ;EACE,0BAA0B;AAC5B;;AAEA;;GAEG;;AAEH;EACE,UAAU;EACV,YAAY;AACd;;AAEA;EACE,uBAAuB;AACzB;;AAEA;EACE,8BAA8B;AAChC;;AAEA;;EAEE,oCAAoC;EACpC,UAAU;AACZ;;AAEA;EACE,0BAA0B;AAC5B;;AAEA;EACE,oCAAoC;AACtC;;AAEA;EACE,oCAAoC;AACtC;;AAEA;EACE,mBAAmB;AACrB;;AAEA;EACE,+CAA+C;AACjD;;AAEA;EACE,oCAAoC;AACtC;;AAEA;EACE,oCAAoC;AACtC;;AAEA;EACE,oCAAoC;AACtC;;AAEA;EACE,oCAAoC;AACtC;;AAEA;EACE,aAAa;EACb,cAAc;AAChB;;AAEA;EACE,aAAa;EACb,cAAc;EACd,UAAU;EACV,YAAY;EACZ,yBAAyB;AAC3B;;AAEA;EACE,qBAAqB;AACvB;;AAEA;EACE,gBAAgB;AAClB;;AAEA;EACE,eAAe;EACf,kBAAkB;EAClB,yBAAyB;EACzB,gBAAgB;AAClB;;AAEA;EACE,6BAA6B;AAC/B;;AAEA,uDAAuD;;AAEvD;;;;GAIG;AACH;EACE,aAAa;EACb,cAAc;EACd,yBAAyB;AAC3B;;AAEA,oCAAoC;;AAEpC,kfAAkf;;AAElf;EACE,uBAAuB;EACvB,wCAAwC;EACxC,wCAAwC;EACxC,2CAA2C;EAC3C,8BAA8B;EAC9B,uBAAuB;EACvB,gCAAgC;EAChC,0BAA0B;AAC5B;;AAEA;EACE,uBAAuB;EACvB,6BAA6B;EAC7B,+CAA+C;EAG/C,4DAA4D;AAC9D;;AAEA;;;iCAGiC;AACjC;EACE,sBAAsB;EACtB,0BAA0B;EAC1B,2BAA2B;EAC3B,iDAAiD;EACjD,wCAAwC;EACxC,wDAAwD;EACxD,6BAA6B;EAC7B;;0EAEwE;EACxE,uBAAuB;EACvB,gCAAgC;EAChC,4BAA4B;EAC5B,SAAS;EACT,2BAA2B;EAC3B,eAAe;EACf,eAAe;AACjB;AACA;EACE,sBAAsB;EACtB,0BAA0B;EAC1B,2BAA2B;EAC3B,wCAAwC;;EAExC,+CAA+C;EAC/C,wCAAwC;EACxC,6BAA6B;EAC7B;;2DAEyD;EACzD,uBAAuB;EACvB,gCAAgC;EAChC,4BAA4B;EAC5B,SAAS;EACT,2BAA2B;EAC3B,eAAe;EACf,eAAe;AACjB;AACA,kBAAkB;AAClB;EACE,sBAAsB;EACtB,0BAA0B;EAC1B,2BAA2B;EAC3B,uBAAuB;EACvB,wCAAwC;EACxC,4CAA4C;EAC5C,6BAA6B;EAC7B,uBAAuB;EACvB,gCAAgC;EAChC,4BAA4B;EAC5B,WAAW;AACb;AACA;EACE,sBAAsB;EACtB,wCAAwC;EACxC,gDAAgD;EAChD,2CAA2C;EAG3C,4DAA4D;AAC9D;;AAEA,cAAc;AACd;EACE,sBAAsB;EACtB,0BAA0B;EAC1B,2BAA2B;EAC3B,uBAAuB;EACvB,wCAAwC;EACxC,2CAA2C;EAC3C,6BAA6B;EAC7B,uBAAuB;EACvB,gCAAgC;EAChC,4BAA4B;EAC5B,WAAW;EACX,eAAe;AACjB;AACA;EACE,sBAAsB;EACtB,wCAAwC;EACxC,+CAA+C;EAC/C,2CAA2C;EAG3C,4DAA4D;AAC9D;;AAEA;EACE,+BAA+B;EAC/B,8BAA8B;EAC9B,kCAAkC;EAClC,mBAAmB;EACnB,uBAAuB;EACvB,0BAA0B;EAC1B,uBAAuB;EACvB,+CAA+C;EAC/C,iEAAiE;EACjE,2CAA2C;EAC3C,0BAA0B;EAC1B,2BAA2B;EAC3B,yBAAyB;EACzB,0BAA0B;EAC1B,8BAA8B;EAC9B,uCAAuC;EACvC,4BAA4B;EAC5B,oBAAoB;EACpB,eAAe;EACf,kBAAkB;AACpB;;AAEA,iEAAiE;AACjE;;EAEE,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,WAAW;EACX,YAAY;EACZ,eAAe;EACf,yBAAyB;EACzB,qBAAqB;AACvB;AACA;EACE,+BAA+B;EAC/B,8BAA8B;EAC9B,kCAAkC;EAClC,mBAAmB;EACnB,sBAAsB;EACtB,uBAAuB;EACvB,0BAA0B;EAC1B,0BAA0B;EAC1B,2BAA2B;EAC3B,yBAAyB;EACzB,oCAAoC;EACpC,+CAA+C;EAC/C,uBAAuB;EACvB,+CAA+C;EAC/C;;2BAEyB;EACzB,4BAA4B;EAC5B,2BAA2B;EAC3B,oBAAoB;EACpB,eAAe;AACjB;;AAEA;EACE,oCAAoC;EACpC,+CAA+C;AACjD;AACA;EACE,uBAAuB;EACvB,wCAAwC;EACxC,2CAA2C;EAC3C,uBAAuB;EACvB,4BAA4B;EAC5B,0BAA0B;EAC1B,0BAA0B;AAC5B;AACA;uEACuE;AACvE;EACE,qCAAqC;AACvC;AACA;EACE,qCAAqC;AACvC;AACA;EACE,wCAAwC;EACxC,+CAA+C;EAC/C,2CAA2C;AAC7C;AACA;EACE,wCAAwC;EACxC,+CAA+C;EAC/C,2CAA2C;AAC7C;;AAEA;EACE,8BAA8B;AAChC;;AAEA;EACE,sBAAsB;EACtB,0BAA0B;EAC1B,2BAA2B;EAC3B,oCAAoC;EACpC,wCAAwC;EACxC,2CAA2C;EAC3C,6BAA6B;EAC7B,uBAAuB;EACvB,gCAAgC;EAChC,4BAA4B;EAC5B,WAAW;EACX,iCAAiC;EACjC,eAAe;EACf,0BAA0B;AAC5B;AACA;EACE,aAAa;EACb,oBAAoB;EACpB,uBAAuB;EACvB,YAAY;AACd;;AAEA;EACE,mCAAmC,EAAE,wBAAwB;EAC7D,qCAAqC;EACrC,kBAAkB;AACpB;;AAEA;EACE,oEAAoE;EACpE,2CAA2C;EAC3C,uBAAuB;EACvB,2BAA2B;AAC7B;;AAEA;EACE,sBAAsB;EACtB,0BAA0B;EAC1B,2BAA2B;EAC3B,yCAAyC;EACzC,gDAAgD;EAChD,2CAA2C;EAC3C,6BAA6B;EAC7B,uBAAuB;EACvB,gCAAgC;EAChC,4BAA4B;AAC9B;;AAEA;EACE,sBAAsB;EACtB,wCAAwC;EACxC,+CAA+C;EAG/C,4DAA4D;AAC9D;AACA;EACE,sBAAsB;AACxB;AACA;EACE,sBAAsB;AACxB;;AAEA;EACE,0BAA0B;EAC1B,gBAAgB;AAClB;AACA;EACE,2BAA2B;EAC3B,gBAAgB;AAClB;;AAEA,mBAAmB;AACnB;EACE,sBAAsB;EACtB,aAAa;EACb,kBAAkB;EAClB,mBAAmB;AACrB;;AAEA,sEAAsE;;AAEtE,2BAA2B;AAC3B;;EAEE,oBAAoB;EACpB,YAAY;EACZ,0BAA0B;EAC1B,8BAA8B;EAC9B,sBAAsB;AACxB;;AAEA;EACE,qBAAqB;AACvB;;AAEA;EACE,gBAAgB;AAClB;;AAEA;EACE,kBAAkB;EAClB,SAAS;EACT,yBAAyB;EACzB,cAAc;AAChB;;AAEA;EACE,kBAAkB;EAClB,YAAY;EACZ,gBAAgB;AAClB;;AAEA;EACE,eAAe;EACf,uBAAuB;EACvB,2CAA2C;AAC7C;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,8BAA8B;EAC9B,mBAAmB;AACrB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,mBAAmB;EACnB,WAAW;AACb;;AAEA;EACE,WAAW;EACX,oCAAoC;EACpC,kBAAkB;EAClB,YAAY;AACd;;AAEA;EACE,oCAAoC;EACpC,kBAAkB;EAClB,YAAY;AACd;;AAEA;EACE,WAAW;EACX,oCAAoC;EACpC,kBAAkB;EAClB,YAAY;AACd;;AAEA;EACE,oCAAoC;EACpC,kBAAkB;EAClB,YAAY;AACd;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,8BAA8B;EAC9B,qBAAqB;EACrB,iBAAiB;EACjB,mBAAmB;AACrB;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,kBAAkB;;EAElB,8BAA8B;EAC9B,qBAAqB;EACrB,iBAAiB;EACjB,mBAAmB;AACrB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,mBAAmB;AACrB;;AAEA;EACE,2BAA2B;AAC7B;;AAEA;EACE,2BAA2B;EAC3B,mCAAmC;EACnC,sCAAsC;AACxC;;AAEA;EACE,2BAA2B;EAC3B,iBAAiB;EACjB,qBAAqB;EACrB,8CAA8C;EAC9C,iDAAiD;AACnD;AACA;EACE,qCAAqC;AACvC;AACA;EACE,+CAA+C;AACjD;;AAEA;;EAEE,oBAAoB;EACpB,YAAY;EACZ,0BAA0B;EAC1B,8BAA8B;EAC9B,gBAAgB;AAClB;AACA;;;sDAGsD;AACtD;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,QAAQ;EACR,YAAY;EACZ,eAAe;EACf,6BAA6B;EAC7B,yCAAyC;EACzC,6BAA6B;EAC7B,oCAAoC;EACpC,eAAe;EACf,gBAAgB;EAChB,mBAAmB;AACrB;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,QAAQ;EACR,YAAY;EACZ,eAAe;EACf,2BAA2B;EAC3B,yCAAyC;EACzC,6BAA6B;EAC7B,oCAAoC;EACpC,eAAe;EACf,gBAAgB;EAChB,mBAAmB;EACnB,gBAAgB;EAChB,iBAAiB;AACnB;;AAEA;;yEAEyE;AACzE;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,YAAY;EACZ,eAAe;EACf,mCAAmC;EACnC,2BAA2B;EAC3B,yCAAyC;EACzC,oCAAoC;EACpC,6BAA6B;EAC7B,eAAe;EACf,mBAAmB;AACrB;AACA;EACE,gBAAgB;AAClB;AACA;iEACiE;AACjE;EACE;IACE,eAAe;IACf,YAAY;IACZ,gBAAgB;IAChB,eAAe;IACf,mBAAmB;EACrB;AACF;AACA;EACE,+BAA+B;AACjC;AACA;EACE,eAAe;EACf,eAAe;EACf,mDAAmD;EACnD,gBAAgB;AAClB;AACA;EACE,6BAA6B;AAC/B;AACA;EACE,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,QAAQ;EACR,gBAAgB;AAClB;;AAEA;;;;;;;;6BAQ6B;AAC7B;EACE;IACE,cAAc;IACd,eAAe;EACjB;AACF;AACA;EACE;IACE,cAAc;IACd,eAAe;EACjB;AACF;AACA;EACE;IACE,cAAc;IACd,eAAe;EACjB;AACF;AACA;EACE;IACE,cAAc;IACd,eAAe;EACjB;AACF;;AAEA;;;+BAG+B;AAC/B;EACE;IACE,yBAAyB;IACzB,0BAA0B;EAC5B;AACF;;AAEA;;kDAEkD;AAClD;EACE;IACE,mBAAmB;IACnB,gBAAgB;EAClB;EACA;IACE,SAAS;EACX;EACA;qDACmD;EACnD;;IAEE,SAAW;IACX,YAAY;EACd;EACA;kEACgE;EAChE;IACE,aAAa;IACb,uBAAuB;EACzB;EACA;IACE,cAAc;IACd,YAAY;EACd;EACA;IACE,cAAc;EAChB;AACF;;AAEA;;iEAEiE;AACjE;EACE;IACE,YAAY;IACZ,eAAe;EACjB;EACA;IACE,eAAe;IACf,2BAA2B;EAC7B;EACA;yEACuE;EACvE;;IAEE,eAAe;IACf,YAAY;EACd;EACA,oEAAoE;EACpE;;IAEE,YAAY;EACd;EACA;IACE,cAAc;IACd,YAAY;IACZ,gBAAgB;IAChB,uBAAuB;EACzB;AACF;;AAEA;;;GAGG;AACH;EACE,qBAAqB;AACvB;;AAEA;EACE,eAAe;EACf,uBAAuB;EACvB,2CAA2C;AAC7C;;AAEA,4BAA4B;;AAE5B,8BAA8B;AAC9B;EACE,WAAW;EACX,aAAa;EACb,kBAAkB;EAClB,aAAa;EACb,uBAAuB;EACvB,mBAAmB;EACnB,oDAAoD;AACtD;;AAEA;;GAEG;AACH;EACE,mBAAmB;EACnB,YAAY;EACZ,cAAc;EACd,eAAe;EAGf,2DAA2D;AAC7D;;AAEA;EACE,kBAAkB;AACpB;;AAEA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,6BAA6B;AAC/B;;AAEA;EACE,oCAAoC;EACpC,6BAA6B;EAC7B,uBAAuB;EACvB,4BAA4B;EAC5B,WAAW;AACb;;AAEA;EACE,oCAAoC;EACpC,6BAA6B;EAC7B,WAAW;EACX,uBAAuB;AACzB;;AAEA;EACE,mCAAmC;AACrC;;AAEA;EACE,sBAAsB;EACtB,wBAAwB;AAC1B;;AAEA;EACE,kBAAkB;AACpB;;AAEA;;;;;8CAK8C;AAC9C;EACE,aAAa;EACb,iBAAiB;EACjB,WAAW;EACX,2BAA2B;AAC7B;AACA;EACE,kBAAkB;EAClB,aAAa;EACb,gBAAgB;EAChB,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,aAAa;EACb;;;;;;yEAMuE;EACvE;;;;GAIC;AACH;AACA;EACE,kBAAkB;EAClB,kBAAkB;EAClB,2CAA2C;EAC3C,oBAAoB;AACtB;AACA;EACE,YAAY;EACZ,aAAa;EACb,WAAW;EACX,aAAa;AACf;AACA;EACE,YAAY;EACZ,aAAa;EACb,cAAc;EACd,WAAW;EACX,2CAA2C;AAC7C;AACA;EACE,kBAAkB;EAClB,UAAU;EACV,gBAAgB;EAChB,sBAAsB;AACxB;AACA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,kBAAkB;EAClB,mBAAmB;EACnB,2CAA2C;EAC3C,mBAAmB;AACrB;AACA;EACE,YAAY;EACZ,WAAW;EACX,cAAc;AAChB;AACA;;qDAEqD;AACrD;EACE,kBAAkB;AACpB;AACA;EACE,kBAAkB;EAClB,WAAW;EACX,YAAY;EACZ,UAAU;EACV,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,iBAAiB;EACjB,qBAAqB;EACrB,mBAAmB;EACnB,uCAAuC;EACvC,4CAA4C;EAC5C,mBAAmB;AACrB;AACA;EACE,eAAe;EACf,gBAAgB;EAChB,yCAAyC;AAC3C;AACA;EACE,YAAY;EACZ,WAAW;EACX,cAAc;AAChB;AACA;EACE,eAAe;EACf,gBAAgB;EAChB,gBAAgB;EAChB,sBAAsB;AACxB;AACA;EACE,eAAe;EACf,gBAAgB;EAChB,sCAAsC;EACtC,SAAS;AACX;AACA;EACE,aAAa;EACb,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,kBAAkB;EAClB,2BAA2B;AAC7B;AACA;EACE,WAAW;EACX,gBAAgB;AAClB;AACA;EACE,mBAAmB;AACrB;AACA;EACE,eAAe;EACf,gBAAgB;EAChB,iDAAiD;EACjD,eAAe;AACjB;AACA;EACE,eAAe;EACf,mDAAmD;EACnD,SAAS;AACX;AACA;EACE,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,mBAAmB;AACrB;AACA;EACE,iCAAiC;EACjC,gBAAgB;EAChB,qBAAqB;AACvB;AACA;EACE,0BAA0B;AAC5B;AACA;EACE,kBAAkB;EAClB,mDAAmD;EACnD,eAAe;EACf,gBAAgB;AAClB;AACA;;;sCAGsC;AACtC;EACE,WAAW;EACX,YAAY;EACZ,4CAA4C;EAC5C,wCAAwC;EACxC,kBAAkB;EAClB,eAAe;EACf,gBAAgB;EAChB,sBAAsB;AACxB;AACA;;EAEE,oCAAoC;EACpC,gCAAgC;EAChC,sBAAsB;AACxB;AACA;6EAC6E;AAC7E;EACE,uDAAuD;EACvD,mDAAmD;AACrD;AACA;EACE;IACE,kBAAkB;EACpB;AACF;;AAEA;EACE,yBAAyB;EACzB,gBAAgB;EAChB,0BAA0B;AAC5B;AACA;EACE,oCAAoC;AACtC;AACA;EACE,kBAAkB;EAClB,gBAAgB;EAChB,yBAAyB;AAC3B;AACA;EACE,yBAAyB;EACzB,uBAAuB;AACzB;;AAEA;EACE,oCAAoC;AACtC;;AAEA;EACE,mBAAmB;AACrB;;AAEA;;;;GAIG;;AAEH;EACE,sBAAsB;EACtB,+CAA+C;EAC/C,2CAA2C;EAC3C,6BAA6B;EAC7B,uBAAuB;EACvB,iBAAiB;EACjB,2BAA2B;EAC3B,0BAA0B;AAC5B;;AAEA;EACE,sCAAsC;EACtC,kCAAkC;EAClC,sBAAsB;AACxB;;AAEA;EACE,oCAAoC;EACpC,2CAA2C;EAC3C,6BAA6B;AAC/B;;AAEA;EACE,uBAAuB;EACvB,+CAA+C;EAC/C,6BAA6B;AAC/B;;AAEA;EACE,sBAAsB;EACtB,+CAA+C;EAC/C,6BAA6B;EAC7B,uBAAuB;EACvB,2BAA2B;EAC3B,iBAAiB;EACjB,0BAA0B;AAC5B;;AAEA;EACE,sBAAsB;EACtB,4CAA4C;EAC5C,2CAA2C;EAC3C,6BAA6B;EAC7B,uBAAuB;EACvB,iBAAiB;EACjB,0BAA0B;AAC5B;;AAEA;EACE,aAAa;EACb,uBAAuB;EACvB,kBAAkB;EAClB,kBAAkB;EAClB,uBAAuB;AACzB;;AAEA;EACE,uBAAuB;EACvB,uBAAuB;AACzB;;AAEA;EACE,sBAAsB;EACtB,gDAAgD;EAChD,2CAA2C;EAC3C,8BAA8B;EAC9B,gCAAgC;AAClC;;AAEA;EACE,oCAAoC;EACpC,mDAAmD;EAGnD,2DAA2D;AAC7D;;AAEA;EACE,uBAAuB;EACvB,2BAA2B;AAC7B;;AAEA;EACE,sBAAsB;EACtB,+CAA+C;EAC/C,2CAA2C;EAC3C,6BAA6B;EAC7B,iCAAiC;EACjC,uBAAuB;EACvB,wBAAwB;EACxB,kCAAkC;EAClC,8BAA8B;EAC9B,0BAA0B;EAC1B,4BAA4B;EAC5B,eAAe;EACf,WAAW;EACX,eAAe;AACjB;;AAEA;EACE,qBAAqB;AACvB;;AAEA;EACE,sBAAsB;EACtB,0BAA0B;EAC1B,2BAA2B;EAC3B,oCAAoC;EACpC,+CAA+C;EAC/C,2CAA2C;EAC3C,6BAA6B;EAC7B,uBAAuB;EACvB,gCAAgC;EAChC,4BAA4B;EAC5B,WAAW;EACX,4BAA4B;EAC5B,eAAe;EACf,eAAe;AACjB;;AAEA;EACE,sBAAsB;EACtB,+CAA+C;EAC/C,2CAA2C;EAC3C,6BAA6B;EAC7B,oCAAoC;EACpC,uBAAuB;EACvB,wBAAwB;EACxB,kCAAkC;EAClC,8BAA8B;EAC9B,0BAA0B;EAC1B,4BAA4B;EAC5B,eAAe;EACf,WAAW;EACX,eAAe;AACjB;;AAEA;EACE,0BAA0B;EAC1B,sBAAsB;EACtB,wBAAwB;EACxB,8BAA8B;AAChC;;AAEA;EACE,iDAAiD;EACjD,8CAA8C;EAC9C,6BAA6B;EAC7B,wCAAwC;AAC1C;AACA,oBAAoB;AACpB;EACE,wCAAwC;AAC1C;AACA;EACE,oCAAoC;AACtC;;AAEA;EACE,sBAAsB;EACtB,yCAAyC;EACzC,gDAAgD;EAChD,2CAA2C;EAC3C,8BAA8B;EAC9B,uBAAuB;AACzB;;AAEA;;GAEG;;AAEH,sJAAsJ;;AAEtJ;EACE,eAAe;EACf,OAAO;EACP;yCACuC;EACvC,4BAA4B;EAC5B,6CAA6C;EAC7C,sCAAsC;EACtC,2BAA2B;EAC3B,wBAAwB;EACxB,6BAA6B;EAC7B,qCAAqC;EACrC,aAAa;EACb,sBAAsB;EACtB,4BAA4B;EAC5B,mBAAmB;EACnB,yDAAyD;EACzD,kBAAkB;EAClB,iEAAiE;EACjE,4BAA4B;EAC5B,6CAA6C;EAC7C,wCAAwC;EACxC,2BAA2B;EAC3B,gBAAgB;EAChB,YAAY;EACZ,gBAAgB;EAChB;;8CAE4C;AAC9C;;AAEA,oDAAoD;AACpD;EACE,+BAA+B,EAAE,mBAAmB;EACpD,0BAA0B,EAAE,kCAAkC;EAC9D,oBAAoB,EAAE,6BAA6B;AACrD;;AAEA;;;;iEAIiE;AACjE;EACE,eAAe;EACf,MAAM;EACN,OAAO;EACP,YAAY;EACZ,2BAA2B;EAC3B,+BAA+B;EAC/B,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,QAAQ;EACR,eAAe;EACf,2BAA2B;EAC3B,wDAAwD;EACxD,oDAAoD;AACtD;AACA;EACE,+BAA+B;AACjC;AACA;EACE,uBAAuB;EACvB,YAAY;EACZ,UAAU;AACZ;AACA;EACE,WAAW;AACb;;AAEA,qEAAqE;AACrE;EACE,cAAc;EACd,WAAW;EACX,YAAY;EACZ,mBAAmB;EACnB,gDAAgD;EAChD,2BAA2B;EAC3B,yBAAyB;EACzB,eAAe;EACf,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,eAAe;EACf,UAAU;AACZ;AACA;EACE,8BAA8B;AAChC;;AAEA;;sEAEsE;AACtE;EACE,WAAW;EACX,YAAY;EACZ,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,UAAU;EACV,cAAc;EACd,gBAAgB;EAChB,gBAAgB;AAClB;;AAEA;;;yBAGyB;AACzB;EACE,WAAW;EACX,YAAY;EACZ,mBAAmB;EACnB,wDAAwD;EACxD,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,cAAc;EACd,gBAAgB;EAChB,eAAe;AACjB;AACA;;;;;uCAKuC;AACvC;EACE,iCAAiC;AACnC;;AAEA;;qEAEqE;AACrE;EACE,WAAW;EACX,UAAU;AACZ;;AAEA;EACE,eAAe;EACf,gBAAgB;EAChB,gCAAgC;EAChC,yBAAyB;EACzB,iBAAiB;EACjB,cAAc;AAChB;;AAEA;EACE,eAAe;EACf,gBAAgB;EAChB,gCAAgC;EAChC,mBAAmB;EACnB,gBAAgB;EAChB,uBAAuB;EACvB,WAAW;EACX,kBAAkB;AACpB;AACA;;;;;;wEAMwE;AACxE;EACE,gBAAgB;EAChB,eAAe;EACf,WAAW;EACX,YAAY;EACZ,mBAAmB;AACrB;;AAEA;8EAC8E;AAC9E;EACE,eAAe;EACf,gBAAgB;EAChB,mBAAmB;AACrB;;AAEA;;;qEAGqE;AACrE;EACE;;;;;GAKC;EACD,qBAAqB;EACrB,6BAA6B;EAC7B,oCAAoC;EACpC,8BAA8B,EAAE,4CAA4C;AAC9E;;AAEA;wEACwE;;AAExE,kFAAkF;AAClF;EACE,eAAe;EACf,uCAAuC;EACvC,+DAA+D;EAC/D,4CAA4C;EAC5C,2BAA2B;EAC3B,WAAW;EACX,YAAY;EACZ,kBAAkB;EAClB,2BAA2B;EAC3B,iDAAiD;EACjD,qDAAqD;EACrD,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,eAAe;EACf,YAAY;EACZ,yBAAyB;EACzB,eAAe;EACf,UAAU;EACV;;;oBAGkB;AACpB;;AAEA;EACE,2CAA2C;AAC7C;;AAEA;EACE,8BAA8B;EAC9B,yBAAyB;EACzB,4CAA4C;AAC9C;;AAEA;EACE,mCAAmC;EACnC,mBAAmB;AACrB;;AAEA;;kEAEkE;AAClE;EACE,SAAO;EACP,aAAa,EAAE,0DAA0D;EACzE,WAAW;EACX,UAAU;EACV,aAAa;EACb,sBAAsB;EACtB,qCAAqC;EACrC,kBAAkB;EAClB,gBAAgB;EAChB,kBAAkB;EAClB,qBAAqB;EACrB,oBAAoB;AACtB;;AAEA;EACE,mBAAmB;AACrB;;AAEA;EACE,aAAa;AACf;;AAEA;;iEAEiE;AACjE;EACE,aAAa;EACb,mBAAmB;EACnB,QAAQ;EACR;iEAC+D;EAC/D,WAAW;EACX,YAAY;EACZ,iBAAiB;EACjB,mBAAmB;EACnB,YAAY;EACZ,uBAAuB;EACvB,eAAe;EACf,qBAAqB;EACrB,sCAAsC;EACtC,oBAAoB;EACpB,eAAe;EACf,gBAAgB;EAChB,mBAAmB;EACnB,gBAAgB;EAChB,sBAAsB;EACtB;;eAEa;AACf;;AAEA;sCACsC;AACtC;EACE,WAAW;EACX,YAAY;EACZ,UAAU;EACV,uBAAuB;EACvB,mBAAmB;EACnB,MAAM;EACN,cAAc,EAAE,0CAA0C;AAC5D;;AAEA;EACE,gCAAgC;EAChC,qBAAqB;AACvB;;AAEA,2CAA2C;AAC3C;EACE,4BAA4B;EAC5B,sBAAsB;AACxB;;AAEA;EACE,sCAAsC;EACtC,sBAAsB;AACxB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,eAAe;EACf,cAAc;EACd,WAAW;EACX,YAAY;AACd;;AAEA;2BAC2B;AAC3B;EACE,eAAe;EACf,WAAW;EACX,YAAY;AACd;;AAEA,qCAAqC;AACrC;EACE,SAAO;EACP,gBAAgB;EAChB,uBAAuB;EACvB,gBAAgB;EAChB,eAAe;EACf,gBAAgB;EAChB,UAAU;EACV,gBAAgB;EAChB;;gDAE8C;AAChD;;AAEA;EACE,UAAU;EACV,YAAY;AACd;;AAEA,+CAA+C;AAC/C;EACE,eAAe;EACf,cAAc;EACd,qCAAqC;EACrC,cAAc;EACd,UAAU;EACV,eAAe;EACf;;;;wBAIsB;AACxB;;AAEA;EACE,UAAU;EACV,YAAY;EACZ,gBAAgB;AAClB;;AAEA;EACE,qCAAqC;AACvC;;AAEA;EACE,qCAAqC;EACrC,wBAAwB;AAC1B;;AAEA,wEAAwE;AACxE;EACE,gBAAgB;EAChB,aAAa;EACb,sBAAsB;EACtB,kBAAkB;EAClB,WAAW;EACX,cAAc,EAAE,oCAAoC;AACtD;;AAEA;EACE,mBAAmB;AACrB;;AAEA;;iEAEiE;AACjE;EACE,WAAW;EACX,cAAc;EACd,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,mBAAmB;EACnB,uBAAuB;EACvB,WAAW;EACX,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,SAAS;EACT,YAAY;EACZ,mBAAmB;EACnB,eAAe;EACf,4BAA4B;EAC5B,gBAAgB;EAChB,WAAW;EACX,sBAAsB;AACxB;;AAEA;;kEAEkE;AAClE;EACE,MAAM;EACN,WAAW;AACb;;AAEA;EACE,gCAAgC;AAClC;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,eAAe;EACf,kBAAkB;EAClB,2CAA2C;EAC3C,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,eAAe;EACf,gBAAgB;EAChB,sBAAsB;EACtB,cAAc;AAChB;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,YAAY;EACZ,SAAO;EACP,UAAU;EACV,gBAAgB;EAChB,gBAAgB;EAChB;;gDAE8C;AAChD;;AAEA;EACE,UAAU;EACV,YAAY;AACd;;AAEA;EACE,eAAe;EACf,gBAAgB;EAChB,qBAAqB;EACrB,mBAAmB;EACnB,gBAAgB;EAChB,uBAAuB;EACvB,gBAAgB;AAClB;;AAEA;;;;;iBAKiB;AACjB;EACE,qEAAqE;EACrE,yCAAyC;EACzC,YAAY;EACZ,sBAAsB;EACtB,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,QAAQ;EACR,2BAA2B;EAC3B,gBAAgB;EAChB,eAAe;EACf,mBAAmB;AACrB;;AAEA;EACE,eAAe;EACf,gBAAgB;EAChB,yCAAyC;AAC3C;;AAEA;EACE,YAAY;EACZ,WAAW;EACX,cAAc;AAChB;;AAEA;;0DAE0D;AAC1D;EACE,WAAW;AACb;AACA;EACE,WAAW;EACX,0CAA0C;EAC1C,eAAe;AACjB;AACA;EACE,aAAa;AACf;AACA;EACE,YAAY;EACZ,WAAW;EACX,eAAe;AACjB;;AAEA,iEAAiE;AACjE;EACE,eAAe;EACf,oCAAoC;EACpC,WAAW;EACX,uBAAuB;AACzB;;AAEA;EACE,eAAe;EACf,4BAA4B;EAC5B,4CAA4C;EAC5C,4BAA4B;EAC5B,8BAA8B;EAC9B,uDAAuD;EACvD,qDAAqD;EACrD,WAAW;EACX,aAAa;EACb,sBAAsB;EACtB,yDAAyD;AAC3D;;AAEA;EACE;IACE,YAAY;IACZ,4BAA4B;EAC9B;EACA;IACE,UAAU;IACV,wBAAwB;EAC1B;AACF;;AAEA,4EAA4E;AAC5E;EACE,kBAAkB;EAClB,YAAY;EACZ,QAAQ;EACR,2BAA2B;EAC3B,WAAW;EACX,YAAY;EACZ,kBAAkB;EAClB,2BAA2B;EAC3B,iDAAiD;EACjD,qDAAqD;EACrD,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,eAAe;EACf,YAAY;EACZ,yBAAyB;EACzB,eAAe;EACf,UAAU;EACV,cAAc;AAChB;;AAEA;EACE,8BAA8B;EAC9B,yBAAyB;EACzB,4CAA4C;AAC9C;;AAEA;;uEAEuE;AACvE;EACE,SAAO;EACP,gBAAgB;EAChB,kBAAkB;EAClB,aAAa;EACb,aAAa;EACb,sBAAsB;EACtB,QAAQ;EACR,qBAAqB;EACrB,2DAA2D;AAC7D;;AAEA;EACE,UAAU;AACZ;AACA;EACE,uBAAuB;AACzB;AACA;EACE,0CAA0C;EAC1C,kBAAkB;AACpB;;AAEA;;iCAEiC;AACjC;EACE,aAAa;EACb,mBAAmB;EACnB,QAAQ;EACR,YAAY;EACZ,mBAAmB;EACnB,eAAe;EACf,gBAAgB;EAChB,yBAAyB;EACzB,qBAAqB;EACrB;;eAEa;EACb,mBAAmB;EACnB,gBAAgB;AAClB;;AAEA;EACE,SAAO;EACP,gBAAgB;EAChB,uBAAuB;EACvB,gBAAgB;AAClB;;AAEA;EACE,2CAA2C;EAC3C,sBAAsB;AACxB;;AAEA;EACE,0CAA0C;EAC1C,sBAAsB;EACtB,gBAAgB;AAClB;;AAEA;+CAC+C;AAC/C;EACE,kBAAkB;AACpB;;AAEA,kBAAkB;AAClB;EACE,UAAU;EACV,WAAW;EACX,kBAAkB;EAClB,cAAc;EACd,0CAA0C;EAC1C,4BAA4B;AAC9B;;AAEA;;EAEE,4BAA4B;AAC9B;;AAEA,kEAAkE;AAClE;EACE,gBAAgB;AAClB;;AAEA;wEACwE;AACxE;EACE,aAAa;EACb,mBAAmB;EACnB,QAAQ;EACR,YAAY;EACZ,mBAAmB;EACnB,eAAe;EACf,gBAAgB;EAChB,sBAAsB;EACtB,oBAAoB;EACpB,iBAAiB;EACjB,YAAY;EACZ,gBAAgB;EAChB,eAAe;EACf,WAAW;EACX,oBAAoB;EACpB,uBAAuB;AACzB;;AAEA;EACE,SAAO;EACP,gBAAgB;EAChB,mBAAmB;EACnB,sBAAsB;EACtB,gBAAgB;AAClB;;AAEA;EACE,sBAAsB;AACxB;AACA;EACE,sBAAsB;AACxB;;AAEA,qCAAqC;AACrC;EACE,eAAe;EACf,qBAAqB;EACrB,gCAAgC;EAChC,cAAc;EACd,qCAAqC;EACrC,cAAc;AAChB;;AAEA;;EAEE,qCAAqC;AACvC;;AAEA,mCAAmC;AACnC;EACE,aAAa;EACb,sBAAsB;AACxB;;AAEA,mEAAmE;AACnE;EACE,aAAa;EACb,sBAAsB;EACtB,QAAQ;EACR,cAAc;EACd,WAAW;AACb;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,SAAS;EACT,WAAW;EACX,kBAAkB;EAClB,iBAAiB;EACjB,gBAAgB;EAChB,yBAAyB;EACzB,gBAAgB;EAChB,gBAAgB;EAChB,YAAY;EACZ,kBAAkB;EAClB,eAAe;EACf,qBAAqB;EACrB,oBAAoB;EACpB;;eAEa;EACb,mBAAmB;EACnB,gBAAgB;EAChB,sBAAsB;AACxB;;AAEA;EACE,2CAA2C;EAC3C,0BAA0B;AAC5B;;AAEA;EACE,wCAAwC;EACxC,yBAAyB;EACzB,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,eAAe;EACf,cAAc;EACd,WAAW;EACX,YAAY;AACd;;AAEA;EACE,SAAO;EACP,gBAAgB;EAChB,uBAAuB;EACvB,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,sBAAsB;AACxB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,QAAQ;EACR,0BAA0B;EAC1B,eAAe;EACf,gBAAgB;EAChB,yBAAyB;EACzB,qBAAqB;EACrB,kBAAkB;EAClB;;cAEY;EACZ,mBAAmB;EACnB,gBAAgB;AAClB;;AAEA;EACE,2CAA2C;EAC3C,yBAAyB;AAC3B;;AAEA;EACE,yBAAyB;EACzB,gBAAgB;EAChB,2CAA2C;AAC7C;;AAEA;EACE,kBAAkB;AACpB;;AAEA;EACE,aAAa;EACb,sBAAsB;AACxB;;AAEA,8EAA8E;AAC9E;EACE,0BAA0B;EAC1B,yBAAyB;EACzB,eAAe;EACf,gBAAgB;AAClB;;AAEA;EACE,0BAA0B;AAC5B;;AAEA;EACE,yBAAyB;AAC3B;;AAEA;EACE,yBAAyB;AAC3B;;AAEA;;EAEE,yBAAyB;AAC3B;;AAEA;EACE,8BAA8B;AAChC;;AAEA;EACE,8BAA8B;AAChC;;AAEA,mDAAmD;AACnD;EACE,kBAAkB;EAClB,kBAAkB;EAClB,eAAe;AACjB;;AAEA;EACE,WAAW;EACX,yBAAyB;AAC3B;;AAEA;EACE,kBAAkB;EAClB,2BAA2B;EAC3B,iBAAiB;EACjB,MAAM;EACN,0BAA0B;EAC1B,wCAAwC;EACxC;;4CAE0C;AAC5C;;AAEA;;6DAE6D;AAC7D;EACE,6BAA6B;EAC7B,8BAA8B;EAC9B;;;2EAGyE;EACzE,gBAAgB;AAClB;;AAEA,4CAA4C;AAC5C;EACE,8BAA8B;EAC9B,4CAA4C;AAC9C;AACA,8DAA8D;;AAE9D;;kCAEkC;AAClC;EACE;;IAEE,OAAO;IACP,WAAW;EACb;EACA;IACE,4BAA4B;IAC5B,wDAAwD;IACxD,gBAAgB;EAClB;EACA;IACE,wBAAwB;IACxB,sDAAsD;EACxD;EACA;IACE,eAAe;IACf,oCAAoC;IACpC,WAAW;IACX,2CAA2C;EAC7C;EACA,oDAAoD;EACpD;;IAEE,WAAW;IACX,eAAe;IACf,2BAA2B;IAC3B,eAAe;EACjB;EACA;IACE,WAAW;IACX,2BAA2B;EAC7B;EACA;IACE,gBAAgB;IAChB,gBAAgB;EAClB;EACA;;;iEAG+D;EAC/D;IACE,mBAAmB;IACnB,mBAAmB;IACnB,iBAAiB;EACnB;EACA;IACE,aAAa;EACf;EACA;IACE,aAAa;EACf;EACA;IACE,eAAe;EACjB;EACA;IACE,iBAAiB;IACjB,QAAQ;EACV;EACA;IACE,YAAY;EACd;EACA;8DAC4D;EAC5D;IACE,kBAAkB;IAClB,uBAAuB;IACvB,YAAY;EACd;AACF;;AAEA,gGAAgG;;AAEhG,kEAAkE;AAClE;EACE,YAAY;EACZ,WAAW;EACX,mBAAmB;EACnB,iBAAiB;EACjB,cAAc;EACd,6BAA6B;AAC/B;;AAEA,8EAA8E;AAC9E;EACE,aAAa;AACf;;AAEA,0DAA0D;AAC1D;EACE,6BAA6B;EAC7B,qBAAqB;EACrB,0BAA0B;EAC1B,cAAc;EACd,gBAAgB;EAChB,kBAAkB;EAClB,uDAAuD;EACvD,wBAAwB;AAC1B;;AAEA;EACE,YAAY;EACZ,aAAa;EACb,mBAAmB;EACnB,eAAe;EACf,eAAe;EACf,gBAAgB;EAChB,yBAAyB;EACzB,sBAAsB;EACtB,yBAAyB;EACzB,wDAAwD;EACxD,2BAA2B;EAC3B,cAAc;AAChB;;AAEA;EACE,oCAAoC;AACtC;;AAEA;;;8DAG8D;;AAE9D,6CAA6C;AAC7C;EACE,sCAAsC;AACxC;;AAEA,qBAAqB;AACrB;EACE,6BAA6B;EAC7B,mEAAmE;EACnE,sCAAsC;AACxC;;AAEA,sDAAsD;AACtD;EACE,qBAAqB;EACrB,gBAAgB;EAChB,aAAa;EACb,sBAAsB;AACxB;;AAEA,uDAAuD;AACvD;;EAEE,wBAAwB;EACxB,sBAAsB;EACtB,6BAA6B;EAC7B,sBAAsB;EACtB,YAAY;EACZ,gBAAgB;EAChB,kBAAkB;AACpB;;AAEA,sCAAsC;AACtC;EACE,uBAAuB;EACvB,SAAO;AACT;;AAEA,kDAAkD;AAClD;;EAEE,0BAA0B;EAC1B,sBAAsB;EACtB,6BAA6B;EAC7B,wBAAwB;EACxB,8BAA8B;EAC9B,kCAAkC;EAClC,yBAAyB;AAC3B;;AAEA,6CAA6C;AAC7C;EACE,yBAAyB;EACzB,uBAAuB;EACvB,sBAAsB;EACtB,0BAA0B;EAC1B,2BAA2B;EAC3B,0BAA0B;EAC1B,2BAA2B;EAC3B,wBAAwB;EACxB,kCAAkC;EAClC,8BAA8B;EAC9B,oBAAoB;EACpB,2BAA2B;EAC3B,kCAAkC;EAClC,yBAAyB;AAC3B;;AAEA,2DAA2D;AAC3D;EACE,uBAAuB;EACvB,6BAA6B;AAC/B;;AAEA,uEAAuE;AACvE;EACE,wBAAwB;AAC1B;;AAEA;EACE,uBAAuB;AACzB;;AAEA,wEAAwE;AACxE;EACE,wBAAwB;AAC1B;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,gBAAgB;EAChB,MAAM;EACN,QAAQ;EACR,cAAc;EACd,iBAAiB;EACjB,4CAA4C;EAC5C,aAAa;AACf;;AAEA;EACE,oBAAoB;AACtB;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,iCAAiC;EACjC,aAAa;EACb,uBAAuB;EACvB,mBAAmB;EACnB,kBAAkB;AACpB;;AAEA;EACE,yBAAyB;AAC3B;;AAEA,iFAAiF;AACjF,kDAAkD;AAClD;EACE,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,eAAe;EACf,aAAa;EACb,SAAS;EACT,YAAY;EACZ,kBAAkB;EAClB,2BAA2B;EAC3B,wDAAwD;EACxD,sCAAsC;EACtC,gBAAgB;EAChB,MAAM;EACN,WAAW;AACb;;AAEA,gEAAgE;AAChE,+DAA+D;AAC/D;EACE,aAAa;EACb,uBAAuB;EACvB,0BAA0B;AAC5B;;AAEA;yEACyE;AACzE;EACE,WAAW;EACX,eAAe;EACf,gBAAgB;AAClB;AACA;EACE,yBAAyB;AAC3B;AACA;EACE,gBAAgB;AAClB;AACA;EACE,8BAA8B;AAChC;;AAEA,6EAA6E;AAC7E;EACE,aAAa;EACb,mBAAmB;EACnB,SAAS;EACT,YAAY;AACd;;AAEA;oEACoE;AACpE;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,YAAY;EACZ,qBAAqB;EACrB,2CAA2C;EAC3C,wDAAwD;EACxD,+DAA+D;EAC/D,gCAAgC;EAChC,mBAAmB;EACnB,YAAY;EACZ,cAAc;AAChB;AACA;EACE,eAAe;AACjB;AACA;EACE,eAAe;EACf,+BAA+B;AACjC;AACA;EACE,eAAe;EACf,gBAAgB;EAChB,gBAAgB;EAChB,gBAAgB;EAChB,uBAAuB;AACzB;AACA;EACE,WAAW;EACX,YAAY;EACZ,kBAAkB;EAClB,YAAY;EACZ,uBAAuB;EACvB,cAAc;EACd,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,eAAe;EACf,UAAU;EACV,eAAe;AACjB;AACA;EACE,yDAAyD;AAC3D;;AAEA;;;mEAGmE;AACnE;EACE,aAAa;EACb,mBAAmB;EACnB,yBAAyB;EACzB,eAAe;EACf,SAAS;EACT,cAAc;EACd,YAAY;AACd;;AAEA;;;wEAGwE;AACxE;;EAEE,WAAW;EACX,gBAAgB;EAChB,gBAAgB;EAChB,YAAY;EACZ,+CAA+C;EAC/C,oEAAoE;EACpE,uBAAuB;EACvB,2BAA2B;EAC3B,eAAe;AACjB;;AAEA;;EAEE,kCAAkC;EAClC,0BAA0B;EAC1B,uBAAuB;EACvB,6BAA6B;AAC/B;;AAEA;;oEAEoE;AACpE;;EAEE,0BAA0B;EAC1B,uBAAuB;EACvB,+BAA+B;AACjC;;AAEA;;EAEE,sBAAsB;AACxB;;AAEA;;EAEE,eAAe;EACf,+BAA+B;AACjC;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,YAAY;EACZ,sBAAsB;EACtB,YAAY;EACZ,mBAAmB;EACnB,qCAAqC;EACrC,sBAAsB;EACtB,oBAAoB;EACpB,eAAe;EACf,gBAAgB;EAChB,uBAAuB;EACvB,eAAe;EACf,mBAAmB;EACnB,cAAc;EACd,yBAAyB;AAC3B;;AAEA;EACE,eAAe;AACjB;;AAEA;EACE,YAAY;AACd;;AAEA;EACE,gBAAgB;EAChB,eAAe;EACf,qCAAqC;AACvC;;AAEA;EACE;IACE,gBAAgB;IAChB,eAAe;EACjB;;EAEA;IACE,aAAa;EACf;;EAEA;IACE,eAAe;EACjB;AACF;;AAEA,8CAA8C;AAC9C;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,WAAW,EAAE,oCAAoC;EACjD,YAAY;EACZ,cAAc;EACd,YAAY;EACZ,oCAAoC;EACpC,qCAAqC;EACrC,eAAe;EACf,yBAAyB;AAC3B;AACA;EACE,aAAa;AACf;AACA;EACE,8BAA8B;EAC9B,mBAAmB;AACrB;;AAEA,oFAAoF;AACpF;EACE,+CAA+C;AACjD;;AAEA,6DAA6D;AAC7D;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,WAAW;EACX,YAAY;EACZ,cAAc;EACd,YAAY;EACZ,oCAAoC;EACpC,qCAAqC;EACrC,sBAAsB;EACtB,eAAe;EACf,eAAe;EACf,yBAAyB;AAC3B;AACA;EACE,aAAa;AACf;;AAEA,+DAA+D;AAC/D;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,WAAW;EACX,YAAY;EACZ,cAAc;EACd,YAAY;EACZ,oCAAoC;EACpC,6BAA6B;EAC7B,sBAAsB;EACtB,eAAe;EACf,eAAe;EACf,yBAAyB;AAC3B;AACA;EACE,aAAa;AACf;;AAEA,gFAAgF;AAChF;EACE,aAAa;EACb,mBAAmB;EACnB,QAAQ;EACR,iBAAiB;AACnB;;AAEA,wFAAwF;AACxF;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,WAAW;EACX,YAAY;EACZ,cAAc;EACd,YAAY;EACZ,oCAAoC;EACpC,6BAA6B;EAC7B,sCAAsC;EACtC,eAAe;EACf,eAAe;EACf,4BAA4B;AAC9B;AACA;EACE,8BAA8B;AAChC;;AAEA,8FAA8F;AAC9F;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,QAAQ;EACR,YAAY;EACZ,eAAe;EACf,cAAc;EACd,YAAY;EACZ,oCAAoC;EACpC,oBAAoB;EACpB,eAAe;EACf,gBAAgB;EAChB,eAAe;EACf,mBAAmB;EACnB,yBAAyB;AAC3B;AACA;EACE,aAAa;AACf;AACA;EACE,qCAAqC;EACrC,sBAAsB;AACxB;AACA;EACE,yDAAyD;EACzD,gCAAgC;AAClC;;AAEA;mEACmE;AACnE;EACE,YAAY;EACZ,mBAAmB;EACnB,mCAAmC;EACnC,+DAA+D;EAC/D,gBAAgB;EAChB,gBAAgB;EAChB,aAAa;EACb,sBAAsB;EACtB,QAAQ;AACV;AACA,qEAAqE;AACrE;EACE,iBAAiB;EACjB,eAAe;EACf,gBAAgB;EAChB,+CAA+C;EAC/C,gCAAgC;EAChC,sDAAsD;AACxD;AACA;;EAEE,oEAAoE;EACpE,gCAAgC;AAClC;AACA,yEAAyE;AACzE;;EAEE,YAAY;EACZ,mBAAmB;EACnB,iEAAiE;EACjE,2CAA2C;AAC7C;AACA,mCAAmC;AACnC;EACE,mCAAmC;EACnC,wDAAwD;AAC1D;AACA;;EAEE,wDAAwD;EACxD,mCAAmC;AACrC;;AAEA;;yDAEyD;AACzD;EACE,YAAY;EACZ,mBAAmB;EACnB,mCAAmC;EACnC,+DAA+D;EAC/D,gBAAgB;EAChB,gBAAgB;EAChB,aAAa;EACb,sBAAsB;EACtB,QAAQ;AACV;AACA;EACE,iBAAiB;EACjB,eAAe;EACf,gBAAgB;EAChB,+CAA+C;EAC/C,gCAAgC;EAChC,sDAAsD;AACxD;AACA;;EAEE,oEAAoE;EACpE,gCAAgC;AAClC;AACA;EACE,mCAAmC;EACnC,wDAAwD;AAC1D;AACA;;EAEE,wDAAwD;EACxD,mCAAmC;AACrC;;AAEA,yBAAyB;AACzB;EACE,aAAa;EACb,mBAAmB;EACnB,QAAQ;EACR,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,iBAAiB;EACjB,gBAAgB;EAChB,SAAS;EACT,UAAU;EACV,MAAM;EACN,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,QAAQ;EACR,mBAAmB;EACnB,mDAAmD;EACnD,gBAAgB;EAChB,UAAU;EACV,gBAAgB;EAChB;;;qBAGmB;AACrB;;AAEA;EACE,YAAY;EACZ,UAAU;EACV,oBAAoB;EACpB,SAAS;AACX;;AAEA;EACE,eAAe;EACf,qCAAqC;EACrC,uBAAuB;EACvB,yBAAiB;UAAjB,iBAAiB;EACjB,cAAc;AAChB;;AAEA;;;kBAGkB;AAClB;EACE,eAAe;EACf,gBAAgB;EAChB,qCAAqC;EACrC,uBAAuB;EACvB,eAAe;EACf,uBAAuB;EACvB,cAAc;AAChB;;AAEA;EACE,qCAAqC;EACrC,0BAA0B;AAC5B;;AAEA;EACE,qCAAqC;EACrC,eAAe;EACf,gBAAgB;AAClB;;AAEA;EACE,qCAAqC;EACrC,qBAAqB;AACvB;;AAEA,mDAAmD;AACnD;EACE,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,WAAW;EACX,YAAY;EACZ,oCAAoC;EACpC,kBAAkB;EAClB,6BAA6B;EAC7B,eAAe;EACf,cAAc;EACd,UAAU;EACV,4BAA4B;AAC9B;;AAEA;EACE,8BAA8B;EAC9B,gCAAgC;AAClC;;AAEA;EACE,eAAe;EACf,yBAAyB;EACzB,cAAc;EACd,qBAAqB;EACrB,wDAAwD;AAC1D;;AAEA;EACE;IACE,aAAa;EACf;;EAEA;IACE,eAAe;IACf,YAAY;EACd;;EAEA;IACE,eAAe;EACjB;AACF;;AAEA;EACE,iBAAiB;AACnB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,mBAAmB;AACrB;;AAEA;EACE,0BAA0B;EAC1B,2BAA2B;EAC3B,2BAA2B;AAC7B;;AAEA,4EAA4E;AAC5E;;;;;;EAME,cAAc;EACd,mBAAmB;EACnB,gBAAgB;EAChB,YAAY;AACd;;AAEA,kEAAkE;AAClE;EACE,uBAAuB;EACvB,gBAAgB;EAChB,gBAAgB;EAChB,iBAAiB;AACnB;;AAEA,iFAAiF;AACjF;EACE,kBAAkB;EAClB,aAAa;EACb,8BAA8B;EAC9B,uBAAuB;EACvB,mBAAmB;EACnB,SAAS;EACT,eAAe;EACf,gBAAgB;AAClB;;AAEA,iEAAiE;AACjE;EACE,aAAa;EACb,sBAAsB;EACtB,QAAQ;EACR,YAAY;AACd;;AAEA;;;8BAG8B;AAC9B;;EAEE,eAAe;EACf,gBAAgB;EAChB,qCAAqC;EACrC,SAAS;EACT,iBAAiB;EACjB,mBAAmB;AACrB;;AAEA;EACE,gBAAgB;EAChB,kBAAkB;AACpB;;AAEA,sFAAsF;AACtF;;EAEE,eAAe;EACf,gBAAgB;EAChB,qCAAqC;EACrC,SAAS;EACT,gBAAgB;EAChB,uBAAuB;AACzB;;AAEA,0CAA0C;AAC1C;EACE,aAAa;EACb,mBAAmB;EACnB,QAAQ;EACR,eAAe;EACf,cAAc;AAChB;;AAEA,8DAA8D;AAC9D;EACE,2BAA2B;EAC3B,kCAAkC;EAClC,iCAAiC;EACjC,iBAAiB;EACjB,UAAU;AACZ;;AAEA;;;2DAG2D;AAC3D;EACE,2BAA2B;EAC3B,kCAAkC;EAClC,oCAAoC;EACpC,gCAAgC;AAClC;;AAEA;;;;;uDAKuD;AACvD;EACE,gBAAgB;EAChB,gBAAgB;AAClB;;AAEA;;;;;;iCAMiC;AACjC;EACE,2BAA2B;EAC3B,kCAAkC;EAClC,oCAAoC;EACpC,UAAU;AACZ;;AAEA,2DAA2D;AAC3D;EACE,2BAA2B;EAC3B,kCAAkC;EAClC,iCAAiC;EACjC,kBAAkB;AACpB;;AAEA,8EAA8E;AAC9E;EACE,eAAe;EACf,gBAAgB;EAChB,0BAA0B;EAC1B,kBAAkB;EAClB,iBAAiB;EACjB,mBAAmB;EACnB,gBAAgB;AAClB;;AAEA,6CAA6C;;AAE7C;;;wEAGwE;AACxE;EACE,mCAAmC;EACnC,6BAA6B;EAC7B,uDAAuD;EACvD,sDAAsD;EACtD,wCAAwC;EACxC,0BAA0B;EAC1B,2BAA2B;AAC7B;;AAEA;EACE,mCAAmC;AACrC;;AAEA;;;;;;EAME,0BAA0B;EAC1B,2BAA2B;EAC3B,YAAY;EACZ,eAAe;EACf,2CAA2C;AAC7C;;AAEA;EACE,6BAA6B;AAC/B;;AAEA;;EAEE,2CAA2C;EAC3C,0CAA0C;EAC1C,yBAAyB;AAC3B;;AAEA;;;;EAIE,kBAAkB;EAClB,YAAY;EACZ,kBAAkB;AACpB;;AAEA;EACE,yBAAyB;AAC3B;;AAEA;EACE,wBAAwB;AAC1B;;AAEA,gEAAgE;AAChE;;;;EAIE,6BAA6B;EAC7B,uBAAuB;EACvB,4BAA4B;AAC9B;;AAEA;;;;;;;;;;EAUE,4BAA4B;AAC9B;;AAEA;EACE,qBAAqB;AACvB;;AAEA;yEACyE;AACzE;EACE,4BAA4B;EAC5B,6BAA6B;EAC7B,+BAA+B;EAC/B,yBAAyB;EACzB,eAAe;EACf,qBAAqB;AACvB;;AAEA;;;;;EAKE,kBAAkB;AACpB;;AAEA;EACE,6BAA6B;AAC/B;;AAEA;;EAEE,gBAAgB;EAChB,gBAAgB;EAChB,qBAAqB;EACrB,cAAc;AAChB;;AAEA;EACE,qBAAqB;AACvB;;AAEA;EACE,0BAA0B;AAC5B;;AAEA;qDACqD;AACrD;EACE,6BAA6B;EAC7B,sDAAsD;EACtD,0DAA0D;AAC5D;;AAEA;EACE,gBAAgB;EAChB,gCAAgC;EAChC,gBAAgB;AAClB;;AAEA;EACE,kDAAkD;AACpD;;AAEA;EACE,iDAAiD;AACnD;;AAEA;EACE,mCAAmC;AACrC;;AAEA;EACE,+BAA+B;EAC/B,gCAAgC;AAClC;;AAEA;EACE,4CAA4C;AAC9C;;AAEA,8BAA8B;;AAE9B;EACE,WAAW;EACX,YAAY;EACZ,gBAAgB;EAChB,aAAa;EACb,uBAAuB;EACvB,mBAAmB;EACnB,mBAAmB;EACnB,cAAc;AAChB;;AAEA;EACE,gBAAgB;EAChB,YAAY;EACZ,aAAa;EACb,mBAAmB;EACnB,eAAe;EACf,YAAY;EACZ,uBAAuB;;EAEvB,0CAA0C;EAC1C,mBAAmB;AACrB;;AAEA;EACE,YAAY;AACd;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,gBAAgB;EAChB,aAAa;EACb,0CAA0C;EAC1C,uBAAuB;EACvB,mBAAmB;EACnB,mBAAmB;EACnB,cAAc;AAChB;;AAEA;EACE,0BAA0B;AAC5B;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,oCAAoC;EACpC,gBAAgB;EAChB,aAAa;EACb,uBAAuB;EACvB,mBAAmB;EACnB,kBAAkB;EAClB,kBAAkB;EAClB,2BAA2B;AAC7B;;AAEA;EACE,kBAAkB;AACpB;;AAEA;EACE,qBAAqB;EACrB,6BAA6B;EAC7B,4CAA4C;EAC5C,sBAAsB;EACtB,uBAAuB;EACvB,8BAA8B;EAC9B,uBAAuB;EACvB,wBAAwB;EACxB,wBAAwB;EACxB,kCAAkC;EAClC,8BAA8B;EAC9B,6BAA6B;EAG7B,sEAAsE;EACtE,0BAA0B;AAC5B;;AAEA,wEAAwE;;AAExE;EACE,aAAa;EACb,8BAA8B;EAC9B,mBAAmB;AACrB;;AAEA;EACE,YAAY;EACZ,sBAAsB;EACtB,SAAS;EACT,kBAAkB;AACpB;;AAEA;EACE,aAAa;EACb,YAAY;EACZ,yBAAyB;EACzB,SAAS;EACT,kBAAkB;AACpB;;AAEA;EACE,8BAA8B;EAC9B,YAAY;AACd;;AAEA;EACE,kDAAkD;AACpD;;AAEA;EACE,gDAAgD;AAClD;;AAEA;EACE,iDAAiD;AACnD;;AAEA;EACE,+CAA+C;AACjD;;AAEA,iDAAiD;;AAEjD,8BAA8B;AAC9B;;EAEE,8BAA8B;EAC9B,eAAe;EAGf,2DAA2D;AAC7D;;AAEA;EACE,6BAA6B;EAC7B,YAAY;AACd;AACA;EACE,2BAA2B;EAC3B;;yDAEuD;AACzD;AACA;EACE,2BAA2B;EAC3B;;;4CAG0C;EAC1C,sDAAsD;AACxD;;AAEA;EACE,kBAAkB;EAClB,YAAY;AACd;;AAEA;EACE,2BAA2B;AAC7B;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,gCAAgC;EAChC,gCAAgC;AAClC;;AAEA;EACE,0CAA0C;EAC1C,2CAA2C;EAG3C,sEAAsE;AACxE;;AAEA;EACE,aAAa;EACb,oBAAoB;EACpB,mBAAmB;EACnB,6BAA6B;EAC7B,WAAW;AACb;;AAEA;EACE,WAAW;EACX,kBAAkB;AACpB;;AAEA;EACE,WAAW;EACX,kBAAkB;AACpB;;AAEA;EACE,aAAa;EACb,0BAA0B;EAC1B,iBAAiB;EACjB,6BAA6B;EAC7B,UAAU;EACV,mBAAmB;EACnB,uBAAuB;AACzB;;AAEA;EACE,0CAA0C;EAC1C,2CAA2C;EAC3C,oCAAoC;EACpC,8BAA8B;EAC9B,kBAAkB;EAClB,gCAAgC;AAClC;;AAEA;EACE,yCAAyC;EACzC,2CAA2C;EAC3C,yCAAyC;AAC3C;;AAEA;EACE,yCAAyC;EACzC,2CAA2C;EAC3C,yCAAyC;EACzC,8BAA8B;EAC9B,gCAAgC;AAClC;;AAEA;EACE,0CAA0C;EAC1C,2CAA2C;EAC3C,oCAAoC;AACtC;;AAEA;EACE,YAAY;EACZ,aAAa;EACb,sBAAsB;EACtB,8BAA8B;AAChC;;AAEA;EACE,aAAa;EACb,uBAAuB;EACvB,mBAAmB;AACrB;;AAEA;EACE,eAAe;EACf,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,kBAAkB;AACpB;;AAEA;EACE,kBAAkB;EAClB,aAAa;EACb,mBAAmB;AACrB;;AAEA;EACE,aAAa;EACb,8BAA8B;EAC9B,mBAAmB;AACrB;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,mBAAmB;EACnB,aAAa;EACb,uBAAuB;EACvB,mBAAmB;EACnB,gCAAgC;AAClC;;AAEA;;;;;;;;;;;;;;GAcG;AACH;EACE,eAAe;AACjB;;AAEA;EACE,eAAe;EACf,gBAAgB;EAChB,gBAAgB;AAClB;;AAEA;EACE,oCAAoC;AACtC;;AAEA;EACE,0BAA0B;EAC1B,gBAAgB;EAChB,oCAAoC;EACpC,iBAAiB;AACnB;;AAEA;EACE,aAAa;AACf;;AAEA;;;;GAIG;AACH;EACE,gDAAgD;EAChD,2CAA2C;EAC3C,8BAA8B;EAC9B,yCAAyC;EACzC,uBAAuB;EACvB,uBAAuB;EACvB,0BAA0B;EAC1B,2BAA2B;EAC3B,sBAAsB;EACtB,wBAAwB;EACxB,oCAAoC;EACpC,yBAAyB;AAC3B;;AAEA;EACE,2BAA2B;EAC3B,kBAAkB;EAClB,WAAW;EACX,YAAY;EACZ,iBAAiB;EACjB,kBAAkB;AACpB;;AAEA;EACE,eAAe;EACf,gBAAgB;EAChB,yBAAyB;EACzB,aAAa;EACb,mBAAmB;AACrB;;AAEA,yHAAyH;;AAEzH,4DAA4D;AAC5D;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,QAAQ;EACR,YAAY;EACZ,eAAe;EACf,YAAY;EACZ,oCAAoC;EACpC,eAAe;EACf,gBAAgB,EAAE,kEAAkE;EACpF,cAAc;EACd,eAAe;EACf,mBAAmB;EACnB;;iBAEe;EACf,qBAAqB;EACrB,aAAa;EACb,oBAAoB;AACtB;AACA;EACE,gEAAgE;EAChE,mBAAmB;AACrB;;AAEA,4DAA4D;AAC5D;;;iDAGiD;AACjD;EACE,YAAY;EACZ,eAAe;EACf,eAAe;EACf,QAAQ;AACV;AACA;EACE,YAAY;EACZ,eAAe;EACf,eAAe;EACf,QAAQ;AACV;AACA;EACE,iFAAiF;EACjF,YAAY;EACZ,eAAe;EACf,eAAe;EACf,QAAQ;AACV;AACA;EACE,UAAU;EACV,WAAW;AACb;AACA;EACE,WAAW;AACb;AACA;EACE,WAAW;AACb;AACA;EACE,WAAW;AACb;;AAEA,4DAA4D;AAC5D;EACE,qCAAqC;EACrC,sBAAsB;AACxB;AACA;EACE,qCAAqC;EACrC,wBAAwB;EACxB,sBAAsB;AACxB;AACA;EACE,YAAY;AACd;;AAEA;EACE,sDAAsD;EACtD,gCAAgC;AAClC;AACA;EACE,yDAAyD;EACzD,gCAAgC;AAClC;;AAEA,+EAA+E;AAC/E;EACE,2BAA2B;EAC3B,oCAAoC;EACpC,gCAAgC;AAClC;AACA;EACE,6BAA6B;EAC7B,gCAAgC;AAClC;;AAEA;EACE,sDAAsD;EACtD,gCAAgC;AAClC;AACA;EACE,yDAAyD;EACzD,gCAAgC;AAClC;;AAEA;EACE,8BAA8B;EAC9B,yBAAyB;AAC3B;AACA;EACE,8BAA8B;EAC9B,yBAAyB;AAC3B;;AAEA;EACE,6CAA6C;EAC7C,wBAAwB;AAC1B;AACA;EACE,6CAA6C;EAC7C,wBAAwB;AAC1B;;AAEA,4DAA4D;AAC5D;;EAEE,YAAY;EACZ,mBAAmB;AACrB;;AAEA;4EAC4E;AAC5E;;EAEE,UAAU;EACV,YAAY;AACd;;AAEA,4DAA4D;AAC5D;EACE,WAAW;EACX,YAAY;EACZ,8BAA8B;EAC9B,6BAA6B;EAC7B,kBAAkB;EAClB,2CAA2C;AAC7C;AACA;EACE;IACE,yBAAyB;EAC3B;AACF;;AAEA,4DAA4D;AAC5D;EACE,aAAa;EACb,mBAAmB;EACnB,cAAc;EACd,cAAc;AAChB;;AAEA;EACE,wBAAwB;AAC1B;;AAEA,4DAA4D;AAC5D;EACE;IACE,YAAY;IACZ,eAAe;IACf,eAAe;EACjB;AACF;;AAEA,6JAA6J;AAC7J;EACE,aAAa;EACb,mBAAmB;EACnB,QAAQ;EACR,eAAe;EACf,iBAAiB;EACjB,iBAAiB;AACnB;AACA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,gBAAgB;EAChB,mBAAmB;EACnB,kBAAkB;EAClB,iDAAiD;EACjD,6BAA6B;EAC7B,qCAAqC;EACrC,oBAAoB;EACpB,eAAe;EACf,gBAAgB;EAChB,gBAAgB;EAChB,mBAAmB;EACnB,eAAe;EACf;;;eAGa;AACf;AACA;EACE,wDAAwD;EACxD,0DAA0D;EAC1D,qCAAqC;AACvC;;AAEA;;;;;qCAKqC;AACrC;EACE,YAAY;AACd;AACA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,YAAY;EACZ,eAAe;EACf,YAAY;EACZ,oCAAoC;EACpC,8BAA8B;EAC9B,yBAAyB;EACzB,oBAAoB;EACpB,eAAe;EACf,gBAAgB;EAChB,cAAc;EACd,mBAAmB;EACnB,eAAe;EACf;;eAEa;AACf;AACA;EACE,8BAA8B;AAChC;AACA;EACE,sDAAsD;EACtD,gCAAgC;AAClC;AACA;EACE,yDAAyD;EACzD,gCAAgC;AAClC;;AAEA,+EAA+E;AAC/E;EACE,+CAA+C;EAC/C,uBAAuB;EACvB,0BAA0B;EAC1B,2CAA2C;AAC7C;AACA;;EAEE,4BAA4B;EAC5B,eAAe;EACf,gBAAgB;EAChB,qCAAqC;AACvC;;AAEA,oEAAoE;;AAEpE,+BAA+B;AAC/B;EACE,aAAa;EACb,SAAS,EAAE,sBAAsB;EACjC,gBAAgB;EAChB,mBAAmB;EACnB,mBAAmB;EACnB,kBAAkB;EAClB,qBAAqB;EACrB,wBAAwB;AAC1B;AACA;EACE,aAAa;AACf;;AAEA;;;uEAGuE;AACvE;EACE,cAAc;EACd,sBAAsB;EACtB,gBAAgB;EAChB,kBAAkB;EAClB,mBAAmB;EACnB,UAAU;AACZ;;AAEA;;;;+BAI+B;AAC/B;EACE,kBAAkB;AACpB;AACA;;EAEE,WAAW;EACX,oBAAoB;EACpB,kBAAkB;EAClB,MAAM;EACN,WAAW;EACX,YAAY;EACZ,UAAU;AACZ;AACA;EACE,QAAQ;EACR;;;;GAIC;AACH;AACA;EACE,OAAO;EACP;;;;GAIC;AACH;AACA,yEAAyE;AACzE;EACE;;;;GAIC;AACH;AACA;EACE;;;;GAIC;AACH;;AAEA,kFAAkF;AAClF;EACE,cAAc;EACd,YAAY,EAAE,4DAA4D;EAC1E,gBAAgB;EAChB,6BAA6B;EAC7B,yCAAyC;EACzC,kCAAkC;EAClC,0BAA0B;EAC1B,aAAa;EACb,sBAAsB;EACtB,sBAAsB,EAAE,qBAAqB;EAC7C,eAAe;EACf,8BAA8B;EAC9B,yBAAiB;UAAjB,iBAAiB;AACnB;AACA;EACE,2DAA2D;AAC7D;AACA;EACE,yDAAyD;EACzD,0DAA0D;AAC5D;;AAEA;;;;6BAI6B;AAC7B;EACE,aAAa;EACb,sBAAsB;EACtB,cAAc;EACd,iBAAiB;EACjB,gBAAgB;AAClB;;AAEA;yEACyE;AACzE;EACE,gBAAgB;EAChB,iBAAiB;EACjB,kBAAkB;EAClB,eAAe;EACf,gBAAgB;EAChB,qCAAqC;EACrC,mBAAmB;AACrB;AACA;EACE,qCAAqC;AACvC;;AAEA,2HAA2H;AAC3H;EACE,aAAa;EACb,sBAAsB;EACtB,uBAAuB;EACvB,YAAY,EAAE,uDAAuD;EACrE,gBAAgB;EAChB,eAAe,EAAE,uBAAuB;EACxC,gBAAgB;EAChB,+BAA+B;EAC/B,gBAAgB,EAAE,yBAAyB;EAC3C,yBAAyB;AAC3B;;AAEA,8EAA8E;AAC9E;EACE,eAAe;EACf,gBAAgB;EAChB,qCAAqC;EACrC,gBAAgB,EAAE,yBAAyB;AAC7C;AACA;EACE;;GAEC,EAAE,kDAAkD;AACvD;AACA;EACE,6BAA6B;AAC/B;AACA;EACE,qCAAqC;AACvC;;AAEA,wDAAwD;AACxD;EACE;IACE,YAAY;IACZ,kBAAkB;EACpB;EACA;IACE,eAAe;EACjB;EACA;IACE,eAAe;EACjB;AACF;;AAEA,0IAA0I;AAC1I;;;oCAGoC;AACpC;EACE,kBAAkB;EAClB,YAAY;EACZ,aAAa;EACb,2BAA2B;EAC3B,kCAAkC;EAClC,oCAAoC;EACpC,gBAAgB;EAChB,eAAe;EACf,aAAa;EACb,sBAAsB;EACtB;;mBAEiB;AACnB;AACA;EACE,kBAAkB;EAClB,SAAS;EACT,WAAW;EACX,UAAU;EACV,WAAW;EACX,YAAY;EACZ,kBAAkB;EAClB,YAAY;EACZ,2CAA2C;EAC3C,yBAAyB;EACzB,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,eAAe;EACf,eAAe;EACf,UAAU;EACV;;;eAGa;AACf;AACA;EACE,UAAU;AACZ;AACA;EACE,qDAAqD;EACrD,gCAAgC;AAClC;AACA;qEACqE;AACrE;EACE,uDAAuD;EACvD,qDAAqD;AACvD;AACA;EACE,WAAW;EACX,cAAc;AAChB;AACA;EACE,SAAO;EACP,aAAa;EACb,kBAAkB;EAClB,aAAa;EACb,sBAAsB;AACxB;AACA;EACE,eAAe;EACf,yBAAyB;EACzB,mBAAmB;EACnB,qBAAqB;AACvB;AACA;EACE,aAAa;EACb,mBAAmB;EACnB,sBAAsB;AACxB;AACA;EACE,WAAW;EACX,YAAY;EACZ,kCAAkC;EAClC,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,gBAAgB;EAChB,eAAe;EACf,cAAc;AAChB;AACA;EACE,gBAAgB;EAChB,eAAe;EACf,yBAAyB;EACzB,gBAAgB;EAChB,SAAO;EACP,YAAY;AACd;AACA;EACE,eAAe;EACf,yBAAyB;EACzB,gBAAgB;AAClB;;AAEA,uIAAuI;AACvI;;;;uEAIuE;AACvE;EACE,YAAY;EACZ,6BAA6B;EAC7B,yCAAyC;EACzC,kCAAkC;EAClC,0BAA0B;AAC5B;AACA;EACE,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,eAAe;EACf,sBAAsB;EACtB,4CAA4C;EAC5C,gCAAgC;AAClC;AACA;EACE,aAAa;EACb,mBAAmB;EACnB,sBAAsB;EACtB,eAAe;EACf,gBAAgB;EAChB,6BAA6B;EAC7B,yBAAyB;EACzB,qBAAqB;AACvB;AACA;EACE,aAAa;EACb,eAAe;EACf,sBAAsB;EACtB,eAAe;EACf,+BAA+B;AACjC;AACA;EACE,6BAA6B;EAC7B,gBAAgB;AAClB;AACA;6EAC6E;AAC7E;EACE,cAAc;EACd,kBAAkB;EAClB,gCAAgC;AAClC;AACA;EACE,aAAa;EACb,sBAAsB;EACtB;0EACwE;EACxE,SAAS;EACT,oBAAoB;AACtB;AACA;EACE,mBAAmB;EACnB,uBAAuB;EACvB,eAAe;AACjB;;AAEA;;;;0DAI0D;AAC1D;EACE;IACE,UAAU;IACV,4BAA4B;EAC9B;EACA;IACE,UAAU;IACV,wBAAwB;EAC1B;AACF;AACA;EACE,kDAAkD;AACpD;;AAEA,gTAAgT;AAChT;EACE,aAAa;EACb;;;;;;sEAMoE;EACpE,8CAA8C;EAC9C,SAAS;EACT;;;;2EAIyE;EACzE,oBAAoB;EACpB;;qEAEmE;EACnE,uBAAuB;AACzB;AACA;EACE,kBAAkB;EAClB,gBAAgB;EAChB,mBAAmB;EACnB;;;;;yBAKuB;EACvB,aAAa;EACb,sBAAsB;EACtB,SAAS;AACX;AACA;EACE,mBAAmB;AACrB;AACA;;;;;;;0DAO0D;AAC1D;EACE;IACE,mBAAmB;EACrB;EACA;IACE,mBAAmB;IACnB,eAAe;EACjB;AACF;AACA,mEAAmE;AACnE;EACE,WAAW;EACX,kBAAkB;EAClB,MAAM;EACN,SAAS;EACT,WAAW;EACX,UAAU;EACV,mCAAmC;AACrC;AACA;;;;0BAI0B;AAC1B;EACE;IACE,mBAAmB;EACrB;EACA;IACE,aAAa;EACf;AACF;;AAEA;;;;;;;;;;;;;iEAaiE;AACjE;EACE,aAAa;EACb,eAAe;AACjB;AACA;;;;;;;qEAOqE;AACrE;EACE,eAAe;EACf,gBAAgB;AAClB;AACA;;8CAE8C;AAC9C;EACE;IACE,YAAY;IACZ,gBAAgB;EAClB;AACF;AACA;;;;;;4EAM4E;AAC5E;EACE,2BAA2B;AAC7B;AACA;EACE;;IAEE,aAAa;EACf;AACF;;AAEA,0GAA0G;AAC1G;EACE,oBAAoB;EACpB,mBAAmB;EACnB,2BAA2B;EAC3B,oCAAoC;EACpC,oCAAoC;EACpC,YAAY;EACZ,YAAY;EACZ,MAAM;AACR;AACA;EACE,YAAY;EACZ,YAAY;EACZ,YAAY;EACZ,oCAAoC;EACpC,uBAAuB;EACvB,gCAAgC;EAChC,oBAAoB;EACpB,eAAe;EACf,gBAAgB;EAChB,eAAe;EACf;;eAEa;AACf;AACA;EACE,qCAAqC;EACrC,sBAAsB;AACxB;AACA;EACE;IACE,WAAW;IACX,eAAe;EACjB;AACF;;AAEA,8GAA8G;AAC9G;;;;gDAIgD;AAChD;EACE;IACE,UAAU;IACV,yCAAyC;EAC3C;EACA;IACE,UAAU;IACV,kCAAkC;EACpC;AACF;AACA;EACE,mDAAmD;AACrD;AACA;EACE,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,sBAAsB;EACtB,2BAA2B;EAC3B,mBAAmB;EACnB,yDAAyD;EACzD,aAAa;EACb,kBAAkB;EAClB,eAAe;EACf,4BAA4B;EAC5B,4BAA4B;AAC9B;AACA;EACE,wDAAwD;AAC1D;AACA;EACE,gEAAgE;EAChE,mBAAmB;AACrB;AACA,qEAAqE;AACrE;EACE,eAAe;AACjB;AACA;EACE,yDAAyD;AAC3D;AACA;EACE,aAAa;EACb,mBAAmB;EACnB,sBAAsB;EACtB,eAAe;EACf,gBAAgB;EAChB,+BAA+B;EAC/B,yBAAyB;EACzB,mBAAmB;EACnB,gBAAgB;EAChB,uBAAuB;EACvB;;;;;;;wBAOsB;EACtB,YAAY;EACZ,cAAc;AAChB;AACA;EACE,aAAa;EACb,mBAAmB;EACnB,SAAS;EACT,eAAe;EACf,iBAAiB;EACjB,kBAAkB;AACpB;AACA;EACE,aAAa;EACb,mBAAmB;EACnB,SAAS;EACT,eAAe;EACf,mBAAmB;AACrB;AACA;EACE,+BAA+B;EAC/B,gBAAgB;AAClB;AACA;EACE,6BAA6B;EAC7B,gBAAgB;AAClB;AACA;EACE,UAAU;EACV,WAAW;EACX,kBAAkB;EAClB,mCAAmC;EACnC,cAAc;AAChB;AACA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,WAAW;EACX,YAAY;EACZ,YAAY;EACZ,uBAAuB;EACvB,6BAA6B;EAC7B,eAAe;EACf,cAAc;EACd,UAAU;AACZ;AACA;;;;2DAI2D;AAC3D;EACE;IACE,eAAe;IACf,YAAY;EACd;EACA;IACE,WAAW;IACX,cAAc;EAChB;EACA;IACE,WAAW;IACX,cAAc;IACd,eAAe;EACjB;AACF;;AAEA,mXAAmX;;AAEnX;EACE,wBAAwB,EAAE,sCAAsC;AAClE;;AAEA;EACE,+CAA+C;EAC/C,2CAA2C;AAC7C;;AAEA;EACE,2CAA2C;AAC7C;;AAEA;EACE,2BAA2B;AAC7B;;AAEA;EACE,0BAA0B;AAC5B;;AAEA;EACE,yBAAyB;AAC3B;;AAEA,6CAA6C;;AAE7C,6BAA6B;AAC7B;EACE,kCAAkC;EAClC,gBAAgB;EAChB,yBAAyB;EACzB,2BAA2B;EAC3B,0BAA0B;EAC1B,sBAAsB;AACxB;;AAEA;EACE,eAAe;AACjB;;AAEA;EACE,wDAAwD;EACxD,yBAAyB;AAC3B;;AAEA;EACE,wDAAwD;EACxD,yBAAyB;AAC3B;;AAEA;;GAEG;AACH;EACE,kCAAkC;EAClC,2BAA2B;EAC3B,0BAA0B;EAC1B,sBAAsB;AACxB;;AAEA;EACE,uBAAuB;EACvB,4BAA4B;EAC5B,sBAAsB;EACtB,8BAA8B;EAC9B,oBAAoB;EACpB,wBAAwB;EACxB,8BAA8B;EAC9B,8BAA8B;AAChC;;AAEA,4FAA4F;AAC5F;;;;EAIE,6BAA6B;AAC/B;;AAEA,kEAAkE;AAClE;;;;EAIE,yBAAyB;EACzB,sBAAsB;EACtB,0BAA0B;EAC1B,2BAA2B;EAC3B,kCAAkC;EAClC,8BAA8B;EAC9B,4BAA4B;AAC9B;;AAEA,mBAAmB;AACnB;EACE,oCAAoC;EACpC,0BAA0B;EAC1B,gBAAgB;EAChB,yBAAyB,EAAE,iCAAiC;EAC5D,WAAW;EACX,eAAe;EACf,2BAA2B;EAC3B,kCAAkC;EAClC,8BAA8B;EAC9B,iBAAiB;AACnB;;AAEA,0DAA0D;AAC1D;EACE,cAAc;EACd,gBAAgB;EAChB,uBAAuB;EACvB,mBAAmB;EACnB,eAAe;AACjB;;AAEA,sEAAsE;AACtE;;;;;;EAME,oCAAoC;AACtC;;AAEA;gFACgF;AAChF;;;;EAIE,6BAA6B;AAC/B;;AAEA;iFACiF;AACjF;EACE,uBAAuB;EACvB,4BAA4B;EAC5B,6BAA6B;AAC/B;;AAEA;EACE,oCAAoC;EACpC,0BAA0B;EAC1B,gBAAgB;EAChB,iBAAiB;AACnB;;AAEA;EACE,0BAA0B;EAC1B,gBAAgB;AAClB;;AAEA,6EAA6E;AAC7E;;;;;;;;;EASE,6BAA6B;AAC/B;;AAEA;;;EAGE,oCAAoC;AACtC;;AAEA;;EAEE,4BAA4B;EAC5B,0BAA0B;EAC1B,gBAAgB;EAChB,2BAA2B;EAC3B,8BAA8B;EAC9B,2BAA2B;EAC3B,kCAAkC;EAClC,2BAA2B;EAC3B,SAAO;EACP,YAAY;EACZ,qBAAqB;AACvB;;AAEA;EACE,8BAA8B;EAC9B,oCAAoC;AACtC;;AAEA;;;EAGE,uBAAuB;EACvB,4BAA4B;EAC5B,iCAAiC;EACjC,wBAAwB;EACxB,8BAA8B;EAC9B,sBAAsB;EACtB,oBAAoB;EACpB,2BAA2B;EAC3B,8BAA8B;EAC9B,2BAA2B;EAC3B,iCAAiC;AACnC;AACA;;;;;;;GAOG;;AAEH;EACE,2BAA2B;AAC7B;;AAEA;;EAEE,2BAA2B;EAC3B,gCAAgC;EAChC,sBAAsB;EACtB,oBAAoB;EACpB,iCAAiC;EACjC,uBAAuB;EACvB,wBAAwB;EACxB,mBAAmB;EACnB,mBAAmB;EACnB,2BAA2B;AAC7B;;AAEA,0DAA0D;;AAE1D,+BAA+B;AAC/B;EACE,wBAAwB;EACxB,8BAA8B;EAC9B,8BAA8B;EAC9B,yCAAyC;EACzC,2BAA2B;AAC7B;;AAEA,uBAAuB;AACvB;EACE,2BAA2B;EAC3B,sBAAsB;EACtB,+BAA+B;EAC/B,kBAAkB;EAClB,mBAAmB;AACrB;;AAEA,oGAAoG;AACpG;;EAEE,yBAAyB;AAC3B;;AAEA;EACE,0BAA0B;EAC1B,2BAA2B;EAC3B,0BAA0B;AAC5B;;AAEA;EACE,sBAAsB;AACxB;;AAEA;EACE,UAAU;EACV,qCAAqC;AACvC;;AAEA;EACE,UAAU;EACV,sCAAsC;EACtC,oBAAoB;AACtB;;AAEA,yBAAyB;AACzB;EACE,+CAA+C;EAC/C,6BAA6B;EAC7B,sBAAsB;EACtB,0BAA0B;EAC1B,2BAA2B;AAC7B;;AAEA,iFAAiF;AACjF;EACE,wCAAwC;EACxC,oDAAoD;AACtD;;AAEA;EACE,wBAAwB;AAC1B;;AAEA;EACE,yBAAyB;EACzB,gBAAgB;EAChB,kBAAkB;AACpB;AACA,qBAAqB;AACrB;EACE,gDAAgD;EAChD,mBAAmB;EACnB,SAAS;AACX;;AAEA;EACE,0CAA0C;EAC1C,oBAAoB;EACpB,sBAAsB;EACtB,2BAA2B;EAC3B,4BAA4B;AAC9B;;AAEA;EACE,gBAAgB;AAClB;;AAEA;EACE,sBAAsB;EACtB,wBAAwB;EACxB,iCAAiC;EACjC,8BAA8B;EAC9B,4BAA4B;EAC5B,sBAAsB;EACtB,mBAAmB;AACrB;AACA;;;EAGE,iCAAiC;AACnC;;AAEA,UAAU;AACV;;;EAGE,iCAAiC;AACnC;;AAEA,kCAAkC;AAClC;EACE,+CAA+C;EAC/C,iCAAiC;AACnC;;AAEA,0BAA0B;AAC1B;EACE,+CAA+C;EAC/C,iCAAiC;AACnC;;AAEA,uCAAuC;AACvC;EACE,6BAA6B;AAC/B;;AAEA,iCAAiC;;AAEjC;EACE,WAAW;EACX,YAAY;EACZ,kBAAkB;EAClB,yBAAyB;AAC3B;;AAEA;EACE,eAAe;EACf,QAAQ;EACR,YAAY;EACZ,yBAAyB;AAC3B;;AAEA;EACE,8BAA8B;EAC9B,kBAAkB;EAClB,4BAA4B;AAC9B;;AAEA;EACE,2BAA2B;AAC7B;;AAEA;EACE,aAAa;EACb,yBAAyB;EACzB,kBAAkB;AACpB;;AAEA;;;;;EAKE,qCAAqC;EACrC,4CAA4C;EAC5C,0CAA0C;AAC5C;;AAEA;;;;;;EAME,8BAA8B;EAC9B,WAAW;EACX,qBAAqB;EACrB,sBAAsB;EACtB,YAAY;EACZ,6BAA6B;EAC7B,gCAAgC;AAClC;;AAEA;EACE,kBAAkB;EAClB,SAAS;EACT,aAAa;EACb,iBAAiB;AACnB;;AAEA;EACE,qBAAqB;EACrB,sBAAsB;AACxB;;AAEA;EACE,wBAAwB;AAC1B;;AAEA;EACE,wBAAwB;AAC1B;;AAEA;EACE,kCAAkC;EAClC,YAAY;EACZ,kBAAkB;AACpB;;AAEA;EACE,yBAAyB;AAC3B;;AAEA,uCAAuC;;AAEvC,8BAA8B;AAC9B;EACE,sCAAsC;AACxC;;AAEA;EACE,oCAAoC;EACpC,gBAAgB;AAClB;;AAEA;EACE,uBAAuB;AACzB;;AAEA;EACE,mBAAmB;AACrB;;AAEA;;EAEE,6BAA6B;EAC7B,+CAA+C;AACjD;;AAEA;EACE,gDAAgD;;EAEhD,6BAA6B;;EAE7B,aAAa;AACf;;AAEA;EACE,aAAa;;EAEb,8BAA8B;AAChC;;AAEA;;;;;;;;GAQG;;AAEH;EACE,eAAe;AACjB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,eAAe;AACjB;;AAEA;EACE,eAAe;EACf,yBAAyB;EACzB,kBAAkB;AACpB;;AAEA;EACE,kCAAkC;EAClC,oBAAoB;EACpB,2BAA2B;EAC3B,oCAAoC;AACtC;;AAEA;EACE,eAAe;EACf,2BAA2B;EAC3B,kBAAkB;AACpB;;AAEA;;;;EAIE,oCAAoC;EACpC,kBAAkB;EAClB,YAAY;EACZ,kBAAkB;AACpB;;AAEA;EACE,gBAAgB;EAChB,oCAAoC;EACpC,yBAAyB;EACzB,2BAA2B;EAC3B,kBAAkB;AACpB;;AAEA;EACE,iCAAiC;EACjC,0BAA0B;AAC5B;;AAEA;EACE,sBAAsB;EACtB,eAAe;EACf,gBAAgB;EAChB,sBAAsB;AACxB;;AAEA;EACE,oCAAoC;AACtC;;AAEA;EACE,2CAA2C;AAC7C;;AAEA;;EAEE,2BAA2B;AAC7B;;AAEA,sDAAsD;AACtD;EACE,2BAA2B;EAC3B,aAAa;EACb,mBAAmB;EACnB,mBAAmB;EACnB,2BAA2B;AAC7B;;AAEA,oDAAoD;AACpD;;;EAGE,+CAA+C;EAC/C,iCAAiC;AACnC;AACA,wEAAwE;AACxE;;;;EAIE,gDAAgD;EAChD,iCAAiC;AACnC;;AAEA;;;;GAIG;;AAEH,oCAAoC;;AAEpC,sEAAsE;AACtE;;EAEE,oCAAoC;EACpC,aAAa;EACb,mBAAmB;EACnB,mBAAmB;EACnB,2BAA2B;AAC7B;;AAEA,iEAAiE;AACjE;EACE,iCAAiC;EACjC,aAAa;EACb,mBAAmB;EACnB,mBAAmB;EACnB,2BAA2B;AAC7B;AACA;EACE,aAAa;EACb,mBAAmB;EACnB,mBAAmB;EACnB,2BAA2B;EAC3B,QAAQ;AACV;;AAEA,wEAAwE;AACxE;;;EAGE,+CAA+C;EAC/C,iCAAiC;AACnC;;AAEA,mEAAmE;AACnE;;;EAGE,gDAAgD;EAChD,oCAAoC;AACtC;AACA;;;;;;;;;EASE,oCAAoC;AACtC;;AAEA,6DAA6D;AAC7D;;;;;;;EAOE,iCAAiC;AACnC;;AAEA,mCAAmC;AACnC;EACE,iCAAiC;EACjC,+CAA+C;EAC/C,sBAAsB;EACtB,0BAA0B;EAC1B,2BAA2B;AAC7B;;AAEA,uEAAuE;AACvE;EACE,wCAAwC;EACxC,oCAAoC;AACtC;;AAEA,uEAAuE;AACvE;;;EAGE,iCAAiC;EACjC,+CAA+C;AACjD;AACA,sEAAsE;AACtE;;;;EAIE,iCAAiC;EACjC,gDAAgD;AAClD;;AAEA,uCAAuC;AACvC;EACE,6BAA6B;AAC/B;;AAEA;EACE,kCAAkC;AACpC;;AAEA;EACE,sBAAsB;EACtB,iCAAiC;EACjC,+CAA+C;EAC/C,2CAA2C;EAC3C,6BAA6B;EAC7B,uBAAuB;EACvB,4BAA4B;AAC9B;;AAEA,iCAAiC;;AAEjC,4BAA4B;AAC5B;EACE,qBAAqB;AACvB;;AAEA;EACE,kBAAkB;EAClB,2CAA2C;EAC3C,iCAAiC;EACjC,gBAAgB;AAClB;;AAEA;EACE,kBAAkB;EAClB,yBAAyB;EACzB,eAAe;EACf,wBAAwB;AAC1B;;AAEA;EACE,aAAa;EACb,8BAA8B;EAC9B,mBAAmB;AACrB;;AAEA;EACE,WAAW;EACX,eAAe;EACf,2CAA2C;EAC3C,cAAc;AAChB;;AAEA;EACE,gBAAgB;AAClB;;AAEA;EACE,yBAAyB;EACzB,eAAe;EACf,iBAAiB;AACnB;;AAEA;EACE,mBAAmB;EACnB,qBAAqB;AACvB;;AAEA;;;;EAIE,mBAAmB;EACnB,qBAAqB;AACvB;;AAEA;;;;4DAI4D;AAC5D;EACE,wCAAwC;EACxC,6CAA6C;EAC7C,oDAAoD;EACpD,eAAe;EACf,8BAA8B;AAChC;;AAEA;;;;gBAIgB;AAChB;EACE,8BAA8B;AAChC;;AAEA;EACE,sEAAsE;EACtE,gBAAgB;AAClB;;AAEA;;;;;;iEAMiE;AACjE;;EAEE,oEAAoE;EACpE;;;4BAG0B;EAC1B,sDAAsD;AACxD;;AAEA;EACE,YAAY;AACd;;AAEA;0DAC0D;AAC1D;EACE,gBAAgB;AAClB;AACA;EACE,aAAa;EACb,sBAAsB;EACtB,QAAQ;EACR,eAAe;EACf;;;yEAGuE;EACvE,gBAAgB;AAClB;AACA;EACE,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,QAAQ;AACV;AACA;EACE,qCAAqC;AACvC;AACA;EACE,qCAAqC;EACrC,gBAAgB;AAClB;;AAEA;EACE,eAAe;EACf,gBAAgB;EAChB,qCAAqC;AACvC;;AAEA;EACE,qCAAqC;AACvC;;AAEA;EACE,gBAAgB;AAClB;;AAEA;EACE,eAAe;EACf,gBAAgB;EAChB,qCAAqC;AACvC;;AAEA;EACE,qCAAqC;AACvC;;AAEA;EACE,YAAY;EACZ,aAAa;EACb,mBAAmB;AACrB;;AAEA,mCAAmC;;AAEnC;EACE,WAAW;EACX,YAAY;EACZ,aAAa;EACb,uBAAuB;EACvB,mBAAmB;EACnB,8BAA8B;EAC9B,uCAAuC;EACvC,wCAAwC;AAC1C;;AAEA;EACE,aAAa;EACb,8BAA8B;EAC9B,mBAAmB;EACnB,8BAA8B;EAC9B,mBAAmB;EACnB,kBAAkB;EAClB,kBAAkB;EAClB,eAAe;EACf,iCAAiC;AACnC;;AAEA;EACE,+BAA+B;EAC/B,sCAAsC;EACtC,iCAAiC;EACjC,aAAa;EACb,8BAA8B;EAC9B,mBAAmB;EACnB,kBAAkB;EAClB,kBAAkB;EAClB,eAAe;AACjB;;AAEA;EACE,kBAAkB;AACpB;;AAEA;;EAEE,eAAe;EACf,sBAAsB;EACtB,kBAAkB;EAClB,yBAAyB;AAC3B;;AAEA,gDAAgD;;AAEhD;EACE,sBAAsB;EACtB,qBAAqB;EACrB,eAAe;AACjB;;AAEA;EACE,qBAAqB;EACrB,gBAAgB;AAClB;;AAEA;EACE,kBAAkB;AACpB;;AAEA;EACE,eAAe;EACf,aAAa;EACb,8BAA8B;EAC9B,mBAAmB;EACnB,oCAAoC;EACpC,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,uBAAuB;EACvB,mBAAmB;AACrB;;AAEA;EACE,sBAAsB;EACtB,eAAe;EACf,WAAW;AACb;;AAEA;;;wDAGwD;AACxD;EACE,aAAa;EACb,mBAAmB;EACnB,mBAAmB;EACnB,uBAAuB;EACvB,QAAQ;EACR,iBAAiB;AACnB;;AAEA;EACE,kBAAkB;EAClB,8BAA8B;EAC9B,6BAA6B;EAC7B,WAAW;EACX,qBAAqB;EACrB,sDAAsD;AACxD;;AAEA;;;;;;;;;;;;;;;8DAe8D;AAC9D;;EAEE,8BAA8B;AAChC;;AAEA;;;;;;;;gFAQgF;;AAEhF,qEAAqE;AACrE;EACE,2BAA2B;EAC3B,qBAAqB;EACrB,sDAAsD;AACxD;;AAEA,8CAA8C;AAC9C;EACE,WAAW;EACX,UAAU;AACZ;AACA;EACE,8BAA8B;AAChC;AACA;EACE,8BAA8B;EAC9B,kBAAkB;AACpB;AACA;EACE,8BAA8B;EAC9B,kBAAkB;AACpB;AACA;EACE,8BAA8B;AAChC;;AAEA;EACE,WAAW;EACX,YAAY;AACd;;AAEA;EACE,2BAA2B;EAC3B,oBAAoB;EACpB,2BAA2B;EAC3B,wBAAwB;EACxB,8BAA8B;EAC9B,2BAA2B;AAC7B;;AAEA;EACE,oCAAoC;AACtC;;AAEA;EACE,qBAAqB;EACrB,wBAAwB;EACxB,oBAAoB;AACtB;;AAEA,mHAAmH;;AAEnH,4BAA4B;AAC5B,wBAAwB;;AAExB;EACE,mBAAmB;EACnB,qBAAqB,EAAE,OAAO;EAC9B,uBAAuB,EAAE,cAAc;AACzC;;AAEA;EACE,yCAAyC;EACzC,qBAAqB,EAAE,eAAe;EACtC,gBAAgB;EAChB,aAAa;EACb,mBAAmB;AACrB;;AAEA;EACE,eAAe,EAAE,yBAAyB;EAC1C,iCAAiC;EACjC,gBAAgB;EAChB,iBAAiB;AACnB;;AAEA;EACE,QAAQ;EACR,WAAW;AACb;;AAEA;EACE,2BAA2B;EAC3B,WAAW;EACX,YAAY;EACZ,mBAAmB;EACnB,iBAAiB;AACnB;;AAEA;;;;+DAI+D;AAC/D;EACE,SAAS;EACT,uBAAuB;EACvB,+BAA+B;EAC/B,gBAAgB;AAClB;;AAEA,oCAAoC;AACpC;EACE,SAAS;EACT,oBAAoB;AACtB;;AAEA;EACE,oBAAoB;AACtB;;AAEA,6DAA6D;AAC7D;EACE,aAAa;AACf;;AAEA;eACe;AACf;EACE;IACE,wCAAwC;IACxC,uBAAuB;IACvB,cAAc;EAChB;AACF;;AAEA,6DAA6D;AAC7D;EACE,gBAAgB;EAChB,+BAA+B;EAC/B,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,yBAAyB;EACzB,SAAS;EACT,gBAAgB;AAClB;;AAEA;;EAEE,kBAAkB;EAClB,uBAAuB;AACzB;;AAEA;EACE,6BAA6B;AAC/B;;AAEA;EACE,uBAAuB;EACvB,8BAA8B;AAChC;;AAEA;EACE,2BAA2B;EAC3B,2BAA2B;EAC3B,6BAA6B;AAC/B;;AAEA;EACE,uBAAuB;AACzB;AACA;EACE,4BAA4B;EAC5B,2BAA2B;EAC3B,2BAA2B;EAC3B,2BAA2B;AAC7B;AACA;EACE,8BAA8B;EAC9B,sBAAsB;EACtB,4BAA4B;EAC5B,2BAA2B;AAC7B;;AAEA;EACE,sBAAsB;EACtB,2BAA2B;EAC3B,2BAA2B;EAC3B,2BAA2B;AAC7B;AACA;EACE,sBAAsB;EACtB,gBAAgB;EAChB,uBAAuB;EACvB,gBAAgB;EAChB,sBAAsB;AACxB;;AAEA;EACE,qBAAqB,EAAE,wBAAwB;AACjD;;AAEA;;;;GAIG;AACH,uBAAuB;AACvB;EACE,mBAAmB;AACrB;;AAEA;;EAEE,WAAW;AACb;;AAEA,2BAA2B;AAC3B;EACE,2BAA2B;AAC7B;;AAEA;EACE;IACE,uBAAuB;EACzB;AACF;AACA;EACE;IACE,uBAAuB,EAAE,mCAAmC;EAC9D;AACF;;AAEA;EACE;IACE,uBAAuB;EACzB;AACF;AACA;EACE;IACE,wBAAwB;EAC1B;AACF;;AAEA;EACE;IACE,wBAAwB;EAC1B;AACF;;AAEA;EACE,gCAAgC;EAChC,wCAAwC;AAC1C;;AAEA;EACE,+BAA+B;EAC/B,gCAAgC;AAClC;;AAEA;EACE,qBAAqB;EACrB,sBAAsB;AACxB;;AAEA;EACE,uBAAuB;EACvB,wBAAwB;AAC1B;;AAEA,2CAA2C;;AAE3C;EACE,aAAa;EACb,sCAAsC;EACtC,4BAA4B;EAC5B,SAAS;AACX;;AAEA;EACE,2BAA2B;AAC7B;;AAEA;EACE;IACE,2BAA2B;EAC7B;AACF;;AAEA;EACE;IACE,0BAA0B;EAC5B;AACF;;AAEA;EACE;IACE,0BAA0B;EAC5B;AACF;;AAEA;EACE,eAAe;EACf,SAAS;AACX;;AAEA;EACE,kBAAkB;AACpB;;AAEA;EACE,gBAAgB;AAClB;;AAEA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,WAAW;EACX,WAAW;;EAEX,0CAA0C;AAC5C;;AAEA;EACE,aAAa;EACb,2BAA2B;EAC3B,mBAAmB;AACrB;;AAEA;EACE,6BAA6B;EAC7B,sBAAsB;EACtB,WAAW;EACX,WAAW;EACX,mBAAmB;AACrB;;AAEA,8DAA8D;;AAE9D;EACE,6BAA6B;EAC7B,sBAAsB;EACtB,+BAA+B;EAC/B,WAAW;AACb;;AAEA;EACE,qCAAqC;AACvC;;AAEA;EACE,eAAe;AACjB;;AAEA;EACE,aAAa;AACf;;AAEA;;EAEE,aAAa;AACf;;AAEA;;;EAGE,WAAW;AACb;;AAEA;EACE,aAAa;AACf;;AAEA;EACE,YAAY;AACd;;AAEA;EACE,YAAY;AACd;;AAEA;EACE,cAAc;AAChB;;AAEA;EACE,YAAY;AACd;;AAEA;EACE,cAAc;AAChB;;AAEA;EACE,cAAc;AAChB;;AAEA;EACE,SAAS;EACT,WAAW;AACb;;AAEA;;EAEE,SAAS;EACT,WAAW;AACb;;AAEA;EACE,WAAW;EACX,YAAY;AACd;;AAEA;EACE,WAAW;EACX,aAAa;AACf;;AAEA;EACE,WAAW;EACX,YAAY;AACd;;AAEA;EACE,SAAS;EACT,YAAY;AACd;;AAEA,iCAAiC;;AAEjC;EACE,kBAAkB;EAClB,gBAAgB;EAChB,kBAAkB;EAClB,mDAAmD;AACrD;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,iCAAiC;EACjC,uCAAuC;EACvC,kBAAkB;EAClB,qBAAqB;EACrB,sBAAsB;EACtB,sCAAsC;AACxC;;AAEA;EACE;IACE,uBAAuB;EACzB;;EAEA;IACE,yBAAyB;EAC3B;AACF;;AAEA,2CAA2C;;AAE3C,6BAA6B;AAC7B;EACE;IACE,UAAU;EACZ;AACF;;AAEA;EACE;IACE,eAAe;EACjB;;EAEA;IACE,uBAAuB;IACvB,eAAe;EACjB;AACF;;AAEA;EACE;IACE,WAAW;IACX,yBAAyB;EAC3B;AACF;;AAEA;EACE;IACE,yBAAyB;EAC3B;;EAEA;;IAEE,SAAO;IACP,eAAe;EACjB;;EAEA;IACE,kBAAkB;IAClB,kBAAkB;EACpB;;EAEA;;IAEE,+BAA+B;IAC/B,iBAAiB;IACjB,gBAAgB;EAClB;;EAEA;IACE,WAAW;EACb;AACF;;AAEA;EACE;IACE,wBAAwB;EAC1B;;EAEA;IACE;MACE,wBAAwB;MACxB,iCAAiC;MACjC,yCAAyC;MACzC,oCAAoC;IACtC;EACF;;EAEA;IACE,WAAW;EACb;;EAEA,kFAAkF;;EAElF;IACE,gBAAgB;EAClB;;EAEA;IACE,sBAAsB;IACtB,6BAA6B;EAC/B;AACF;;AAEA;EACE;IACE,gBAAgB;EAClB;AACF;;AAEA;;;;;GAKG;AACH;EACE,+BAA+B;EAC/B,kBAAkB;EAClB,gBAAgB;EAChB,kBAAkB;EAClB,eAAe;EACf,WAAW;AACb;;AAEA,WAAW;;AAEX;EACE,+BAA+B;EAC/B,kBAAkB;EAClB,gBAAgB;EAChB,eAAe;EACf,8BAA8B;EAC9B,YAAY;AACd;;AAEA;EACE,+BAA+B;EAC/B,cAAc;EACd,2BAA2B;EAC3B,8BAA8B;EAC9B,sBAAsB;AACxB;;AAEA;EACE,YAAY;EACZ,kBAAkB;EAClB,WAAW;AACb;;AAEA;EACE,oCAAoC;EACpC,kBAAkB;EAClB,wCAAwC;AAC1C;;AAEA;;;GAGG;;AAEH;EACE,aAAa;AACf;;AAEA;EACE,uBAAuB;AACzB;;AAEA;EACE;IACE,aAAa;EACf;AACF;;AAEA;EACE;IACE,SAAS;IACT,yBAAyB;EAC3B;AACF;;AAEA;EACE;IACE,aAAa;IACb,iCAAiC;IACjC,wBAAwB;EAC1B;AACF;;AAEA;EACE;IACE,SAAS;IACT,yBAAyB;EAC3B;AACF;;AAEA;EACE;IACE,UAAU;EACZ;AACF;;AAEA;EACE,uBAAuB;AACzB;;AAEA;EACE,kBAAkB;AACpB;;AAEA;;EAEE,YAAY;AACd;;AAEA;EACE,YAAY;EACZ,aAAa;EACb,uBAAuB;AACzB;;AAEA;EACE,iCAAiC;EACjC,+CAA+C;EAG/C,4DAA4D;AAC9D;;AAEA;EACE,sBAAsB;EACtB,0BAA0B;EAC1B,2BAA2B;EAC3B,oCAAoC;EACpC,wCAAwC;EACxC,2CAA2C;EAC3C,8BAA8B;EAC9B,gCAAgC;EAChC,4BAA4B;AAC9B;;AAEA,sIAAsI;;AAEtI,YAAY;AACZ;EACE,6CAA6C;AAC/C;;AAEA,gDAAgD;AAChD;EACE,2BAA2B;EAC3B,uBAAuB;AACzB;AACA;EACE,oCAAoC;EACpC,2BAA2B;AAC7B;AACA;;EAEE,wBAAwB;AAC1B;;AAEA,+FAA+F;AAC/F;EACE,4CAA4C;EAC5C,0CAA0C;EAC1C,0BAA0B;EAC1B,2BAA2B;EAC3B,+BAA+B;EAC/B,2BAA2B;EAC3B,iBAAiB;EACjB,sDAAsD;EACtD,2BAA2B;EAC3B,6BAA6B;EAC7B,2BAA2B;EAC3B,8BAA8B;EAC9B,+BAA+B;EAC/B,4BAA4B;EAC5B,+BAA+B;AACjC;;AAEA,kEAAkE;AAClE;;;EAGE,8BAA8B;EAC9B,+BAA+B;EAC/B,4BAA4B;EAC5B,+BAA+B;AACjC;;AAEA,yDAAyD;AACzD;EACE,wBAAwB;AAC1B;;AAEA,+BAA+B;AAC/B;EACE,uCAAuC;AACzC;AACA;EACE,qCAAqC;AACvC;;AAEA;;;;iDAIiD;AACjD;EACE,sDAAsD;EACtD,6BAA6B;EAC7B,0BAA0B;EAC1B,wCAAwC;EACxC,sCAAsC;EACtC,gBAAgB;EAChB,4BAA4B;EAC5B,8BAA8B;EAC9B,2BAA2B;AAC7B;;AAEA;2EAC2E;AAC3E;;EAEE,8BAA8B;EAC9B,2BAA2B;EAC3B,kCAAkC;AACpC;;AAEA,sBAAsB;AACtB;EACE,4CAA4C;AAC9C;;AAEA,UAAU;AACV;EACE,8CAA8C;AAChD;;AAEA,kCAAkC;AAClC;EACE,8BAA8B;AAChC;;AAEA,iBAAiB;AACjB;EACE,sCAAsC;EACtC,QAAQ;EACR,2BAA2B;AAC7B;AACA;EACE,wBAAwB;EACxB,2BAA2B;AAC7B;AACA;EACE,oCAAoC;EACpC,gBAAgB;AAClB;AACA;;EAEE,kCAAkC;AACpC;;AAEA,0CAA0C;AAC1C;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;AACV;;AAEA;EACE,eAAe;EACf,sCAAsC;EACtC,cAAc;EACd,uBAAuB;AACzB;AACA;EACE,kCAAkC;AACpC;;AAEA;;;;;;gEAMgE;AAChE;EACE,aAAa;EACb,yBAAyB;EACzB,mBAAmB;EACnB,QAAQ;EACR,mBAAmB;AACrB;AACA;EACE,aAAa;EACb,mBAAmB;EACnB,QAAQ;AACV;AACA;;sCAEsC;AACtC;EACE,aAAa;EACb,yBAAyB;EACzB,mBAAmB;EACnB,kBAAkB;AACpB;AACA;;;;;;;;yEAQyE;AACzE;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,sBAAsB;EACtB,YAAY;EACZ,cAAc;EACd,iBAAiB;EACjB,yCAAyC;EACzC,2CAA2C;EAC3C,oCAAoC;EACpC,YAAY;EACZ,8DAA8D;EAC9D,sBAAsB;EACtB,eAAe;EACf,4BAA4B;AAC9B;AACA;EACE,8BAA8B;AAChC;AACA;;;;6EAI6E;AAC7E;EACE,gBAAgB;EAChB,SAAS;EACT,UAAU;EACV,2BAA2B;AAC7B;AACA;;sCAEsC;AACtC;EACE,kBAAkB;EAClB,mBAAmB;EACnB,gBAAgB;AAClB;AACA;;;;;;;;;;yDAUyD;AACzD;EACE,kBAAkB;EAClB,SAAS;EACT,WAAW;AACb;AACA;EACE,gBAAgB;EAChB,MAAM;AACR;AACA;EACE,kBAAkB;EAClB,QAAQ;EACR,QAAQ;AACV;AACA;EACE,aAAa;EACb,sBAAsB;EACtB,SAAS;EACT,iBAAiB;EACjB,gBAAgB;EAChB,gBAAgB;AAClB;AACA;EACE,6CAA6C;EAC7C,yCAAyC;AAC3C;;AAEA,sBAAsB;AACtB;EACE,kCAAkC;EAClC,yCAAyC;EACzC,6BAA6B;AAC/B;AACA;EACE,kCAAkC;EAClC,yCAAyC;AAC3C;AACA;EACE,sCAAsC;AACxC;;AAEA,gCAAgC;AAChC;EACE,wCAAwC;EACxC,8BAA8B;EAC9B,sDAAsD;AACxD;;AAEA,4CAA4C;AAC5C;EACE,8CAA8C;EAC9C,qCAAqC;EACrC,0BAA0B;EAC1B,2BAA2B;EAC3B,+BAA+B;EAC/B,iBAAiB;EACjB,uDAAuD;EACvD,4BAA4B;AAC9B;;AAEA,4BAA4B;AAC5B;EACE,wCAAwC;EACxC,sDAAsD;EACtD,0BAA0B;EAC1B,oCAAoC;EACpC,6BAA6B;AAC/B;AACA;EACE,yCAAyC;AAC3C;;AAEA;;;;;;;;;;;;;;;;;;;;sDAoBsD;AACtD;EACE,sBAAsB;EACtB,0BAA0B;EAC1B,2BAA2B;EAC3B,qBAAqB;EACrB,4BAA4B;AAC9B;AACA;EACE,aAAa;AACf;AACA;EACE,gBAAgB;EAChB,SAAS;EACT,aAAa;EACb,WAAW;EACX,eAAe;EACf,YAAY;AACd;AACA;;EAEE,WAAW;EACX,eAAe;EACf,YAAY;AACd;;AAEA,qBAAqB;;AAErB;;;6EAG6E;AAC7E;;EAEE,2BAA2B;EAC3B,oBAAoB;EACpB,qBAAqB;EACrB,wCAAwC;EACxC,oBAAoB;EACpB,6BAA6B;EAC7B,mDAAmD;EACnD,oCAAoC;EACpC,8BAA8B;EAC9B,wBAAwB;EACxB,0BAA0B;EAC1B,uBAAuB;AACzB;;AAEA,wBAAwB;AACxB;EACE,+CAA+C;EAC/C,6BAA6B;EAC7B,0BAA0B;EAC1B,uBAAuB;EACvB,4BAA4B;EAC5B,sCAAsC;EACtC,iCAAiC;AACnC;AACA;EACE,oCAAoC;EACpC,0BAA0B;EAC1B,qBAAqB;AACvB;AACA;EACE,yCAAyC;AAC3C;AACA;EACE,kCAAkC;AACpC;;AAEA,gBAAgB;AAChB;EACE,yCAAyC;EACzC,6CAA6C;AAC/C;AACA;EACE,iCAAiC;AACnC;AACA;EACE,iCAAiC;AACnC;;AAEA,wBAAwB;AACxB;;EAEE,0BAA0B;EAC1B,uBAAuB;EACvB,4BAA4B;EAC5B,iCAAiC;AACnC;AACA;;EAEE,+CAA+C;EAC/C,6BAA6B;EAC7B,sCAAsC;EACtC,oCAAoC;EACpC,0BAA0B;EAC1B,sBAAsB;EACtB,uBAAuB;EACvB,wBAAwB;EACxB,8BAA8B;EAC9B,kCAAkC;AACpC;AACA;;;;;;EAME,yCAAyC;EACzC,kCAAkC;AACpC;;AAEA,uBAAuB;AACvB;EACE,oCAAoC;EACpC,2CAA2C;EAC3C,wCAAwC;AAC1C;;AAEA,yBAAyB;AACzB;;;;;;;;EAQE,kCAAkC;AACpC;;AAEA,sBAAsB;AACtB;EACE,mCAAmC;AACrC;AACA;EACE,+CAA+C;EAC/C,6BAA6B;EAC7B,uBAAuB;EACvB,0BAA0B;EAC1B,sCAAsC;AACxC;AACA;EACE,yCAAyC;AAC3C;;AAEA,qBAAqB;AACrB;EACE,oCAAoC;EACpC,0BAA0B;EAC1B,kCAAkC;EAClC,4BAA4B;AAC9B;;AAEA,sBAAsB;AACtB;EACE,oCAAoC;EACpC,0BAA0B;AAC5B;;AAEA;EACE,2CAA2C;EAC3C,yCAAyC;EACzC,iCAAiC;EACjC,6BAA6B;EAC7B,gCAAgC;AAClC;;AAEA;EACE,iCAAiC;EACjC,gDAAgD;EAGhD,4DAA4D;AAC9D;;AAEA;EACE,4BAA4B;EAC5B,+CAA+C;EAC/C,2CAA2C;EAC3C,6BAA6B;EAC7B,6BAA6B;EAC7B,uBAAuB;EACvB,0BAA0B;AAC5B;;AAEA;EACE,+CAA+C;EAC/C,2CAA2C;AAC7C;;AAEA;EACE,sCAAsC;EACtC,kCAAkC;EAClC,sBAAsB;AACxB;;AAEA;EACE,sBAAsB;EACtB,+CAA+C;EAC/C,6BAA6B;EAC7B,uBAAuB;EACvB,iBAAiB;EACjB,0BAA0B;AAC5B;;AAEA;EACE,oCAAoC;EACpC,2CAA2C;AAC7C;;AAEA;;EAEE,sBAAsB;EACtB,yCAAyC;EACzC,2CAA2C;AAC7C;;AAEA;EACE,kBAAkB;EAClB,oCAAoC;AACtC;;AAEA;EACE,oCAAoC;AACtC;;AAEA;EACE,oCAAoC;EACpC,aAAa;AACf;;AAEA;EACE,sBAAsB;AACxB;;AAEA;;;;kDAIkD;AAClD;EACE,uCAAuC;AACzC;;AAEA;EACE,kDAAkD;AACpD;;AAEA;EACE,aAAa;EACb;;;;;;iEAM+D;EAC/D,8CAA8C;EAC9C;;2EAEyE;EACzE,SAAS;EACT;4DAC0D;EAC1D,uBAAuB;AACzB;AACA;EACE,oCAAoC;EACpC,YAAY;EACZ,WAAW;EACX,kBAAkB;EAClB,oDAAoD;EACpD,eAAe;EACf,0BAA0B;EAC1B,uBAAuB;AACzB;;AAEA;EACE;IACE,aAAa;IACb,8BAA8B;IAC9B,SAAS;EACX;AACF;;AAEA;EACE,sBAAsB;EACtB,qCAAqC;AACvC;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,8BAA8B;EAC9B,mBAAmB;AACrB;;AAEA;EACE,SAAS;AACX;;AAEA;EACE,aAAa;EACb,SAAS;AACX;;AAEA;EACE,aAAa;AACf;;AAEA;EACE,iBAAiB;AACnB;;AAEA,iEAAiE;;AAEjE,6EAA6E;AAC7E;;EAEE,0BAA0B;EAC1B,sBAAsB;EACtB,wBAAwB;EACxB,8BAA8B;EAC9B,kCAAkC;EAClC,0BAA0B;EAC1B,8BAA8B;EAC9B,qBAAqB;EACrB,yBAAyB;AAC3B;;AAEA;;EAEE,0BAA0B;EAC1B,sBAAsB;EACtB,wBAAwB;EACxB,8BAA8B;EAC9B,kCAAkC;EAClC,6BAA6B;EAC7B,8BAA8B;EAC9B,qBAAqB;EACrB,yBAAyB;AAC3B;;AAEA,6BAA6B;AAC7B;EACE,sBAAsB;EACtB,uBAAuB;EACvB,mBAAmB;EACnB,yBAAyB;EACzB,yBAAyB;EACzB,yBAAyB;EACzB,8BAA8B;EAC9B,qBAAqB;AACvB;;AAEA;EACE,yBAAyB;EACzB,yBAAyB;EACzB,sBAAsB;EACtB,uBAAuB;EACvB,8BAA8B;EAC9B,qBAAqB;AACvB;;AAEA,kCAAkC;AAClC;EACE,0BAA0B;EAC1B,8BAA8B;EAC9B,qBAAqB;EACrB,yBAAyB;AAC3B;;AAEA,+CAA+C;AAC/C;;EAEE,8BAA8B;EAC9B,qBAAqB;EACrB,wBAAwB;AAC1B;;AAEA;;EAEE,8BAA8B;EAC9B,qBAAqB;EACrB,yBAAyB;AAC3B;;AAEA;;;8BAG8B;AAC9B;EACE,oBAAoB;AACtB;AACA;EACE,wCAAwC;EACxC,sCAAsC;AACxC;AACA;EACE,sBAAsB;EACtB,yDAAyD;EACzD,qDAAqD;AACvD;;AAEA,mEAAmE;AACnE;EACE,gBAAgB;EAChB,YAAY;AACd;;AAEA;EACE;IACE,eAAe;EACjB;EACA;IACE,sBAAsB;IACtB,uBAAuB;EACzB;AACF;;AAEA,kbAAkb;;AAElb;;;;;;;;;;;;;;;;;;;;;;;;;;iDA0BiD;AACjD;;GAEG;;AAEH;;;;;;;;;;;;uDAYuD;AACvD;EACE,aAAa;EACb,sBAAsB;EACtB,gBAAgB;EAChB,SAAS;EACT,UAAU;EACV,2BAA2B;EAC3B,mBAAmB;AACrB;;AAEA;;;;;wBAKwB;AACxB;EACE,kBAAkB;EAClB,gBAAgB;EAChB,gBAAgB;EAChB,qBAAqB;EACrB,sDAAsD;AACxD;AACA;EACE,WAAW;EACX,UAAU;AACZ;AACA;EACE,8BAA8B;AAChC;AACA;EACE,8BAA8B;EAC9B,kBAAkB;AACpB;AACA;EACE,8BAA8B;EAC9B,kBAAkB;AACpB;AACA;EACE,8BAA8B;AAChC;;AAEA,0BAA0B;AAC1B;;;;;;;6DAO6D;AAC7D;EACE,kBAAkB;EAClB,QAAQ;EACR,WAAW;EACX,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,2CAA2C;AAC7C;AACA;EACE,6CAA6C;AAC/C;;AAEA;;;qEAGqE;AACrE;EACE,WAAW;EACX,kBAAkB;EAClB,yBAAyB;EACzB,iBAAiB;EACjB,oBAAoB;AACtB;;AAEA;;;;;;;;;;sDAUsD;AACtD;EACE,gBAAgB;EAChB,MAAM;EACN,UAAU;AACZ;;AAEA,2FAA2F;AAC3F;EACE,iCAAiC;EACjC,qCAAqC;EACrC,eAAe;EACf,gBAAgB;EAChB,gBAAgB;EAChB,2CAA2C;EAC3C,YAAY;EACZ,eAAe;EACf,sBAAsB;EACtB,sBAAsB;EACtB,yBAAiB;UAAjB,iBAAiB;AACnB;;AAEA,4EAA4E;AAC5E;EACE,mCAAmC;EACnC,qBAAqB;AACvB;AACA;EACE,iCAAiC;EACjC,qBAAqB;AACvB;;AAEA,8DAA8D;AAC9D;EACE,yDAAyD;AAC3D;AACA;EACE,0DAA0D;AAC5D;;AAEA,iEAAiE;AACjE;EACE,aAAa;AACf;;AAEA;;;sDAGsD;AACtD;;EAEE,aAAa;EACb,mBAAmB;EACnB,2BAA2B;EAC3B,QAAQ;EACR,WAAW;EACX,mBAAmB;AACrB;;AAEA;;;0CAG0C;AAC1C;;EAEE,yBAAyB;AAC3B;AACA;;EAEE,uBAAuB;AACzB;;AAEA;EACE,gBAAgB;EAChB,YAAY;EACZ,UAAU;EACV,SAAS;EACT,eAAe;EACf,eAAe;EACf,gBAAgB;EAChB,qCAAqC;EACrC,oBAAoB;AACtB;;AAEA,8DAA8D;AAC9D;EACE,cAAc;EACd,mBAAmB;EACnB,gBAAgB;AAClB;;AAEA,+CAA+C;AAC/C;;EAEE,cAAc;AAChB;;AAEA;EACE,mCAAmC;EACnC,mBAAmB;EACnB,kBAAkB;AACpB;AACA;EACE,6BAA6B;AAC/B;;AAEA;;;;uEAIuE;AACvE;EACE,2BAA2B;EAC3B,qCAAqC;EACrC,eAAe;EACf,gBAAgB;EAChB,kBAAkB;EAClB,2CAA2C;EAC3C,sBAAsB;EACtB,4BAA4B;EAC5B,mBAAmB;EACnB,gBAAgB;AAClB;;AAEA,uFAAuF;AACvF;EACE,kBAAkB;AACpB;;AAEA,sFAAsF;AACtF;EACE,6BAA6B;EAC7B,UAAU;EACV,gBAAgB;AAClB;AACA;EACE,2BAA2B;EAC3B,UAAU;AACZ;;AAEA,kEAAkE;AAClE;EACE,6DAA6D;AAC/D;;AAEA,8DAA8D;AAC9D;EACE,yDAAyD;AAC3D;AACA;EACE,0DAA0D;AAC5D;;AAEA;mFACmF;AACnF;EACE,uBAAuB;AACzB;;AAEA;;uDAEuD;AACvD;EACE,8CAA8C;AAChD;;AAEA,oCAAoC;AACpC;EACE,8BAA8B;AAChC;;AAEA,0CAA0C;AAC1C;EACE,qBAAqB;EACrB,uBAAuB;EACvB,kCAAkC;AACpC;;AAEA,sBAAsB;AACtB;EACE,kBAAkB;EAClB,6BAA6B;EAC7B,oCAAoC;EACpC,0BAA0B;EAC1B,sCAAsC;EACtC,8BAA8B;EAC9B,8BAA8B;AAChC;;AAEA,iBAAiB;AACjB;EACE,2BAA2B;EAC3B,wCAAwC;EACxC,kBAAkB;AACpB;;AAEA;;;;yDAIyD;AACzD;EACE,0DAA0D;AAC5D;;AAEA;;;;iEAIiE;AACjE;EACE;0EACwE;EACxE;;IAEE,gBAAgB;EAClB;EACA;IACE,aAAa;EACf;EACA;;;2EAGyE;EACzE;IACE,2BAA2B;EAC7B;EACA;IACE,mBAAmB;IACnB,mBAAmB;IACnB,2BAA2B;EAC7B;EACA;;;;;;;IAOE,cAAc;EAChB;EACA;;;qEAGmE;EACnE;IACE,uBAAuB;IACvB,sBAAsB;EACxB;EACA,gDAAgD;EAChD;;IAEE,aAAa;EACf;;EAEA,sDAAsD;EACtD;IACE,aAAa;EACf;;EAEA;IACE,mBAAmB;IACnB,oCAAoC;IACpC,kCAAkC;IAClC,gBAAgB;EAClB;;EAEA;IACE,aAAa;IACb,uBAAuB;IACvB,QAAQ;IACR,6BAA6B;IAC7B,8BAA8B;IAC9B,+BAA+B;IAC/B,4BAA4B;IAC5B,8DAA8D;IAC9D,2BAA2B;IAC3B,qBAAqB;IACrB,sBAAsB;IACtB,2BAA2B;EAC7B;EACA;;;yDAGuD;EACvD;IACE,sBAAsB;IACtB,0BAA0B;IAC1B,2BAA2B;IAC3B,yBAAyB;IACzB,sBAAsB;EACxB;EACA;IACE,YAAY;EACd;EACA;IACE,yBAAyB;IACzB,gBAAgB;IAChB,+BAA+B;IAC/B,eAAe;IACf,cAAc;IACd,gBAAgB;EAClB;EACA;uEACqE;EACrE;IACE,sBAAsB;IACtB,QAAQ;EACV;EACA;IACE,YAAY;IACZ,eAAe;EACjB;EACA,sDAAsD;EACtD;IACE,iBAAiB;EACnB;EACA;IACE,mBAAmB;EACrB;;EAEA,6DAA6D;EAC7D;IACE,aAAa;EACf;AACF;;AAEA;;;;yEAIyE;AACzE;EACE,4BAA4B;EAC5B;;IAEE,0BAA0B;IAC1B,gBAAgB;EAClB;EACA;IACE,kBAAkB;EACpB;EACA;;IAEE,0BAA0B;IAC1B,iBAAiB;IACjB,eAAe;EACjB;EACA;IACE,SAAS;IACT,aAAa;EACf;;EAEA,gEAAgE;EAChE;;;;IAIE,uBAAuB;IACvB,0BAA0B;IAC1B,0BAA0B;EAC5B;EACA;IACE,eAAe;EACjB;EACA;IACE,QAAQ;EACV;;EAEA,yBAAyB;EACzB;;IAEE,uBAAuB;IACvB,eAAe;EACjB;EACA;IACE,uBAAuB;IACvB,eAAe;IACf,0BAA0B;EAC5B;;EAEA,SAAS;EACT;IACE,eAAe;IACf,yBAAyB;EAC3B;EACA;IACE,4BAA4B;EAC9B;;EAEA,UAAU;EACV;IACE,6BAA6B;EAC/B;EACA;IACE,8BAA8B;EAChC;EACA;IACE,0BAA0B;EAC5B;EACA;IACE,8BAA8B;EAChC;EACA;;;;;IAKE,gBAAgB;IAChB,uBAAuB;IACvB,eAAe;EACjB;EACA;IACE,uBAAuB;IACvB,aAAa;EACf;EACA;IACE,uBAAuB;IACvB,eAAe;EACjB;EACA;;IAEE,uBAAuB;IACvB,4BAA4B;EAC9B;EACA;IACE,2BAA2B;IAC3B,uBAAuB;EACzB;EACA;;;;IAIE,4BAA4B;IAC5B,eAAe;EACjB;;EAEA,gBAAgB;EAChB;IACE,eAAe;EACjB;;EAEA;mDACiD;EACjD;;IAEE,kCAAkC;IAClC,uBAAuB;IACvB,eAAe;EACjB;EACA;IACE,4BAA4B;EAC9B;EACA;IACE,WAAW;IACX,YAAY;IACZ,4BAA4B;IAC5B,kBAAkB;EACpB;EACA;IACE,kBAAkB;IAClB,eAAe;EACjB;EACA,+DAA+D;EAC/D;IACE,+BAA+B;IAC/B,2BAA2B;EAC7B;EACA;;IAEE,oBAAoB;EACtB;EACA;IACE,uBAAuB;IACvB,eAAe;EACjB;AACF;;AAEA;;;;;mEAKmE;AACnE;EACE,uBAAuB;EACvB,aAAa;EACb,sBAAsB;EACtB,SAAS;AACX;AACA;EACE;IACE,0BAA0B;IAC1B,2BAA2B;IAC3B,gBAAgB;EAClB;AACF;;AAEA,qBAAqB;AACrB;EACE,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,SAAS;EACT,eAAe;AACjB;AACA;EACE,aAAa;EACb,qBAAqB;EACrB,SAAS;EACT,eAAe;EACf,YAAY;AACd;AACA;EACE,SAAS;EACT,eAAe;EACf,gBAAgB;EAChB,6BAA6B;EAC7B,iBAAiB;AACnB;AACA;EACE,eAAe;EACf,+BAA+B;AACjC;AACA;EACE,aAAa;EACb,QAAQ;AACV;AACA;EACE,uBAAuB;EACvB,0BAA0B;AAC5B;;AAEA,wBAAwB;AACxB;EACE,aAAa;EACb,gDAAgD;EAChD,SAAS;EACT,cAAc;AAChB;AACA;EACE,2BAA2B;EAC3B,yCAAyC;EACzC,mBAAmB;EACnB,kBAAkB;EAClB,aAAa;EACb,sBAAsB;EACtB,QAAQ;EACR,YAAY;AACd;AACA;EACE,aAAa;EACb,mBAAmB;EACnB,QAAQ;EACR,YAAY;AACd;AACA;EACE,WAAW;EACX,YAAY;EACZ,kBAAkB;EAClB,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,cAAc;EACd,gCAAgC;EAChC,wDAAwD;EACxD,eAAe;AACjB;AACA;EACE,eAAe;EACf,gBAAgB;EAChB,+BAA+B;EAC/B,mBAAmB;EACnB,gBAAgB;EAChB,uBAAuB;AACzB;AACA;EACE,aAAa;EACb,qBAAqB;EACrB,8BAA8B;EAC9B,QAAQ;EACR,YAAY;AACd;AACA;EACE,eAAe;EACf,gBAAgB;EAChB,iBAAiB;EACjB,6BAA6B;EAC7B,mBAAmB;AACrB;AACA;EACE,eAAe;EACf,aAAa;EACb,eAAe;EACf,YAAY;EACZ,mBAAmB;AACrB;AACA;EACE,WAAW;EACX,YAAY;EACZ,cAAc;AAChB;AACA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,eAAe;EACf,gBAAgB;EAChB,mBAAmB;AACrB;AACA;wEACwE;AACxE;EACE,gCAAgC;AAClC;AACA;EACE,wCAAwC;AAC1C;AACA;EACE,uCAAuC;EACvC,gBAAgB;AAClB;AACA;EACE,gBAAgB;EAChB,+BAA+B;AACjC;;AAEA,oBAAoB;AACpB;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,gBAAgB;EAChB,2CAA2C;EAC3C,wDAAwD;EACxD,gCAAgC;EAChC,eAAe;EACf,gBAAgB;EAChB,mBAAmB;EACnB,YAAY;AACd;;AAEA,mEAAmE;AACnE;EACE,SAAO;EACP,aAAa;EACb,aAAa;EACb,oDAAoD;EACpD,SAAS;AACX;AACA;EACE,aAAa;EACb,sBAAsB;EACtB,SAAS;EACT,aAAa;EACb,YAAY;AACd;AACA;EACE,SAAW;AACb;AACA;EACE,SAAW;AACb;AACA;EACE,SAAW;AACb;;AAEA,eAAe;AACf;EACE,2BAA2B;EAC3B,yCAAyC;EACzC,mBAAmB;EACnB,kBAAkB;EAClB,aAAa;EACb,sBAAsB;EACtB,QAAQ;EACR,YAAY;EACZ,aAAa;AACf;AACA;EACE,aAAa;EACb,uBAAuB;EACvB,8BAA8B;EAC9B,QAAQ;EACR,eAAe;EACf,cAAc;AAChB;AACA;EACE,YAAY;AACd;AACA;EACE,SAAS;EACT,eAAe;EACf,gBAAgB;EAChB,6BAA6B;EAC7B,aAAa;EACb,mBAAmB;EACnB,QAAQ;EACR,eAAe;AACjB;AACA;EACE,eAAe;EACf,eAAe;EACf,+BAA+B;AACjC;AACA;EACE,aAAa;EACb,mBAAmB;EACnB,QAAQ;AACV;AACA;EACE,SAAO;EACP,aAAa;EACb,YAAY;EACZ,aAAa;EACb,sBAAsB;AACxB;AACA;EACE,SAAO;EACP,gBAAgB;EAChB,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,+BAA+B;EAC/B,eAAe;AACjB;AACA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,eAAe;AACjB;AACA;EACE,6BAA6B;EAC7B,gBAAgB;AAClB;;AAEA,oDAAoD;AACpD;;EAEE,uBAAuB;EACvB,uBAAuB;EACvB,0BAA0B;EAC1B,0BAA0B;AAC5B;AACA;EACE,WAAW;EACX,YAAY;EACZ,kBAAkB;EAClB,yCAAyC;EACzC,2BAA2B;EAC3B,+BAA+B;EAC/B,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,eAAe;EACf,UAAU;AACZ;AACA;EACE,6BAA6B;EAC7B,6BAA6B;AAC/B;;AAEA,2BAA2B;AAC3B;EACE,SAAO;EACP,aAAa;EACb,YAAY;AACd;;AAEA,YAAY;AACZ;EACE,2BAA2B;EAC3B,yCAAyC;EACzC,kBAAkB;EAClB,8DAA8D;EAC9D,iBAAiB;EACjB,gBAAgB;AAClB;AACA;EACE,eAAe;EACf,+BAA+B;EAC/B,kBAAkB;AACpB;AACA;EACE,aAAa;EACb,mBAAmB;EACnB,QAAQ;AACV;AACA;EACE,WAAW;EACX,WAAW;EACX,kBAAkB;AACpB;AACA;EACE,eAAe;EACf,gBAAgB;EAChB,6BAA6B;AAC/B;AACA;EACE,eAAe;EACf,+BAA+B;AACjC;;AAEA,2DAA2D;AAC3D;EACE,SAAO;EACP,aAAa;EACb,aAAa;EACb,sBAAsB;EACtB,QAAQ;AACV;AACA;EACE,kBAAkB;EAClB,SAAO;EACP,iBAAiB;AACnB;AACA;EACE,kBAAkB;EAClB,QAAQ;EACR,aAAa;EACb,sBAAsB;EACtB,mBAAmB;EACnB,uBAAuB;EACvB,oBAAoB;AACtB;AACA;EACE,eAAe;EACf,gBAAgB;EAChB,6BAA6B;AAC/B;AACA;EACE,eAAe;EACf,+BAA+B;AACjC;;AAEA,kBAAkB;AAClB;EACE,gBAAgB;EAChB,SAAS;EACT,UAAU;EACV,aAAa;EACb,sBAAsB;EACtB,QAAQ;EACR,cAAc;AAChB;AACA;EACE,aAAa;EACb,oDAAoD;EACpD,mBAAmB;EACnB,QAAQ;EACR,eAAe;AACjB;AACA;EACE,WAAW;EACX,YAAY;EACZ,kBAAkB;AACpB;AACA;EACE,+BAA+B;EAC/B,gBAAgB;EAChB,uBAAuB;EACvB,mBAAmB;AACrB;AACA;EACE,gBAAgB;EAChB,6BAA6B;EAC7B,kCAAkC;AACpC;AACA;EACE,iBAAiB;EACjB,+BAA+B;EAC/B,kCAAkC;AACpC;;AAEA,iDAAiD;AACjD;EACE,SAAO;EACP,aAAa;EACb,cAAc;AAChB;AACA;EACE,WAAW;EACX,yBAAyB;EACzB,eAAe;AACjB;AACA;EACE,gBAAgB;EAChB,MAAM;EACN,2BAA2B;EAC3B,gBAAgB;EAChB,eAAe;EACf,gBAAgB;EAChB,+BAA+B;EAC/B,iBAAiB;EACjB,gDAAgD;AAClD;AACA;EACE,iBAAiB;EACjB,gDAAgD;EAChD,6BAA6B;AAC/B;AACA;EACE,4BAA4B;EAC5B,kCAAkC;AACpC;;AAEA,sEAAsE;AACtE;EACE;IACE,gDAAgD;EAClD;EACA;IACE,qCAAqC;EACvC;EACA;IACE,aAAa;IACb,UAAU;EACZ;EACA;IACE,aAAa;IACb,UAAU;EACZ;EACA;IACE,mBAAmB;EACrB;EACA;IACE,aAAa;EACf;AACF;;AAEA,WAAW;AACX;EACE;IACE,aAAa;EACf;EACA;IACE,eAAe;EACjB;EACA;IACE,WAAW;EACb;EACA;IACE,SAAO;EACT;EACA;IACE,WAAW;EACb;EACA;IACE,eAAe;EACjB;EACA;;IAEE,aAAa;EACf;EACA;IACE,sBAAsB;EACxB;EACA;IACE,aAAa;EACf;EACA;IACE,aAAa;EACf;AACF;;AAEA,2DAA2D;AAC3D;EACE,aAAa;EACb,mBAAmB;EACnB,sBAAsB;EACtB,eAAe;AACjB;AACA;EACE,WAAW;EACX,YAAY;EACZ,UAAU;EACV,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,gBAAgB;EAChB,qCAAqC;EACrC,kCAAkC;EAClC,6BAA6B;AAC/B;AACA;EACE,WAAW;EACX,YAAY;EACZ,mBAAmB;AACrB;AACA;EACE,eAAe;EACf,+BAA+B;AACjC;AACA;EACE,aAAa;EACb,mBAAmB;EACnB,sBAAsB;EACtB,eAAe;EACf,YAAY;AACd;AACA;EACE,+BAA+B;EAC/B,eAAe;EACf,gBAAgB;EAChB,gBAAgB;EAChB,uBAAuB;EACvB,mBAAmB;AACrB;AACA,qEAAqE;AACrE;EACE,+BAA+B;AACjC;;AAEA,2EAA2E;AAC3E,+DAA+D;AAC/D;EACE,WAAW;AACb;AACA;EACE,YAAY;EACZ,yBAAyB;AAC3B;AACA;EACE,6BAA6B;AAC/B;AACA;EACE,aAAa;EACb,uBAAuB;EACvB,eAAe;AACjB;AACA;EACE,aAAa;EACb,mBAAmB;EACnB,sBAAsB;EACtB,6BAA6B;EAC7B,gCAAgC;AAClC;AACA;EACE,0BAA0B;EAC1B,eAAe;AACjB;AACA;EACE,qBAAqB;EACrB,iCAAiC;EACjC,eAAe;AACjB;AACA;EACE,+BAA+B;EAC/B,eAAe;EACf,6BAA6B;AAC/B;AACA;EACE,aAAa;EACb,gDAAgD;EAChD,sBAAsB;EACtB,gCAAgC;AAClC;AACA;EACE,yCAAyC;EACzC,kCAAkC;EAClC,iCAAiC;EACjC,2BAA2B;EAC3B,YAAY;AACd;AACA;EACE,eAAe;EACf,gBAAgB;EAChB,6BAA6B;EAC7B,6BAA6B;AAC/B;AACA;EACE,aAAa;EACb,eAAe;EACf,gBAAgB;AAClB;AACA;EACE;IACE,0BAA0B;EAC5B;AACF;AACA;EACE,aAAa;EACb,sBAAsB;EACtB,mBAAmB;EACnB,QAAQ;EACR,wBAAwB;EACxB,eAAe;AACjB;AACA;EACE,eAAe;EACf,gBAAgB;AAClB;;AAEA,gFAAgF;AAChF;EACE,yCAAyC;EACzC,kCAAkC;EAClC,iCAAiC;EACjC,gCAAgC;EAChC,2BAA2B;AAC7B;AACA;EACE,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,sBAAsB;AACxB;AACA;EACE,6BAA6B;AAC/B;AACA;EACE,aAAa;EACb,gCAAgC;AAClC;AACA;EACE,aAAa;EACb,kBAAkB;EAClB,gCAAgC;AAClC;AACA;EACE,aAAa;EACb,sBAAsB;EACtB,gBAAgB;AAClB;AACA;EACE,+BAA+B;AACjC;;AAEA,gEAAgE;AAChE;EACE,gBAAgB;EAChB,gCAAgC;EAChC,4CAA4C;EAC5C,kCAAkC;EAClC,6BAA6B;AAC/B;AACA;EACE,cAAc;EACd,WAAW;EACX,aAAa;EACb,yCAAyC;EACzC,kCAAkC;EAClC,2BAA2B;EAC3B,gCAAgC;AAClC;AACA;EACE,aAAa;EACb,sBAAsB;EACtB,sBAAsB;AACxB;AACA;EACE;IACE,YAAY;EACd;AACF;;AAEA,8DAA8D;AAC9D;EACE,cAAc;EACd,YAAY;EACZ,WAAW;EACX,eAAe;AACjB;AACA;EACE,cAAc;EACd,YAAY;EACZ,WAAW;AACb;AACA;EACE;IACE,YAAY;EACd;AACF;;AAEA,wDAAwD;AACxD;EACE,gBAAgB;EAChB,eAAe;EACf,mBAAmB;AACrB;AACA;6DAC6D;AAC7D;EACE,cAAc;EACd,WAAW;EACX,YAAY;EACZ;;;;;GAKC;EAED,sBAAsB;EAEtB,qBAAqB;EAErB,kBAAkB;AACpB;;AAEA;+CAC+C;AAC/C;EACE,aAAa;EACb,mBAAmB;EACnB,cAAc;EACd,mBAAmB;EACnB,+CAA+C;AACjD;AACA;EACE,cAAc;EACd,YAAY;EACZ,WAAW;AACb;AACA;EACE;IACE,aAAa;EACf;AACF","sourcesContent":["/* ============================================================\n   DESIGN SYSTEM — READ ME FIRST\n   ============================================================\n   For NEW screens, build with these reusable pieces only:\n\n     Buttons  → <CustomButton> (src/components/CustomButton.js), CSS in\n                SECTION 11a (.az-btn / .az-btn--<variant> / .az-btn--<size>)\n     Tables   → <CustomTable> (src/components/CustomTable.js), CSS in\n                SECTION 12 (.az-table). 98% of datatables already use it;\n                the only exceptions are nested expandable sub-tables that\n                need antd's <Table.Summary>, which CustomTable doesn't wrap.\n     Cards    → .page-card-header / .page-card-title / .page-card-subtitle\n                / .page-card-actions / .page-card-body / .page-card-form\n                (SECTION \"PAGE CARD\") for whole-page layout, OR the generic\n                .az-card (SECTION 11c) for boxed content that isn't a full\n                page, or .sd-kpi / .sd-card (SECTION \"SMART DASHBOARD\")\n                for dashboard KPI tiles and chart cards.\n     Search   → .az-search-input (SECTION 11a, next to the button system).\n     Status   → <StatusCard>/<StatusConnector> (src/components/StatusCard.js)\n                for the horizontal stat-pipeline strip used on listing pages.\n\n   All of the above pull their colors/radii/shadows from the :root tokens\n   directly below — change a token once, every component using it updates.\n   --colorDark/--colorLight/--colorDark-rgb are the exception: they're set\n   at runtime per-org by src/utils/themeManager.js, so the :root values here\n   are just the pre-JS fallback, not something to hand-edit per theme.\n\n   Classes named customCard, customButtonOutlinedBoard/Cancel, customSearch,\n   searchBtn, themeCard etc. below are PRE-EXISTING and still load-bearing\n   across ~200+ older pages — do not delete them. They've been pointed at\n   the same tokens so they stay visually identical to the new system, but\n   new code should reach for the components above instead of these classes.\n   ============================================================ */\n\n:root {\n  /* Brand accent — runtime default before themeManager.js applies the\n     org's theme; also the fallback in every var(--colorDark, ...) usage. */\n  --colorDark: #007aff;\n  --colorLight: rgba(0, 122, 255, 0.08);\n  --colorDark-rgb: 0, 122, 255;\n\n  /* Neutral surfaces — NOT theme-dependent, same for every org. */\n  --az-gray-50: #f9f9f9;\n  --az-gray-300: #e5e7eb;\n  --az-gray-600: #4f4f4f;\n\n  /* Text — opacity-based scale matches the Figma design system exactly. */\n  --az-text-primary: rgba(0, 0, 0, 0.9);\n  --az-text-secondary: rgba(0, 0, 0, 0.6);\n  --az-text-tertiary: rgba(0, 0, 0, 0.5);\n  --az-border-subtle: rgba(0, 0, 0, 0.08);\n\n  /* Radius scale — use --az-radius-pill instead of hardcoding 1000px /\n     9999px; both render identically as \"fully round\" but having two\n     spellings scattered across the file makes future edits error-prone. */\n  --az-radius-sm: 8px;\n  --az-radius-md: 16px;\n  --az-radius-lg: 16px;\n  --az-radius-pill: 9999px;\n\n  /* Shadows */\n  --az-shadow-card: 0 1px 4px rgba(0, 0, 0, 0.08);\n  --az-shadow-card-lg: 0 8px 16px rgba(38, 88, 174, 0.08);\n\n  /* Spacing scale (4px base) */\n  --az-space-1: 4px;\n  --az-space-2: 8px;\n  --az-space-3: 12px;\n  --az-space-4: 16px;\n  --az-space-5: 20px;\n\n  /* ── Base colors ── */\n  --az-white: #ffffff;\n  --az-white-rgb: 255, 255, 255;\n  --az-black: #000000;\n  --az-black-rgb: 0, 0, 0;\n  --az-black-e0: #000000e0; /* disabled-text alpha-black; kept as its own\n     8-digit hex rather than converted to rgba(var(--az-black-rgb), 0.88) —\n     0xe0 isn't an exact 0.88, so this preserves the exact original pixel. */\n\n  /* ── Extended gray scale — every exact gray value found hardcoded across\n     this file, given its own slot (not rounded to a neighbor) so this\n     extraction never shifts a single pixel of existing color. Slots between\n     the original 25/50/100/150/200/300/600 anchors are numbered to preserve\n     lightest-to-darkest order. ── */\n  --az-gray-135: #f3f4f6;\n  --az-gray-175: #f0f0f0;\n  --az-gray-310: #e4e4e4;\n  --az-gray-350: #dcdcdc;\n  --az-gray-400: #d1d5db;\n  --az-gray-420: #cac8c7;\n  --az-gray-450: #bcbcbc;\n  --az-gray-480: #aaaaaa;\n  --az-gray-500: #9ca3af;\n  --az-gray-550: #888888;\n  --az-gray-570: #6b7280;\n  --az-gray-580: #707070;\n  --az-gray-50-rgb: 249, 249, 249;\n  --az-gray-630: #595959;\n  --az-gray-650: #444444;\n  --az-gray-680: #3d3d3d;\n  --az-gray-700: #374151;\n  --az-gray-750: #333333;\n  --az-gray-910: #1a1a1a;\n  --az-gray-950: #111111;\n  --az-gray-970: #050202;\n\n  /* ── Slate (blue-tinted gray) — distinct family, not on the neutral scale ── */\n  --az-slate-100: #e2e8f0;\n  --az-slate-300: #a8bcc1;\n  --az-slate-500: #64748b;\n  --az-slate-900: #111827;\n\n  /* ── Blue (non-theme — legacy/antd-adjacent accents, distinct from the\n     per-org --colorDark accent) ── */\n  --az-blue-500: #1890ff;\n  --az-blue-600: #0666d1;\n  --az-blue-900: #06377e;\n  /* The .az-login page's deliberately fixed brand blue (see that section's\n     own comment) — same numeric value as colorDark's default fallback, but\n     a DIFFERENT token on purpose: this one must never change with the org\n     theme, colorDark is meant to. Do not point .az-login at colorDark. */\n  --az-login-fixed-blue: #007aff;\n\n  /* ── Red / danger ── */\n  --az-red-100: #fef2f2;\n  --az-red-400: #f44336;\n  --az-red-500: #d74b4b;\n  --az-red-500-rgb: 215, 75, 75;\n  --az-red-600: #ff3b30;\n  --az-red-600-rgb: 255, 59, 48;\n  --az-red-900-rgb: 105, 9, 9;\n\n  /* ── Green / success ── */\n  --az-green-600: #438f04;\n  --az-green-700: #02872e;\n\n  /* ── Orange — one semantic status color (the .orange utility class),\n     not theme-related. Legacy hover/active shades that used to live here\n     were removed — those states now track the org's live theme accent\n     (var(--colorDark)) directly, see .custom-checkbox above. ── */\n  --az-orange-400: #ea6e03;\n\n  /* ── One-off shadow/overlay tints (RGB-only — always used via rgba() at\n     a specific opacity, never as a solid fill) ── */\n  --az-shadow-tint-rgb: 38, 88, 174;\n  --az-navy-shadow-rgb: 9, 30, 66;\n  --az-blush-rgb: 234, 229, 229;\n}\n\n/* SECTION 1: Base & global styles (font, scrollbar, placeholder colors, utility classes) */\n\nbody {\n  /* App-wide typeface — matches the Figma design system (Plus Jakarta Sans).\n     Every component below should inherit this rather than hardcoding its\n     own font-family, so a typeface change only ever happens here. */\n  font-family: \"Plus Jakarta Sans\", \"Noto Sans\", sans-serif !important;\n  font-weight: 500;\n  color: var(--az-black);\n}\n\n/* Chrome, Safari, Edge, Opera */\ninput::-webkit-outer-spin-button,\ninput::-webkit-inner-spin-button {\n  -webkit-appearance: none;\n  margin: 0;\n}\n\n/* Firefox */\ninput[type=\"number\"] {\n  -moz-appearance: textfield;\n}\n\n/* a:hover {\n  color: var(--primaryDark) !important;\n} */\n\n::-webkit-scrollbar {\n  width: 2px;\n  height: 10px;\n}\n\n::-webkit-scrollbar-track {\n  background: transparent;\n}\n\n::-webkit-scrollbar-thumb {\n  background: var(--primaryDark);\n}\n\n::placeholder,\n.ant-select-selection-placeholder {\n  color: var(--az-gray-580) !important;\n  opacity: 1;\n}\n\n.ant-select-selection-placeholder {\n  text-transform: capitalize;\n}\n\n:-ms-input-placeholder {\n  color: var(--az-gray-580) !important;\n}\n\n::-ms-input-placeholder {\n  color: var(--az-gray-580) !important;\n}\n\n.mb-30 {\n  margin-bottom: 30px;\n}\n\n.brdrChecked {\n  border: 1px solid var(--primaryDark) !important;\n}\n\ntextarea::-webkit-input-placeholder {\n  color: var(--az-gray-350) !important;\n}\n\ntextarea:-moz-placeholder {\n  color: var(--az-gray-350) !important;\n}\n\ntextarea::-moz-placeholder {\n  color: var(--az-gray-350) !important;\n}\n\ntextarea:-ms-input-placeholder {\n  color: var(--az-gray-350) !important;\n}\n\n.w-70 {\n  flex: 0 0 70%;\n  max-width: 70%;\n}\n\n.w-30 {\n  flex: 0 0 28%;\n  max-width: 28%;\n  width: 28%;\n  height: 100%;\n  transition: all 0.5s ease;\n}\n\n.mv20 {\n  margin: 20px 0 20px 0;\n}\n\n.mt10 {\n  margin-top: 10px;\n}\n\n.brandText {\n  font-size: 18px;\n  font-style: italic;\n  color: var(--primaryDark);\n  font-weight: 500;\n}\n\n#navbarNav {\n  flex-grow: inherit !important;\n}\n\n/* SECTION 2: Layout — sidebar open/close transitions */\n\n/* .sidebar.open ~ .w-70 {\n  flex: 0 0 60%;\n  max-width: 60%;\n  width: calc(100vw - 40%);\n} */\n.sidebar.open .w-30 {\n  flex: 0 0 25%;\n  max-width: 30%;\n  transition: all 0.5s ease;\n}\n\n/* /////////////////////////////// */\n\n/* SECTION 3: Button styles — legacy system applied as a className directly on antd's <Button>; still load-bearing across ~200+ pages so nothing here gets deleted/renamed. customButtonOutlinedBoard/Cancel point at the same --az-* tokens as SECTION 11a so they stay visually identical, and are height/font-size-matched to the same 44px/16px standard as .az-btn--xl so they don't look mismatched sitting next to a CustomButton in the same toolbar row. For any NEW button use <CustomButton> instead. */\n\n.customButtonOutlinedDraft {\n  width: 150px !important;\n  color: var(--primaryTextDark) !important;\n  background-color: transparent !important;\n  border-color: var(--primaryDark) !important;\n  border-radius: 22px !important;\n  height: 43px !important;\n  transition: all 0.3s ease-in-out;\n  font-size: 14px !important;\n}\n\n.customButtonOutlinedDraft:hover {\n  width: 150px !important;\n  color: var(--primaryTextDark);\n  background-color: var(--primaryDark) !important;\n  -webkit-box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.01);\n  -moz-box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.01);\n  box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.01);\n}\n\n/* color was #fff !important — invisible on the light .customTabCard\n   background this is actually used on (a white icon on a near-white\n   card). Switched to a visible dark-gray that still picks up the\n   theme accent on hover below. */\n.customReload {\n  width: auto !important;\n  font-size: 12px !important;\n  font-weight: 500 !important;\n  color: rgba(var(--az-black-rgb), 0.55) !important;\n  background-color: transparent !important;\n  border-color: rgba(var(--az-black-rgb), 0.12) !important;\n  border-radius: 6px !important;\n  /* Was 38px + a 5px margin all round (~48px total) for what's just a\n     small icon on the DEPOSITS APPROVED tile — that oversized footprint\n     is what pushed the title/stats down and left an odd gap above them. */\n  height: 24px !important;\n  transition: all 0.5s ease-in-out;\n  text-shadow: none !important;\n  margin: 0;\n  padding: 2px 8px !important;\n  border-width: 0;\n  max-width: 100%;\n}\n.customReload:hover {\n  width: auto !important;\n  font-size: 12px !important;\n  font-weight: 500 !important;\n  color: var(--primaryTextDark) !important;\n\n  background-color: var(--primaryDark) !important;\n  border-color: var(--az-white) !important;\n  border-radius: 6px !important;\n  /* Kept the same box (height/margin/padding) as the resting state —\n     growing them on hover reflowed the button past the now fixed-height\n     DEPOSITS APPROVED card, spilling outside its border. */\n  height: 24px !important;\n  transition: all 0.5s ease-in-out;\n  text-shadow: none !important;\n  margin: 0;\n  padding: 2px 8px !important;\n  border-width: 0;\n  max-width: 100%;\n}\n/* Cancel button */\n.profileCropModal .ant-modal-footer .ant-btn-default {\n  width: auto !important;\n  font-size: 12px !important;\n  font-weight: 500 !important;\n  color: black !important;\n  background-color: transparent !important;\n  border-color: var(--primaryLight) !important;\n  border-radius: 6px !important;\n  height: 30px !important;\n  transition: all 0.5s ease-in-out;\n  text-shadow: none !important;\n  margin: 5px;\n}\n.profileCropModal .ant-modal-footer .ant-btn-default:hover {\n  width: auto !important;\n  color: var(--primaryTextDark) !important;\n  background-color: var(--primaryLight) !important;\n  border-color: var(--primaryDark) !important;\n  -webkit-box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.01);\n  -moz-box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.01);\n  box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.01);\n}\n\n/* OK button */\n.profileCropModal .ant-modal-footer .ant-btn-primary {\n  width: auto !important;\n  font-size: 12px !important;\n  font-weight: 500 !important;\n  color: black !important;\n  background-color: transparent !important;\n  border-color: var(--primaryDark) !important;\n  border-radius: 6px !important;\n  height: 30px !important;\n  transition: all 0.5s ease-in-out;\n  text-shadow: none !important;\n  margin: 5px;\n  max-width: 100%;\n}\n.profileCropModal .ant-modal-footer .ant-btn-primary:hover {\n  width: auto !important;\n  color: var(--primaryTextDark) !important;\n  background-color: var(--primaryDark) !important;\n  border-color: var(--primaryDark) !important;\n  -webkit-box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.01);\n  -moz-box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.01);\n  box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.01);\n}\n\n.customButtonOutlinedBoard {\n  display: inline-flex !important;\n  align-items: center !important;\n  justify-content: center !important;\n  gap: 6px !important;\n  height: 44px !important;\n  padding: 0 16px !important;\n  border: none !important;\n  border-radius: var(--az-radius-pill) !important;\n  background: var(--colorLight, rgba(0, 122, 255, 0.07)) !important;\n  color: var(--colorDark, #007aff) !important;\n  font-size: 16px !important;\n  font-weight: 600 !important;\n  line-height: 1 !important;\n  cursor: pointer !important;\n  white-space: nowrap !important;\n  transition: background 0.15s !important;\n  text-shadow: none !important;\n  margin: 0 !important;\n  max-width: 100%;\n  position: relative;\n}\n\n/* Make <Link>/<a> inside button fill the entire clickable area */\n.customButtonOutlinedBoard > span > a,\n.customButtonOutlinedBoard > a {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: 100%;\n  height: 100%;\n  padding: 0 11px;\n  color: inherit !important;\n  text-decoration: none;\n}\n.customButtonOutlinedCancel {\n  display: inline-flex !important;\n  align-items: center !important;\n  justify-content: center !important;\n  gap: 6px !important;\n  width: auto !important;\n  height: 44px !important;\n  padding: 0 16px !important;\n  font-size: 16px !important;\n  font-weight: 600 !important;\n  line-height: 1 !important;\n  color: var(--az-gray-600) !important;\n  background-color: var(--az-gray-135) !important;\n  border: none !important;\n  border-radius: var(--az-radius-pill) !important;\n  transition:\n    background-color 0.15s ease-in-out,\n    color 0.15s ease-in-out;\n  text-shadow: none !important;\n  box-shadow: none !important;\n  margin: 0 !important;\n  max-width: 100%;\n}\n\n.customButtonOutlinedCancel:hover {\n  color: var(--az-gray-600) !important;\n  background-color: var(--az-gray-300) !important;\n}\n.customRadioButtonOutlinedBoard .ant-radio-button-wrapper {\n  color: black !important;\n  background-color: transparent !important;\n  border-color: var(--primaryDark) !important;\n  height: 36px !important;\n  line-height: 34px !important;\n  padding: 0 16px !important;\n  font-size: 14px !important;\n}\n/* Segmented-control corners (antd's own first/last-child radius comes\n   from the global borderRadius:6 token — bump to 8px per spec here). */\n.customRadioButtonOutlinedBoard .ant-radio-button-wrapper:first-child {\n  border-radius: 8px 0 0 8px !important;\n}\n.customRadioButtonOutlinedBoard .ant-radio-button-wrapper:last-child {\n  border-radius: 0 8px 8px 0 !important;\n}\n.customRadioButtonOutlinedBoard .ant-radio-button-wrapper:hover {\n  color: var(--primaryTextDark) !important;\n  background-color: var(--primaryDark) !important;\n  border-color: var(--primaryDark) !important;\n}\n.customRadioButtonOutlinedBoard .ant-radio-button-wrapper-checked {\n  color: var(--primaryTextDark) !important;\n  background-color: var(--primaryDark) !important;\n  border-color: var(--primaryDark) !important;\n}\n\n.ant-table-column-sorter .ant-table-column-sorter-full {\n  color: var(--primaryTextLight);\n}\n\n.customButtonOutlinedBoardDisabled {\n  width: auto !important;\n  font-size: 12px !important;\n  font-weight: 500 !important;\n  color: var(--az-gray-580) !important;\n  background-color: transparent !important;\n  border-color: var(--az-gray-580) !important;\n  border-radius: 6px !important;\n  height: 30px !important;\n  transition: all 0.5s ease-in-out;\n  text-shadow: none !important;\n  margin: 5px;\n  /* padding: 2px 4px !important; */\n  border-width: 0;\n  max-width: 100% !important;\n}\n.cancelBtn {\n  display: flex;\n  justify-content: end;\n  align-items: flex-start;\n  float: right;\n}\n\n.custom-popconfirm .ant-popover-inner {\n  background-color: var(--az-red-100); /* light red/pink tone */\n  border: 1px solid var(--az-green-600);\n  border-radius: 8px;\n}\n\n.customButtonOutlinedBoard:hover {\n  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.13) !important;\n  color: var(--colorDark, #007aff) !important;\n  border: none !important;\n  box-shadow: none !important;\n}\n\n.customButtonBoard {\n  width: auto !important;\n  font-size: 12px !important;\n  font-weight: 500 !important;\n  color: var(--primaryTextLight) !important;\n  background-color: var(--primaryLight) !important;\n  border-color: var(--primaryDark) !important;\n  border-radius: 6px !important;\n  height: 25px !important;\n  transition: all 0.5s ease-in-out;\n  text-shadow: none !important;\n}\n\n.customButtonBoard:hover {\n  width: auto !important;\n  color: var(--primaryTextDark) !important;\n  background-color: var(--primaryDark) !important;\n  -webkit-box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.01);\n  -moz-box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.01);\n  box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.01);\n}\n.resetLink {\n  color: gray !important;\n}\n.resetLink:hover {\n  color: gray !important;\n}\n\n.green {\n  color: var(--az-green-700);\n  font-weight: 600;\n}\n.orange {\n  color: var(--az-orange-400);\n  font-weight: 600;\n}\n\n/* commission box */\n.reviewCommissionBox {\n  border: 1px solid gray;\n  padding: 10px;\n  margin-right: 10px;\n  border-radius: 10px;\n}\n\n/* SECTION 4: Filter section (search/filter bars, block/pill styles) */\n\n/***** Filter Section *****/\n.customInput .ant-select-selector,\n.customInput .ant-input-affix-wrapper {\n  border: 0 !important;\n  height: 32px;\n  font-size: 12px !important;\n  border-radius: 25px !important;\n  border: 1px solid gray;\n}\n\n.ant-select-dropdown {\n  padding: 0 !important;\n}\n\n.ant-form-item:has(.ant-select) .ant-form-item-explain-error {\n  margin-top: 14px;\n}\n\n.anticon .customStep {\n  position: relative;\n  top: -3px;\n  color: var(--az-blue-500);\n  line-height: 1;\n}\n\n.custom-steps .ant-steps-item-title {\n  text-align: center;\n  /* Example */\n  font-size: large;\n}\n\n.ant-select-dropdown .ant-select-item {\n  font-size: 12px;\n  padding: 8px !important;\n  border-bottom: 1px solid var(--az-gray-135);\n}\n\n.filterSection {\n  display: flex;\n  flex-direction: column;\n  justify-content: space-between;\n  align-items: center;\n}\n\n.filterSection1 {\n  display: flex;\n  flex-direction: row;\n  align-items: center;\n  margin: 5px;\n}\n\n#fcComboBtn1 {\n  margin: 5px;\n  border: 1px solid var(--primaryDark);\n  border-radius: 5px;\n  color: white;\n}\n\n#fcComboBtn2 {\n  border: 1px solid var(--primaryDark);\n  border-radius: 5px;\n  color: white;\n}\n\n#forwordToAZ {\n  margin: 5px;\n  border: 1px solid var(--primaryDark);\n  border-radius: 5px;\n  color: white;\n}\n\n#reverseFromAZ {\n  border: 1px solid var(--primaryDark);\n  border-radius: 5px;\n  color: white;\n}\n\n.depositDistrictFilter {\n  display: flex;\n  flex-direction: column;\n  justify-content: space-between;\n  align-items: flex-end;\n  margin-top: -60px;\n  margin-bottom: 60px;\n}\n\n.remittanceFilterSection {\n  display: flex;\n  flex-direction: column;\n  padding-right: 4px;\n\n  justify-content: space-between;\n  align-items: flex-end;\n  margin-top: -45px;\n  margin-bottom: 20px;\n}\n\n.row-filter-section {\n  display: flex;\n  flex-direction: row;\n  justify-content: center;\n  align-items: center;\n}\n\n.districtSelect {\n  margin-top: 10px !important;\n}\n\n.customSearch .ant-input-group .ant-input-group-addon {\n  box-shadow: none !important;\n  /* border-top-right-radius: 25px; */\n  /* border-bottom-right-radius: 25px; */\n}\n\n.customSearch .ant-input-group .ant-input-group-addon .ant-input-search-button {\n  box-shadow: none !important;\n  padding: 5px 10px;\n  background-color: red;\n  /* border-top-right-radius: 25px !important; */\n  /* border-bottom-right-radius: 25px !important; */\n}\n.ant-input-search-button {\n  background-color: var(--primaryLight);\n}\n.ant-input-search-button:hover {\n  background-color: var(--primaryDark) !important;\n}\n\n.customInput .ant-select-selector,\n.customInput .ant-input-affix-wrapper {\n  border: 0 !important;\n  height: 32px;\n  font-size: 12px !important;\n  border-radius: 25px !important;\n  font-weight: 500;\n}\n/* Stat chip (\"Total Customer : 389\" etc.) — same pill treatment as\n   .customButtonOutlinedBoard so these read as part of the same design\n   system instead of an older plain-bordered-box style. Used widely\n   across Future Fulfillment / Finance ledger pages. */\n.blockStyle {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  gap: 6px;\n  height: 36px;\n  padding: 0 16px;\n  background: var(--az-gray-50);\n  border: 1px solid var(--az-border-subtle);\n  color: var(--az-text-primary);\n  border-radius: var(--az-radius-pill);\n  font-size: 13px;\n  font-weight: 600;\n  white-space: nowrap;\n}\n\n.blockStyle1 {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  gap: 6px;\n  height: 36px;\n  padding: 0 16px;\n  background: var(--az-white);\n  border: 1px solid var(--az-border-subtle);\n  color: var(--az-text-primary);\n  border-radius: var(--az-radius-pill);\n  font-size: 13px;\n  font-weight: 600;\n  white-space: nowrap;\n  margin-top: 10px;\n  margin-left: 10px;\n}\n\n/* Active filter tag (e.g. \"Creation Date: 2026-03-26 to 2026-06-26 ✕\") —\n   used by the shared CustomTag component across ~50 list pages. Same pill\n   treatment as .blockStyle/.customButtonOutlinedBoard for consistency. */\n.custom-tag {\n  display: inline-flex;\n  align-items: center;\n  gap: 6px;\n  height: 32px;\n  padding: 0 14px;\n  /* background: var(--az-gray-50); */\n  background: var(--az-white);\n  border: 1px solid var(--az-border-subtle);\n  border-radius: var(--az-radius-pill);\n  color: var(--az-text-primary);\n  font-size: 13px;\n  white-space: nowrap;\n}\n.custom-tag-label {\n  font-weight: 600;\n}\n/* Phones: a long filter tag (e.g. a date range) must wrap inside the\n   content column instead of sticking out past the screen edge. */\n@media (max-width: 767px) {\n  .custom-tag {\n    max-width: 100%;\n    height: auto;\n    min-height: 32px;\n    flex-wrap: wrap;\n    white-space: normal;\n  }\n}\n.custom-tag-value {\n  color: var(--az-text-secondary);\n}\n.custom-tag-close {\n  cursor: pointer;\n  font-size: 11px;\n  color: var(--az-text-tertiary, rgba(0, 0, 0, 0.45));\n  margin-left: 2px;\n}\n.custom-tag-close:hover {\n  color: var(--az-text-primary);\n}\n.filterStyle {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  gap: 8px;\n  margin-top: -5px;\n}\n\n/* ── Grid columns sized only for bigger screens ───────────────────────\n   AntD gives a <Col md={12}> (no xs / span) NO width below md — it shrinks\n   to its content, so form fields render as narrow as their placeholder on\n   phones. ~330 Cols across the app are written that way, so instead of\n   editing each one: below each breakpoint, a Col that only has sizes for\n   larger breakpoints (and no base span) takes the full row. Cols that do\n   set xs/sm/span keep their own widths. Base span = class \"ant-col-<n>\";\n   [class*=\"ant-col-1\"] can't match \"ant-col-md-1…\", so the :not() list\n   below safely detects it. */\n@media (max-width: 1199.98px) {\n  .ant-col[class*=\"ant-col-xl-\"]:not([class*=\"ant-col-xs-\"]):not([class*=\"ant-col-sm-\"]):not([class*=\"ant-col-md-\"]):not([class*=\"ant-col-lg-\"]):not([class*=\"ant-col-1\"]):not([class*=\"ant-col-2\"]):not([class*=\"ant-col-3\"]):not([class*=\"ant-col-4\"]):not([class*=\"ant-col-5\"]):not([class*=\"ant-col-6\"]):not([class*=\"ant-col-7\"]):not([class*=\"ant-col-8\"]):not([class*=\"ant-col-9\"]) {\n    flex: 0 0 100%;\n    max-width: 100%;\n  }\n}\n@media (max-width: 991.98px) {\n  .ant-col[class*=\"ant-col-lg-\"]:not([class*=\"ant-col-xs-\"]):not([class*=\"ant-col-sm-\"]):not([class*=\"ant-col-md-\"]):not([class*=\"ant-col-1\"]):not([class*=\"ant-col-2\"]):not([class*=\"ant-col-3\"]):not([class*=\"ant-col-4\"]):not([class*=\"ant-col-5\"]):not([class*=\"ant-col-6\"]):not([class*=\"ant-col-7\"]):not([class*=\"ant-col-8\"]):not([class*=\"ant-col-9\"]) {\n    flex: 0 0 100%;\n    max-width: 100%;\n  }\n}\n@media (max-width: 767.98px) {\n  .ant-col[class*=\"ant-col-md-\"]:not([class*=\"ant-col-xs-\"]):not([class*=\"ant-col-sm-\"]):not([class*=\"ant-col-1\"]):not([class*=\"ant-col-2\"]):not([class*=\"ant-col-3\"]):not([class*=\"ant-col-4\"]):not([class*=\"ant-col-5\"]):not([class*=\"ant-col-6\"]):not([class*=\"ant-col-7\"]):not([class*=\"ant-col-8\"]):not([class*=\"ant-col-9\"]) {\n    flex: 0 0 100%;\n    max-width: 100%;\n  }\n}\n@media (max-width: 575.98px) {\n  .ant-col[class*=\"ant-col-sm-\"]:not([class*=\"ant-col-xs-\"]):not([class*=\"ant-col-1\"]):not([class*=\"ant-col-2\"]):not([class*=\"ant-col-3\"]):not([class*=\"ant-col-4\"]):not([class*=\"ant-col-5\"]):not([class*=\"ant-col-6\"]):not([class*=\"ant-col-7\"]):not([class*=\"ant-col-8\"]):not([class*=\"ant-col-9\"]) {\n    flex: 0 0 100%;\n    max-width: 100%;\n  }\n}\n\n/* Phones: a gutter <Row> placed straight in the page canvas (no padded\n   wrapper) bleeds 8px past both screen edges via its negative margins and\n   scrolls the page sideways — keep it inside; its Cols' gutter padding\n   still gives an even inset. */\n@media (max-width: 767px) {\n  .page-content-canvas > .ant-row {\n    margin-left: 0 !important;\n    margin-right: 0 !important;\n  }\n}\n\n/* Tablets: horizontal Steps (e.g. Create Manual Order) keep each title\n   on one line and cut it off (\"Custom…\") — let titles wrap instead. AntD\n   already switches Steps to vertical on phones. */\n@media (min-width: 532px) and (max-width: 991.98px) {\n  .ant-steps-horizontal .ant-steps-item-title {\n    white-space: normal;\n    line-height: 1.3;\n  }\n  .ant-steps-horizontal .ant-steps-item-title::after {\n    top: 16px;\n  }\n  /* equal share for every step — otherwise the long last title takes\n     its full natural width and squeezes the others */\n  .ant-steps-horizontal .ant-steps-item,\n  .ant-steps-horizontal .ant-steps-item:last-child {\n    flex: 1 1 0;\n    min-width: 0;\n  }\n  /* icon + title side by side as flex, so the title wraps within the\n     space left after the number circle instead of being clipped */\n  .ant-steps-horizontal .ant-steps-item-container {\n    display: flex;\n    align-items: flex-start;\n  }\n  .ant-steps-horizontal .ant-steps-item-content {\n    flex: 1 1 auto;\n    min-width: 0;\n  }\n  .ant-steps-horizontal .ant-steps-item-icon {\n    flex-shrink: 0;\n  }\n}\n\n/* Phones/small tablets: the header action row (Download / Filters / Add)\n   is wider than the content column — let it wrap instead of pushing the\n   Add button off-screen and scrolling the whole page sideways. */\n@media (max-width: 767px) {\n  .page-card-header > div {\n    min-width: 0;\n    max-width: 100%;\n  }\n  .filterStyle {\n    flex-wrap: wrap;\n    justify-content: flex-start;\n  }\n  /* Same for pages that lay out their totals chips (.blockStyle1) or\n     header buttons (.az-btn) in an inline-styled flex row of their own */\n  div:has(> .blockStyle1),\n  div:has(> .az-btn) {\n    flex-wrap: wrap;\n    row-gap: 8px;\n  }\n  /* Long breadcrumb labels truncate instead of widening the top bar */\n  .app-bc,\n  .app-bc-list {\n    min-width: 0;\n  }\n  .app-bc-label {\n    flex-shrink: 1;\n    min-width: 0;\n    overflow: hidden;\n    text-overflow: ellipsis;\n  }\n}\n\n/* .font-md .ant-select-selector, .font-md .ant-input-affix-wrapper {\n  font-size: 14px !important;\n  font-weight: 500;\n} */\n.ant-select-dropdown {\n  padding: 0 !important;\n}\n\n.ant-select-dropdown .ant-select-item {\n  font-size: 12px;\n  padding: 8px !important;\n  border-bottom: 1px solid var(--az-gray-135);\n}\n\n/* SECTION 5: Sign-in page */\n\n/***** SignIn Page Style *****/\n.signInPage {\n  width: 100%;\n  height: 100vh;\n  position: relative;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  background-position: right 35% bottom 45% !important;\n}\n\n/* .signInPage .assetsSection img {\n  width: 293px;\n} */\n.signInPage .SignInFormSection .customCard {\n  border-radius: 14px;\n  width: 401px;\n  margin: 0 auto;\n  cursor: pointer;\n  -webkit-box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.1);\n  -moz-box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.1);\n  box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.1);\n}\n\n.signInPage .SignInFormSection .customCard .ant-card-body {\n  padding: 55px 66px;\n}\n\n.signInPage .SignInFormSection .customCard .customFormControl {\n  border-radius: 6px !important;\n  height: 50px !important;\n  padding: 10px 20px !important;\n}\n\n.customFormControl {\n  border: 1px solid var(--az-gray-310);\n  border-radius: 6px !important;\n  height: 40px !important;\n  padding: 6px 16px !important;\n  width: 100%;\n}\n\n.tableInputFields {\n  border: 1px solid var(--az-gray-310);\n  border-radius: 4px !important;\n  width: 45px;\n  padding: 4px !important;\n}\n\n.tableInputErrors {\n  color: var(--az-red-400) !important;\n}\n\n.variantTable .editFormControl {\n  width: 100% !important;\n  padding: 10px !important;\n}\n\n.signInPage .SignInFormSection .customCard .ant-input-prefix {\n  margin-right: 20px;\n}\n\n/* ── New split-screen login (SignIn/index.js) — client-neutral, no\n   background image; brand panel colored purely from --colorDark so it\n   already matches whatever theme (blue by default) the platform is on,\n   with no hardcoded client name/logo. Kept separate from the classes\n   above (.signInPage etc.) since resetPassword.js/ChangePassword still\n   use those for their own, unchanged pages. */\n.az-login {\n  display: flex;\n  min-height: 100vh;\n  width: 100%;\n  background: var(--az-white);\n}\n.az-login__brand {\n  position: relative;\n  flex: 1 1 50%;\n  overflow: hidden;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  padding: 64px;\n  /* Fixed blue, not var(--colorDark): that variable reflects whichever\n     org theme was last active in this browser (see themeManager.js) and\n     defaults to orange (THEMES[0]), not blue — visible proof at\n     /Users/admin/Desktop/Agrozone/admin-portal/src/utils/themeManager.js:24\n     (\"blue\" preset is #007AFF). Pre-login there's no signed-in org to\n     react to anyway, so this page always shows the platform's own blue\n     rather than leaking whatever theme a previous session left behind. */\n  background: linear-gradient(\n    135deg,\n    var(--az-login-fixed-blue) 0%,\n    var(--az-blue-900) 100%\n  );\n}\n.az-login__brandGlow {\n  position: absolute;\n  border-radius: 50%;\n  background: rgba(var(--az-white-rgb), 0.12);\n  pointer-events: none;\n}\n.az-login__brandGlow--one {\n  width: 420px;\n  height: 420px;\n  top: -140px;\n  right: -120px;\n}\n.az-login__brandGlow--two {\n  width: 320px;\n  height: 320px;\n  bottom: -110px;\n  left: -90px;\n  background: rgba(var(--az-white-rgb), 0.08);\n}\n.az-login__brandContent {\n  position: relative;\n  z-index: 1;\n  max-width: 420px;\n  color: var(--az-white);\n}\n.az-login__mark {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  padding: 10px 18px;\n  border-radius: 14px;\n  background: rgba(var(--az-white-rgb), 0.92);\n  margin-bottom: 32px;\n}\n.az-login__markLogo {\n  height: 28px;\n  width: auto;\n  display: block;\n}\n/* \"Powered by Traidster\" pill pinned to the bottom-right corner of the\n   login page (over the white form panel, hence the hairline border) —\n   same pill + dark wordmark as the sidebar footer. */\n.az-login {\n  position: relative;\n}\n.az-login__poweredBy {\n  position: absolute;\n  right: 24px;\n  bottom: 20px;\n  z-index: 1;\n  display: inline-flex;\n  align-items: center;\n  gap: 6px;\n  padding: 7px 16px;\n  border-radius: 9999px;\n  background: #ffffff;\n  border: 1px solid rgba(23, 26, 33, 0.1);\n  box-shadow: 0 1px 3px rgba(23, 26, 33, 0.06);\n  white-space: nowrap;\n}\n.az-login__poweredByLabel {\n  font-size: 11px;\n  font-weight: 600;\n  color: var(--traidster-charcoal, #171a21);\n}\n.az-login__poweredByLogo {\n  height: 14px;\n  width: auto;\n  display: block;\n}\n.az-login__brandTitle {\n  font-size: 32px;\n  font-weight: 700;\n  margin: 0 0 16px;\n  color: var(--az-white);\n}\n.az-login__brandTagline {\n  font-size: 16px;\n  line-height: 1.6;\n  color: rgba(var(--az-white-rgb), 0.85);\n  margin: 0;\n}\n.az-login__panel {\n  flex: 1 1 50%;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  padding: 40px 24px;\n  background: var(--az-white);\n}\n.az-login__formWrap {\n  width: 100%;\n  max-width: 360px;\n}\n.az-login__header {\n  margin-bottom: 32px;\n}\n.az-login__title {\n  font-size: 26px;\n  font-weight: 700;\n  color: var(--az-text-primary, rgba(0, 0, 0, 0.9));\n  margin: 0 0 8px;\n}\n.az-login__subtitle {\n  font-size: 14px;\n  color: var(--az-text-secondary, rgba(0, 0, 0, 0.6));\n  margin: 0;\n}\n.az-login__row {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  margin-bottom: 16px;\n}\n.az-login__link {\n  color: var(--az-login-fixed-blue);\n  font-weight: 500;\n  text-decoration: none;\n}\n.az-login__link:hover {\n  text-decoration: underline;\n}\n.az-login__footerText {\n  text-align: center;\n  color: var(--az-text-secondary, rgba(0, 0, 0, 0.6));\n  font-size: 14px;\n  margin-top: 16px;\n}\n/* Solid fixed-blue submit button — deliberately not .customButton (which\n   uses var(--primaryDark)/var(--primaryLight) and would otherwise render\n   in whatever org theme is cached, e.g. orange, with low-contrast white\n   text on a near-white background). */\n.az-login__submit.ant-btn {\n  width: 100%;\n  height: 44px;\n  background-color: var(--az-login-fixed-blue);\n  border-color: var(--az-login-fixed-blue);\n  border-radius: 8px;\n  font-size: 15px;\n  font-weight: 600;\n  color: var(--az-white);\n}\n.az-login__submit.ant-btn:hover,\n.az-login__submit.ant-btn:focus {\n  background-color: var(--az-blue-600);\n  border-color: var(--az-blue-600);\n  color: var(--az-white);\n}\n/* Same reasoning as the button — keep the checkbox fixed-blue rather than\n   inheriting the global .ant-checkbox-checked rule's var(--secondaryDark). */\n.az-login .ant-checkbox-checked .ant-checkbox-inner {\n  background-color: var(--az-login-fixed-blue) !important;\n  border-color: var(--az-login-fixed-blue) !important;\n}\n@media (max-width: 767px) {\n  .az-login__panel {\n    padding: 24px 20px;\n  }\n}\n\n.login-form-forgot {\n  color: var(--az-gray-580);\n  font-weight: 500;\n  text-decoration: underline;\n}\n.primaryColor {\n  color: var(--az-gray-580) !important;\n}\n.orText {\n  text-align: center;\n  margin-bottom: 0;\n  color: var(--az-gray-580);\n}\n.formHeading p {\n  color: var(--az-gray-580);\n  /* line-height: 30px; */\n}\n\n.defaultColor {\n  color: var(--az-gray-580) !important;\n}\n\n.signInForm .ant-form-item {\n  margin-bottom: 10px;\n}\n\n/* .downloadCsv {\n  margin-right: 5px;\n  border-radius: 50% solid black;\n  font-weight: bolder;\n} */\n\n.customButton {\n  width: 100% !important;\n  background-color: var(--primaryDark) !important;\n  border-color: var(--primaryDark) !important;\n  border-radius: 6px !important;\n  height: 43px !important;\n  line-height: 30px;\n  padding-top: 0px !important;\n  font-size: 16px !important;\n}\n\n.customButton:disabled {\n  background-color: lightgray !important;\n  border-color: lightgray !important;\n  color: gray !important;\n}\n\n.customButtonLink {\n  color: var(--primaryDark) !important;\n  border-color: var(--primaryDark) !important;\n  border-radius: 6px !important;\n}\n\n.customButtonLink:hover {\n  color: white !important;\n  background-color: var(--primaryDark) !important;\n  border-radius: 6px !important;\n}\n\n.customButton1 {\n  width: 100% !important;\n  border: 1px solid var(--primaryDark) !important;\n  border-radius: 6px !important;\n  height: 25px !important;\n  padding-top: 0px !important;\n  line-height: 30px;\n  font-size: 14px !important;\n}\n\n.customButton2 {\n  width: 100% !important;\n  background-color: var(--az-white) !important;\n  border-color: var(--primaryDark) !important;\n  border-radius: 6px !important;\n  height: 43px !important;\n  line-height: 30px;\n  font-size: 14px !important;\n}\n\n.nextBtn {\n  display: flex;\n  align-content: flex-end;\n  justify-items: end;\n  /* left: 1100px; */\n  width: 150px !important;\n}\n\n.addComboProduct {\n  width: 150px !important;\n  color: white !important;\n}\n\n.login-form-button {\n  width: 100% !important;\n  background-color: var(--primaryLight) !important;\n  border-color: var(--primaryDark) !important;\n  border-radius: 50px !important;\n  transition: all 0.3s ease-in-out;\n}\n\n.login-form-button:hover {\n  color: var(--primaryDark) !important;\n  background-color: var(--primaryTextDark) !important;\n  -webkit-box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.1);\n  -moz-box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.1);\n  box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.1);\n}\n\n.allocateBtn {\n  height: 25px !important;\n  padding-top: 0px !important;\n}\n\n.customButtonAction {\n  width: auto !important;\n  background-color: var(--primaryDark) !important;\n  border-color: var(--primaryDark) !important;\n  border-radius: 6px !important;\n  color: var(--az-white) !important;\n  height: 43px !important;\n  display: flex !important;\n  justify-content: center !important;\n  align-items: center !important;\n  font-size: 14px !important;\n  padding: 4px 10px !important;\n  border-width: 0;\n  width: 100%;\n  cursor: pointer;\n}\n\na {\n  text-decoration: none;\n}\n\n.customButtonOutlinedDisable {\n  width: auto !important;\n  font-size: 12px !important;\n  font-weight: 500 !important;\n  color: var(--az-gray-970) !important;\n  background-color: var(--az-gray-420) !important;\n  border-color: var(--primaryDark) !important;\n  border-radius: 6px !important;\n  height: 25px !important;\n  transition: all 0.5s ease-in-out;\n  text-shadow: none !important;\n  margin: 5px;\n  padding: 4px 10px !important;\n  border-width: 0;\n  max-width: 100%;\n}\n\n.disableBtn {\n  width: auto !important;\n  background-color: var(--az-gray-420) !important;\n  border-color: var(--primaryDark) !important;\n  border-radius: 6px !important;\n  color: var(--az-gray-970) !important;\n  height: 43px !important;\n  display: flex !important;\n  justify-content: center !important;\n  align-items: center !important;\n  font-size: 14px !important;\n  padding: 4px 10px !important;\n  border-width: 0;\n  width: 100%;\n  cursor: pointer;\n}\n\n.submitDeposit {\n  font-size: 15px !important;\n  width: 200% !important;\n  display: flex !important;\n  align-items: center !important;\n}\n\n.ant-checkbox-checked .ant-checkbox-inner {\n  background-color: var(--secondaryDark) !important;\n  border-color: var(--secondaryLight) !important;\n  border-radius: 4px !important;\n  border-color: var(--az-white) !important;\n}\n/* Make tick white */\n.ant-checkbox-checked.ant-checkbox-disabled .ant-checkbox-inner::after {\n  border-color: var(--az-white) !important;\n}\n.ant-checkbox-disabled + span {\n  color: var(--az-black-e0) !important;\n}\n\n.customButtonOutlined {\n  width: 100% !important;\n  color: var(--primaryTextLight) !important;\n  background-color: var(--primaryLight) !important;\n  border-color: var(--primaryDark) !important;\n  border-radius: 22px !important;\n  height: 43px !important;\n}\n\n/* .action .ant-input-number {\n  width: 60px;\n} */\n\n/* SECTION 6: Sidebar — collapsed 96px icon-only, expanded 212px icon+label; submenu panel is a fixed dark overlay via portal, doesn't shift content */\n\n.sidebar {\n  position: fixed;\n  left: 0;\n  /* Starts at the topbar's bottom border — the brand lives on the topbar's\n     white band above it (.sb-topbrand) */\n  top: var(--sb-topbar-height);\n  height: calc(100vh - var(--sb-topbar-height));\n  /* Collapsed default: icon-only rail */\n  width: var(--sb-rail-width);\n  /* Figma fill: #1B1B1B */\n  background: var(--sb-rail-bg);\n  /* Figma auto-layout: vertical flow */\n  display: flex;\n  flex-direction: column;\n  /* Figma alignment: center */\n  align-items: center;\n  /* Figma gap: 8px — applied between brand, nav, profile */\n  gap: var(--sb-gap);\n  /* Figma padding: 16px all sides — only vertical when collapsed */\n  padding: var(--sb-padding) 0;\n  /* Figma stroke: #FFFFFF 10% → right border */\n  border-right: 1px solid var(--sb-stroke);\n  /* Figma corner radius: 0 */\n  border-radius: 0;\n  z-index: 100;\n  overflow: hidden;\n  transition:\n    width 0.22s cubic-bezier(0.4, 0, 0.2, 1),\n    padding 0.22s cubic-bezier(0.4, 0, 0.2, 1);\n}\n\n/* Expanded: Figma W 212px, padding 16px all sides */\n.sidebar.expanded {\n  width: var(--sb-expanded-width); /* Figma W: 212px */\n  padding: var(--sb-padding); /* Figma padding: 16px all sides */\n  align-items: stretch; /* children fill full width */\n}\n\n/* ── Brand band (top-left) ─────────────────────────────────────\n   Fixed over the topbar's left end: same height, white background and\n   bottom border as .page-topbar, so logo + breadcrumb/search read as one\n   continuous header and the dark rail starts underneath. Width follows the\n   rail (collapsed/expanded) so the logo stays aligned over it. */\n.sb-topbrand {\n  position: fixed;\n  top: 0;\n  left: 0;\n  z-index: 102;\n  width: var(--sb-rail-width);\n  height: var(--sb-topbar-height);\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  gap: 8px;\n  padding: 0 16px;\n  background: var(--az-white);\n  border-bottom: 1px solid rgba(var(--az-black-rgb), 0.06);\n  transition: width 0.22s cubic-bezier(0.4, 0, 0.2, 1);\n}\n.sb-topbrand.expanded {\n  width: var(--sb-expanded-width);\n}\n.sb-topbrand .sb-brand-box {\n  background: transparent;\n  height: 100%;\n  padding: 0;\n}\n.sb-topbrand:not(.expanded) .sb-brand-box {\n  width: 64px;\n}\n\n/* Burger — small screens only (rendered by Sidebar.js when mobile) */\n.sb-burger-btn {\n  flex-shrink: 0;\n  width: 40px;\n  height: 40px;\n  border-radius: 10px;\n  border: 1px solid rgba(var(--az-black-rgb), 0.1);\n  background: var(--az-white);\n  color: var(--az-gray-910);\n  font-size: 18px;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  cursor: pointer;\n  padding: 0;\n}\n.sb-burger-btn:hover {\n  background: var(--az-gray-175);\n}\n\n/* ── Brand row ─────────────────────────────────────────────────\n   Figma: padding comes from parent (sidebar), not brand row itself\n   Row is full-width, 72px tall, bottom border stroke                */\n.sb-brand-row {\n  width: 100%;\n  height: 80px;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  padding: 0;\n  flex-shrink: 0;\n  overflow: hidden;\n  border-radius: 0;\n}\n\n/* Single unified brand box — Figma node 43:6090/42:3418: one rounded-12\n   tile, light accent tint, holds either the org initial (collapsed) or\n   the full org name (expanded) in the SAME accent color. Not a separate\n   avatar + label pair. */\n.sb-brand-box {\n  width: 100%;\n  height: 64px;\n  border-radius: 12px;\n  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.1);\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  flex-shrink: 0;\n  overflow: hidden;\n  padding: 0 12px;\n}\n/* An uploaded org logo is almost always a flat image with its own opaque\n   (usually white) background baked in, not a transparent PNG — on the\n   translucent theme-tint box above, that embedded rectangle shows up as a\n   visibly mismatched card. Solid white here absorbs it regardless of what\n   any given org's logo file looks like, without needing to touch or\n   inspect the uploaded image itself. */\n.sb-brand-box--logo {\n  background: var(--az-white, #fff);\n}\n\n/* Collapsed: 64×64 square, centered by .sb-brand-row — same inset\n   pattern as .sb-rail-item above, so the logo box sits flush with the\n   nav icons below it (Figma node 44:7820: 64×64, not edge-to-edge) */\n.sidebar:not(.expanded) .sb-brand-box {\n  width: 64px;\n  padding: 0;\n}\n\n.sb-brand-initial {\n  font-size: 34px;\n  font-weight: 800;\n  color: var(--colorDark, #007aff);\n  text-transform: uppercase;\n  letter-spacing: 0;\n  line-height: 1;\n}\n\n.sb-brand-fullname {\n  font-size: 20px;\n  font-weight: 500;\n  color: var(--colorDark, #007aff);\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  width: 100%;\n  text-align: center;\n}\n/* Client org logos vary in shape (wide wordmark, square mark, tall lockup —\n   every client can upload something different in a SaaS app), so scale\n   dynamically to fit rather than assume one aspect ratio: max-height caps\n   it within .sb-brand-box's 64px row, max-width keeps it inside the box's\n   own available width (accounting for the box's 12px side padding), and\n   object-fit preserves the logo's real proportions at whatever size it\n   ends up scaled to — never stretched, never overflowing either axis. */\n.sb-brand-logo {\n  max-height: 45px;\n  max-width: 100%;\n  width: auto;\n  height: auto;\n  object-fit: contain;\n}\n\n/* Traidster wordmark image in the top brand box (platform login only) —\n   wider aspect ratio than an org logo, so cap width instead of just height. */\n.sb-brand-logo--traidster {\n  max-width: 100%;\n  max-height: 32px;\n  object-fit: contain;\n}\n\n/* Platform login, collapsed rail — same cobalt→cyan gradient as the\n   wordmark's \"ai\" (Traidster Internal Brand Guide, 26 Sep 2026), applied\n   to the \"T\" initial in place of the per-org --colorDark used elsewhere,\n   since this represents the platform itself, not a client's theme. */\n.sb-brand-initial--gradient {\n  background: linear-gradient(\n    135deg,\n    var(--traidster-cobalt) 0%,\n    var(--traidster-midpoint) 50%,\n    var(--traidster-cyan) 100%\n  );\n  background-clip: text;\n  -webkit-background-clip: text;\n  -webkit-text-fill-color: transparent;\n  color: var(--traidster-cobalt); /* fallback if background-clip unsupported */\n}\n\n/* Only one of the two text variants is mounted at a time, swapped in JS\n   (Sidebar.js) by collapsed state — no CSS-only opacity dance needed. */\n\n/* ── Collapse toggle — Figma: circle at sidebar right edge, vertically centered */\n.sb-collapse-btn {\n  position: fixed;\n  left: calc(var(--sb-rail-width) - 14px);\n  /* vertical centre of the rail, which starts below the topbar */\n  top: calc(50% + var(--sb-topbar-height) / 2);\n  transform: translateY(-50%);\n  width: 28px;\n  height: 28px;\n  border-radius: 50%;\n  background: var(--az-white);\n  border: 1px solid rgba(var(--az-black-rgb), 0.12);\n  box-shadow: 0 2px 8px rgba(var(--az-black-rgb), 0.15);\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  cursor: pointer;\n  z-index: 101;\n  color: var(--az-gray-630);\n  font-size: 11px;\n  padding: 0;\n  transition:\n    left 0.22s cubic-bezier(0.4, 0, 0.2, 1),\n    background 0.15s ease,\n    color 0.15s ease;\n}\n\n.sb-collapse-btn.expanded {\n  left: calc(var(--sb-expanded-width) - 14px);\n}\n\n.sb-collapse-btn:hover {\n  background: var(--az-gray-175);\n  color: var(--az-gray-910);\n  border-color: rgba(var(--az-black-rgb), 0.2);\n}\n\n.sb-collapse-btn:focus-visible {\n  outline: 2px solid var(--sb-accent);\n  outline-offset: 2px;\n}\n\n/* ── Nav rail ─────────────────────────────────────────────────\n   Figma: flex-grow fills space between brand and profile\n   Figma gap 8px is inherited from parent sidebar gap            */\n.sb-rail-nav {\n  flex: 1;\n  min-height: 0; /* allows flex child to shrink + scroll on short screens */\n  width: 100%;\n  padding: 0;\n  display: flex;\n  flex-direction: column;\n  /* Figma gap: 8px between nav items */\n  gap: var(--sb-gap);\n  overflow-y: auto;\n  overflow-x: hidden;\n  scrollbar-width: none;\n  align-items: stretch;\n}\n\n.sidebar:not(.expanded) .sb-rail-nav {\n  align-items: center;\n}\n\n.sb-rail-nav::-webkit-scrollbar {\n  display: none;\n}\n\n/* ── Module item ───────────────────────────────────────────────\n   Expanded : full-width row, icon + label + optional chevron\n   Collapsed: 44×44 centred icon square                         */\n.sb-rail-item {\n  display: flex;\n  align-items: center;\n  gap: 8px;\n  /* Expanded: full width, left-to-right — Figma node 42:3428 uses\n     padding-driven height (px-8 py-12), not a fixed box height */\n  width: 100%;\n  height: auto;\n  padding: 12px 8px;\n  border-radius: 12px;\n  border: none;\n  background: transparent;\n  cursor: pointer;\n  text-decoration: none;\n  color: rgba(var(--az-white-rgb), 0.65);\n  font-family: inherit;\n  font-size: 14px;\n  font-weight: 500;\n  white-space: nowrap;\n  overflow: hidden;\n  box-sizing: border-box;\n  transition:\n    background 0.13s,\n    color 0.13s;\n}\n\n/* Collapsed: 64×64 square, icon centred — Figma node 43:6425 (64×64,\n   12px padding around a 40×40 icon) */\n.sidebar:not(.expanded) .sb-rail-item {\n  width: 64px;\n  height: 64px;\n  padding: 0;\n  justify-content: center;\n  border-radius: 12px;\n  gap: 0;\n  flex-shrink: 0; /* never compress icons on short screens */\n}\n\n.sb-rail-item:hover {\n  background: var(--sb-item-hover);\n  color: var(--sb-text);\n}\n\n/* Active: solid blue pill — Figma accent */\n.sb-rail-item.active {\n  background: var(--sb-accent);\n  color: var(--az-white);\n}\n\n.sidebar:not(.expanded) .sb-rail-item.active {\n  background: var(--sb-accent-collapsed);\n  color: var(--az-white);\n}\n\n.sb-rail-icon {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  font-size: 19px;\n  flex-shrink: 0;\n  width: 22px;\n  height: 22px;\n}\n\n/* Collapsed rail — Figma node 43:6427 etc: 40×40 icon glyph inside the\n   64×64 tap target above */\n.sidebar:not(.expanded) .sb-rail-icon {\n  font-size: 26px;\n  width: 40px;\n  height: 40px;\n}\n\n/* Label — fades out when collapsed */\n.sb-rail-label {\n  flex: 1;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  text-align: left;\n  font-size: 16px;\n  font-weight: 500;\n  opacity: 1;\n  max-width: 140px;\n  transition:\n    opacity 0.12s ease 0.07s,\n    max-width 0.22s cubic-bezier(0.4, 0, 0.2, 1);\n}\n\n.sidebar:not(.expanded) .sb-rail-label {\n  opacity: 0;\n  max-width: 0;\n}\n\n/* Right-side chevron — hidden when collapsed */\n.sb-rail-chevron {\n  font-size: 16px;\n  line-height: 1;\n  color: rgba(var(--az-white-rgb), 0.3);\n  flex-shrink: 0;\n  opacity: 1;\n  max-width: 20px;\n  transition:\n    opacity 0.12s ease 0.07s,\n    max-width 0.22s cubic-bezier(0.4, 0, 0.2, 1),\n    color 0.13s,\n    transform 0.18s ease;\n}\n\n.sidebar:not(.expanded) .sb-rail-chevron {\n  opacity: 0;\n  max-width: 0;\n  overflow: hidden;\n}\n\n.sb-rail-item:hover .sb-rail-chevron {\n  color: rgba(var(--az-white-rgb), 0.6);\n}\n\n.sb-rail-item.active .sb-rail-chevron {\n  color: rgba(var(--az-white-rgb), 0.8);\n  transform: rotate(90deg);\n}\n\n/* ── Settings section — lives inside sb-rail-nav, pushed to bottom ── */\n.sb-settings-section {\n  margin-top: auto;\n  display: flex;\n  flex-direction: column;\n  gap: var(--sb-gap);\n  width: 100%;\n  flex-shrink: 0; /* never compress on short screens */\n}\n\n.sidebar:not(.expanded) .sb-settings-section {\n  align-items: center;\n}\n\n/* ── Profile section — pinned to sidebar bottom ────────────────\n   Figma: same padding as sidebar (handled by parent),\n   top stroke border separates from nav                         */\n.sb-profile-section {\n  width: 100%;\n  flex-shrink: 0;\n  padding-top: 8px;\n}\n\n.sidebar:not(.expanded) .sb-profile-section {\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  justify-content: center;\n  width: auto;\n  padding-top: 8px;\n}\n\n.sb-profile-inner {\n  display: flex;\n  align-items: center;\n  gap: 10px;\n  padding: 8px;\n  border-radius: 10px;\n  cursor: pointer;\n  transition: background 0.13s;\n  overflow: hidden;\n  width: 100%;\n  box-sizing: border-box;\n}\n\n/* Collapsed: shrink to fit just the avatar circle — otherwise the gap\n   before .sb-profile-info still renders even though that element has\n   collapsed to max-width: 0, pushing the avatar left of center. */\n.sidebar:not(.expanded) .sb-profile-inner {\n  gap: 0;\n  width: auto;\n}\n\n.sb-profile-inner:hover {\n  background: var(--sb-item-hover);\n}\n\n.sb-avatar-circle {\n  width: 36px;\n  height: 36px;\n  min-width: 36px;\n  border-radius: 50%;\n  background: rgba(var(--az-white-rgb), 0.14);\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  font-size: 14px;\n  font-weight: 700;\n  color: var(--az-white);\n  flex-shrink: 0;\n}\n\n.sb-profile-info {\n  display: flex;\n  flex-direction: column;\n  min-width: 0;\n  flex: 1;\n  opacity: 1;\n  max-width: 160px;\n  overflow: hidden;\n  transition:\n    opacity 0.12s ease 0.08s,\n    max-width 0.22s cubic-bezier(0.4, 0, 0.2, 1);\n}\n\n.sidebar:not(.expanded) .sb-profile-info {\n  opacity: 0;\n  max-width: 0;\n}\n\n.sb-profile-name {\n  font-size: 13px;\n  font-weight: 600;\n  color: var(--sb-text);\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  line-height: 1.4;\n}\n\n/* ── \"Powered by\" footer — a full-bleed white strip across the very\n   bottom of the rail: negative margins cancel the sidebar's own padding\n   (16px sides when expanded, 16px bottom always) so it runs edge to edge\n   with no radius or gap. Holds the Traidster wordmark (dark/charcoal\n   variant, since it sits on white) — Traidster Internal Brand Guide,\n   26 Sep 2026. */\n.sb-powered-by {\n  margin: 8px calc(-1 * var(--sb-padding)) calc(-1 * var(--sb-padding));\n  width: calc(100% + 2 * var(--sb-padding));\n  height: 40px;\n  box-sizing: border-box;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  gap: 5px;\n  background: var(--az-white);\n  border-radius: 0;\n  padding: 0 14px;\n  white-space: nowrap;\n}\n\n.sb-powered-by-label {\n  font-size: 10px;\n  font-weight: 600;\n  color: var(--traidster-charcoal, #171a21);\n}\n\n.sb-powered-by-logo {\n  height: 12px;\n  width: auto;\n  display: block;\n}\n\n/* Collapsed: same full-width white strip, wordmark only (\"Powered by\" is\n   shown in the expanded rail only). The collapsed rail has no side\n   padding, so only the bottom padding needs cancelling. */\n.sidebar:not(.expanded) .sb-profile-section {\n  width: 100%;\n}\n.sidebar:not(.expanded) .sb-powered-by {\n  width: 100%;\n  margin: 8px 0 calc(-1 * var(--sb-padding));\n  padding: 0 12px;\n}\n.sidebar:not(.expanded) .sb-powered-by-label {\n  display: none;\n}\n.sidebar:not(.expanded) .sb-powered-by-logo {\n  height: auto;\n  width: 100%;\n  max-width: 72px;\n}\n\n/* ── Submenu panel — dark theme (matches Figma) ────────────── */\n.sb-backdrop {\n  position: fixed;\n  inset: var(--sb-topbar-height) 0 0 0;\n  z-index: 98;\n  background: transparent;\n}\n\n.sb-submenu {\n  position: fixed;\n  top: var(--sb-topbar-height);\n  height: calc(100% - var(--sb-topbar-height));\n  width: var(--sb-panel-width);\n  background: var(--sb-panel-bg);\n  border-right: 1px solid rgba(var(--az-white-rgb), 0.05);\n  box-shadow: 4px 0 24px rgba(var(--az-black-rgb), 0.5);\n  z-index: 99;\n  display: flex;\n  flex-direction: column;\n  animation: sbSubmenuIn 0.18s cubic-bezier(0.4, 0, 0.2, 1);\n}\n\n@keyframes sbSubmenuIn {\n  from {\n    opacity: 0.5;\n    transform: translateX(-10px);\n  }\n  to {\n    opacity: 1;\n    transform: translateX(0);\n  }\n}\n\n/* ── Submenu panel close button — Figma: same circle as sidebar toggle ── */\n.sb-panel-close-btn {\n  position: absolute;\n  right: -14px;\n  top: 50%;\n  transform: translateY(-50%);\n  width: 28px;\n  height: 28px;\n  border-radius: 50%;\n  background: var(--az-white);\n  border: 1px solid rgba(var(--az-black-rgb), 0.12);\n  box-shadow: 0 2px 8px rgba(var(--az-black-rgb), 0.15);\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  cursor: pointer;\n  z-index: 102;\n  color: var(--az-gray-630);\n  font-size: 11px;\n  padding: 0;\n  flex-shrink: 0;\n}\n\n.sb-panel-close-btn:hover {\n  background: var(--az-gray-175);\n  color: var(--az-gray-910);\n  border-color: rgba(var(--az-black-rgb), 0.2);\n}\n\n/* Panel body — no header, content starts from top.\n   Figma node 42:3368: 16px padding, 16px gap between groups\n   (the gap is applied per-group via .sb-submodule margin-top below). */\n.sb-panel-body {\n  flex: 1;\n  overflow-y: auto;\n  overflow-x: hidden;\n  padding: 16px;\n  display: flex;\n  flex-direction: column;\n  gap: 4px;\n  scrollbar-width: thin;\n  scrollbar-color: rgba(var(--az-white-rgb), 0.1) transparent;\n}\n\n.sb-panel-body::-webkit-scrollbar {\n  width: 4px;\n}\n.sb-panel-body::-webkit-scrollbar-track {\n  background: transparent;\n}\n.sb-panel-body::-webkit-scrollbar-thumb {\n  background: rgba(var(--az-white-rgb), 0.1);\n  border-radius: 4px;\n}\n\n/* Page links inside the panel — white text on dark bg.\n   Figma node 42:3397/42:3375: rounded-12 box, 8px padding all around,\n   not a flat full-bleed strip. */\n.sb-panel-item {\n  display: flex;\n  align-items: center;\n  gap: 4px;\n  padding: 8px;\n  border-radius: 12px;\n  font-size: 14px;\n  font-weight: 400;\n  color: var(--az-gray-450);\n  text-decoration: none;\n  transition:\n    background 0.11s,\n    color 0.11s;\n  white-space: nowrap;\n  overflow: hidden;\n}\n\n.sb-panel-item-label {\n  flex: 1;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  text-align: left;\n}\n\n.sb-panel-item:hover {\n  background: rgba(var(--az-white-rgb), 0.07);\n  color: var(--az-white);\n}\n\n.sb-panel-item.active {\n  background: rgba(var(--az-white-rgb), 0.1);\n  color: var(--az-white);\n  font-weight: 600;\n}\n\n/* Nested — Figma indents pages only by the width of the group header's\n   icon+gap (≈20px), not a full 32px tab-stop */\n.sb-panel-item.nested {\n  padding-left: 20px;\n}\n\n/* Dot indicator */\n.sb-dot {\n  width: 5px;\n  height: 5px;\n  border-radius: 50%;\n  flex-shrink: 0;\n  background: rgba(var(--az-white-rgb), 0.2);\n  transition: background 0.12s;\n}\n\n.sb-panel-item:hover .sb-dot,\n.sb-panel-item.active .sb-dot {\n  background: var(--sb-accent);\n}\n\n/* Submodule group — Figma node 42:3368 gap: 16px between groups */\n.sb-submodule {\n  margin-top: 12px;\n}\n\n/* Submodule header — chevron on LEFT, white text, medium weight —\n   Figma node 42:3369/42:3391: 16px text, font-weight 500, solid white */\n.sb-subhead {\n  display: flex;\n  align-items: center;\n  gap: 4px;\n  padding: 8px;\n  border-radius: 12px;\n  font-size: 16px;\n  font-weight: 500;\n  color: var(--az-white);\n  text-transform: none;\n  letter-spacing: 0;\n  border: none;\n  background: none;\n  cursor: pointer;\n  width: 100%;\n  font-family: inherit;\n  transition: color 0.12s;\n}\n\n.sb-subhead-label {\n  flex: 1;\n  text-align: left;\n  white-space: normal;\n  word-break: break-word;\n  line-height: 1.3;\n}\n\n.sb-subhead:hover {\n  color: var(--az-white);\n}\n.sb-subhead.active {\n  color: var(--az-white);\n}\n\n/* Chevron — now on the LEFT in JSX */\n.sb-chevron {\n  font-size: 15px;\n  display: inline-block;\n  transition: transform 0.18s ease;\n  line-height: 1;\n  color: rgba(var(--az-white-rgb), 0.4);\n  flex-shrink: 0;\n}\n\n.sb-subhead:hover .sb-chevron,\n.sb-subhead.active .sb-chevron {\n  color: rgba(var(--az-white-rgb), 0.7);\n}\n\n/* Pages inside an open submodule */\n.sb-sub-pages {\n  display: flex;\n  flex-direction: column;\n}\n\n/* ── Settings sidebar (SettingsSidebar.js — light background) ── */\n.sb-nav {\n  display: flex;\n  flex-direction: column;\n  gap: 2px;\n  padding: 8px 0;\n  width: 100%;\n}\n\n.sb-item {\n  display: flex;\n  align-items: center;\n  gap: 10px;\n  width: 100%;\n  padding: 10px 14px;\n  font-size: 13.5px;\n  font-weight: 500;\n  color: var(--az-gray-700);\n  text-align: left;\n  background: none;\n  border: none;\n  border-radius: 8px;\n  cursor: pointer;\n  text-decoration: none;\n  font-family: inherit;\n  transition:\n    background 0.12s,\n    color 0.12s;\n  white-space: nowrap;\n  overflow: hidden;\n  box-sizing: border-box;\n}\n\n.sb-item:hover {\n  background: rgba(var(--az-black-rgb), 0.04);\n  color: var(--az-slate-900);\n}\n\n.sb-item-active {\n  background: var(--primaryLight, #ede9fe);\n  color: var(--primaryDark);\n  font-weight: 600;\n}\n\n.sb-icon {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  font-size: 16px;\n  flex-shrink: 0;\n  width: 18px;\n  height: 18px;\n}\n\n.sb-label {\n  flex: 1;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  text-align: left;\n}\n\n.sb-children {\n  display: flex;\n  flex-direction: column;\n}\n\n.sb-child {\n  display: flex;\n  align-items: center;\n  gap: 8px;\n  padding: 8px 14px 8px 36px;\n  font-size: 13px;\n  font-weight: 400;\n  color: var(--az-gray-570);\n  text-decoration: none;\n  border-radius: 6px;\n  transition:\n    background 0.1s,\n    color 0.1s;\n  white-space: nowrap;\n  overflow: hidden;\n}\n\n.sb-child:hover {\n  background: rgba(var(--az-black-rgb), 0.04);\n  color: var(--az-gray-700);\n}\n\n.sb-child-active {\n  color: var(--primaryDark);\n  font-weight: 600;\n  background: rgba(var(--az-black-rgb), 0.03);\n}\n\n.sb-child-nested {\n  padding-left: 48px;\n}\n\n.sb-sub-children {\n  display: flex;\n  flex-direction: column;\n}\n\n/* Settings sidebar subhead — override the dark-bg version for light context */\n.sb-nav .sb-subhead {\n  padding: 8px 14px 8px 24px;\n  color: var(--az-gray-700);\n  font-size: 13px;\n  font-weight: 600;\n}\n\n.sb-nav .sb-subhead:hover {\n  color: var(--az-slate-900);\n}\n\n.sb-nav .sb-subhead.sb-subhead-active {\n  color: var(--primaryDark);\n}\n\n.sb-nav .sb-chevron {\n  color: var(--az-gray-500);\n}\n\n.sb-nav .sb-subhead:hover .sb-chevron,\n.sb-nav .sb-subhead.sb-subhead-active .sb-chevron {\n  color: var(--primaryDark);\n}\n\n.sb-nav .sb-dot {\n  background: var(--az-gray-400);\n}\n\n.sb-nav .sb-child-active .sb-dot {\n  background: var(--primaryDark);\n}\n\n/* ── Layout: home-section shifts with sidebar ── */\n.welcomeScreen {\n  position: relative;\n  text-align: center;\n  margin-top: 20%;\n}\n\n.sidebar.panel-open ~ .home-section .contentSection .timlineSection .w-30 {\n  left: 66rem;\n  transition: all 0.5s ease;\n}\n\n.home-section {\n  position: relative;\n  background: var(--az-white);\n  min-height: 100vh;\n  top: 0;\n  left: var(--sb-rail-width);\n  width: calc(100% - var(--sb-rail-width));\n  transition:\n    left 0.22s cubic-bezier(0.4, 0, 0.2, 1),\n    width 0.22s cubic-bezier(0.4, 0, 0.2, 1);\n}\n\n/* Off-white canvas below the topbar — Figma's \"Rectangle 17\" (#f9f9f9),\n   a separate layer from the topbar's white so the topbar reads as its\n   own panel rather than blending into the page background. */\n.page-content-canvas {\n  background: var(--az-gray-50);\n  min-height: calc(100vh - 80px);\n  /* Pages that put a gutter <Row> straight in the canvas bleed its -8px\n     margins past the right edge, adding a sideways scrollbar at every\n     width. clip (not hidden) trims that empty strip without creating a\n     scroll container, so sticky headers keep working and nothing shifts. */\n  overflow-x: clip;\n}\n\n/* Content shifts when sidebar is expanded */\n.sidebar.expanded ~ .home-section {\n  left: var(--sb-expanded-width);\n  width: calc(100% - var(--sb-expanded-width));\n}\n/* Submenu panel is a fixed overlay — does NOT shift content */\n\n/* Small screens (keep in sync with MOBILE_QUERY in Sidebar.js): the rail\n   becomes an off-canvas drawer opened by the burger in .sb-topbrand, and\n   content takes the full width. */\n@media (max-width: 767px) {\n  .home-section,\n  .sidebar.expanded ~ .home-section {\n    left: 0;\n    width: 100%;\n  }\n  .sidebar {\n    transform: translateX(-100%);\n    transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);\n    box-shadow: none;\n  }\n  .sidebar.mobile-open {\n    transform: translateX(0);\n    box-shadow: 4px 0 24px rgba(var(--az-black-rgb), 0.35);\n  }\n  .sb-mobile-backdrop {\n    position: fixed;\n    inset: var(--sb-topbar-height) 0 0 0;\n    z-index: 99;\n    background: rgba(var(--az-black-rgb), 0.35);\n  }\n  /* Brand band: burger + logo, sized to its content */\n  .sb-topbrand,\n  .sb-topbrand.expanded {\n    width: auto;\n    max-width: 60vw;\n    justify-content: flex-start;\n    padding: 0 12px;\n  }\n  .sb-topbrand .sb-brand-box {\n    width: auto;\n    justify-content: flex-start;\n  }\n  .sb-topbrand .sb-brand-logo {\n    max-height: 36px;\n    max-width: 120px;\n  }\n  /* Topbar content starts after the brand band (burger + logo). Scoped\n     under .home-section so it outranks the base .page-topbar rule that\n     comes later in this file. The breadcrumb is dropped — the page title\n     right below already names the page — so search + help fit. */\n  .home-section .page-topbar {\n    padding-left: 196px;\n    padding-right: 12px;\n    flex-wrap: nowrap;\n  }\n  .home-section .page-topbar .app-bc {\n    display: none;\n  }\n  .home-section .page-topbar .client-chip__label {\n    display: none;\n  }\n  .home-section .page-topbar .client-chip__name {\n    max-width: 90px;\n  }\n  .home-section .page-topbar .page-topbar-right {\n    flex-wrap: nowrap;\n    gap: 8px;\n  }\n  .home-section .page-topbar .page-topbar-search {\n    min-width: 0;\n  }\n  /* Submenu drills in over the drawer (rail + panel side by side don't\n     fit a phone); its close button steps back to the drawer */\n  .sb-submenu {\n    left: 0 !important;\n    width: min(280px, 85vw);\n    z-index: 101;\n  }\n}\n\n/* SECTION 7: Navbar (sticky top bar, header logo, badges, contentSection padding, pageHeader) */\n\n/* Logo in the header navbar — visible when sidebar is collapsed */\n.header-collapsed-logo {\n  height: 32px;\n  width: auto;\n  object-fit: contain;\n  margin-right: 8px;\n  flex-shrink: 0;\n  transition: opacity 0.2s ease;\n}\n\n/* Hide the header logo when sidebar is expanded (it shows in the brand row) */\n.sidebar.expanded ~ .home-section .header-collapsed-logo {\n  display: none;\n}\n\n/* ── Home page settings sidebar (in-flow, not fixed) ── */\n.home-settings-sidebar {\n  position: relative !important;\n  left: auto !important;\n  height: calc(100vh - 62px);\n  flex-shrink: 0;\n  overflow-y: auto;\n  overflow-x: hidden;\n  border-right: 1px solid rgba(var(--az-black-rgb), 0.08);\n  z-index: auto !important;\n}\n\n.home-settings-sidebar__header {\n  height: 44px;\n  display: flex;\n  align-items: center;\n  padding: 0 16px;\n  font-size: 12px;\n  font-weight: 600;\n  color: var(--az-gray-550);\n  letter-spacing: 0.04em;\n  text-transform: uppercase;\n  border-bottom: 1px solid rgba(var(--az-black-rgb), 0.06);\n  background: var(--az-white);\n  flex-shrink: 0;\n}\n\n.home-settings-sidebar .home-settings-menu.customSideMenu {\n  height: calc(100% - 44px) !important;\n}\n\n/* ── Mobile Drawer (mobSider) ─────────────────────────────────\n   Shared rules for the AntD Drawer used in Header & HomeHeader.\n   Width is set via the `width` prop on the Drawer component (280px).\n──────────────────────────────────────────────────────────── */\n\n/* White bg — replaces old orange (#d18146) */\n.mobSider.ant-drawer .ant-drawer-content {\n  background: var(--az-white) !important;\n}\n\n/* Drawer title bar */\n.mobSider .ant-drawer-header {\n  padding: 12px 16px !important;\n  border-bottom: 1px solid rgba(var(--az-black-rgb), 0.08) !important;\n  background: var(--az-white) !important;\n}\n\n/* Body: zero padding — menu handles its own spacing */\n.mobSider .ant-drawer-body {\n  padding: 0 !important;\n  overflow: hidden;\n  display: flex;\n  flex-direction: column;\n}\n\n/* Sidebar wrapper inside drawer: in-flow (not fixed) */\n.mobSider .sidebar,\n.mobSider .sidebar.open {\n  display: flex !important;\n  flex-direction: column;\n  position: relative !important;\n  width: 100% !important;\n  height: 100%;\n  overflow-y: auto;\n  overflow-x: hidden;\n}\n\n/* Menu fills the entire drawer body */\n.mobSider .customSideMenu {\n  height: 100% !important;\n  flex: 1;\n}\n\n/* Icon container — same sizing as .sidebar.open */\n.mobSider .ant-menu-item-icon,\n.mobSider .ant-menu-submenu-title .ant-menu-item-icon {\n  min-width: 20px !important;\n  width: 20px !important;\n  margin-right: 10px !important;\n  display: flex !important;\n  align-items: center !important;\n  justify-content: center !important;\n  flex-shrink: 0 !important;\n}\n\n/* menuImg — same as .sidebar.open .menuImg */\n.mobSider .menuImg {\n  color: var(--primaryDark);\n  height: 18px !important;\n  width: 18px !important;\n  min-width: 18px !important;\n  min-height: 18px !important;\n  max-width: 18px !important;\n  max-height: 18px !important;\n  display: flex !important;\n  justify-content: center !important;\n  align-items: center !important;\n  margin: 0 !important;\n  border-radius: 0 !important;\n  background: transparent !important;\n  flex-shrink: 0 !important;\n}\n\n/* Settings sidebar inside drawer: fill height, no border */\n.mobSider .home-settings-sidebar {\n  height: 100% !important;\n  border-right: none !important;\n}\n\n/* Hide the \"Platform Settings\" label — drawer title already shows it */\n.mobSider .home-settings-sidebar__header {\n  display: none !important;\n}\n\n.mobSider .home-settings-sidebar .home-settings-menu.customSideMenu {\n  height: 100% !important;\n}\n\n/* Hide Bootstrap's navbar-toggler — sidebar has its own toggle button */\n.navbar-toggler {\n  display: none !important;\n}\n\n.custom-navbar {\n  width: 100%;\n  height: 62px;\n  position: sticky;\n  top: 0;\n  right: 0;\n  padding-top: 0;\n  padding-bottom: 0;\n  background-color: var(--az-white) !important;\n  z-index: 1099;\n}\n\n.custom-navbar .navbar-collapse {\n  justify-content: end;\n}\n\n.badgeSec {\n  width: 37px;\n  height: 37px;\n  background: var(--secondaryLight);\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  border-radius: 50%;\n}\n\n.notBadge .ant-img {\n  display: block !important;\n}\n\n/* ── App Breadcrumb ────────────────────────────────────────────────────────── */\n/* ── Top bar: breadcrumb row + global search ── */\n.page-topbar {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  flex-wrap: wrap;\n  row-gap: 10px;\n  gap: 16px;\n  height: 80px;\n  padding: 10px 28px;\n  background: var(--az-white);\n  border-bottom: 1px solid rgba(var(--az-black-rgb), 0.06);\n  /* Stays put while the page scrolls. */\n  position: sticky;\n  top: 0;\n  z-index: 40;\n}\n\n/* Client onboarding steps (Organization create → first admin) */\n/* Client edit → step 2 while the existing admin is looked up */\n.org-admin-loading {\n  display: flex;\n  justify-content: center;\n  padding: 48px 0 !important;\n}\n\n/* One line: ① Organization details ── ② Organization admin. Titles never\n   wrap (overrides the tablet Steps wrap rule); width fits the content. */\n.onboarding-steps.ant-steps {\n  width: auto;\n  max-width: 100%;\n  margin: 0 0 12px;\n}\n.onboarding-steps.ant-steps .ant-steps-item {\n  flex: 0 1 auto !important;\n}\n.onboarding-steps.ant-steps .ant-steps-item:not(:last-child) {\n  min-width: 300px;\n}\n.onboarding-steps.ant-steps .ant-steps-item-title {\n  white-space: nowrap !important;\n}\n\n/* Left side of the topbar: client chip (platform CLIENT_VIEW) + breadcrumb */\n.page-topbar-left {\n  display: flex;\n  align-items: center;\n  gap: 12px;\n  min-width: 0;\n}\n\n/* \"Viewing client: <name>\" — platform user inside a client. Theme tint so\n   it reads as context, not an alert; × returns to Manage Clients. */\n.client-chip {\n  display: inline-flex;\n  align-items: center;\n  gap: 6px;\n  height: 34px;\n  padding: 0 6px 0 12px;\n  border-radius: var(--az-radius-pill, 999px);\n  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.1);\n  border: 1px solid rgba(var(--colorDark-rgb, 0, 122, 255), 0.25);\n  color: var(--colorDark, #007aff);\n  white-space: nowrap;\n  min-width: 0;\n  flex-shrink: 1;\n}\n.client-chip__icon {\n  font-size: 14px;\n}\n.client-chip__label {\n  font-size: 12px;\n  color: var(--az-text-secondary);\n}\n.client-chip__name {\n  font-size: 14px;\n  font-weight: 600;\n  max-width: 220px;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n.client-chip__exit {\n  width: 24px;\n  height: 24px;\n  border-radius: 50%;\n  border: none;\n  background: transparent;\n  color: inherit;\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  cursor: pointer;\n  padding: 0;\n  font-size: 11px;\n}\n.client-chip__exit:hover {\n  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.15);\n}\n\n/* flex:1 1 auto (not just margin-left:auto) — without this, the search\n   input's flex:1 1 200px below has no spare width to grow into (this\n   wrapper just shrink-wraps its content), so it settles near its 200px\n   basis instead of growing up to its 372px max-width like Figma. */\n.page-topbar-right {\n  display: flex;\n  align-items: center;\n  justify-content: flex-end;\n  flex-wrap: wrap;\n  gap: 12px;\n  flex: 1 1 auto;\n  min-width: 0;\n}\n\n/* ── Reusable search input — Figma node 42:2958. .page-topbar-search is\n   kept as an alias (it's the name already used by AppLayout's top bar);\n   use .az-search-input for any new page-level search/filter input so\n   there's one visual definition for \"search\" across the whole app. ── */\n.az-search-input,\n.page-topbar-search {\n  width: 100%;\n  max-width: 372px;\n  min-width: 160px;\n  height: 44px;\n  border-radius: var(--az-radius-pill) !important;\n  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.08) !important;\n  border: none !important;\n  box-shadow: none !important;\n  flex: 1 1 200px;\n}\n\n.az-search-input .ant-input,\n.page-topbar-search .ant-input {\n  background: transparent !important;\n  font-size: 16px !important;\n  letter-spacing: -0.32px;\n  color: var(--az-text-primary);\n}\n\n/* !important here too — antd's own injected placeholder styling otherwise\n   wins over the plain font-size set above and the placeholder silently\n   renders at antd's default size instead of inheriting this 16px. */\n.az-search-input .ant-input::placeholder,\n.page-topbar-search .ant-input::placeholder {\n  font-size: 16px !important;\n  letter-spacing: -0.32px;\n  color: var(--az-text-secondary);\n}\n\n.az-search-input .ant-input-prefix,\n.page-topbar-search .ant-input-prefix {\n  margin-inline-end: 8px;\n}\n\n.az-search-input .ant-input-prefix .anticon,\n.page-topbar-search .ant-input-prefix .anticon {\n  font-size: 22px;\n  color: var(--az-text-secondary);\n}\n\n.need-help-btn {\n  display: inline-flex;\n  align-items: center;\n  gap: 6px;\n  height: 44px;\n  padding: 0 20px 0 14px;\n  border: none;\n  border-radius: 70px;\n  background: var(--colorDark, #007aff);\n  color: var(--az-white);\n  font-family: inherit;\n  font-size: 16px;\n  font-weight: 500;\n  letter-spacing: -0.32px;\n  cursor: pointer;\n  white-space: nowrap;\n  flex-shrink: 0;\n  transition: opacity 0.15s;\n}\n\n.need-help-btn .anticon {\n  font-size: 24px;\n}\n\n.need-help-btn:hover {\n  opacity: 0.9;\n}\n\n.need-help-popover {\n  max-width: 220px;\n  font-size: 13px;\n  color: rgba(var(--az-black-rgb), 0.7);\n}\n\n@media (max-width: 768px) {\n  .page-topbar-search {\n    min-width: 120px;\n    flex: 1 1 120px;\n  }\n\n  .need-help-btn .need-help-label {\n    display: none;\n  }\n\n  .need-help-btn {\n    padding: 0 14px;\n  }\n}\n\n/* ── Shared row-action edit (Pen) button ── */\n.az-edit-pen-btn {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: 40px; /* Figma node 42:3067: size-[40px] */\n  height: 40px;\n  flex-shrink: 0;\n  border: none;\n  border-radius: var(--az-radius-pill);\n  background: var(--colorDark, #007aff);\n  cursor: pointer;\n  transition: opacity 0.15s;\n}\n.az-edit-pen-btn:hover {\n  opacity: 0.85;\n}\n.az-edit-pen-btn:disabled {\n  background: var(--az-gray-350);\n  cursor: not-allowed;\n}\n\n/* ── Pill-shaped Select — for filter dropdowns that should match az-btn radius ── */\n.az-pill-select .ant-select-selector {\n  border-radius: var(--az-radius-pill) !important;\n}\n\n/* ── Row view (Eye) button — same pill shape as edit, blue */\n.az-eye-btn {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: 40px;\n  height: 40px;\n  flex-shrink: 0;\n  border: none;\n  border-radius: var(--az-radius-pill);\n  background: var(--colorDark, #007aff);\n  color: var(--az-white);\n  font-size: 18px;\n  cursor: pointer;\n  transition: opacity 0.15s;\n}\n.az-eye-btn:hover {\n  opacity: 0.85;\n}\n\n/* ── Row delete button — same pill shape as edit, danger red */\n.az-delete-btn {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: 40px;\n  height: 40px;\n  flex-shrink: 0;\n  border: none;\n  border-radius: var(--az-radius-pill);\n  background: var(--az-red-500);\n  color: var(--az-white);\n  font-size: 18px;\n  cursor: pointer;\n  transition: opacity 0.15s;\n}\n.az-delete-btn:hover {\n  opacity: 0.85;\n}\n\n/* ── Action column button group — aligns edit/delete/dots buttons in a row ── */\n.action-btn {\n  display: flex;\n  align-items: center;\n  gap: 6px;\n  flex-wrap: nowrap;\n}\n\n/* ── Row \"more actions\" dots button — Figma node 42:3069: size-[40px] bg-[#ededed] ── */\n.az-dots-btn {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: 40px;\n  height: 40px;\n  flex-shrink: 0;\n  border: none;\n  border-radius: var(--az-radius-pill);\n  background: var(--az-gray-50);\n  color: rgba(var(--az-black-rgb), 0.65);\n  font-size: 24px;\n  cursor: pointer;\n  transition: background 0.15s;\n}\n.az-dots-btn:hover {\n  background: var(--az-gray-175);\n}\n\n/* ── Row \"Hold\" toggle button — Figma node 42:3066/43:6118: h-[40px] pill, HandPalm icon ── */\n.az-hold-btn {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  gap: 8px;\n  height: 40px;\n  padding: 0 16px;\n  flex-shrink: 0;\n  border: none;\n  border-radius: var(--az-radius-pill);\n  font-family: inherit;\n  font-size: 14px;\n  font-weight: 600;\n  cursor: pointer;\n  white-space: nowrap;\n  transition: opacity 0.15s;\n}\n.az-hold-btn:hover {\n  opacity: 0.85;\n}\n.az-hold-btn--filled {\n  background: var(--colorDark, #007aff);\n  color: var(--az-white);\n}\n.az-hold-btn--ghost {\n  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.08);\n  color: var(--colorDark, #007aff);\n}\n\n/* ── Row \"more actions\" dropdown — Figma node 45:336: white card,\n   rounded-16, soft shadow, plain text rows, danger row in red ── */\n.az-dots-menu .ant-dropdown-menu {\n  padding: 8px;\n  border-radius: 16px;\n  border: 1px solid var(--az-gray-50);\n  box-shadow: 0px 24px 48px -12px rgba(var(--az-black-rgb), 0.18);\n  overflow: hidden;\n  min-width: 200px;\n  display: flex;\n  flex-direction: column;\n  gap: 4px;\n}\n/* Each item rendered as a ghost pill button — visible before hover */\n.az-dots-menu .ant-dropdown-menu-item {\n  padding: 9px 18px;\n  font-size: 14px;\n  font-weight: 500;\n  border-radius: var(--az-radius-pill) !important;\n  color: var(--colorDark, #007aff);\n  background: var(--colorLight, rgba(0, 122, 255, 0.07));\n}\n.az-dots-menu\n  .ant-dropdown-menu-item:not(.ant-dropdown-menu-item-disabled):hover {\n  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.13) !important;\n  color: var(--colorDark, #007aff);\n}\n/* Disabled — matches az-btn:disabled (opacity 0.4, cursor not-allowed) */\n.az-dots-menu .ant-dropdown-menu-item-disabled,\n.az-dots-menu .ant-dropdown-menu-item-disabled:hover {\n  opacity: 0.4;\n  cursor: not-allowed;\n  background: var(--colorLight, rgba(0, 122, 255, 0.07)) !important;\n  color: var(--colorDark, #007aff) !important;\n}\n/* Danger items (Deactivate etc.) */\n.az-dots-menu .ant-dropdown-menu-item-danger {\n  color: var(--az-red-500) !important;\n  background: rgba(var(--az-red-500-rgb), 0.07) !important;\n}\n.az-dots-menu\n  .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover {\n  background: rgba(var(--az-red-500-rgb), 0.14) !important;\n  color: var(--az-red-500) !important;\n}\n\n/* ── Sidebar profile dropdown (Customise Profile / Change Password / Logout) —\n   same themed-pill treatment as .az-dots-menu so it follows the org theme\n   instead of AntD's untouched default popup styling ── */\n.sb-profile-menu .ant-dropdown-menu {\n  padding: 8px;\n  border-radius: 16px;\n  border: 1px solid var(--az-gray-50);\n  box-shadow: 0px 24px 48px -12px rgba(var(--az-black-rgb), 0.18);\n  overflow: hidden;\n  min-width: 200px;\n  display: flex;\n  flex-direction: column;\n  gap: 4px;\n}\n.sb-profile-menu .ant-dropdown-menu-item {\n  padding: 9px 18px;\n  font-size: 14px;\n  font-weight: 500;\n  border-radius: var(--az-radius-pill) !important;\n  color: var(--colorDark, #007aff);\n  background: var(--colorLight, rgba(0, 122, 255, 0.07));\n}\n.sb-profile-menu\n  .ant-dropdown-menu-item:not(.ant-dropdown-menu-item-disabled):hover {\n  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.13) !important;\n  color: var(--colorDark, #007aff);\n}\n.sb-profile-menu .ant-dropdown-menu-item-danger {\n  color: var(--az-red-500) !important;\n  background: rgba(var(--az-red-500-rgb), 0.07) !important;\n}\n.sb-profile-menu\n  .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover {\n  background: rgba(var(--az-red-500-rgb), 0.14) !important;\n  color: var(--az-red-500) !important;\n}\n\n/* ── Breadcrumb nav ── */\n.app-bc {\n  display: flex;\n  align-items: center;\n  gap: 6px;\n  min-height: 26px;\n}\n\n.app-bc-list {\n  display: flex;\n  align-items: center;\n  flex-wrap: nowrap;\n  list-style: none;\n  margin: 0;\n  padding: 0;\n  gap: 0;\n  overflow: hidden;\n}\n\n.app-bc-item {\n  display: flex;\n  align-items: center;\n  gap: 6px;\n  white-space: nowrap;\n  /* Smooth expand/collapse via max-width + opacity */\n  max-width: 400px;\n  opacity: 1;\n  overflow: hidden;\n  transition:\n    max-width 0.28s cubic-bezier(0.4, 0, 0.2, 1),\n    opacity 0.2s ease,\n    margin 0.25s ease;\n}\n\n.app-bc-item--hidden {\n  max-width: 0;\n  opacity: 0;\n  pointer-events: none;\n  margin: 0;\n}\n\n.app-bc-sep {\n  font-size: 14px;\n  color: rgba(var(--az-black-rgb), 0.7);\n  letter-spacing: -0.32px;\n  user-select: none;\n  flex-shrink: 0;\n}\n\n/* Figma node 44:7327 specified 16px — standardized down to 14px, matching\n   the rest of the app's text sizing. Every crumb is the same 70% black,\n   Regular weight; only the current (last) crumb is SemiBold — no separate\n   \"link\" color. */\n.app-bc-label {\n  font-size: 16px;\n  font-weight: 400;\n  color: rgba(var(--az-black-rgb), 0.7);\n  letter-spacing: -0.32px;\n  cursor: pointer;\n  transition: color 0.15s;\n  flex-shrink: 0;\n}\n\n.app-bc-label:hover {\n  color: rgba(var(--az-black-rgb), 0.9);\n  text-decoration: underline;\n}\n\n.app-bc-label--active {\n  color: rgba(var(--az-black-rgb), 0.7);\n  cursor: default;\n  font-weight: 600;\n}\n\n.app-bc-label--active:hover {\n  color: rgba(var(--az-black-rgb), 0.7);\n  text-decoration: none;\n}\n\n/* Toggle chevron button — only visible on mobile */\n.app-bc-toggle {\n  display: none;\n  align-items: center;\n  justify-content: center;\n  width: 22px;\n  height: 22px;\n  border: 1px solid var(--az-gray-300);\n  border-radius: 5px;\n  background: var(--az-gray-50);\n  cursor: pointer;\n  flex-shrink: 0;\n  padding: 0;\n  transition: background 0.15s;\n}\n\n.app-bc-toggle:hover {\n  background: var(--az-gray-135);\n  border-color: var(--az-gray-400);\n}\n\n.app-bc-chevron {\n  font-size: 16px;\n  color: var(--az-gray-570);\n  line-height: 1;\n  display: inline-block;\n  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);\n}\n\n@media (max-width: 991px) {\n  .app-bc-toggle {\n    display: flex;\n  }\n\n  .app-bc-list {\n    flex-wrap: wrap;\n    row-gap: 2px;\n  }\n\n  .app-bc-label {\n    font-size: 16px;\n  }\n}\n\n.contentSection {\n  padding: 8px 24px;\n}\n\n.contentSection .ant-page-header {\n  padding: 10px;\n  border-radius: 10px;\n  margin-bottom: 16px;\n}\n\n.pageHeader h2 {\n  font-size: 18px !important;\n  font-weight: 500 !important;\n  margin-bottom: 0 !important;\n}\n\n/* Prevent title from shrinking character-by-character in flex pageHeaders */\n.pageHeader h1,\n.pageHeader h2,\n.pageHeader h3,\n.pageHeader h4,\n.pageHeader h5,\n.pageHeader .ant-typography {\n  flex-shrink: 0;\n  white-space: nowrap;\n  margin-bottom: 0;\n  min-width: 0;\n}\n\n/* ── Page card — white card used on all listing & form pages ── */\n.page-card {\n  background: transparent;\n  border-radius: 0;\n  box-shadow: none;\n  overflow: visible;\n}\n\n/* ── Page card header — title + actions sit in the gray body (outside card) ── */\n.page-card-header {\n  padding: 0 0 2px 0;\n  display: flex;\n  justify-content: space-between;\n  align-items: flex-start;\n  border-bottom: none;\n  gap: 12px;\n  flex-wrap: wrap;\n  min-height: 48px;\n}\n\n/* ── Page title + subtitle group (left side of card header) ── */\n.page-card-title-group {\n  display: flex;\n  flex-direction: column;\n  gap: 2px;\n  min-width: 0;\n}\n\n/* ── Page title inside the header — Figma node 44:6511/42:2999 specified\n   34px, standardized down to 28px app-wide. --hero kept as an alias now\n   that the base class matches it exactly, so existing usages of either\n   class render the same. ── */\n.page-card-title,\n.page-card-title--hero {\n  font-size: 28px;\n  font-weight: 500;\n  color: rgba(var(--az-black-rgb), 0.9);\n  margin: 0;\n  line-height: 1.52;\n  white-space: normal;\n}\n\n.page-title-count {\n  font-weight: 400;\n  font-size: inherit;\n}\n\n/* ── Page subtitle below the title — Figma node 42:3158: 14px Regular, 60% black ── */\n.page-card-subtitle,\n.page-card-subtitle--hero {\n  font-size: 14px;\n  font-weight: 400;\n  color: rgba(var(--az-black-rgb), 0.6);\n  margin: 0;\n  line-height: 1.5;\n  letter-spacing: -0.28px;\n}\n\n/* ── Actions area (search + buttons) ── */\n.page-card-actions {\n  display: flex;\n  align-items: center;\n  gap: 8px;\n  flex-wrap: wrap;\n  flex-shrink: 0;\n}\n\n/* ── Body content — white card containing only the table ── */\n.page-card-body {\n  background: var(--az-white);\n  border-radius: var(--az-radius-sm);\n  box-shadow: var(--az-shadow-card);\n  overflow: visible;\n  padding: 0;\n}\n\n/* ── \"Hero\" body card — Figma node 42:3006 \"Latest Orders\": one white\n   rounded card wrapping the status cards + filter tags + table together,\n   floating on the page's light-gray background. Opt-in modifier so the\n   plain .page-card-body used elsewhere is unaffected. ── */\n.page-card-body--hero {\n  background: var(--az-white);\n  border-radius: var(--az-radius-lg);\n  box-shadow: var(--az-shadow-card-lg);\n  /* padding: var(--az-space-4); */\n}\n\n/* Manage Clients (Home/index.js): the org cards (.az-card) already carry\n   their own white card background — this page doesn't also want the\n   --hero wrapper's white card-behind-the-cards frame, just the light-gray\n   page background showing through around them. Opt-in modifier, scoped to\n   this one page only — --hero's own base look stays unchanged everywhere\n   else it's used (Dashboard, Order Dashboard, etc.). */\n.page-card-body--hero-transparent {\n  background: none;\n  box-shadow: none;\n}\n\n/* ── Table variant of the hero card: same 16px radius + shadow as\n   --hero, but no padding. A table's own toolbar/cells already provide\n   their own spacing, and the edit-columns button is specifically\n   designed to sit flush against this card's real border — --hero's\n   16px stat-tile padding is what pushed it away from that border.\n   Use this (not --hero) when the card wraps a table rather than a\n   row of padded stat tiles. ── */\n.page-card-body--hero-flush {\n  background: var(--az-white);\n  border-radius: var(--az-radius-lg);\n  box-shadow: var(--az-shadow-card-lg);\n  padding: 0;\n}\n\n/* ── Form page card: white card for create/edit forms ── */\n.page-card-form {\n  background: var(--az-white);\n  border-radius: var(--az-radius-sm);\n  box-shadow: var(--az-shadow-card);\n  padding: 24px 28px;\n}\n\n/* ── Page-level title rendered outside the card (above form/table cards) ── */\n.page-card-section-title {\n  font-size: 18px;\n  font-weight: 700;\n  color: var(--az-slate-900);\n  margin: 0 0 14px 0;\n  padding-bottom: 0;\n  border-bottom: none;\n  line-height: 1.3;\n}\n\n/* SECTION 8: Accordion / collapse sections */\n\n/* Was background: var(--primaryLight) [10%-opacity accent] + white text —\n   nearly unreadable (\"blurry\") since white-on-10%-tint has almost no\n   contrast. Use a solid light surface + dark text, same pairing as\n   .page-card-header / .status-card use everywhere else in the system. */\n.accordionSection .ant-collapse-header {\n  background-color: var(--az-gray-50);\n  padding: 13px 20px !important;\n  border-top-right-radius: var(--az-radius-md) !important;\n  border-top-left-radius: var(--az-radius-md) !important;\n  color: var(--az-text-primary) !important;\n  font-size: 16px !important;\n  font-weight: 600 !important;\n}\n\n.accordionSection .ant-collapse-header:hover {\n  background-color: var(--az-gray-50);\n}\n\n.accordionSection\n  .ant-collapse\n  > .ant-collapse-item\n  > .ant-collapse-header\n  .ant-collapse-arrow,\n.accordionSection .ant-collapse-header .ant-collapse-expand-icon {\n  font-size: 16px !important;\n  font-weight: 500 !important;\n  float: right;\n  margin-top: 8px;\n  color: var(--colorDark, #007aff) !important;\n}\n\n.accordionSection .ant-collapse-content-box {\n  padding: 13px 20px !important;\n}\n\n.accordionSection .ant-collapse-borderless > .ant-collapse-item:last-child,\n.accordionSection .ant-collapse {\n  border-bottom-right-radius: 14px !important;\n  border-bottom-left-radius: 14px !important;\n  border-color: transparent;\n}\n\n.accordionSection\n  .ant-col\n  .ant-select:not(.ant-select-customize-input)\n  .ant-select-selector {\n  border-radius: 6px;\n  height: 50px;\n  padding: 10px 20px;\n}\n\n.accordionSection .ant-form-item {\n  display: block !important;\n}\n\n.accordionSection .ant-form-item-label > label::after {\n  display: none !important;\n}\n\n/* Form cards (new design — replaces accordionSection wrapper) */\n.contentSection\n  .ant-form-vertical\n  .ant-select:not(.ant-select-customize-input)\n  .ant-select-selector {\n  border-radius: 6px !important;\n  height: 40px !important;\n  padding: 5px 16px !important;\n}\n\n.contentSection\n  .ant-form-vertical\n  .ant-select-single\n  .ant-select-selector\n  .ant-select-selection-item,\n.contentSection\n  .ant-form-vertical\n  .ant-select-single\n  .ant-select-selector\n  .ant-select-selection-placeholder {\n  line-height: 28px !important;\n}\n\n.infoAccordion .panelHeader .ant-collapse-content-box {\n  padding: 0 !important;\n}\n\n/* Was color: var(--primaryTextLight) [white] with no background set —\n   invisible white-on-white column-header text once the panel expanded. */\n.infoAccordion .panelHeader .ant-collapse-content-box .infoSection .infoHeader {\n  padding: 8px 20px !important;\n  background: var(--az-gray-50);\n  color: var(--az-text-secondary);\n  text-transform: uppercase;\n  font-size: 12px;\n  letter-spacing: 0.3px;\n}\n\n.infoAccordion\n  .panelHeader\n  .ant-collapse-content-box\n  .infoSection\n  .infoDetails {\n  padding: 20px 20px;\n}\n\n.pdng {\n  padding: 10px 20px !important;\n}\n\n.infoSection .infoHeader .infoTh p,\n.infoSection .infoDetails .infoTd p {\n  margin-bottom: 0;\n  font-weight: 600;\n  word-break: break-all;\n  padding: 0 5px;\n}\n\n.infoTd {\n  word-break: break-all;\n}\n\n.infoSection .infoDetails .infoTd p {\n  padding: 0 10px !important;\n}\n\n/* Was color: var(--primaryTextLight) [white] on an unset (white) background\n   — invisible text, same bug as .infoHeader above. */\n.infoSection .pymntHdr {\n  padding: 15px 20px !important;\n  background: var(--colorLight, rgba(0, 122, 255, 0.08));\n  border-radius: var(--az-radius-sm) var(--az-radius-sm) 0 0;\n}\n\n.infoSection .pymntHdr p {\n  margin-bottom: 0;\n  color: var(--colorDark, #007aff);\n  font-weight: 600;\n}\n\n.infoTd .ant-switch {\n  background-color: var(--secondaryLight) !important;\n}\n\n.infoTd .ant-switch-checked {\n  background-color: var(--secondaryDark) !important;\n}\n\n.even {\n  background-color: var(--az-gray-50);\n}\n\n.even:last-child {\n  border-bottom-left-radius: 16px;\n  border-bottom-right-radius: 16px;\n}\n\n.ant-collapse-borderless {\n  background-color: var(--az-white) !important;\n}\n\n/* SECTION 9: Images & media */\n\n.valueImage {\n  width: 60px;\n  height: 60px;\n  overflow: hidden;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  border-radius: 10px;\n  margin: 0 auto;\n}\n\n.combo {\n  padding-top: 3px;\n  width: 100px;\n  display: flex;\n  flex-direction: row;\n  flex-wrap: wrap;\n  color: white;\n  justify-content: center;\n\n  border: 1px solid rgb(var(--az-blush-rgb));\n  border-radius: 10px;\n}\n\n.comboImg {\n  padding: 2px;\n}\n\n.valueImage {\n  width: 40px;\n  height: 40px;\n  overflow: hidden;\n  display: flex;\n  border: 1px solid rgb(var(--az-blush-rgb));\n  justify-content: center;\n  align-items: center;\n  border-radius: 10px;\n  margin: 0 auto;\n}\n\n.infoDetails .valueImage {\n  margin: inherit !important;\n}\n\n.editImage {\n  width: 80px;\n  height: 80px;\n  border: 1px solid var(--az-gray-310);\n  overflow: hidden;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  margin-right: 15px;\n  border-radius: 5px;\n  background: var(--az-white);\n}\n\n.varImageSec {\n  position: relative;\n}\n\n.removeIcon {\n  all: unset !important;\n  position: absolute !important;\n  background-color: var(--az-white) !important;\n  width: 20px !important;\n  height: 20px !important;\n  border-radius: 50px !important;\n  top: -0.5rem !important;\n  right: 0.5rem !important;\n  display: flex !important;\n  justify-content: center !important;\n  align-items: center !important;\n  text-align: center !important;\n  -webkit-box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.1) !important;\n  -moz-box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.1);\n  box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.1) !important;\n  cursor: pointer !important;\n}\n\n/* SECTION 10: Action/feedback buttons (Approve, Reject, Hold, Return) */\n\n.btnSection {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n}\n\n.btnSection .feedbackBtn {\n  height: 50px;\n  color: var(--az-white);\n  border: 0;\n  border-radius: 6px;\n}\n\n.btnSection .feedbackBtn .reject {\n  display: flex;\n  height: 50px;\n  color: var(--primaryDark);\n  border: 0;\n  border-radius: 6px;\n}\n\n.btnSection .feedbackBtn:hover {\n  color: var(--primaryTextLight);\n  opacity: 0.8;\n}\n\n.return {\n  background-color: var(--secondaryLight) !important;\n}\n\n.hold {\n  background-color: var(--primaryLight) !important;\n}\n\n.approve {\n  background-color: var(--secondaryDark) !important;\n}\n\n.reject {\n  background-color: var(--primaryDark) !important;\n}\n\n/* SECTION 11: Custom card (legacy card system) */\n\n/***** Custom Card Style *****/\n.customCard,\n.ant-collapse {\n  border-radius: 14px !important;\n  cursor: pointer;\n  -webkit-box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.1);\n  -moz-box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.1);\n  box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.1);\n}\n\n.customCard {\n  border-radius: 8px !important;\n  height: 100%;\n}\n.themeCard {\n  transform: translateY(-3px);\n  box-shadow:\n    rgba(var(--az-navy-shadow-rgb), 0.25) 0px 4px 8px -2px,\n    rgba(var(--az-navy-shadow-rgb), 0.08) 0px 0px 0px 1px;\n}\n.themeCard:hover {\n  transform: translateY(-3px);\n  /* box-shadow:\n    rgba(0, 0, 0, 0.4) 0px 2px 4px,\n    rgba(0, 0, 0, 0.3) 0px 7px 13px -3px,\n    rgba(0, 0, 0, 0.2) 0px -3px 0px inset; */\n  box-shadow: 0 6px 16px rgba(var(--az-black-rgb), 0.08);\n}\n\n.customCard .ant-card-body {\n  padding: 14px 20px;\n  height: 100%;\n}\n\n.counterText h4 {\n  margin-bottom: 0 !important;\n}\n\n.searchBtn {\n  display: flex;\n  align-items: center;\n  background: var(--primaryDark);\n  border-color: var(--primaryDark);\n  transition: all 0.2s ease-in-out;\n}\n\n.searchBtn:hover {\n  background: var(--primaryLight) !important;\n  border-color: var(--primaryDark) !important;\n  -webkit-box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.1);\n  -moz-box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.1);\n  box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.1) !important;\n}\n\n.dateRange {\n  display: flex;\n  justify-content: end;\n  margin-top: -2.2rem;\n  position: absolute !important;\n  right: 2.2%;\n}\n\n.satrtDate {\n  width: 100%;\n  border-radius: 5px;\n}\n\n.endDate {\n  width: 100%;\n  border-radius: 5px;\n}\n\n.uploadCsv {\n  display: flex;\n  /* justify-content: end; */\n  margin-top: -5rem;\n  position: absolute !important;\n  right: 50%;\n  border-radius: 50px;\n  height: 43px !important;\n}\n\n.modalCloseBtn {\n  background: var(--primaryLight) !important;\n  border-color: var(--primaryDark) !important;\n  color: var(--primaryDark) !important;\n  border-radius: 50px !important;\n  margin-right: 15px;\n  transition: all 0.3s ease-in-out;\n}\n\n.modalCloseBtn:hover {\n  background: var(--primaryDark) !important;\n  border-color: var(--primaryDark) !important;\n  color: var(--primaryTextLight) !important;\n}\n\n.modalCloseBtnFilled {\n  background: var(--primaryDark) !important;\n  border-color: var(--primaryDark) !important;\n  color: var(--primaryTextLight) !important;\n  border-radius: 50px !important;\n  transition: all 0.3s ease-in-out;\n}\n\n.modalCloseBtnFilled:hover {\n  background: var(--primaryLight) !important;\n  border-color: var(--primaryDark) !important;\n  color: var(--primaryDark) !important;\n}\n\n.innerCon {\n  height: 100%;\n  display: flex;\n  flex-direction: column;\n  justify-content: space-between;\n}\n\n.innerSecCard {\n  display: flex;\n  justify-content: center;\n  align-items: center;\n}\n\n.innerSecCard .cardHeading h5 {\n  font-size: 13px;\n  margin-bottom: 0;\n}\n\n.innerSecCard .cardImg {\n  display: flex;\n  align-items: center;\n  margin-right: 15px;\n}\n\n.pd .ant-card-body {\n  padding: 10px 15px;\n  display: flex;\n  align-items: center;\n}\n\n.imgSec {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n}\n\n.imgCir {\n  width: 45px;\n  height: 45px;\n  border-radius: 50px;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  background: var(--secondaryDark);\n}\n\n/* .card1Bg {\n  background: var(--primaryDark);\n}\n.card2Bg {\n  background: #e6a827;\n}\n.card3Bg {\n  background: #a8bcc1;\n}\n.card4Bg {\n  background: #f44336;\n}\n.card5Bg {\n  background: #5baf25;\n} */\n.innerCon .textSec .cardHeading h5 {\n  font-size: 13px;\n}\n\n.innerCon .textSec .counterText h4 {\n  font-size: 44px;\n  font-weight: 300;\n  margin-bottom: 0;\n}\n\n.cardActive {\n  border: 2px solid var(--primaryDark);\n}\n\n.uploadBtn label {\n  font-size: 12px !important;\n  font-weight: 600;\n  color: var(--az-gray-580) !important;\n  margin-right: 5px;\n}\n\n.uploadBtn label::after {\n  display: none;\n}\n\n/* .uploadBtn .ant-upload-list-text {\n  position: absolute;\n  right: 6.3rem;\n  top: 2.5rem;\n} */\n.docUploadBtn {\n  background-color: var(--primaryLight) !important;\n  border-color: var(--primaryDark) !important;\n  border-radius: 32px !important;\n  color: var(--primaryTextLight) !important;\n  height: 25px !important;\n  padding: 2px !important;\n  font-size: 11px !important;\n  font-weight: 500 !important;\n  width: 90px !important;\n  display: flex !important;\n  /* justify-content: space-between; */\n  /* align-items: center; */\n}\n\n.upImg {\n  background: var(--az-white);\n  border-radius: 50%;\n  width: 19px;\n  height: 19px;\n  line-height: 19px;\n  margin-right: 15px;\n}\n\n.infoText {\n  font-size: 10px;\n  font-weight: 600;\n  color: var(--az-gray-580);\n  display: flex;\n  align-items: center;\n}\n\n/* SECTION 11a: AZ button system — matches Figma; variants primary/ghost/soft/secondary/danger, sizes lg/md(default)/sm */\n\n/* ── Base ─────────────────────────────────────────────── */\n.az-btn {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  gap: 6px;\n  height: 36px;\n  padding: 0 16px;\n  border: none;\n  border-radius: var(--az-radius-pill);\n  font-size: 13px;\n  font-weight: 500; /* Figma node 42:3001/42:3004: font-['Plus_Jakarta_Sans:Medium'] */\n  line-height: 1;\n  cursor: pointer;\n  white-space: nowrap;\n  transition:\n    background 0.15s,\n    opacity 0.15s;\n  text-decoration: none;\n  outline: none;\n  font-family: inherit;\n}\n.az-btn:focus-visible {\n  outline: 2px solid rgba(var(--colorDark-rgb, 0, 122, 255), 0.45);\n  outline-offset: 2px;\n}\n\n/* ── Sizes ────────────────────────────────────────────── */\n/* Figma node 42:3000/42:3003 specified h-[54px]/20px — standardized down to\n   44px/16px app-wide (matches .need-help-btn and .az-search-input, which\n   were already at this size) since this is the size used by the Filter and\n   Download trigger buttons throughout the app. */\n.az-btn--xl {\n  height: 44px;\n  padding: 0 24px;\n  font-size: 16px;\n  gap: 8px;\n}\n.az-btn--lg {\n  height: 44px;\n  padding: 0 24px;\n  font-size: 15px;\n  gap: 8px;\n}\n.az-btn--sm {\n  /* Figma's table row-action pill (Inspect/List, node 298:244/298:247): h-[40px] */\n  height: 40px;\n  padding: 0 16px;\n  font-size: 14px;\n  gap: 4px;\n}\n.az-btn--icon-only {\n  padding: 0;\n  width: 36px;\n}\n.az-btn--icon-only.az-btn--xl {\n  width: 44px;\n}\n.az-btn--icon-only.az-btn--lg {\n  width: 44px;\n}\n.az-btn--icon-only.az-btn--sm {\n  width: 40px;\n}\n\n/* ── Variants ─────────────────────────────────────────── */\n.az-btn--primary {\n  background: var(--colorDark, #007aff);\n  color: var(--az-white);\n}\n.az-btn--primary:hover:not(:disabled) {\n  background: var(--colorDark, #007aff);\n  filter: brightness(0.88);\n  color: var(--az-white);\n}\n.az-btn--primary:not(:hover):not(:disabled) {\n  filter: none;\n}\n\n.az-btn--ghost {\n  background: var(--colorLight, rgba(0, 122, 255, 0.07));\n  color: var(--colorDark, #007aff);\n}\n.az-btn--ghost:hover:not(:disabled) {\n  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.13);\n  color: var(--colorDark, #007aff);\n}\n\n/* White pill button — Figma node 42:3000/42:3003 (Download / Sort & Filters) */\n.az-btn--white {\n  background: var(--az-white);\n  border: 1px solid var(--az-gray-135);\n  color: var(--colorDark, #007aff);\n}\n.az-btn--white:hover:not(:disabled) {\n  background: var(--az-gray-50);\n  color: var(--colorDark, #007aff);\n}\n\n.az-btn--soft {\n  background: var(--colorLight, rgba(0, 122, 255, 0.07));\n  color: var(--colorDark, #007aff);\n}\n.az-btn--soft:hover:not(:disabled) {\n  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.13);\n  color: var(--colorDark, #007aff);\n}\n\n.az-btn--secondary {\n  background: var(--az-gray-135);\n  color: var(--az-gray-600);\n}\n.az-btn--secondary:hover:not(:disabled) {\n  background: var(--az-gray-300);\n  color: var(--az-gray-600);\n}\n\n.az-btn--danger {\n  background: rgba(var(--az-red-600-rgb), 0.08);\n  color: var(--az-red-600);\n}\n.az-btn--danger:hover:not(:disabled) {\n  background: rgba(var(--az-red-600-rgb), 0.14);\n  color: var(--az-red-600);\n}\n\n/* ── Disabled ─────────────────────────────────────────── */\n.az-btn:disabled,\n.az-btn[disabled] {\n  opacity: 0.4;\n  cursor: not-allowed;\n}\n\n/* Loading is a temporary busy state, not a permanent disabled state —\n   keep the button (and its label) fully visible, just swap in the spinner */\n.az-btn--loading:disabled,\n.az-btn--loading[disabled] {\n  opacity: 1;\n  cursor: wait;\n}\n\n/* ── Loading spinner ──────────────────────────────────── */\n.az-btn__spinner {\n  width: 14px;\n  height: 14px;\n  border: 2px solid currentColor;\n  border-top-color: transparent;\n  border-radius: 50%;\n  animation: az-btn-spin 0.6s linear infinite;\n}\n@keyframes az-btn-spin {\n  to {\n    transform: rotate(360deg);\n  }\n}\n\n/* ── Icon wrapper ─────────────────────────────────────── */\n.az-btn__icon {\n  display: flex;\n  align-items: center;\n  font-size: 1em;\n  line-height: 0;\n}\n\n.ant-notification {\n  z-index: 9999 !important;\n}\n\n/* ── Responsive ───────────────────────────────────────── */\n@media (max-width: 768px) {\n  .az-btn--lg {\n    height: 40px;\n    padding: 0 20px;\n    font-size: 14px;\n  }\n}\n\n/* SECTION 11a-2: Pill tabs — reusable segmented tab group (Figma node 42:4182/42:4184), replaces inline-styled tab buttons duplicated across several pages */\n.az-pill-tabs {\n  display: flex;\n  align-items: center;\n  gap: 8px;\n  flex-wrap: wrap;\n  padding-top: 10px;\n  margin-left: 10px;\n}\n.az-pill-tab {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  min-width: 176px;\n  border-radius: 12px;\n  padding: 20px 24px;\n  border: 1px solid rgba(var(--az-black-rgb), 0.08);\n  background: var(--az-gray-50);\n  color: rgba(var(--az-black-rgb), 0.6);\n  font-family: inherit;\n  font-size: 14px;\n  font-weight: 500;\n  line-height: 1.2;\n  white-space: nowrap;\n  cursor: pointer;\n  transition:\n    background 0.15s,\n    border-color 0.15s,\n    color 0.15s;\n}\n.az-pill-tab--active {\n  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.1);\n  border-color: rgba(var(--colorDark-rgb, 0, 122, 255), 0.4);\n  color: rgba(var(--az-black-rgb), 0.9);\n}\n\n/* ── Status variant (PillTabs variant=\"status\") — Enquiry/Order Dashboards,\n   most Reports pages: these pills are a STATUS filter, not sequential\n   stages, so they read as our standard button pill (.az-btn family) rather\n   than the Leads-Dashboard stage-card look above. Every pill is sized to\n   match the single widest one via JS (see PillTabs.js) instead of each\n   auto-sizing to its own label. ── */\n.az-pill-tabs--status {\n  row-gap: 8px;\n}\n.az-pill-tab--status {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  height: 36px;\n  padding: 0 16px;\n  border: none;\n  border-radius: var(--az-radius-pill);\n  background: var(--az-gray-135);\n  color: var(--az-gray-600);\n  font-family: inherit;\n  font-size: 13px;\n  font-weight: 500;\n  line-height: 1;\n  white-space: nowrap;\n  cursor: pointer;\n  transition:\n    background 0.15s,\n    color 0.15s;\n}\n.az-pill-tab--status:hover {\n  background: var(--az-gray-300);\n}\n.az-pill-tab--status-active {\n  background: var(--colorLight, rgba(0, 122, 255, 0.07));\n  color: var(--colorDark, #007aff);\n}\n.az-pill-tab--status-active:hover {\n  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.13);\n  color: var(--colorDark, #007aff);\n}\n\n/* ── Pill Select — height/border matched to Figma \"Select State/District\" ── */\n.az-pill-select .ant-select-selector {\n  border-radius: var(--az-radius-pill) !important;\n  height: 37px !important;\n  padding: 0 20px !important;\n  border-color: var(--az-gray-175) !important;\n}\n.az-pill-select .ant-select-selection-item,\n.az-pill-select .ant-select-selection-placeholder {\n  line-height: 35px !important;\n  font-size: 14px;\n  font-weight: 500;\n  color: rgba(var(--az-black-rgb), 0.9);\n}\n\n/* SECTION 11b: Status card — reusable stat card for listing pages */\n\n/* Scrollable strip container */\n.status-card-strip {\n  display: flex;\n  gap: 16px; /* Figma: gap-[16px] */\n  overflow-x: auto;\n  padding-bottom: 6px;\n  margin-bottom: 16px;\n  position: relative;\n  scrollbar-width: none;\n  -ms-overflow-style: none;\n}\n.status-card-strip::-webkit-scrollbar {\n  display: none;\n}\n\n/* Dot connector between cards — Figma node 44:6486 — overlaps the strip's\n   16px gap on both sides so it sits centered between the two cards.\n   margin-top = step-label-area(26px) + (card-height(108px) - connector-height(9px))/2\n   = 26 + 49.5 ≈ 75px — aligns the dot at the card's vertical centre. */\n.status-connector {\n  flex-shrink: 0;\n  align-self: flex-start;\n  margin-top: 75px;\n  margin-left: -16px;\n  margin-right: -16px;\n  z-index: 1;\n}\n\n/* Edge fades — only rendered when StatusCardStrip's scroll-position check\n   says there's actually more content in that direction (--fade-left /\n   --fade-right). Without that gating, a static always-on fade visually\n   dims the last real card even when it's fully visible and there's\n   nothing left to scroll to. */\n.status-card-strip-wrap {\n  position: relative;\n}\n.status-card-strip-wrap--fade-right::after,\n.status-card-strip-wrap--fade-left::before {\n  content: \"\";\n  pointer-events: none;\n  position: absolute;\n  top: 0;\n  width: 48px;\n  height: 100%;\n  z-index: 2;\n}\n.status-card-strip-wrap--fade-right::after {\n  right: 0;\n  background: linear-gradient(\n    to right,\n    rgba(var(--az-gray-50-rgb), 0),\n    var(--az-gray-50)\n  );\n}\n.status-card-strip-wrap--fade-left::before {\n  left: 0;\n  background: linear-gradient(\n    to left,\n    rgba(var(--az-gray-50-rgb), 0),\n    var(--az-gray-50)\n  );\n}\n/* Inside the white \"hero\" card the strip fades to white, not page-gray */\n.page-card-body--hero .status-card-strip-wrap--fade-right::after {\n  background: linear-gradient(\n    to right,\n    rgba(var(--az-white-rgb), 0),\n    var(--az-white)\n  );\n}\n.page-card-body--hero .status-card-strip-wrap--fade-left::before {\n  background: linear-gradient(\n    to left,\n    rgba(var(--az-white-rgb), 0),\n    var(--az-white)\n  );\n}\n\n/* Individual card — Figma: p-[20px] gap-[4px] rounded-[12px] shrink-0 w-[160px] */\n.status-card {\n  flex-shrink: 0;\n  width: 160px; /* Figma: exact fixed width (120px content + 20px pad × 2) */\n  overflow: hidden;\n  background: var(--az-gray-50);\n  border: 1px solid var(--az-border-subtle);\n  border-radius: var(--az-radius-md);\n  padding: var(--az-space-5);\n  display: flex;\n  flex-direction: column;\n  gap: var(--az-space-1); /* Figma: gap-[4px] */\n  cursor: pointer;\n  transition: border-color 0.15s;\n  user-select: none;\n}\n.status-card:hover {\n  border-color: rgba(var(--colorDark-rgb, 0, 122, 255), 0.25);\n}\n.status-card--active {\n  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.08);\n  border-color: rgba(var(--colorDark-rgb, 0, 122, 255), 0.4);\n}\n\n/* Column wrapper — holds the step label ABOVE the card box (outside its\n   border/background), matching Figma node 44:6774 where step labels are a\n   separate row above the card strip. All wrappers render the step div (even\n   empty) so every column is the same height → connector dot aligns exactly\n   at card vertical centre. */\n.status-card-col {\n  display: flex;\n  flex-direction: column;\n  flex-shrink: 0;\n  margin-left: 10px;\n  margin-top: 10px;\n}\n\n/* Step label — Figma node 44:6774: 12px Medium, 60% black, always 22px\n   tall (min-height) so wrappers stay equal height even without a step. */\n.status-card__step {\n  min-height: 22px;\n  line-height: 22px;\n  margin-bottom: 4px;\n  font-size: 12px;\n  font-weight: 500;\n  color: rgba(var(--az-black-rgb), 0.6);\n  white-space: nowrap;\n}\n.status-card-col:has(.status-card--active) .status-card__step {\n  color: rgba(var(--az-black-rgb), 0.9);\n}\n\n/* Label — Figma: h-[34px] flex-col justify-center text-[14px] font-medium text-[rgba(0,0,0,0.6)] leading-[1.2] w-[120px] */\n.status-card__label {\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n  height: 34px; /* Figma: h-[34px] — fixed height holds up to 2 lines */\n  overflow: hidden;\n  font-size: 14px; /* Figma: text-[14px] */\n  font-weight: 500;\n  color: var(--az-text-secondary);\n  line-height: 1.2; /* Figma: leading-[1.2] */\n  overflow-wrap: break-word;\n}\n\n/* Count — Figma: text-[20px] font-bold text-[rgba(0,0,0,0.8)] leading-[1.5] */\n.status-card__count {\n  font-size: 20px;\n  font-weight: 700;\n  color: rgba(var(--az-black-rgb), 0.8);\n  line-height: 1.5; /* Figma: leading-[1.5] */\n}\n.status-card__count--zero {\n  color: var(\n    --az-text-tertiary\n  ); /* Figma: text-[rgba(0,0,0,0.5)] for zero values */\n}\n.status-card--active .status-card__count {\n  color: var(--az-text-primary);\n}\n.status-card--active .status-card__label {\n  color: rgba(var(--az-black-rgb), 0.7);\n}\n\n/* Responsive: on small screens let cards shrink a bit */\n@media (max-width: 768px) {\n  .status-card {\n    width: 130px;\n    padding: 14px 16px;\n  }\n  .status-card__label {\n    font-size: 12px;\n  }\n  .status-card__count {\n    font-size: 17px;\n  }\n}\n\n/* SECTION 11c: Generic card — reusable tile for boxed content that isn't a full page or a stat strip; used for Manage Clients org cards */\n/* Sized to match the Product Movement stage tiles (.customTabCard /\n   MovementTile — Figma's fixed 203x150 card, see SECTION 11d below) so the\n   platform's Manage Clients cards read as the same design-system card\n   size, not a bespoke bigger one. */\n.az-card {\n  position: relative;\n  width: 203px;\n  height: 150px;\n  background: var(--az-white);\n  border-radius: var(--az-radius-md);\n  border: 1px solid var(--az-gray-300);\n  overflow: hidden;\n  cursor: pointer;\n  display: flex;\n  flex-direction: column;\n  transition:\n    border-color 0.2s,\n    box-shadow 0.2s;\n}\n.az-card__edit-btn {\n  position: absolute;\n  top: 10px;\n  right: 10px;\n  z-index: 2;\n  width: 26px;\n  height: 26px;\n  border-radius: 50%;\n  border: none;\n  background: rgba(var(--az-black-rgb), 0.06);\n  color: var(--az-gray-630);\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  font-size: 13px;\n  cursor: pointer;\n  opacity: 0;\n  transition:\n    opacity 0.15s,\n    background 0.15s,\n    color 0.15s;\n}\n.az-card:hover .az-card__edit-btn {\n  opacity: 1;\n}\n.az-card__edit-btn:hover {\n  background: var(--az-card-accent, rgba(0, 0, 0, 0.1));\n  color: var(--colorDark, #007aff);\n}\n/* Set --az-card-accent inline per-instance (theme color) to tint the\n   border on hover; falls back to the default gray border if unset. */\n.az-card:hover {\n  border-color: var(--az-card-accent, var(--az-gray-300));\n  box-shadow: 0 4px 16px rgba(var(--az-black-rgb), 0.1);\n}\n.az-card__accent-bar {\n  height: 5px;\n  flex-shrink: 0;\n}\n.az-card__body {\n  flex: 1;\n  min-height: 0;\n  padding: 14px 16px;\n  display: flex;\n  flex-direction: column;\n}\n.az-card__eyebrow {\n  font-size: 11px;\n  color: var(--az-gray-480);\n  margin-bottom: 10px;\n  letter-spacing: 0.3px;\n}\n.az-card__header {\n  display: flex;\n  align-items: center;\n  gap: var(--az-space-3);\n}\n.az-card__avatar {\n  width: 38px;\n  height: 38px;\n  border-radius: var(--az-radius-sm);\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  font-weight: 700;\n  font-size: 14px;\n  flex-shrink: 0;\n}\n.az-card__title {\n  font-weight: 700;\n  font-size: 14px;\n  color: var(--az-gray-950);\n  line-height: 1.3;\n  flex: 1;\n  min-width: 0;\n}\n.az-card__meta {\n  font-size: 12px;\n  color: var(--az-gray-630);\n  line-height: 1.8;\n}\n\n/* SECTION 11d: Movement group — reusable stage-group tile used on Material/Money Movement, replaces old ad-hoc inline-bordered boxes */\n/* Grouping container — a light gray background + border box around a\n   set of stage tiles (e.g. \"INBOUND\", \"CURRENTLY WITH DPP\"), shared by\n   Material Movement and Money Movement. Not a clickable tile itself\n   (no cursor/hover) — just a visual section boundary; the individual\n   .customTabCard tiles inside carry their own border on top of this. */\n.az-movement-group {\n  height: 100%;\n  background: var(--az-gray-50);\n  border: 1px solid var(--az-border-subtle);\n  border-radius: var(--az-radius-md);\n  padding: var(--az-space-3);\n}\n.az-movement-group__header {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  flex-wrap: wrap;\n  gap: var(--az-space-2);\n  padding: var(--az-space-2) var(--az-space-1);\n  margin-bottom: var(--az-space-2);\n}\n.az-movement-group__title {\n  display: flex;\n  align-items: center;\n  gap: var(--az-space-2);\n  font-size: 15px;\n  font-weight: 700;\n  color: var(--az-text-primary);\n  text-transform: uppercase;\n  letter-spacing: 0.2px;\n}\n.az-movement-group__totals {\n  display: flex;\n  flex-wrap: wrap;\n  gap: var(--az-space-4);\n  font-size: 13px;\n  color: var(--az-text-secondary);\n}\n.az-movement-group__totals b {\n  color: var(--az-text-primary);\n  font-weight: 700;\n}\n/* Centered variant for groups whose tiles stack vertically with a plain\n   centered title (e.g. \"CURRENTLY WITH DPP\") instead of a title+totals bar */\n.az-movement-group__title--center {\n  display: block;\n  text-align: center;\n  margin-bottom: var(--az-space-2);\n}\n.az-movement-group__tiles {\n  display: flex;\n  flex-direction: column;\n  /* Matches Material Movement's measured Figma stacked-tile gap (18px, see\n     .material-movement-grid/colStack) so both pages read as one design. */\n  gap: 18px;\n  align-items: stretch;\n}\n.az-movement-group__tiles--row {\n  flex-direction: row;\n  justify-content: center;\n  flex-wrap: wrap;\n}\n\n/* Plays every time the tile grid panel mounts — i.e. every time you go\n   \"back\" from the compact bar — mirroring the bar's own enter animation\n   so the whole toggle reads as one continuous expand/collapse motion.\n   Scoped to its own class (not added to .page-card-body--hero directly)\n   since that class is shared with many unrelated pages. */\n@keyframes az-dashboard-panel-enter {\n  from {\n    opacity: 0;\n    transform: translateY(-12px);\n  }\n  to {\n    opacity: 1;\n    transform: translateY(0);\n  }\n}\n.az-dashboard-panel-enter {\n  animation: az-dashboard-panel-enter 0.28s ease-out;\n}\n\n/* SECTION 11d-2: Dashboard grid — shared by Material/Money Movement; fixed-width tracks (not 1fr) keep every card-to-card gap pixel-identical to Figma at any screen width — a bigger screen just centers the same layout with more margin instead of stretching columns around cards that stay a fixed 203px */\n.az-movement-dashboard-grid {\n  display: grid;\n  /* auto-fit + a fixed 203px track (not minmax(...,1fr)) keeps every\n     column at exactly its Figma width — never smaller (so cards don't get\n     squeezed) and never bigger either. A 1fr track would grow to fill\n     whatever extra width the panel has, but the 203px-capped tile inside\n     it wouldn't grow with it, leaving uneven dead space around whichever\n     cards happened to sit in a stretched column. Fixed tracks avoid that\n     entirely: the whole row is always the same size, on any screen. */\n  grid-template-columns: repeat(auto-fit, 203px);\n  gap: 60px;\n  /* stretch (not start): the divider between groups (::before below) is\n     anchored to top:0/bottom:0 of its own grid item, so that item needs to\n     span the full row height — with align-items:start each item only sizes\n     to its own content, and a shorter neighboring column's divider stops\n     short of the taller column beside it instead of reaching the bottom. */\n  align-items: stretch;\n  /* On a wide/monitor screen the fixed-width row no longer reaches the\n     panel's edges — center it so the leftover space becomes balanced\n     margin on both sides instead of one lopsided gap on the right. */\n  justify-content: center;\n}\n.az-movement-dashboard-grid__group {\n  position: relative;\n  min-width: 203px;\n  grid-column: span 1;\n  /* Material Movement always gives this exactly one child (its own\n     internal colStack/gap wrapper), so this has no effect there — it\n     exists so Money Movement/FC Collection Ledger's two direct children\n     (plain header tile, then the .az-movement-group__tiles stack) get the\n     same 18px base gap as Material Movement's tile columns, instead of\n     touching with 0px. */\n  display: flex;\n  flex-direction: column;\n  gap: 18px;\n}\n.az-movement-dashboard-grid__group--wide {\n  grid-column: span 2;\n}\n/* Money Movement's \"CURRENTLY WITH FC\" group (COLLECTED AT FC + PENDING\n   REVIEW) — on a bigger-than-current screen this widens to a 2-column\n   group and lays its two tiles side by side, the same way Material\n   Movement's \"STOCK AT FC\" group already does. Scoped to just this one\n   group/class pair (not a blanket rule on every group) because that's\n   what the design calls for here — e.g. \"CURRENTLY WITH DPP\"'s pair stays\n   stacked at any width. Below the breakpoint both classes are no-ops, so\n   the group renders exactly as it does everywhere else. */\n@media (min-width: 1600px) {\n  .az-movement-dashboard-grid__group--wide-lg {\n    grid-column: span 2;\n  }\n  .az-movement-group__tiles--wide-lg {\n    flex-direction: row;\n    flex-wrap: wrap;\n  }\n}\n/* Divider line between groups — Figma's rotated Line/Line8 nodes */\n.az-movement-dashboard-grid__group:not(:first-child)::before {\n  content: \"\";\n  position: absolute;\n  top: 0;\n  bottom: 0;\n  left: -31px;\n  width: 1px;\n  background: var(--az-border-subtle);\n}\n/* Below this width even two 203px columns plus the 60px gutter no longer\n   fit alongside the divider's own offset, so once auto-fit is down to a\n   single column, collapse the \"wide\" span and drop the divider (it would\n   otherwise draw a line above every wrapped row instead of just between\n   side-by-side groups). */\n@media (max-width: 640px) {\n  .az-movement-dashboard-grid__group--wide {\n    grid-column: span 1;\n  }\n  .az-movement-dashboard-grid__group:not(:first-child)::before {\n    display: none;\n  }\n}\n\n/* Deposit Approval always has exactly 2 \"--wide\" (span 2) groups. A fixed\n   grid-template-columns track (e.g. repeat(4, 203px)) fixes the auto-fit\n   column-count ambiguity (see below) but doesn't shrink — at any effective\n   viewport narrower than ~1200px (a smaller window, or browser zoom above\n   ~125%) the fixed 992px-wide track overflows the panel instead of\n   reflowing. Flex does what grid can't here: flex-wrap lets the Online\n   group drop below Cash once there's no room for both, and\n   justify-content:center (inherited from .az-movement-dashboard-grid)\n   applies per wrapped line, so each group re-centers on its own row\n   instead of leaving the whole pair pinned to one side — exactly the\n   graceful degradation a fixed column count can't provide. Each group's\n   own width comes from its content (2×203px cards + 18px gap = 424px via\n   .az-movement-group__tiles--row's existing centering), so this needs no\n   width math of its own, unlike the grid-column-span approach. */\n.az-movement-dashboard-grid--fixed-4 {\n  display: flex;\n  flex-wrap: wrap;\n}\n/* Each section grows to claim an equal half of the available width (not\n   just its own 424px content width) so its 2 cards center within that\n   whole half — Cash centered in the left half, Online centered in the\n   right half — instead of the pair clustering together in the middle of\n   a much wider panel with dead space on both outer edges. min-width keeps\n   a section from being squeezed smaller than its 2 cards actually need;\n   below that, flex-wrap (above) drops it to its own full-width row instead,\n   where it then centers across the whole row exactly the same way. */\n.az-movement-dashboard-grid--fixed-4 .az-movement-dashboard-grid__group--wide {\n  flex: 1 1 424px;\n  min-width: 424px;\n}\n/* Phones: the content column is narrower than 2 cards (424px) — let the\n   group shrink so its tiles row (already flex-wrap) stacks the cards\n   instead of overflowing the page sideways. */\n@media (max-width: 640px) {\n  .az-movement-dashboard-grid--fixed-4 .az-movement-dashboard-grid__group--wide {\n    min-width: 0;\n    flex-basis: 100%;\n  }\n}\n/* Container query (not a viewport media query) because the actual wrap\n   point depends on this element's own rendered width — which shifts with\n   sidebar collapsed/expanded state and isn't reliably tied to viewport\n   width alone. Threshold matches the two groups' combined natural width\n   (2 × 424px + 60px gap = 908px): below it Online has already wrapped\n   under Cash, so the vertical divider (meant to sit between side-by-side\n   groups) would draw a stray line to the left of the wrapped row instead. */\n.az-movement-dashboard-grid--fixed-4 {\n  container-type: inline-size;\n}\n@container (max-width: 907px) {\n  .az-movement-dashboard-grid--fixed-4\n    .az-movement-dashboard-grid__group:not(:first-child)::before {\n    display: none;\n  }\n}\n\n/* SECTION 11d-3: Flow toggle — \"Forward Flow\"/\"Reverse Flow\" segmented pill switch (Figma node 298:328) */\n.az-flow-toggle {\n  display: inline-flex;\n  align-items: center;\n  background: var(--az-white);\n  border: 1px solid var(--az-gray-135);\n  border-radius: var(--az-radius-pill);\n  height: 44px;\n  padding: 6px;\n  gap: 0;\n}\n.az-flow-toggle__option {\n  width: 155px;\n  height: 36px;\n  border: none;\n  border-radius: var(--az-radius-pill);\n  background: transparent;\n  color: var(--colorDark, #007aff);\n  font-family: inherit;\n  font-size: 16px;\n  font-weight: 500;\n  cursor: pointer;\n  transition:\n    background 0.15s,\n    color 0.15s;\n}\n.az-flow-toggle__option--active {\n  background: var(--colorDark, #007aff);\n  color: var(--az-white);\n}\n@media (max-width: 480px) {\n  .az-flow-toggle__option {\n    width: auto;\n    padding: 0 16px;\n  }\n}\n\n/* SECTION 11e: Flow summary bar — collapsible totals bar for Material Movement (Figma node 298:161/298:177) */\n/* Simple fade-in for bars that don't have a FLIP origin to animate from\n   (the COMPLETE FLOW bar). The per-stage compact bar skips this — its\n   caller (material-movement/index.js) drives its entrance via a JS FLIP\n   transform instead, so it needs a bare mount with no competing CSS\n   animation on the same `transform` property. */\n@keyframes az-flow-summary-bar-enter {\n  from {\n    opacity: 0;\n    transform: translateY(-10px) scaleY(0.95);\n  }\n  to {\n    opacity: 1;\n    transform: translateY(0) scaleY(1);\n  }\n}\n.az-flow-summary-bar--fade-in {\n  animation: az-flow-summary-bar-enter 0.25s ease-out;\n}\n.az-flow-summary-bar {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  gap: var(--az-space-4);\n  background: var(--az-white);\n  border-radius: 16px;\n  box-shadow: 0 0 5px rgba(var(--az-shadow-tint-rgb), 0.03);\n  padding: 20px;\n  margin-bottom: 5px;\n  cursor: pointer;\n  transition: box-shadow 0.15s;\n  transform-origin: top center;\n}\n.az-flow-summary-bar:hover {\n  box-shadow: 0 0 8px rgba(var(--az-shadow-tint-rgb), 0.1);\n}\n.az-flow-summary-bar:focus-visible {\n  outline: 2px solid rgba(var(--colorDark-rgb, 0, 122, 255), 0.45);\n  outline-offset: 2px;\n}\n/* Static/non-interactive bar (COMPLETE FLOW) — no click affordance */\n.az-flow-summary-bar--static {\n  cursor: default;\n}\n.az-flow-summary-bar--static:hover {\n  box-shadow: 0 0 5px rgba(var(--az-shadow-tint-rgb), 0.03);\n}\n.az-flow-summary-bar__title {\n  display: flex;\n  align-items: center;\n  gap: var(--az-space-2);\n  font-size: 16px;\n  font-weight: 700;\n  color: var(--az-text-secondary);\n  text-transform: uppercase;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  /* Fixed (not min-) so the stats column starts at the same x-position in\n     every bar regardless of title length — otherwise \"Total SKUs\"/\"Value\"\n     visibly jumps left/right between the summary bar and the compact\n     per-stage bar stacked right below it. 350px comfortably fits the\n     longest title actually in use (\"PENDING ONLINE DEPOSIT CONFIRMATION\",\n     ~338px at this weight/size) with headroom; overflow+ellipsis is just a\n     safety net so a future longer title truncates instead of breaking the\n     shared alignment. */\n  width: 350px;\n  flex-shrink: 0;\n}\n.az-flow-summary-bar__stats {\n  display: flex;\n  align-items: center;\n  gap: 16px;\n  flex-wrap: wrap;\n  margin-left: 16px;\n  margin-right: auto;\n}\n.az-flow-summary-bar__stat-group {\n  display: flex;\n  align-items: center;\n  gap: 12px;\n  font-size: 16px;\n  white-space: nowrap;\n}\n.az-flow-summary-bar__stat-label {\n  color: var(--az-text-secondary);\n  font-weight: 500;\n}\n.az-flow-summary-bar__stat-value {\n  color: var(--az-text-primary);\n  font-weight: 700;\n}\n.az-flow-summary-bar__dot {\n  width: 3px;\n  height: 3px;\n  border-radius: 50%;\n  background: var(--az-border-subtle);\n  flex-shrink: 0;\n}\n.az-flow-summary-bar__toggle {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: 20px;\n  height: 20px;\n  border: none;\n  background: transparent;\n  color: var(--az-text-primary);\n  cursor: pointer;\n  flex-shrink: 0;\n  padding: 0;\n}\n/* Below ~991px the fixed 350px title left too little room for the stats\n   group, forcing each stat onto its own line inside that leftover sliver\n   instead of sitting in a row. Let the bar wrap onto two rows instead —\n   title on top, stats filling their own full-width row underneath, still\n   wrapping internally if the viewport is narrower still. */\n@media (max-width: 991px) {\n  .az-flow-summary-bar {\n    flex-wrap: wrap;\n    row-gap: 8px;\n  }\n  .az-flow-summary-bar__title {\n    width: auto;\n    flex-shrink: 1;\n  }\n  .az-flow-summary-bar__stats {\n    width: 100%;\n    margin-left: 0;\n    margin-right: 0;\n  }\n}\n\n/* SECTION 12: Tables. Legacy raw-<Table> systems (stateCustomTable, deltaTable, lotCustomTable, nastedTable, punch-order-table, custom-table) were removed — every table now renders through CustomTable/.az-table (SECTION 11a); keeping them risked a page re-attaching one and fighting .az-table's styling via matching-specificity !important rules, which happened before. */\n\n.ant-notification {\n  z-index: 9999 !important; /* Ensure the z-index is high enough */\n}\n\n.wareHouseCard:nth-child(even) {\n  background-color: var(--az-gray-175) !important;\n  border-bottom: 1px solid var(--primaryDark);\n}\n\n.wareHouseCard:nth-child(odd) {\n  border-bottom: 1px solid var(--primaryDark);\n}\n\n.wareHouseCard:last-child {\n  border-bottom: 0 !important;\n}\n\n.textTransform table > tbody > tr td:first-child {\n  text-transform: capitalize;\n}\n\n.textTransform table > tbody > tr td:nth-child(2) {\n  text-transform: uppercase;\n}\n\n/* SECTION 13: Custom side menu (AntD Menu) */\n\n/***** Custom Side Menu *****/\n.customSideMenu {\n  background: transparent !important;\n  overflow-y: auto;\n  height: calc(100% - 70px);\n  padding-right: 0 !important;\n  margin-right: 0 !important;\n  width: 100% !important;\n}\n\n.customSideMenu.ant-menu-vertical {\n  border-right: 0;\n}\n\n.customSubMenu {\n  border-bottom: 1px solid rgba(var(--az-black-rgb), 0.08);\n  color: var(--az-gray-950);\n}\n\n.ant-menu-item {\n  border-bottom: 1px solid rgba(var(--az-black-rgb), 0.08);\n  color: var(--az-gray-950);\n}\n\n/* .ant-menu-vertical > .ant-menu-item .ant-menu-title-content a:hover {\n  color: #954c0a !important;\n} */\n.customSideMenu .ant-menu-sub.ant-menu-inline {\n  background: transparent !important;\n  padding-right: 0 !important;\n  margin-right: 0 !important;\n  width: 100% !important;\n}\n\n.customSideMenu .ant-menu-sub.ant-menu-inline .ant-menu-item {\n  height: 38px !important;\n  line-height: 38px !important;\n  width: 100% !important;\n  padding-right: 12px !important;\n  margin: 0 !important;\n  display: flex !important;\n  align-items: center !important;\n  border-bottom: none !important;\n}\n\n/* Depth-2 pages: align text with 2nd letter of submodule title (submodule at 52px + ~8px) */\n.customSideMenu\n  .ant-menu-sub.ant-menu-inline\n  .ant-menu-sub.ant-menu-inline\n  .ant-menu-item {\n  padding-left: 60px !important;\n}\n\n/* Sub-item title content — block so overflow clips text cleanly */\n.customSideMenu\n  .ant-menu-sub.ant-menu-inline\n  .ant-menu-item\n  .ant-menu-title-content {\n  display: block !important;\n  width: 100% !important;\n  max-width: 100% !important;\n  overflow: hidden !important;\n  text-overflow: ellipsis !important;\n  white-space: nowrap !important;\n  padding-left: 7px !important;\n}\n\n/* Sub-item links */\n.customSideMenu .ant-menu-sub.ant-menu-inline .ant-menu-item a {\n  color: var(--az-gray-650) !important;\n  font-size: 12px !important;\n  font-weight: 400;\n  display: block !important; /* block so text-overflow works */\n  width: 100%;\n  max-width: 100%;\n  overflow: hidden !important;\n  text-overflow: ellipsis !important;\n  white-space: nowrap !important;\n  line-height: 38px;\n}\n\n/* Span inside sub-item link also needs overflow control */\n.customSideMenu .ant-menu-sub.ant-menu-inline .ant-menu-item a span {\n  display: block;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n  max-width: 100%;\n}\n\n/* Inactive sub-item text — force #444 to prevent orange inheritance */\n.customSideMenu .ant-menu-sub .ant-menu-item:not(.ant-menu-item-selected) a,\n.customSideMenu\n  .ant-menu-sub\n  .ant-menu-item:not(.ant-menu-item-selected)\n  a\n  span {\n  color: var(--az-gray-650) !important;\n}\n\n/* ── Depth-1 submodule titles: align text with 2nd letter of module title\n   Module text starts at ~44px (14px pad + 20px icon + 10px gap); +8px = 52px  */\n.customSideMenu\n  .ant-menu-sub.ant-menu-inline\n  > .ant-menu-submenu\n  > .ant-menu-submenu-title {\n  padding-left: 52px !important;\n}\n\n/* ── Depth-1 direct pages (Finance, Reports — directPages with no submodule) ──\n   Same indent as submodule titles so they align with module text's 2nd letter. */\n.customSideMenu .ant-menu-sub.ant-menu-inline > .ant-menu-item {\n  height: 42px !important;\n  line-height: 42px !important;\n  padding-left: 52px !important;\n}\n\n.customSideMenu .ant-menu-sub.ant-menu-inline > .ant-menu-item a {\n  color: var(--az-gray-750) !important;\n  font-size: 13px !important;\n  font-weight: 500;\n  line-height: 42px;\n}\n\n.customSideMenu .ant-menu-sub.ant-menu-inline > .ant-menu-item a span {\n  font-size: 13px !important;\n  font-weight: 500;\n}\n\n/* Inactive depth-1 direct page — keep #333 (overrides the #444 rule above) */\n.customSideMenu\n  .ant-menu-sub.ant-menu-inline\n  > .ant-menu-item:not(.ant-menu-item-selected)\n  a,\n.customSideMenu\n  .ant-menu-sub.ant-menu-inline\n  > .ant-menu-item:not(.ant-menu-item-selected)\n  a\n  span {\n  /* color: black !important; */\n}\n\n.customSideMenu .ant-menu-sub.ant-menu-inline .ant-menu-item a:hover,\n.customSideMenu .ant-menu-sub .ant-menu-item:hover a,\n.customSideMenu .ant-menu-sub .ant-menu-item:hover a span {\n  color: var(--primaryDark) !important;\n}\n\n.customSideMenu .ant-menu-title-content,\n.customSideMenu .ant-menu-title-content a {\n  /* color: #bla !important; */\n  font-size: 13px !important;\n  font-weight: 500;\n  text-align: left !important;\n  white-space: nowrap !important;\n  overflow: hidden !important;\n  text-overflow: ellipsis !important;\n  line-height: 1.3 !important;\n  flex: 1;\n  min-width: 0;\n  padding: 0 !important;\n}\n\n.customSideMenu > .ant-menu-submenu {\n  padding-bottom: 0px !important;\n  /* padding-left: -10px !important; */\n}\n\n.customSideMenu > .ant-menu-submenu > .ant-menu-submenu-title,\n.customSubMenu > .ant-menu-submenu > .ant-menu-submenu-title,\n.customSideMenu > .ant-menu-item {\n  height: 46px !important;\n  line-height: 46px !important;\n  padding: 0 12px 0 14px !important;\n  display: flex !important;\n  align-items: center !important;\n  width: 100% !important;\n  margin: 0 !important;\n  border-radius: 0 !important;\n  white-space: nowrap !important;\n  overflow: hidden !important;\n  box-sizing: border-box !important;\n}\n/* .customSubMenu > .ant-menu-submenu-title {\n  display: flex;\n  align-items: start;\n  justify-content: space-between;\n  margin: 0 !important;\n  width: 100% !important;\n  padding-right: 0 !important;\n} */\n\n.customSubMenu > .ant-menu-submenu-title .ant-menu-item-icon + span {\n  margin-left: 0px !important;\n}\n\n.customSideMenu > .ant-menu-submenu > .ant-menu-submenu-title:hover,\n.customSideMenu > .ant-menu-item:hover {\n  border-radius: 0 !important;\n  transition: background 0.2s ease;\n  width: 100% !important;\n  margin: 0 !important;\n  padding: 0 12px 0 14px !important;\n  height: 46px !important;\n  display: flex !important;\n  flex-direction: row;\n  align-items: center;\n  justify-content: flex-start;\n}\n\n/* ================= POPUP SUBMENU FIX ================= */\n\n/* Popup submenu title layout */\n.ant-menu-submenu-popup .ant-menu-submenu-title {\n  display: flex !important;\n  flex-direction: row !important;\n  align-items: center !important;\n  justify-content: space-between !important;\n  text-align: left !important;\n}\n\n/* Popup submenu text */\n.ant-menu-submenu-popup .ant-menu-title-content {\n  text-align: left !important;\n  width: auto !important;\n  display: inline-flex !important;\n  margin-right: 10px;\n  padding-right: 30px;\n}\n\n/* Gap between icon and label is handled by icon margin-right in .sidebar.open .ant-menu-item-icon */\n.ant-menu-item .ant-menu-item-icon + span,\n.ant-menu-submenu-title .ant-menu-item-icon + span {\n  margin-left: 0 !important;\n}\n\n.customSideMenu.ant-menu-inline {\n  border-right: 0 !important;\n  padding-right: 0 !important;\n  margin-right: 0 !important;\n}\n\n.customSideMenu.ant-menu-inline .ant-menu-item {\n  width: 100% !important;\n}\n\n.sidebar .customSideMenu > li > div > .ant-menu-submenu-arrow {\n  opacity: 0;\n  color: rgba(var(--az-black-rgb), 0.4);\n}\n\n.sidebar.open .customSideMenu > li > div > .ant-menu-submenu-arrow {\n  opacity: 1;\n  color: rgba(var(--az-black-rgb), 0.45);\n  transition: all 0.1s;\n}\n\n/* sub sub menu selected*/\n.ant-menu:not(.ant-menu-horizontal) .ant-menu-item-selected {\n  background-color: var(--primaryDark) !important;\n  border-radius: 0px !important;\n  width: 100% !important;\n  margin-right: 0 !important;\n  margin-bottom: 0 !important;\n}\n\n/* Sub-page selected overrides the broad rule above — warm-white bg + dark text */\n.customSideMenu .ant-menu-sub .ant-menu-item-selected {\n  background-color: transparent !important;\n  border-left: 3px solid var(--primaryDark) !important;\n}\n\n.ant-menu-inline .ant-menu-item::after {\n  display: none !important;\n}\n\n.ant-menu-inline {\n  height: calc(100% - 80px);\n  overflow-y: auto;\n  overflow-x: hidden;\n}\n/* ant all sub menu */\n.ant-menu-submenu-popup .ant-menu-sub {\n  background-color: var(--primaryLight) !important;\n  padding-right: 10px;\n  gap: 10px;\n}\n\n.ant-menu-submenu-popup .ant-menu-sub .ant-menu-item {\n  border-bottom: 1px solid var(--az-gray-50);\n  margin: 0 !important;\n  width: 100% !important;\n  padding-right: 0 !important;\n  text-align: start !important;\n}\n\n.ant-menu-submenu-popup .ant-menu-sub .ant-menu-item:last-child {\n  border-bottom: 0;\n}\n\n.ant-menu-submenu-popup .ant-menu-sub .ant-menu-item .ant-menu-title-content a {\n  color: var(--az-white);\n  display: flex !important;\n  justify-content: start !important;\n  align-items: center !important;\n  text-align: start !important;\n  width: 100% !important;\n  padding-right: 10px;\n}\n.ant-menu-item,\n.ant-menu-submenu-title,\n.ant-menu-submenu-popup .ant-menu-item {\n  color: var(--az-white) !important;\n}\n\n/* Hover */\n.ant-menu-item:hover,\n.ant-menu-submenu-title:hover,\n.ant-menu-submenu-popup .ant-menu-item:hover {\n  color: var(--az-white) !important;\n}\n\n/* Selected menu item background */\n.ant-menu-item-selected {\n  background-color: var(--primaryDark) !important;\n  color: var(--az-white) !important;\n}\n\n/* Selected submenu item */\n.ant-menu-submenu-popup .ant-menu-item-selected {\n  background-color: var(--primaryDark) !important;\n  color: var(--az-white) !important;\n}\n\n/* Remove AntD default blue indicator */\n.ant-menu-item-selected::after {\n  border-right: none !important;\n}\n\n/* SECTION 14: Timeline section */\n\n.timlineSection {\n  width: 100%;\n  height: 100%;\n  position: relative;\n  transition: all 0.5s ease;\n}\n\n.timlineSection .timelineSectionInner {\n  position: fixed;\n  right: 0;\n  height: 100%;\n  transition: all 0.5s ease;\n}\n\n.timlineSection .timelineHeader {\n  background: var(--az-gray-350);\n  padding: 13px 31px;\n  border-top-left-radius: 14px;\n}\n\n.timlineSection .timelineHeader h5 {\n  margin-bottom: 0 !important;\n}\n\n.timlineSection .timelineBody {\n  padding: 30px;\n  height: calc(90% - 100px);\n  overflow-y: scroll;\n}\n\n.timlineSection\n  .timelineBody\n  .ant-timeline\n  .ant-timeline-item\n  .ant-timeline-item-head-blue {\n  color: var(--az-slate-300) !important;\n  border-color: var(--az-slate-300) !important;\n  background: var(--az-slate-300) !important;\n}\n\n.timlineSection\n  .timelineBody\n  .ant-timeline\n  .ant-timeline-item\n  .ant-timeline-item-content\n  span {\n  background: var(--az-gray-135);\n  width: auto;\n  display: inline-block;\n  color: var(--az-black);\n  padding: 5px;\n  border-top-right-radius: 16px;\n  border-bottom-right-radius: 16px;\n}\n\n.subTimeline {\n  position: relative;\n  top: 1rem;\n  right: 1.5rem;\n  padding-bottom: 0;\n}\n\n.subTimeline .ant-timeline-item .ant-timeline-item-head-blue {\n  width: 6px !important;\n  height: 6px !important;\n}\n\n.subTimeline .ant-timeline-item-last .ant-timeline-item-content {\n  min-height: 0 !important;\n}\n\n.subTimeline .ant-timeline-item .ant-timeline-item-tail {\n  display: none !important;\n}\n\n.subTimeline .ant-timeline-item .ant-timeline-item-content span {\n  background: transparent !important;\n  opacity: 0.2;\n  font-style: italic;\n}\n\n.ant-timeline-item-last > .ant-timeline-item-tail {\n  display: block !important;\n}\n\n/* SECTION 15: Variant / product page */\n\n/***** Varint page Style *****/\n.greenText {\n  color: var(--secondaryDark) !important;\n}\n\n.uploadText {\n  color: var(--az-gray-630) !important;\n  font-weight: 500;\n}\n\n.h-100 {\n  height: auto !important;\n}\n\n.addProductForm .ant-form-item {\n  margin-bottom: 10px;\n}\n\n.borderRadius textarea,\n.borderRadius Select {\n  border-radius: 6px !important;\n  border: 1px solid var(--az-gray-310) !important;\n}\n\n.dragBox .ant-upload-drag {\n  border: 2px dashed var(--az-gray-450) !important;\n\n  border-radius: 6px !important;\n\n  padding: 10px;\n}\n\n.dragBox .ant-form-item-control-input-content > p + span {\n  display: flex;\n\n  justify-content: space-between;\n}\n\n/* .dragBox .ant-form-item-control-input-content > p + span .ant-upload-drag,\n\n.dragBox .ant-form-item-control-input-content > p + span .ant-upload-list-text {\n\n  width: 50%;\n\n  padding: 0 15px;\n\n} */\n\n.dragBox .ant-upload.ant-upload-drag p.ant-upload-text {\n  font-size: 14px;\n}\n\n.infoTexts {\n  display: flex;\n  align-items: center;\n  padding: 10px 0;\n}\n\n.infoTexts .anticon {\n  font-size: 24px;\n  color: var(--az-gray-450);\n  margin-right: 10px;\n}\n\n.addBtnMore {\n  background: transparent !important;\n  border: 0 !important;\n  box-shadow: none !important;\n  color: var(--az-gray-580) !important;\n}\n\n.addMoreBtn {\n  font-size: 18px;\n  color: var(--secondaryDark);\n  margin-right: 10px;\n}\n\n.addProductForm\n  .ant-col\n  .ant-select:not(.ant-select-customize-input)\n  .ant-select-selector {\n  border: 1px solid var(--az-gray-310);\n  border-radius: 6px;\n  height: 50px;\n  padding: 10px 20px;\n}\n\nspan.srch-icon {\n  padding: 5px 8px;\n  border: 1px solid var(--az-gray-350);\n  border-left: 0 !important;\n  background: var(--az-white);\n  border-radius: 2px;\n}\n\n.ant-table-filter-trigger {\n  color: var(--az-white) !important;\n  font-size: 16px !important;\n}\n\n.underLogoText {\n  /* margin-top: 18px; */\n  font-size: 20px;\n  font-weight: 500;\n  color: var(--az-black);\n}\n\n.site-form-item-icon .ant-input-prefix {\n  color: var(--az-gray-580) !important;\n}\n\n.ant-input-affix-wrapper:not(.ant-input-affix-wrapper-disabled):hover {\n  border-color: var(--primaryDark) !important;\n}\n\n.ant-input-affix-wrapper:focus,\n.ant-input-affix-wrapper-focused {\n  box-shadow: none !important;\n}\n\n/* Open (expanded) alone — NO highlight, just layout */\n.ant-menu-submenu-open .ant-menu-submenu-title {\n  border-radius: 0 !important;\n  display: flex;\n  flex-direction: row;\n  align-items: center;\n  justify-content: flex-start;\n}\n\n/* Open + selected — depth-0 module: darkest shade */\n.customSideMenu\n  > .ant-menu-submenu.ant-menu-submenu-open.ant-menu-submenu-selected\n  > .ant-menu-submenu-title {\n  background-color: var(--primaryDark) !important;\n  color: var(--az-white) !important;\n}\n/* Open + selected — depth-1 submodule: mid-shade for visual hierarchy */\n.customSideMenu\n  .ant-menu-sub\n  > .ant-menu-submenu.ant-menu-submenu-open.ant-menu-submenu-selected\n  > .ant-menu-submenu-title {\n  background-color: var(--primaryLight) !important;\n  color: var(--az-white) !important;\n}\n\n/* .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn,\n.ant-tabs-tab:hover {\n  color: #5baf24 !important;\n  text-shadow: none !important;\n} */\n\n/* AntD v5 menu active + hover fix */\n\n/* DEFAULT menu text — sidebar inline items use dark text (white bg) */\n.customSideMenu .ant-menu-item,\n.customSideMenu .ant-menu-submenu-title {\n  color: var(--az-gray-950) !important;\n  display: flex;\n  flex-direction: row;\n  align-items: center;\n  justify-content: flex-start;\n}\n\n/* Popup (collapsed sidebar flyout) stays white text on dark bg */\n.ant-menu-submenu-popup .ant-menu-item {\n  color: var(--az-white) !important;\n  display: flex;\n  flex-direction: row;\n  align-items: center;\n  justify-content: flex-start;\n}\n.sidebar-menu-item {\n  display: flex;\n  flex-direction: row;\n  align-items: center;\n  justify-content: flex-start;\n  gap: 8px;\n}\n\n/* HOVER (non-selected only) — module headers use dark bg + white text */\n.ant-menu-item:not(.ant-menu-item-selected):hover,\n.ant-menu-submenu-title:hover,\n.ant-menu-submenu-popup .ant-menu-item:not(.ant-menu-item-selected):hover {\n  background-color: var(--primaryDark) !important;\n  color: var(--az-white) !important;\n}\n\n/* Sub-page hover overrides above — light tint + primaryDark text */\n.customSideMenu\n  .ant-menu-sub\n  .ant-menu-item:not(.ant-menu-item-selected):hover {\n  background-color: var(--primaryLight) !important;\n  color: var(--primaryDark) !important;\n}\n.customSideMenu\n  .ant-menu-sub\n  .ant-menu-item:not(.ant-menu-item-selected):hover\n  a,\n.customSideMenu\n  .ant-menu-sub\n  .ant-menu-item:not(.ant-menu-item-selected):hover\n  a\n  span {\n  color: var(--primaryDark) !important;\n}\n\n/* Open + selected submenu title (both depths) → white text */\n.customSideMenu\n  > .ant-menu-submenu.ant-menu-submenu-open.ant-menu-submenu-selected\n  > .ant-menu-submenu-title,\n.customSideMenu\n  .ant-menu-sub\n  > .ant-menu-submenu.ant-menu-submenu-open.ant-menu-submenu-selected\n  > .ant-menu-submenu-title {\n  color: var(--az-white) !important;\n}\n\n/* SELECTED item (ALWAYS visible) */\n.ant-menu-item-selected {\n  color: var(--az-white) !important;\n  background-color: var(--primaryDark) !important;\n  width: 100% !important;\n  margin-right: 0 !important;\n  margin-bottom: 0 !important;\n}\n\n/* Sub-page selected overrides above — warm-white bg + dark bold text */\n.customSideMenu .ant-menu-sub .ant-menu-item-selected {\n  background-color: transparent !important;\n  color: var(--primaryDark) !important;\n}\n\n/* PARENT submenu when child selected — depth-0 module: darkest shade */\n.customSideMenu\n  > .ant-menu-submenu.ant-menu-submenu-selected\n  > .ant-menu-submenu-title {\n  color: var(--az-white) !important;\n  background-color: var(--primaryDark) !important;\n}\n/* PARENT submenu when child selected — depth-1 submodule: mid shade */\n.customSideMenu\n  .ant-menu-sub\n  > .ant-menu-submenu.ant-menu-submenu-selected\n  > .ant-menu-submenu-title {\n  color: var(--az-white) !important;\n  background-color: var(--primaryLight) !important;\n}\n\n/* Remove AntD default blue indicator */\n.ant-menu-item-selected::after {\n  border-right: none !important;\n}\n\n.ant-table-filter-column {\n  justify-content: center !important;\n}\n\n.cancelButton {\n  width: 100% !important;\n  color: var(--az-black) !important;\n  background-color: var(--az-gray-300) !important;\n  border-color: var(--az-gray-300) !important;\n  border-radius: 6px !important;\n  height: 43px !important;\n  line-height: 43px !important;\n}\n\n/* SECTION 16: Profile dropdown */\n\n/**** Profile Dropdown *****/\n.profileDropdownMenu {\n  padding: 0 !important;\n}\n\n.profileDropdownMenu .ant-dropdown-menu-item {\n  padding: 10px 12px;\n  border-bottom: 1px solid var(--az-gray-300);\n  color: var(--az-black) !important;\n  font-weight: 500;\n}\n\n.profileDropdownMenu .ant-dropdown-menu-item .dropMenuIcon {\n  margin-right: 10px;\n  color: var(--primaryDark);\n  font-size: 16px;\n  vertical-align: text-top;\n}\n\n.customerDetails {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n}\n\n.detailsList {\n  width: 100%;\n  padding: 10px 0;\n  border-bottom: 1px solid var(--az-gray-300);\n  margin: 0 15px;\n}\n\n.detailsList h6 {\n  margin-bottom: 0;\n}\n\n.cardData {\n  color: var(--az-gray-580);\n  font-size: 14px;\n  margin-left: 10px;\n}\n\n.ant-select-single .ant-select-selector .ant-select-selection-search {\n  top: 5px !important;\n  left: 20px !important;\n}\n\n.ant-select-single\n  .ant-select-selector\n  .ant-select-selection-search\n  .selectSearch {\n  top: 2px !important;\n  left: 20px !important;\n}\n\n/* Same tokens as .status-card (AZ Sales Agent's stat-card strip) so\n   every stage tile in the system — Material Movement, Money Movement,\n   Sales Commission Voucher, deposit/pending-payment cards, etc. — shares\n   one literal source of color/background/radius instead of separately\n   hardcoded values that happened to match by coincidence. */\n.customTabCard {\n  background: var(--az-gray-50) !important;\n  border-radius: var(--az-radius-md) !important;\n  border: 1px solid var(--az-border-subtle) !important;\n  cursor: pointer;\n  transition: border-color 0.15s;\n}\n\n/* Material/Money Movement stage tiles use a 12px radius (Figma node\n   298:448), narrower than the 16px shared .customTabCard default other\n   dashboards use — scoped via a second class so those other callers\n   (Dashcard.js, Fulfillment/index.js, Reports/depositeApproval.js) are\n   unaffected. */\n.customTabCard.movement-tile-card {\n  border-radius: 12px !important;\n}\n\n.customTabCard:hover {\n  border-color: rgba(var(--colorDark-rgb, 0, 122, 255), 0.25) !important;\n  box-shadow: none;\n}\n\n/* .is-active: self-targeting modifier for components that toggle the\n   highlight directly on the tile (most callers) — kept alongside the\n   .customTabCardActive ancestor-class pattern (used by Fulfillment/\n   index.js) so both styles of caller keep working. Previously several\n   pages tried to drive this via an inline style={{backgroundColor:...}}\n   on the same element, which can never win against the !important\n   above — that's why \"active\" tiles never visibly highlighted. */\n.customTabCardActive .customTabCard,\n.customTabCard.is-active {\n  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.08) !important;\n  /* Solid, full-opacity theme color (not translucent) — Figma node\n     298:460 specifies a solid border, and it must stay theme-aware\n     (var(--colorDark)) so it renders in the org's own accent color,\n     not a hardcoded blue. */\n  border: 1px solid var(--colorDark, #007aff) !important;\n}\n\n.customTabCard .ant-card-body {\n  height: 100%;\n}\n\n/* Stage-tile title + stat rows — matches Figma's stage-card layout\n   (gap-[20px] title→stats, gap-[4px] between stat rows) */\n.movement-tile__title {\n  line-height: 1.2;\n}\n.movement-tile__stats {\n  display: flex;\n  flex-direction: column;\n  gap: 4px;\n  font-size: 14px;\n  /* Without this, these rows inherit antd's default 1.5715 line-height —\n     over 21px per row — which alone is taller than the card's fixed\n     150px height can spare once a 2-line title is added on top.\n     Tightened to fit the actual card, not antd's generic body spacing. */\n  line-height: 1.3;\n}\n.movement-tile__stat-row {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  gap: 8px;\n}\n.movement-tile__stat-label {\n  color: rgba(var(--az-black-rgb), 0.6);\n}\n.movement-tile__stat-value {\n  color: rgba(var(--az-black-rgb), 0.8);\n  font-weight: 600;\n}\n\n.cardRow .cardHeading h5 {\n  font-size: 14px;\n  font-weight: 500;\n  color: rgba(var(--az-black-rgb), 0.6);\n}\n\n.customTabCardActive .customTabCard .cardHeading h5 {\n  color: rgba(var(--az-black-rgb), 0.7);\n}\n\n.customTabCard .innerCon .cardHeading h5 {\n  margin-bottom: 0;\n}\n\n.customTabCard .innerCon .counterText h4 {\n  font-size: 20px;\n  font-weight: 700;\n  color: rgba(var(--az-black-rgb), 0.8);\n}\n\n.customTabCardActive .customTabCard .counterText h4 {\n  color: rgba(var(--az-black-rgb), 0.9);\n}\n\n.customTabCard .ant-card-body .innerCon .textSec {\n  height: 100%;\n  display: flex;\n  align-items: center;\n}\n\n/* SECTION 17: Delta table header */\n\n.deltaTableHeader {\n  width: 100%;\n  padding: 5px;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  background: var(--primaryDark);\n  border-top-left-radius: 16px !important;\n  border-top-right-radius: 16px !important;\n}\n\n.deltaTableHeader .innerGreenBox {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  background: var(--az-gray-450);\n  border-radius: 10px;\n  margin-right: 10px;\n  padding: 10px 15px;\n  cursor: pointer;\n  border: 1px solid var(--az-white);\n}\n\n.innerGreenBox.activeBox {\n  /* border: 3px solid #5baf25; */\n  background-color: var(--secondaryDark);\n  color: var(--az-white) !important;\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  margin-right: 10px;\n  padding: 10px 15px;\n  cursor: pointer;\n}\n\n.deltaTableHeader .innerGreenBox .boxHeading {\n  margin-right: 15px;\n}\n\n.deltaTableHeader .innerGreenBox .boxHeading,\n.deltaTableHeader .innerGreenBox .boxDesc {\n  font-size: 16px;\n  color: var(--az-white);\n  margin-bottom: 0px;\n  text-transform: uppercase;\n}\n\n/* SECTION 18: Dropdown menu (action dropdown) */\n\n.dropdownAct .ant-dropdown-link {\n  color: var(--az-black);\n  vertical-align: super;\n  font-size: 24px;\n}\n\n.dropMenu {\n  padding: 0 !important;\n  min-width: 10rem;\n}\n\n.dropMenu li {\n  padding: 10px 15px;\n}\n\n.dropBtn {\n  font-size: 14px;\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  color: var(--az-gray-580) !important;\n  font-weight: 500;\n}\n\n.action_btn {\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n  align-items: center;\n}\n\n.action_btn button {\n  width: auto !important;\n  min-width: 100%;\n  margin: 5px;\n}\n\n/* Row-action pill pair (Inspect/List, View/Ship, etc.) — Figma's action\n   cells sit side by side, centered in the row, not stacked like the\n   older .action_btn (which many other pages still rely on, so this is\n   a separate class rather than a change to that one). */\n.az-table-row-actions {\n  display: flex;\n  flex-direction: row;\n  align-items: center;\n  justify-content: center;\n  gap: 8px;\n  flex-wrap: nowrap;\n}\n\n.table-responsive {\n  position: relative;\n  border-radius: 16px !important;\n  overflow-x: scroll !important;\n  width: 100%;\n  scrollbar-width: thin;\n  scrollbar-color: var(--az-gray-400) var(--az-gray-135);\n}\n\n/* Some datatables (e.g. SalesData.js, LotsTable.js) wrap their <CustomTable>\n   usage in an extra, redundant outer .table-responsive div, ABOVE the\n   table's own sticky wrapper (.az-table-wrap for the AntD-fallback engine,\n   .az-ts-wrap for the TanStack engine — both covered here). Any non-visible\n   overflow on an ancestor between a position:sticky element and the real\n   page viewport becomes ITS containing block instead — and since this div\n   is never actually what scrolls (the page is), that stickiness would bind\n   to this permanently-static box and never correctly engage. :has() targets\n   exactly this ancestor-wrapping case. :not(.az-table-scroll-inner) excludes\n   CustomTable's OWN internal .table-responsive (CustomTable.js) — without\n   it, a nested CustomTable rendered inside an expandable row (e.g.\n   BasketTableV3.js) would match this rule via ITS OWN .az-table-wrap/\n   .az-ts-wrap appearing as a deep descendant, incorrectly disabling the\n   OUTER table's real horizontal-scroll boundary and breaking its layout.\n   Higher specificity than the plain .table-responsive rule above (two\n   selectors vs one), so it wins regardless of source order. */\n.table-responsive:not(.az-table-scroll-inner):has(.az-table-wrap),\n.table-responsive:not(.az-table-scroll-inner):has(.az-ts-wrap) {\n  overflow-x: visible !important;\n}\n\n/* ═══════════════════════════════════════════════════════════════════════════\n   TABLE SCROLL & ALIGNMENT\n   ───────────────────────────────────────────────────────────────────────────\n   AntD's own scroll.y (passed from CustomTable.js, bounded to the remaining\n   viewport) makes the row area scroll internally with its header pinned at\n   the top of that scrollport — a native, long-standing AntD feature, no\n   `sticky` prop needed. Page-level stickiness for the whole table block\n   (header + bounded rows + pagination) is handled once, on .az-table-wrap.\n   ═══════════════════════════════════════════════════════════════════════════ */\n\n/* Horizontal-only scroll context, for tables wider than their card */\n.az-table .ant-table-container {\n  overflow-x: auto !important;\n  scrollbar-width: thin;\n  scrollbar-color: var(--az-gray-400) var(--az-gray-135);\n}\n\n/* Webkit scrollbar styling on the container */\n.az-table .ant-table-container::-webkit-scrollbar {\n  height: 6px;\n  width: 6px;\n}\n.az-table .ant-table-container::-webkit-scrollbar-corner {\n  background: var(--az-gray-135);\n}\n.az-table .ant-table-container::-webkit-scrollbar-track {\n  background: var(--az-gray-135);\n  border-radius: 4px;\n}\n.az-table .ant-table-container::-webkit-scrollbar-thumb {\n  background: var(--az-gray-400);\n  border-radius: 4px;\n}\n.az-table .ant-table-container::-webkit-scrollbar-thumb:hover {\n  background: var(--az-gray-500);\n}\n\n.productImage {\n  width: 60px;\n  height: 60px;\n}\n\n.goBackBtn {\n  background: none !important;\n  border: 0 !important;\n  box-shadow: none !important;\n  display: flex !important;\n  align-items: center !important;\n  font-weight: 500 !important;\n}\n\n.goBackBtn:hover {\n  color: var(--az-gray-680) !important;\n}\n\n.goBackBtn:focus {\n  outline: 0 !important;\n  box-shadow: 0 !important;\n  border: 0 !important;\n}\n\n/* SECTION 19: Modal styles (content/header/title/close button, width variants, cartModal, punch-order-container) */\n\n/***** Warehouse Modal *****/\n/* Global modal design */\n\n.ant-modal-content {\n  border-radius: 20px;\n  padding: 0 !important; /* OK */\n  height: auto !important; /* IMPORTANT */\n}\n\n.ant-modal-header {\n  background: var(--primaryDark) !important;\n  padding: 0 !important; /* 👈 KEY FIX */\n  min-height: 64px;\n  display: flex;\n  align-items: center;\n}\n\n.ant-modal-title {\n  padding: 0 24px; /* 👈 move padding here */\n  color: var(--az-white) !important;\n  font-weight: 600;\n  line-height: 64px;\n}\n\n.ant-modal-close {\n  top: 50%;\n  right: 10px;\n}\n\n.ant-modal-close-x {\n  background: var(--az-white);\n  width: 30px;\n  height: 30px;\n  border-radius: 10px;\n  line-height: 30px;\n}\n\n/* Scroll body safely. Scoped under .ant-modal-root .ant-modal so it beats\n   antd's own \".ant-modal .ant-modal-body { padding: 0 }\" — the old bare\n   .ant-modal-body rule lost to it, leaving only an uneven margin (content\n   flush with the right and bottom edges). The body scrolls instead of the\n   popup running off-screen (.ant-modal-wrap doesn't scroll). */\n.ant-modal-root .ant-modal .ant-modal-body {\n  margin: 0;\n  padding: 20px 24px 24px;\n  max-height: calc(100vh - 200px);\n  overflow-y: auto;\n}\n\n/* antd's own footer (OK / Cancel) */\n.ant-modal-root .ant-modal .ant-modal-footer {\n  margin: 0;\n  padding: 0 24px 20px;\n}\n\n.ant-modal-root .ant-modal .ant-modal-body:has(+ .ant-modal-footer) {\n  padding-bottom: 16px;\n}\n\n/* Modal.confirm has no header bar — even padding all round */\n.ant-modal-root .ant-modal.ant-modal-confirm .ant-modal-body {\n  padding: 24px;\n}\n\n/* Phones: fixed-width variants (widthModal2, widthModalPunchOrder, …) fit\n   the screen */\n@media (max-width: 767px) {\n  .ant-modal-root .ant-modal {\n    max-width: calc(100vw - 32px) !important;\n    min-width: 0 !important;\n    margin: 0 auto;\n  }\n}\n\n/* Customer form popups (confirm details, shipping address) */\n.customer-modal__subtitle {\n  margin: 0 0 12px;\n  color: var(--az-text-secondary);\n  font-weight: 500;\n}\n\n.customer-modal__actions {\n  display: flex;\n  justify-content: flex-end;\n  gap: 10px;\n  margin-top: 20px;\n}\n\n.customer-modal .infoSection .infoDetails .infoTd,\n.customer-modal .infoSection .infoDetails .infoTd p {\n  word-break: normal;\n  overflow-wrap: anywhere;\n}\n\n.customer-modal .infoSection .infoDetails ol {\n  padding-left: 18px !important;\n}\n\n.widthModal {\n  width: 900px !important;\n  /* height: 900px !important; */\n}\n\n.widthModalInv {\n  max-width: 800px !important;\n  min-width: 730px !important;\n  max-height: 0 auto !important;\n}\n\n.widthModal1 {\n  width: \"70%\" !important;\n}\n.widthModalInv {\n  /* height: 10% !important; */\n  max-width: 800px !important;\n  min-width: 730px !important;\n  max-height: 100% !important;\n}\n.widthModal2 {\n  /* width: 1300px !important; */\n  height: 10% !important;\n  max-width: 1000px !important;\n  min-width: 900px !important;\n}\n\n.widthModalPunchOrder {\n  height: 10% !important;\n  max-width: 820px !important;\n  min-width: 820px !important;\n  margin-left: 0px !important;\n}\n.widthModalLarge {\n  width: 95vw !important;\n  max-width: 100vw;\n  height: 80vh !important;\n  max-height: 50vh;\n  /* overflow-y: auto; */\n}\n\n.widthModalLarge .ant-table-footer {\n  padding: 0 !important; /* Removes all padding */\n}\n\n/* .widthModalLarge {\n  width: 90vw !important;\n  height: 40vh;\n  max-height: 40vh;\n} */\n/* Antd v5 form fixes */\n.ant-form-item {\n  margin-bottom: 16px;\n}\n\n.ant-picker,\n.ant-select {\n  width: 100%;\n}\n\n/* Bootstrap conflict fix */\nlabel {\n  margin-bottom: 0 !important;\n}\n\n@media (min-width: 768px) {\n  .widthModalLarge {\n    width: 750px !important;\n  }\n}\n@media (min-width: 850px) {\n  .widthModalLarge {\n    width: 820px !important; /* Customize this value as needed */\n  }\n}\n\n@media (min-width: 1024px) {\n  .widthModalLarge {\n    width: 970px !important;\n  }\n}\n@media (min-width: 1200px) {\n  .widthModalLarge {\n    width: 1150px !important;\n  }\n}\n\n@media (min-width: 1300px) {\n  .widthModalLarge {\n    width: 1250px !important;\n  }\n}\n\n.ant-modal-mask {\n  backdrop-filter: none !important;\n  -webkit-backdrop-filter: none !important;\n}\n\n.ant-modal-root .ant-modal {\n  pointer-events: auto !important;\n  /* Ensure modal can be clicked */\n}\n\n.punch-order-container {\n  width: 50% !important;\n  padding: 0% !important;\n}\n\n.cartModal {\n  width: 900px !important;\n  height: 400px !important;\n}\n\n/* SECTION 20: Grid & card layout helpers */\n\n.card-container {\n  display: grid;\n  grid-template-columns: repeat(12, 1fr);\n  grid-template-rows: 1fr auto;\n  gap: 10px;\n}\n\n.cl {\n  grid-column: auto / span 12;\n}\n\n@media screen and (min-width: 600px) {\n  .cl-3-lg {\n    grid-column: auto / span 12;\n  }\n}\n\n@media screen and (min-width: 1000px) {\n  .cl-3-lg {\n    grid-column: auto / span 6;\n  }\n}\n\n@media screen and (min-width: 1600px) {\n  .cl-3-lg {\n    grid-column: auto / span 4;\n  }\n}\n\n.place-order-button {\n  position: fixed;\n  bottom: 0;\n}\n\n.ant-modal-wrap {\n  overflow-y: hidden;\n}\n\n.shipperData:last-child {\n  margin-bottom: 0;\n}\n\n.customSearch {\n  position: absolute !important;\n  width: 250px !important;\n  left: 22rem;\n  top: 2.8rem;\n\n  /* border: 1px solid var(--primaryDark); */\n}\n\n.comboListItem {\n  display: flex;\n  justify-content: flex-start;\n  margin-bottom: 50px;\n}\n\n.rangePicker {\n  position: absolute !important;\n  width: auto !important;\n  left: 52rem;\n  top: 5.3rem;\n  border-radius: 20px;\n}\n\n/* SECTION 21: Absolute-positioned page-level action buttons */\n\n.absltBtn {\n  position: absolute !important;\n  width: auto !important;\n  /* color: #954c0a !important; */\n  top: 1.5rem;\n}\n\n.manageBtn {\n  padding: 0px 10px 0px 10px !important;\n}\n\n.absltBtn span {\n  font-size: 12px;\n}\n\n.bankBtn {\n  right: 8.2rem;\n}\n\n.stateBtn,\n.blockBtn {\n  right: 8.5rem;\n}\n\n.districtBtn,\n.vlgBtn,\n.marketBtn {\n  right: 9rem;\n}\n\n.branchBtn {\n  right: 9.2rem;\n}\n\n.subDstBtn {\n  right: 11rem;\n}\n\n.pinBtn {\n  right: 10rem;\n}\n\n.ctgryBtn {\n  right: 10.2rem;\n}\n\n.sbCtgryBtn {\n  right: 12rem;\n}\n\n.orgBtn {\n  right: 14.1rem;\n}\n\n.fcBtn {\n  right: 13.9rem;\n}\n\n.azPartnerBtn {\n  top: 5rem;\n  right: 3rem;\n}\n\n.sellerBtn,\n.cstmrBtn {\n  top: 5rem;\n  right: 2rem;\n}\n\n.productBtn {\n  top: 2.4rem;\n  right: 10rem;\n}\n\n.variantTable .productBtn {\n  top: 1.5rem;\n  right: 2.2rem;\n}\n\n.sllrPrdctBtn {\n  top: 1.2rem;\n  right: 19rem;\n}\n\n.comboBtn {\n  top: 4rem;\n  right: 19rem;\n}\n\n/* SECTION 22: Miscellaneous UI */\n\n.borderBottom {\n  text-align: center;\n  margin-top: 50px;\n  /* bottom: 80px; */\n  border-bottom: 5px solid rgb(var(--az-red-900-rgb));\n}\n\n.loader {\n  width: 48px;\n  height: 48px;\n  border: 5px solid var(--az-white);\n  border-bottom-color: var(--primaryDark);\n  border-radius: 50%;\n  display: inline-block;\n  box-sizing: border-box;\n  animation: rotation 1s linear infinite;\n}\n\n@keyframes rotation {\n  0% {\n    transform: rotate(0deg);\n  }\n\n  100% {\n    transform: rotate(360deg);\n  }\n}\n\n/* SECTION 23: Responsive / media queries */\n\n/***** Responsive Style *****/\n@media (max-width: 280px) {\n  .navbar-brand {\n    width: 40%;\n  }\n}\n\n@media (max-width: 320px) {\n  .navbar-brand {\n    margin-right: 0;\n  }\n\n  .navbar-toggler {\n    padding: 0.25rem 0.5rem;\n    font-size: 1rem;\n  }\n}\n\n@media only screen and (min-width: 1024px) and (max-width: 1200px) {\n  .sidebar.panel-open ~ .home-section .contentSection .timlineSection .w-30 {\n    left: 51rem;\n    transition: all 0.5s ease;\n  }\n}\n\n@media only screen and (max-width: 991px) {\n  .bodySection {\n    display: block !important;\n  }\n\n  .w-70,\n  .w-30 {\n    flex: 1;\n    max-width: 100%;\n  }\n\n  .timlineSection .timelineSectionInner {\n    position: relative;\n    left: 0 !important;\n  }\n\n  .infoSection .infoHeader .ant-row,\n  .infoDetails .ant-row {\n    display: -webkit-box !important;\n    flex-wrap: nowrap;\n    overflow-x: auto;\n  }\n\n  ::-webkit-scrollbar {\n    height: 0px;\n  }\n}\n\n@media (max-width: 767px) {\n  .mobHide {\n    display: none !important;\n  }\n\n  @media only screen and (max-width: 601px) {\n    .filterSection {\n      display: flex !important;\n      flex-direction: column !important;\n      justify-content: space-between !important;\n      /* align-items: center !important; */\n    }\n  }\n\n  .signInPage .SignInFormSection .customCard {\n    width: 100%;\n  }\n\n  /* home-section shifts with sidebar on all screen sizes — desktop CSS handles it */\n\n  .table-responsive .ant-table-content {\n    overflow-x: auto;\n  }\n\n  .mobFLex {\n    flex-direction: column;\n    align-items: start !important;\n  }\n}\n\n@media only screen and (min-width: 768px) and (max-width: 991px) {\n  .table-responsive .ant-table-content {\n    overflow-x: auto;\n  }\n}\n\n/* .tblData{\n  border: 1px solid black;\n  border-collapse: collapse;\n  text-align: center;\n  margin: 10px 0px 10px 0px;\n} */\n.tblMain {\n  /* border-collapse: collapse; */\n  border-radius: 1em;\n  overflow: hidden;\n  text-align: center;\n  font-size: 12px;\n  width: 100%;\n}\n\n/* th, td */\n\n.tblHead {\n  background: var(--primaryLight);\n  text-align: center;\n  font-weight: 500;\n  font-size: 12px;\n  color: var(--primaryTextLight);\n  height: 50px;\n}\n\n.tblData {\n  /* border: 0.5px solid white; */\n  padding: 0.8em;\n  background: hsl(0, 3%, 92%);\n  border-bottom: 2px solid white;\n  color: var(--az-black);\n}\n\n.input {\n  border: none;\n  text-align: center;\n  width: 100%;\n}\n\n.expandBtn {\n  border: 1px solid var(--primaryDark);\n  border-radius: 5px;\n  background-color: var(--primaryTextDark);\n}\n\n/* .css-mine {\n  margin-top: 2em;\n  clear: both;\n} */\n\n.tblBody {\n  margin: 1.5em;\n}\n\n.customSelect {\n  width: 200px !important;\n}\n\n@media only screen and (min-width: 993px) {\n  .mobSider {\n    display: none;\n  }\n}\n\n@media only screen and (min-width: 90.063em) and (max-width: 120em) {\n  .sidebar.panel-open ~ .home-section .contentSection .timlineSection .w-30 {\n    left: 76%;\n    transition: all 0.5s ease;\n  }\n}\n\n@media (max-width: 750px) {\n  .contentSection {\n    display: flex;\n    flex-direction: column !important;\n    /* align-items: start; */\n  }\n}\n\n@media only screen and (min-width: 120.063em) {\n  .sidebar.panel-open ~ .home-section .contentSection .timlineSection .w-30 {\n    left: 75%;\n    transition: all 0.5s ease;\n  }\n}\n\n@media (min-width: 1200px) and (max-width: 1920px) {\n  .w-40 {\n    width: 60%;\n  }\n}\n\n.btn-div {\n  margin: 2rem !important;\n}\n\n.ant-result {\n  align-self: center;\n}\n\nbody,\n#root {\n  height: 100%;\n}\n\n.errorPageBody {\n  height: 100%;\n  display: flex;\n  justify-content: center;\n}\n\n.customButtonOutlinedPunch:hover {\n  color: var(--az-white) !important;\n  background-color: var(--primaryDark) !important;\n  -webkit-box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.01);\n  -moz-box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.01);\n  box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.01);\n}\n\n.customButtonOutlinedPunch {\n  width: auto !important;\n  font-size: 12px !important;\n  font-weight: 500 !important;\n  color: var(--primaryDark) !important;\n  background-color: transparent !important;\n  border-color: var(--primaryDark) !important;\n  border-radius: 22px !important;\n  transition: all 0.5s ease-in-out;\n  text-shadow: none !important;\n}\n\n/* AZ table — new Figma design (borderless, white header, row dividers, theme-colored pagination); used by default in CustomTable.js */\n\n/* Spinner */\n.az-table .ant-spin-dot-item {\n  background-color: var(--colorDark) !important;\n}\n\n/* Remove outer table border / radius override */\n.az-table .ant-table {\n  border-radius: 0 !important;\n  border: none !important;\n}\n.az-table .ant-table-container {\n  border-inline-start: none !important;\n  border-top: none !important;\n}\n.az-table .ant-table-container::before,\n.az-table .ant-table-container::after {\n  display: none !important;\n}\n\n/* ── Header — Figma node 42:3030: text-[14px] font-medium text-[rgba(0,0,0,0.6)] h-[52px] ── */\n.az-table .ant-table-thead > tr > th {\n  background-color: var(--az-white) !important;\n  color: var(--az-text-secondary) !important;\n  font-size: 14px !important;\n  font-weight: 500 !important;\n  text-transform: none !important;\n  text-align: left !important;\n  letter-spacing: 0;\n  border-bottom: 1px solid var(--az-gray-300) !important;\n  border-top: none !important;\n  padding: 15px 16px !important;\n  box-shadow: none !important;\n  white-space: nowrap !important;\n  word-break: keep-all !important;\n  overflow: visible !important;\n  text-overflow: unset !important;\n}\n\n/* Inner header wrappers: no wrapping, no clipping, no ellipsis. */\n.az-table .ant-table-column-sorters,\n.az-table .ant-table-column-title,\n.az-table-col-title {\n  white-space: nowrap !important;\n  word-break: keep-all !important;\n  overflow: visible !important;\n  text-overflow: unset !important;\n}\n\n/* Remove separator pseudo-element between header cells */\n.az-table .ant-table-thead > tr > th::before {\n  display: none !important;\n}\n\n/* First / last header radius */\n.az-table .ant-table-thead > tr > th:first-child {\n  border-start-start-radius: 0 !important;\n}\n.az-table .ant-table-thead > tr > th:last-child {\n  border-start-end-radius: 0 !important;\n}\n\n/* ── Body cells — Figma node 42:3059 specified text-[16px], but every table in\n   the app (including this AntD-fallback branch) is standardized on 14px row\n   text for consistency — see .az-ts-td below, the TanStack branch's equivalent.\n   Padding is 26px so a row containing a 36px .customButtonOutlinedBoard action\n   button totals exactly 88px: 26 + 36 + 26. ── */\n.az-table .ant-table-tbody > tr > td {\n  border-bottom: 1px solid var(--az-gray-135) !important;\n  padding: 26px 16px !important;\n  font-size: 14px !important;\n  color: var(--az-text-primary) !important;\n  background: var(--az-white) !important;\n  text-align: left;\n  transition: background 0.15s;\n  white-space: nowrap !important;\n  overflow: hidden !important;\n}\n\n/* Columns with ellipsis:true — AntD wraps content and adds this class;\n   text-overflow:ellipsis is scoped here so it only fires where intended. */\n.az-table .ant-table-cell-ellipsis,\n.az-table .ant-table-cell-ellipsis .ant-table-cell-content {\n  white-space: nowrap !important;\n  overflow: hidden !important;\n  text-overflow: ellipsis !important;\n}\n\n/* No zebra striping */\n.az-table .ant-table-tbody > tr:nth-child(even) > td {\n  background-color: var(--az-white) !important;\n}\n\n/* Hover */\n.az-table .ant-table-tbody > tr:hover > td {\n  background-color: var(--az-gray-50) !important;\n}\n\n/* Remove last row bottom border */\n.az-table .ant-table-tbody > tr:last-child > td {\n  border-bottom: none !important;\n}\n\n/* ── Sorter ── */\n.az-table .ant-table-column-sorters {\n  justify-content: flex-start !important;\n  gap: 4px;\n  text-align: left !important;\n}\n.az-table .ant-table-column-title {\n  flex: initial !important;\n  text-align: left !important;\n}\n.az-table .ant-table-column-sorter {\n  color: var(--az-gray-400) !important;\n  margin-left: 4px;\n}\n.az-table .ant-table-column-sorter-up.active,\n.az-table .ant-table-column-sorter-down.active {\n  color: var(--colorDark) !important;\n}\n\n/* ── Column title + hide-column icon ── */\n.az-table-col-title {\n  display: inline-flex;\n  align-items: center;\n  gap: 6px;\n}\n\n.az-table-hide-col {\n  cursor: pointer;\n  color: rgba(var(--az-black-rgb), 0.45);\n  flex-shrink: 0;\n  transition: color 0.15s;\n}\n.az-table-hide-col:hover {\n  color: var(--colorDark) !important;\n}\n\n/* ── Edit column toolbar — sits directly above the table. Most pages also\n   render their own header row (stat chips / Download button) right above\n   CustomTable, and with margin-bottom:0 here those two rows had no\n   breathing room between them, making \"Edit column\" look like a stray\n   button floating just outside the table rather than part of the page's\n   layout. margin-top gives it consistent separation from whatever the\n   page renders above, on every page that uses CustomTable. ── */\n.az-table-toolbar {\n  display: flex;\n  justify-content: flex-end;\n  align-items: center;\n  gap: 8px;\n  padding: 10px 0 8px;\n}\n.az-table-toolbar-extra {\n  display: flex;\n  align-items: center;\n  gap: 8px;\n}\n/* Page-specific toolbar content (e.g. Download) passed via CustomTable's\n   `extra` prop — right-aligned so it sits at the end of the row, matching\n   the edit-columns button below it. */\n.az-table-toolbar-extra-row {\n  display: flex;\n  justify-content: flex-end;\n  align-items: center;\n  margin-bottom: 8px;\n}\n/* Figma node 298:193 spec: Hug (44px) x Fixed (40px), padding 6/12/6/12,\n   gap spacing-1 (4px), fill #F4F4F4 (== --az-gray-150), radius/max on the\n   left corners only — right corners stay square so that edge reads as\n   part of the card border once flush against it, not a floating pill.\n   No margin-right set here: pages wrap CustomTable in very different\n   amounts of card padding (.page-card-body's 0 vs .page-card-body--hero's\n   16px, or none at all), so a hardcoded offset can only ever be right for\n   one of them. The actual offset is measured against whichever card\n   really encloses this button and applied inline — see CustomTable.js. */\n.az-edit-column-btn {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  gap: var(--az-space-1);\n  height: 40px;\n  flex-shrink: 0;\n  padding: 6px 12px;\n  background: var(--primaryDark) !important;\n  border-color: var(--primaryDark) !important;\n  /* background: var(--az-gray-135); */\n  border: none;\n  border-radius: var(--az-radius-pill) 0 0 var(--az-radius-pill);\n  color: var(--az-white);\n  cursor: pointer;\n  transition: background 0.15s;\n}\n.az-edit-column-btn:hover {\n  background: var(--az-gray-300);\n}\n/* AntDFallback's outer wrapper (CustomTable.js) — see .az-ts-wrap for the\n   TanStack engine's identical rule and the full rationale: position:sticky\n   here (not just on the header) so the WHOLE table block — header, rows\n   bounded to the remaining viewport via scroll.y, pagination — moves and\n   settles together, guaranteeing pagination is never scrolled out of view. */\n.az-table-wrap {\n  position: sticky;\n  top: 80px;\n  z-index: 2;\n  background: var(--az-white);\n}\n/* Non-scrolling wrapper around the table's scroll container (CustomTable.js).\n   border-radius: 16px, rounding is provided by the enclosing .page-card-body /\n   .page-card-body--hero-flush card. */\n.az-table-header-overlay {\n  position: relative;\n  border-radius: 16px;\n  overflow: hidden;\n}\n/* Zero-height wrapper for the edit-columns button, so the button overlays\n   the header row instead of adding its own height above it. Base rule has\n   NO position:sticky — SalesData.js reuses this same class for its own,\n   differently-structured column picker (not nested in CustomTable's\n   .az-table-header-overlay, no bounded scroll box under it), where sticky\n   positioning would bubble all the way to the page and pin the button\n   under the topbar once scrolled. The scoped rule below opts CustomTable's\n   own usage into sticky at top:0 — the same offset as .az-ts-thead / AntD's\n   own sticky header, both independent sticky elements sharing the same\n   bounded scroll container (.az-ts-scroll), which need to match or they\n   visually drift apart as rows scroll past underneath. */\n.az-edit-column-sticky {\n  position: relative;\n  height: 0;\n  z-index: 21;\n}\n.az-table-header-overlay .az-edit-column-sticky {\n  position: sticky;\n  top: 0;\n}\n.az-edit-column-sticky .az-edit-column-btn {\n  position: absolute;\n  top: 6px;\n  right: 0;\n}\n.az-edit-column-list {\n  display: flex;\n  flex-direction: column;\n  gap: 10px;\n  max-height: 280px;\n  min-width: 180px;\n  overflow-y: auto;\n}\n.az-edit-column-list .ant-checkbox-checked .ant-checkbox-inner {\n  background-color: var(--colorDark) !important;\n  border-color: var(--colorDark) !important;\n}\n\n/* ── Expand icon ── */\n.az-table .ant-table-row-expand-icon {\n  color: var(--colorDark) !important;\n  border-color: var(--colorDark) !important;\n  border-radius: 4px !important;\n}\n.az-table .ant-table-row-expand-icon:hover {\n  color: var(--colorDark) !important;\n  border-color: var(--colorDark) !important;\n}\n.az-table .ant-table-row-expand-icon-cell {\n  background: var(--az-white) !important;\n}\n\n/* ── Expanded / nested row ── */\n.az-table .ant-table-expanded-row > .ant-table-cell {\n  background: var(--az-gray-50) !important;\n  padding: 0 0 0 48px !important;\n  border-bottom: 1px solid var(--az-gray-300) !important;\n}\n\n/* Nested table header inside expanded row */\n.az-table .ant-table-expanded-row .ant-table-thead > tr > th {\n  background-color: var(--az-gray-50) !important;\n  color: var(--az-slate-500) !important;\n  font-size: 12px !important;\n  font-weight: 500 !important;\n  text-transform: none !important;\n  letter-spacing: 0;\n  border-bottom: 1px solid var(--az-slate-100) !important;\n  padding: 8px 14px !important;\n}\n\n/* Nested table body cells */\n.az-table .ant-table-expanded-row .ant-table-tbody > tr > td {\n  background: var(--az-gray-50) !important;\n  border-bottom: 1px solid var(--az-gray-135) !important;\n  font-size: 12px !important;\n  color: var(--az-gray-700) !important;\n  padding: 10px 14px !important;\n}\n.az-table .ant-table-expanded-row .ant-table-tbody > tr:hover > td {\n  background: var(--az-gray-135) !important;\n}\n\n/* A nested CustomTable rendered inside an expanded row (e.g.\n   BasketTableV3.js's expandedRowRender) reuses the same wrapper classes as\n   a top-level page table, none of which are appropriate this deep:\n   - .az-ts-wrap's position:sticky/top:80px (see that rule's own comment)\n     assumes it's pinning below .page-topbar; nested here it instead sticks\n     to the page viewport from inside the row.\n   - .az-table-sticky-region's min-height:100vh-ish (see that rule's own\n     comment) is sized for a full top-level table; nested here it would\n     force the expanded row to reserve almost a full screen of blank space.\n   - AntD/rc-table wraps expandable-row content in its own\n     .ant-table-expanded-row-fixed div (rc-table's ExpandedRow.js) with an\n     INLINE style — width: <outer table's own visible width>px, sticky,\n     overflow:hidden — sized to whatever the OUTER table's own (narrower)\n     columns add up to. Confirmed via DevTools: that inline width+overflow\n     is what clips the nested table's last column and its edit-columns\n     button, regardless of how the nested table's own wrapper chain is\n     sized underneath it. Since it's an inline style, it needs !important\n     to override. We take over as the sole horizontal-scroll owner here —\n     .az-ts-scroll below still has its own overflow-x:auto, now free to\n     size itself off the real containing <td> (bounded by table-layout via\n     colSpan) instead of this narrower inline width. */\n.az-table .ant-table-expanded-row .ant-table-expanded-row-fixed {\n  width: 100% !important;\n  max-width: 100% !important;\n  position: static !important;\n  left: auto !important;\n  overflow: visible !important;\n}\n.az-table .ant-table-expanded-row .az-table-sticky-region {\n  min-height: 0;\n}\n.az-table .ant-table-expanded-row .az-ts-wrap {\n  position: static;\n  top: auto;\n  z-index: auto;\n  width: 100%;\n  max-width: 100%;\n  min-width: 0;\n}\n.az-table .ant-table-expanded-row .az-table-header-overlay,\n.az-table .ant-table-expanded-row .az-ts-scroll {\n  width: 100%;\n  max-width: 100%;\n  min-width: 0;\n}\n\n/* ── Pagination ── */\n\n/* Wrapper row that holds the pagination inside the table.\n   position:sticky + bottom:0, opaque background, so on a table tall enough\n   to scroll the page, pagination settles at the bottom of the viewport\n   instead of requiring a scroll all the way past the last row to reach it. */\n.az-table .ant-table-pagination,\n.az-table .ant-table-pagination.ant-pagination {\n  position: sticky !important;\n  bottom: 0 !important;\n  z-index: 4 !important;\n  background: var(--az-gray-50) !important;\n  margin: 0 !important;\n  padding: 10px 20px !important;\n  border-top: 1px solid var(--az-gray-300) !important;\n  justify-content: flex-end !important;\n  align-items: center !important;\n  display: flex !important;\n  flex-wrap: wrap !important;\n  row-gap: 6px !important;\n}\n\n/* Page number buttons */\n.az-table .ant-pagination-item {\n  border: 1px solid var(--az-gray-300) !important;\n  border-radius: 6px !important;\n  min-width: 32px !important;\n  height: 32px !important;\n  line-height: 30px !important;\n  background: var(--az-white) !important;\n  margin-inline-end: 4px !important;\n}\n.az-table .ant-pagination-item a {\n  color: var(--az-gray-700) !important;\n  font-size: 13px !important;\n  padding: 0 !important;\n}\n.az-table .ant-pagination-item:hover {\n  border-color: var(--colorDark) !important;\n}\n.az-table .ant-pagination-item:hover a {\n  color: var(--colorDark) !important;\n}\n\n/* Active page */\n.az-table .ant-pagination-item-active {\n  border-color: var(--colorDark) !important;\n  background-color: var(--colorDark) !important;\n}\n.az-table .ant-pagination-item-active a {\n  color: var(--az-white) !important;\n}\n.az-table .ant-pagination-item-active:hover a {\n  color: var(--az-white) !important;\n}\n\n/* Prev / Next buttons */\n.az-table .ant-pagination-prev,\n.az-table .ant-pagination-next {\n  min-width: 32px !important;\n  height: 32px !important;\n  line-height: 30px !important;\n  margin-inline-end: 4px !important;\n}\n.az-table .ant-pagination-prev .ant-pagination-item-link,\n.az-table .ant-pagination-next .ant-pagination-item-link {\n  border: 1px solid var(--az-gray-300) !important;\n  border-radius: 6px !important;\n  background: var(--az-white) !important;\n  color: var(--az-gray-570) !important;\n  font-size: 13px !important;\n  width: 32px !important;\n  height: 32px !important;\n  display: flex !important;\n  align-items: center !important;\n  justify-content: center !important;\n}\n.az-table\n  .ant-pagination-prev:not(.ant-pagination-disabled):hover\n  .ant-pagination-item-link,\n.az-table\n  .ant-pagination-next:not(.ant-pagination-disabled):hover\n  .ant-pagination-item-link {\n  border-color: var(--colorDark) !important;\n  color: var(--colorDark) !important;\n}\n\n/* Disabled prev/next */\n.az-table .ant-pagination-disabled .ant-pagination-item-link {\n  color: var(--az-gray-400) !important;\n  border-color: var(--az-gray-135) !important;\n  background: var(--az-gray-50) !important;\n}\n\n/* Jump prev / next (…) */\n.az-table\n  .ant-pagination-jump-prev\n  .ant-pagination-item-container\n  .ant-pagination-item-link-icon,\n.az-table\n  .ant-pagination-jump-next\n  .ant-pagination-item-container\n  .ant-pagination-item-link-icon {\n  color: var(--colorDark) !important;\n}\n\n/* Page size changer */\n.az-table .ant-pagination-options {\n  margin-inline-start: 8px !important;\n}\n.az-table .ant-pagination-options .ant-select-selector {\n  border: 1px solid var(--az-gray-300) !important;\n  border-radius: 6px !important;\n  height: 32px !important;\n  font-size: 13px !important;\n  background: var(--az-white) !important;\n}\n.az-table .ant-pagination-options .ant-select-selector:hover {\n  border-color: var(--colorDark) !important;\n}\n\n/* Total count text */\n.az-table .ant-pagination-total-text {\n  color: var(--az-gray-570) !important;\n  font-size: 12px !important;\n  margin-inline-end: auto !important;\n  line-height: 32px !important;\n}\n\n/* ── Empty state ── */\n.az-table .ant-empty-description {\n  color: var(--az-gray-500) !important;\n  font-size: 13px !important;\n}\n\n.custom-button3 {\n  border-color: var(--primaryDark) !important;\n  background: var(--primaryDark) !important;\n  color: var(--az-white) !important;\n  border-radius: 2px !important;\n  transition: all 0.5s ease-in-out;\n}\n\n.custom-button3:hover {\n  color: var(--az-white) !important;\n  background-color: var(--primaryLight) !important;\n  -webkit-box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.01);\n  -moz-box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.01);\n  box-shadow: 0px 3px 12px 0px rgba(var(--az-black-rgb), 0.01);\n}\n\n.btn-primary {\n  /* width: 100% !important; */\n  background-color: var(--primaryDark) !important;\n  border-color: var(--primaryDark) !important;\n  border-radius: 6px !important;\n  /* height: 36px !important; */\n  /* line-height: 30px; */\n  font-size: 14px !important;\n}\n\n.btn-primary:hover {\n  background-color: var(--primaryDark) !important;\n  border-color: var(--primaryDark) !important;\n}\n\n.btn-primary:disabled {\n  background-color: lightgray !important;\n  border-color: lightgray !important;\n  color: gray !important;\n}\n\n.btn-secondary {\n  width: 100% !important;\n  border: 1px solid var(--primaryDark) !important;\n  border-radius: 6px !important;\n  height: 36px !important;\n  line-height: 30px;\n  font-size: 14px !important;\n}\n\n.btn-secondary:hover {\n  color: var(--primaryDark) !important;\n  border-color: var(--primaryDark) !important;\n}\n\n.ant-radio-group-solid\n  .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {\n  color: var(--az-white);\n  background: var(--primaryDark) !important;\n  border-color: var(--primaryDark) !important;\n}\n\n.ant-radio-button-wrapper:hover {\n  position: relative;\n  color: var(--primaryDark) !important;\n}\n\n.ant-btn-link {\n  color: var(--primaryDark) !important;\n}\n\n.ant-btn-link:hover {\n  color: var(--primaryDark) !important;\n  opacity: 0.75;\n}\n\n.ant-btn-link:disabled {\n  color: gray !important;\n}\n\n/* Unlike .az-login's checkbox (deliberately fixed-blue, see that rule's own\n   comment — there's no signed-in org yet at login time), this checkbox is\n   used inside the authenticated app, where a real per-org theme is always\n   active — so its checked/hover states should track that theme's accent\n   (var(--colorDark)), not a fixed legacy color. */\n.custom-checkbox .ant-checkbox-checked .ant-checkbox-inner::after {\n  border-color: var(--colorDark, #007aff);\n}\n\n.custom-checkbox .ant-checkbox:hover .ant-checkbox-inner {\n  border-color: var(--colorDark, #007aff) !important;\n}\n\n.material-movement-grid {\n  display: grid;\n  /* Fixed 203px tracks, same reasoning as .az-movement-dashboard-grid: the\n     outer wide (span-2) column can end up wider than these two sub-columns\n     actually need, and a 1fr track would stretch to fill that instead of\n     staying at the card's real size — leaving an oversized, uneven gap\n     between LOT INSPECTION and BASKET CREATION instead of Figma's fixed\n     20px. Fixed tracks keep that gap pixel-stable; auto-fit still wraps\n     them to stacked rows if the column is too narrow for both. */\n  grid-template-columns: repeat(auto-fit, 203px);\n  /* Figma's real gap between two cards inside the same group (e.g.\n     LOT INSPECTION / BASKET CREATION side by side) — narrower than\n     the 60px gap between separate groups in .az-movement-dashboard-grid. */\n  gap: 20px;\n  /* Center rather than left-align in case the outer wide column is a\n     little bigger than these two fixed tracks + gap need. */\n  justify-content: center;\n}\n.tabBox {\n  border: 1px solid var(--az-gray-310);\n  padding: 8px;\n  margin: 8px;\n  border-radius: 8px;\n  box-shadow: 0 2px 4px rgba(var(--az-black-rgb), 0.1);\n  cursor: pointer;\n  transition: transform 0.2s;\n  background-color: white;\n}\n\n@media screen and (min-width: 800px) {\n  .material-movement-grid {\n    display: grid;\n    grid-template-columns: 1fr 1fr;\n    gap: 10px;\n  }\n}\n\n.tabBox:hover {\n  transform: scale(1.05);\n  background-color: var(--primaryLight);\n}\n\n.tabHeader {\n  display: flex;\n  flex-direction: column;\n  justify-content: space-between;\n  align-items: center;\n}\n\n.tabHeader h3 {\n  margin: 0;\n}\n\n.tabInfo {\n  display: flex;\n  gap: 16px;\n}\n\n.tabContent {\n  margin-top: 0;\n}\n\n.ant-select-single .ant-select-selector .ant-select-selection-search {\n  top: 0 !important;\n}\n\n/* SECTION 24: Menu icon overrides (AntD icon visibility fixes) */\n\n/* AntD menu icon container — collapsed: 36px circle; expanded: 20px inline */\n.sidebar:not(.open) .ant-menu-item-icon,\n.sidebar:not(.open) .ant-menu-submenu-title .ant-menu-item-icon {\n  min-width: 36px !important;\n  width: 36px !important;\n  display: flex !important;\n  align-items: center !important;\n  justify-content: center !important;\n  margin-right: 0 !important;\n  visibility: visible !important;\n  opacity: 1 !important;\n  flex-shrink: 0 !important;\n}\n\n.sidebar.open .ant-menu-item-icon,\n.sidebar.open .ant-menu-submenu-title .ant-menu-item-icon {\n  min-width: 20px !important;\n  width: 20px !important;\n  display: flex !important;\n  align-items: center !important;\n  justify-content: center !important;\n  margin-right: 10px !important;\n  visibility: visible !important;\n  opacity: 1 !important;\n  flex-shrink: 0 !important;\n}\n\n/* Actual image inside menu */\n.menuIconImg {\n  width: 24px !important;\n  height: 24px !important;\n  object-fit: contain;\n  display: block !important;\n  margin: 0 auto !important;\n  flex-shrink: 0 !important;\n  visibility: visible !important;\n  opacity: 1 !important;\n}\n\n.customSideMenu .ant-menu-item-icon .menuImg .menuIconImg {\n  display: block !important;\n  margin: 0 auto !important;\n  width: 24px !important;\n  height: 24px !important;\n  visibility: visible !important;\n  opacity: 1 !important;\n}\n\n/* Remove bootstrap interference */\n.menuImg img {\n  max-width: none !important;\n  visibility: visible !important;\n  opacity: 1 !important;\n  display: block !important;\n}\n\n/* Ensure icons are always visible in sidebar */\n.sidebar .ant-menu-item-icon,\n.sidebar .ant-menu-submenu-title .ant-menu-item-icon {\n  visibility: visible !important;\n  opacity: 1 !important;\n  display: flex !important;\n}\n\n.sidebar .ant-menu-item-icon img,\n.sidebar .ant-menu-submenu-title .ant-menu-item-icon img {\n  visibility: visible !important;\n  opacity: 1 !important;\n  display: block !important;\n}\n\n/* Fix: Link wrapping Button — <a><button> is invalid HTML so clicks on button\n   padding/border don't reach the anchor. Disable pointer events on the button\n   so all clicks hit the parent <a> instead. Hover visuals are preserved via\n   the <a>:hover rule below. */\na > .ant-btn {\n  pointer-events: none;\n}\na:hover > .ant-btn:not([disabled]) {\n  color: var(--ant-color-primary, #ffffff);\n  border-color: var(--ant-color-primary);\n}\na:hover > .ant-btn-primary:not([disabled]) {\n  color: var(--az-white);\n  background-color: var(--ant-color-primary-hover, #4096ff);\n  border-color: var(--ant-color-primary-hover, #4096ff);\n}\n\n/* SECTION 25: Responsive card sections (Material/Money Movement) */\n.cardSectionn {\n  overflow-x: auto;\n  min-width: 0;\n}\n\n@media (max-width: 767px) {\n  .cardSectionn .ant-row {\n    flex-wrap: wrap;\n  }\n  .customTabCard {\n    width: 100% !important;\n    min-width: 0 !important;\n  }\n}\n\n/* SECTION 26: AZ TanStack enterprise table (TanStack Table v8 + Virtual v3). AntD's scroll.y renders two separate <table> elements and syncs scrollLeft via JS, which can drift and misalign columns — fixed here with one <table> + <colgroup> as the single source of column widths inside one scroll container; thead sticks via position:sticky, pinned columns use position:sticky with TanStack-calculated offsets, no custom JS syncing. */\n\n/* ── Sticky-slack wrapper ──\n   position:sticky only actually engages while its own immediate CONTAINING\n   BLOCK has \"slack\" left below the sticky element — room within that same\n   parent for the element's reserved layout box to keep extending past the\n   sticky point. Without it (confirmed empirically: even a single plain\n   position:relative ancestor wrapping just a spacer + a sticky box, with\n   nothing after the sticky box inside that same wrapper, is enough to\n   silently defeat stickiness — no clamping at all, indistinguishable from\n   position:static), the browser just never sticks the element, no matter\n   how clean the rest of the ancestor chain is. .az-ts-wrap (the actual\n   sticky element, below) is normally the last substantial thing on a page\n   — nothing trails it inside whatever page-specific div happens to wrap\n   <CustomTable> — so that natural parent almost never has slack on its\n   own. This wrapper, rendered unconditionally by CustomTable.js itself\n   (so it doesn't depend on the surrounding page's markup), guarantees at\n   least one remaining-viewport's worth of it. The +120px on top of that is\n   deliberate, not rounding — CustomTable.js sizes the table's own content\n   to come out at exactly 100vh-80px (so the stuck table fills the screen\n   with no gap below pagination), which means content height and this\n   min-height would otherwise be mathematically identical — i.e. zero\n   slack by construction — and confirmed empirically, that's not enough\n   for the browser to reliably engage stickiness at all (unlike a visibly\n   large margin, which works cleanly). The extra 120px is spare capacity in\n   THIS wrapper only, never in the table's own visible content, so it\n   doesn't reintroduce a gap under pagination — it can only ever show as\n   blank space below the table if a user keeps scrolling past the point\n   where pagination is already fully on-screen. */\n/* .az-table-sticky-region {\n  min-height: calc(100vh - 80px + 120px);\n} */\n\n/* ── Outer wrapper ──\n   position:sticky here (not just on the header row) so the WHOLE table —\n   header, bounded/scrollable rows, pagination — moves together as one\n   block once scrolled into place: the page scrolls normally past whatever\n   sits above the table, then this wrapper pins at top:80 (right below the\n   sticky .page-topbar) and everything inside it (including pagination, a\n   later sibling of .az-table-header-overlay — see CustomTable.js) fits\n   within the remaining viewport, so pagination never ends up scrolled out\n   of view below the fold. The row area inside (.az-ts-scroll) is bounded\n   with its own internal scrollbar and sticky header — see .az-ts-thead —\n   which is what makes the header stick correctly in the first place\n   (position:sticky on <thead>/<th> needs a genuinely scrolling ancestor;\n   confirmed via an isolated repro outside this app). */\n.az-ts-wrap {\n  display: flex;\n  flex-direction: column;\n  position: sticky;\n  top: 80px;\n  z-index: 2;\n  background: var(--az-white);\n  border-radius: 16px;\n}\n\n/* ── Scroll container (both axes) ──\n   Bounded height (see CustomTable.js's scrollMaxHeight, applied inline) so\n   rows beyond what fits scroll internally instead of growing the table —\n   and therefore the page — past one screen's worth of height. This\n   genuinely-scrolling container is also what the sticky header depends on\n   — see .az-ts-thead. */\n.az-ts-scroll {\n  position: relative;\n  overflow-x: auto;\n  overflow-y: auto;\n  scrollbar-width: thin;\n  scrollbar-color: var(--az-gray-400) var(--az-gray-135);\n}\n.az-ts-scroll::-webkit-scrollbar {\n  height: 6px;\n  width: 6px;\n}\n.az-ts-scroll::-webkit-scrollbar-corner {\n  background: var(--az-gray-135);\n}\n.az-ts-scroll::-webkit-scrollbar-track {\n  background: var(--az-gray-135);\n  border-radius: 4px;\n}\n.az-ts-scroll::-webkit-scrollbar-thumb {\n  background: var(--az-gray-400);\n  border-radius: 4px;\n}\n.az-ts-scroll::-webkit-scrollbar-thumb:hover {\n  background: var(--az-gray-500);\n}\n\n/* ── Loading overlay ── */\n/* z-index below .az-edit-column-btn's 20 (see SECTION 11a-ish comment above\n   it) — both sit in the same stacking context (.az-table-header-overlay,\n   position:relative with no z-index of its own), and this overlay comes\n   later in the DOM (nested inside .az-ts-scroll, a sibling rendered after\n   the button). Equal z-index would let DOM order decide, which paints this\n   overlay on top and hides the button behind it for as long as the table\n   is loading — exactly the \"outside the scroll area\" independence the\n   button's positioning comment says it's supposed to have. */\n.az-ts-loading-overlay {\n  position: absolute;\n  inset: 0;\n  z-index: 15;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  background: rgba(var(--az-white-rgb), 0.72);\n}\n.az-ts-loading-overlay .ant-spin-dot-item {\n  background-color: var(--colorDark) !important;\n}\n\n/* ── Table element ──\n   table-layout:auto — browser grows each column to fit its widest cell.\n   <col minWidth> sets the floor (from header text); data drives the final width.\n   border-collapse:separate allows position:sticky on pinned cells. */\n.az-ts-table {\n  width: 100%;\n  table-layout: auto;\n  border-collapse: separate;\n  border-spacing: 0;\n  font-family: inherit;\n}\n\n/* ── Header row ──\n   Sticky relative to .az-ts-scroll, its own real (bounded, overflow-y:auto)\n   scrolling ancestor — rows scroll internally beneath it, unaffected by\n   the outer page. This MUST stay in lockstep with .az-edit-column-sticky\n   (same top:0 offset) — the two are independent sticky elements, and if\n   one pins while the other stays in normal flow they visually drift apart.\n   (position:sticky on <thead>/<th> only renders correctly with a genuinely\n   scrolling ancestor like this one — tried it against an unbounded,\n   ever-growing table and confirmed, via an isolated repro outside this\n   app, that rows silently overlap the header there. Bounded + internal\n   scroll is a requirement, not a stylistic choice.) */\n.az-ts-thead {\n  position: sticky;\n  top: 0;\n  z-index: 2;\n}\n\n/* ── Header cells — Figma: 14px / Medium(500) / rgba(0,0,0,0.6) / always left-aligned ── */\n.az-ts-th {\n  background-color: var(--az-white);\n  color: rgba(var(--az-black-rgb), 0.6);\n  font-size: 14px;\n  font-weight: 500;\n  text-align: left;\n  border-bottom: 1px solid var(--az-gray-300);\n  height: 52px;\n  padding: 0 16px;\n  vertical-align: middle;\n  box-sizing: border-box;\n  user-select: none;\n}\n\n/* Pinned header cells — left-pinned columns use the Figma highlight token */\n.az-ts-th--pinned-left {\n  background-color: var(--colorLight);\n  z-index: 3 !important;\n}\n.az-ts-th--pinned-right {\n  background-color: var(--az-white);\n  z-index: 3 !important;\n}\n\n/* Shadow lines at the pinning edge to signal hidden content */\n.az-ts-th--last-left {\n  box-shadow: 4px 0 8px -4px rgba(var(--az-black-rgb), 0.1);\n}\n.az-ts-th--first-right {\n  box-shadow: -4px 0 8px -4px rgba(var(--az-black-rgb), 0.1);\n}\n\n/* Column-separator pseudo-element between header cells: hidden */\n.az-ts-th::before {\n  display: none;\n}\n\n/* ── Sortable header button ──\n   white-space:nowrap keeps the entire label on one line.\n   Column widths control visibility — set the width prop wide enough and the\n   full label is always shown, no clipping, no dots. */\n.az-ts-sort-btn,\n.az-ts-th-inner {\n  display: flex;\n  align-items: center;\n  justify-content: flex-start;\n  gap: 7px;\n  width: 100%;\n  white-space: nowrap;\n}\n\n/* Right/center-aligned columns (numeric values, via inferAlign()) — the\n   header's label + hide/sort icons follow the same alignment as the body\n   cells below them, instead of always packing to the left regardless of\n   where the column's actual values sit. */\n.az-ts-sort-btn.az-ts-th-inner--right,\n.az-ts-th-inner.az-ts-th-inner--right {\n  justify-content: flex-end;\n}\n.az-ts-sort-btn.az-ts-th-inner--center,\n.az-ts-th-inner.az-ts-th-inner--center {\n  justify-content: center;\n}\n\n.az-ts-sort-btn {\n  background: none;\n  border: none;\n  padding: 0;\n  margin: 0;\n  cursor: pointer;\n  font-size: 14px;\n  font-weight: 500;\n  color: rgba(var(--az-black-rgb), 0.6);\n  font-family: inherit;\n}\n\n/* Label: never shrinks, never wraps, always shows full text */\n.az-ts-th-label {\n  flex-shrink: 0;\n  white-space: nowrap;\n  text-align: left;\n}\n\n/* Eye icon and sort arrows also never shrink */\n.az-table-hide-col,\n.az-ts-sort-btn > svg {\n  flex-shrink: 0;\n}\n\n.az-ts-sort-btn:focus-visible {\n  outline: 2px solid var(--colorDark);\n  outline-offset: 2px;\n  border-radius: 2px;\n}\n.az-ts-sort-btn:hover {\n  color: var(--az-text-primary);\n}\n\n/* ── Body cells — Figma specified 16px, but standardized to 14px across every\n   table in the app (matches .az-ts-th's header size and the AntD-fallback\n   branch's .ant-table-tbody > tr > td above) ── Regular(400) / rgba(0,0,0,0.9) / 88px row.\n   Padding is 24px so a row containing the 40px .az-btn--sm action pill\n   (Figma node 298:244/298:247) totals exactly 88px: 24 + 40 + 24. ── */\n.az-ts-td {\n  background: var(--az-white);\n  color: rgba(var(--az-black-rgb), 0.9);\n  font-size: 14px;\n  font-weight: 400;\n  padding: 24px 20px;\n  border-bottom: 1px solid var(--az-gray-135);\n  box-sizing: border-box;\n  transition: background 0.12s;\n  white-space: nowrap;\n  overflow: hidden;\n}\n\n/* Small-size rows — same 14px row text as the default size now, just tighter padding */\n.az-ts-wrap--small .az-ts-td {\n  padding: 10px 16px;\n}\n\n/* Pinned body cells — left-pinned columns carry the Figma highlight + Medium weight */\n.az-ts-td--pinned-left {\n  background: var(--colorLight);\n  z-index: 1;\n  font-weight: 500;\n}\n.az-ts-td--pinned-right {\n  background: var(--az-white);\n  z-index: 1;\n}\n\n/* Keep the highlight visible on row hover for pinned-left cells */\n.az-ts-tr:hover .az-ts-td--pinned-left {\n  background-color: rgba(var(--colorDark-rgb), 0.13) !important;\n}\n\n/* Shadow lines on pinned body cells match the header shadow */\n.az-ts-td--last-left {\n  box-shadow: 4px 0 8px -4px rgba(var(--az-black-rgb), 0.1);\n}\n.az-ts-td--first-right {\n  box-shadow: -4px 0 8px -4px rgba(var(--az-black-rgb), 0.1);\n}\n\n/* Ellipsis: only shows … when text genuinely overflows the column.\n   Short text that fits shows completely — no dots. Hover title shows full value. */\n.az-ts-td--ellipsis {\n  text-overflow: ellipsis;\n}\n\n/* ── Row hover — entire row, NOT individual cells ──\n   The !important overrides the background on pinned cells too,\n   so the full row highlights uniformly during hover. */\n.az-ts-tr:hover .az-ts-td {\n  background-color: var(--az-gray-50) !important;\n}\n\n/* Remove last row's bottom border */\n.az-ts-tbody .az-ts-tr:last-child .az-ts-td {\n  border-bottom: none !important;\n}\n\n/* Virtual spacer rows must be invisible */\n.az-ts-tr--spacer td {\n  padding: 0 !important;\n  border: none !important;\n  background: transparent !important;\n}\n\n/* ── Empty state ── */\n.az-ts-empty {\n  text-align: center;\n  padding: 48px 16px !important;\n  color: var(--az-gray-500) !important;\n  font-size: 13px !important;\n  background: var(--az-white) !important;\n  white-space: normal !important;\n  border-bottom: none !important;\n}\n\n/* ── Footer ── */\n.az-ts-footer {\n  background: var(--az-white);\n  border-top: 1px solid var(--az-gray-300);\n  padding: 12px 16px;\n}\n\n/* ── Pagination ──\n   CustomTable wraps TanStackTable with class \"az-table\" so the\n   existing .az-table .ant-table-pagination rules apply to the\n   AntD <Pagination className=\"ant-table-pagination\"> element\n   that sits directly inside. No duplicated CSS needed. */\n.az-ts-wrap .ant-table-pagination.ant-pagination {\n  border-radius: 0 0 var(--az-radius-md) var(--az-radius-md);\n}\n\n/* ── Mobile — card layout ─────────────────────────────────────\n   Below 480 px the horizontal table becomes impossible to use.\n   Switch to a vertical card where each cell shows its column\n   label in a ::before pseudo-element.\n   ─────────────────────────────────────────────────────────────*/\n@media (max-width: 480px) {\n  /* Card-layout rows stack to full natural height below — a sticky,\n     viewport-bounded table box doesn't fit that model on small screens. */\n  .az-ts-wrap,\n  .az-table-wrap {\n    position: static;\n  }\n  .az-table-sticky-region {\n    min-height: 0;\n  }\n  /* The edit-columns button's sticky lock-step with the (now hidden, see\n     .az-ts-thead below) header row doesn't fit the card-stack layout below.\n     Pagination stays sticky to the screen bottom — with 100 tall cards per\n     page it's the only way to reach it without scrolling the whole list. */\n  .az-edit-column-sticky {\n    position: static !important;\n  }\n  .az-ts-scroll {\n    overflow-x: visible;\n    overflow-y: visible;\n    max-height: none !important;\n  }\n  .az-ts-table,\n  .az-ts-thead,\n  .az-ts-tbody,\n  .az-ts-header-row,\n  .az-ts-tr,\n  .az-ts-th,\n  .az-ts-td {\n    display: block;\n  }\n  /* React sets an inline minWidth (sum of every column's width, easily\n     700-1000px+) on .az-ts-table for the horizontal-scroll layout — block\n     display alone doesn't clear it, so the card stack below would still\n     sit inside that oversized box instead of the viewport's width. */\n  .az-ts-table {\n    min-width: 0 !important;\n    width: 100% !important;\n  }\n  /* colgroup has no visual role in block layout */\n  .az-ts-table colgroup,\n  .az-ts-table col {\n    display: none;\n  }\n\n  /* Hide the header row — labels come from data-label */\n  .az-ts-thead {\n    display: none;\n  }\n\n  .az-ts-tr {\n    margin-bottom: 12px;\n    border: 1px solid var(--az-gray-300);\n    border-radius: var(--az-radius-md);\n    overflow: hidden;\n  }\n\n  .az-ts-td {\n    display: flex;\n    align-items: flex-start;\n    gap: 8px;\n    padding: 10px 16px !important;\n    white-space: normal !important;\n    text-overflow: unset !important;\n    overflow: visible !important;\n    /* cancel sticky positioning — not meaningful in card layout */\n    position: static !important;\n    left: auto !important;\n    right: auto !important;\n    box-shadow: none !important;\n  }\n  /* Each cell carries an inline width/maxWidth = its desktop column width\n     (CustomTable) — in the card it must span the card instead, and the\n     value side must be allowed to shrink and wrap, otherwise long\n     emails/names/descriptions get clipped by the card. */\n  .az-ts-td {\n    width: auto !important;\n    max-width: none !important;\n    text-align: left !important;\n    overflow-wrap: break-word;\n    word-break: break-word;\n  }\n  .az-ts-td > * {\n    min-width: 0;\n  }\n  .az-ts-td::before {\n    content: attr(data-label);\n    font-weight: 500;\n    color: var(--az-text-secondary);\n    font-size: 13px;\n    flex-shrink: 0;\n    min-width: 110px;\n  }\n  /* The sidebar rail stays visible on phones, so the card is narrow —\n     stack label above value so the value gets the card's full width. */\n  .az-ts-td {\n    flex-direction: column;\n    gap: 2px;\n  }\n  .az-ts-td::before {\n    min-width: 0;\n    font-size: 12px;\n  }\n  /* Reclaim side padding for content next to the rail */\n  .contentSection {\n    padding: 8px 12px;\n  }\n  .az-ts-td:last-child {\n    border-bottom: none;\n  }\n\n  /* Utility columns (Sr.No., checkboxes) are noise on mobile */\n  .az-ts-td[data-col-utility=\"true\"] {\n    display: none;\n  }\n}\n\n/* ══ Phones (≤767px) — compact type, controls and spacing ══════════════\n   Desktop/tablet sizing is untouched: everything below only applies under\n   this breakpoint. Values are the desktop ones scaled down (titles 28→20,\n   controls 44/40→36, 16→14px button text, tighter form padding/gaps).\n   !important only where the desktop rule it overrides already uses it. */\n@media (max-width: 767px) {\n  /* Page titles + subtitles */\n  .page-card-title,\n  h2.page-card-title {\n    font-size: 20px !important;\n    line-height: 1.3;\n  }\n  .page-card-title .page-title-count {\n    font-size: inherit;\n  }\n  .page-card-subtitle,\n  .page-card-subtitle--hero {\n    font-size: 12px !important;\n    line-height: 1.45;\n    margin-top: 2px;\n  }\n  .page-card-header {\n    gap: 10px;\n    min-height: 0;\n  }\n\n  /* Buttons: page actions, form Submit/Cancel, Download/Filters */\n  .customButtonOutlinedBoard,\n  .customButtonOutlinedCancel,\n  .az-btn,\n  .az-btn--xl {\n    height: 36px !important;\n    font-size: 14px !important;\n    padding: 0 14px !important;\n  }\n  .az-btn__icon {\n    font-size: 12px;\n  }\n  .filterStyle {\n    gap: 6px;\n  }\n\n  /* Topbar search + help */\n  .page-topbar-search,\n  .az-search-input {\n    height: 36px !important;\n    font-size: 13px;\n  }\n  .need-help-btn {\n    height: 36px !important;\n    min-width: 36px;\n    padding: 0 10px !important;\n  }\n\n  /* Tabs */\n  .ant-tabs-tab {\n    font-size: 13px;\n    padding: 8px 0 !important;\n  }\n  .ant-tabs-tab + .ant-tabs-tab {\n    margin-left: 20px !important;\n  }\n\n  /* Forms */\n  .page-card-form {\n    padding: 16px 14px !important;\n  }\n  .ant-form-item {\n    margin-bottom: 12px !important;\n  }\n  .ant-form-item-label > label {\n    font-size: 13px !important;\n  }\n  .ant-form-vertical .ant-form-item-label {\n    padding-bottom: 4px !important;\n  }\n  .ant-form-item .ant-input-affix-wrapper,\n  .ant-form-item input.ant-input,\n  .ant-form-item .ant-picker,\n  .ant-form-item .ant-input-number,\n  .ant-form-item .customFormControl {\n    min-height: 36px;\n    height: 36px !important;\n    font-size: 14px;\n  }\n  .ant-form-item .ant-input-affix-wrapper input.ant-input {\n    height: auto !important;\n    min-height: 0;\n  }\n  .ant-form-item textarea.ant-input {\n    height: auto !important;\n    font-size: 14px;\n  }\n  .ant-form-item .ant-select-single .ant-select-selector,\n  .contentSection .ant-form-vertical .ant-select:not(.ant-select-customize-input) .ant-select-selector {\n    height: 36px !important;\n    padding: 2px 12px !important;\n  }\n  .ant-form-item .ant-select-multiple .ant-select-selector {\n    min-height: 36px !important;\n    height: auto !important;\n  }\n  .ant-form-item .ant-select-single .ant-select-selection-item,\n  .ant-form-item .ant-select-single .ant-select-selection-placeholder,\n  .contentSection .ant-form-vertical .ant-select-single .ant-select-selector .ant-select-selection-item,\n  .contentSection .ant-form-vertical .ant-select-single .ant-select-selector .ant-select-selection-placeholder {\n    line-height: 30px !important;\n    font-size: 14px;\n  }\n\n  /* Table cards */\n  .az-ts-td {\n    font-size: 13px;\n  }\n\n  /* Compact pager (CustomTable switches to AntD's simple mode on phones):\n     one centred row, pinned to the screen bottom */\n  .az-table .ant-table-pagination.ant-pagination,\n  .az-table .ant-table-pagination {\n    justify-content: center !important;\n    padding: 8px !important;\n    font-size: 13px;\n  }\n  .az-table .ant-pagination-simple .ant-pagination-simple-pager {\n    margin-right: 4px !important;\n  }\n  .az-table .ant-pagination-simple .ant-pagination-simple-pager input {\n    width: 36px;\n    height: 30px;\n    margin-right: 4px !important;\n    text-align: center;\n  }\n  .az-table .ant-pagination-total-text {\n    margin-right: auto;\n    font-size: 12px;\n  }\n  /* AntD hides the rows-per-page changer below 576px — keep it */\n  .az-table .ant-pagination .ant-pagination-options {\n    display: inline-flex !important;\n    margin-left: 4px !important;\n  }\n  .az-table .ant-pagination-simple .ant-pagination-prev,\n  .az-table .ant-pagination-simple .ant-pagination-next {\n    margin: 0 !important;\n  }\n  .az-table .ant-pagination-options .ant-select-selector {\n    height: 30px !important;\n    font-size: 13px;\n  }\n}\n\n/* SECTION SMART DASHBOARD — src/pages/Dashboard (.sd-*)\n   One screen on desktop: header · KPI row · main area (70% graphs | 30%\n   pies) filling exactly the viewport below the 80px topbar — charts and\n   donuts flex to the space left, no page scroll. Narrow or short screens\n   fall back to a normal scrolling stack. One accent (--colorDark) for\n   single-series data; text in text tokens; solid hairline grids. */\n.sd-page {\n  padding: 14px 20px 16px;\n  display: flex;\n  flex-direction: column;\n  gap: 12px;\n}\n@media (min-width: 992px) and (min-height: 640px) {\n  .sd-page {\n    height: calc(100vh - 80px);\n    height: calc(100dvh - 80px);\n    overflow: hidden;\n  }\n}\n\n/* Header — one row */\n.sd-head {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  gap: 12px;\n  flex-wrap: wrap;\n}\n.sd-head__text {\n  display: flex;\n  align-items: baseline;\n  gap: 10px;\n  flex-wrap: wrap;\n  min-width: 0;\n}\n.sd-head__title {\n  margin: 0;\n  font-size: 20px;\n  font-weight: 600;\n  color: var(--az-text-primary);\n  line-height: 1.25;\n}\n.sd-head__sub {\n  font-size: 13px;\n  color: var(--az-text-secondary);\n}\n.sd-head__actions {\n  display: flex;\n  gap: 8px;\n}\n.sd-head__actions .ant-btn {\n  height: 36px !important;\n  font-size: 14px !important;\n}\n\n/* KPI tiles — compact */\n.sd-kpis {\n  display: grid;\n  grid-template-columns: repeat(4, minmax(0, 1fr));\n  gap: 12px;\n  flex-shrink: 0;\n}\n.sd-kpi {\n  background: var(--az-white);\n  border: 1px solid var(--az-border-subtle);\n  border-radius: 12px;\n  padding: 12px 14px;\n  display: flex;\n  flex-direction: column;\n  gap: 6px;\n  min-width: 0;\n}\n.sd-kpi__top {\n  display: flex;\n  align-items: center;\n  gap: 8px;\n  min-width: 0;\n}\n.sd-kpi__icon {\n  width: 24px;\n  height: 24px;\n  border-radius: 7px;\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  flex-shrink: 0;\n  color: var(--colorDark, #007aff);\n  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.1);\n  font-size: 13px;\n}\n.sd-kpi__label {\n  font-size: 13px;\n  font-weight: 500;\n  color: var(--az-text-secondary);\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n.sd-kpi__main {\n  display: flex;\n  align-items: flex-end;\n  justify-content: space-between;\n  gap: 8px;\n  min-width: 0;\n}\n.sd-kpi__value {\n  font-size: 22px;\n  font-weight: 600;\n  line-height: 1.15;\n  color: var(--az-text-primary);\n  white-space: nowrap;\n}\n.sd-kpi__meta {\n  margin-top: 4px;\n  display: flex;\n  flex-wrap: wrap;\n  gap: 4px 8px;\n  align-items: center;\n}\n.sd-kpi__spark {\n  width: 96px;\n  height: 38px;\n  flex-shrink: 0;\n}\n.sd-kpi__delta {\n  display: inline-flex;\n  align-items: center;\n  gap: 3px;\n  font-size: 12px;\n  font-weight: 600;\n  white-space: nowrap;\n}\n/* Theme colours only: up in the theme colour, down in neutral ink — the\n   arrow carries the direction, so meaning never rests on colour alone */\n.sd-kpi__delta--good {\n  color: var(--colorDark, #007aff);\n}\n.sd-kpi__delta--bad {\n  color: var(--az-text-secondary, #6b7280);\n}\n.sd-kpi__delta--flat {\n  color: var(--az-text-tertiary, #9ca3af);\n  font-weight: 500;\n}\n.sd-kpi__period {\n  font-weight: 400;\n  color: var(--az-text-secondary);\n}\n\n/* Sample-data tag */\n.sd-sample-tag {\n  display: inline-flex;\n  align-items: center;\n  gap: 4px;\n  padding: 1px 8px;\n  border-radius: var(--az-radius-pill, 999px);\n  background: rgba(var(--colorDark-rgb, 0, 122, 255), 0.1);\n  color: var(--colorDark, #007aff);\n  font-size: 11px;\n  font-weight: 500;\n  white-space: nowrap;\n  cursor: help;\n}\n\n/* Main area: 70% graphs | 30% pies, filling the remaining height */\n.sd-main {\n  flex: 1;\n  min-height: 0;\n  display: grid;\n  grid-template-columns: minmax(0, 7fr) minmax(0, 3fr);\n  gap: 12px;\n}\n.sd-col {\n  display: flex;\n  flex-direction: column;\n  gap: 12px;\n  min-height: 0;\n  min-width: 0;\n}\n.sd-col--graphs .sd-card--revenue {\n  flex: 3 1 0;\n}\n.sd-col--graphs .sd-card--bars {\n  flex: 2 1 0;\n}\n.sd-col--pies .sd-card {\n  flex: 1 1 0;\n}\n\n/* Card shell */\n.sd-card {\n  background: var(--az-white);\n  border: 1px solid var(--az-border-subtle);\n  border-radius: 12px;\n  padding: 12px 16px;\n  display: flex;\n  flex-direction: column;\n  gap: 8px;\n  min-width: 0;\n  min-height: 0;\n}\n.sd-card__header {\n  display: flex;\n  align-items: flex-start;\n  justify-content: space-between;\n  gap: 8px;\n  flex-wrap: wrap;\n  flex-shrink: 0;\n}\n.sd-card__titles {\n  min-width: 0;\n}\n.sd-card__title {\n  margin: 0;\n  font-size: 14px;\n  font-weight: 600;\n  color: var(--az-text-primary);\n  display: flex;\n  align-items: center;\n  gap: 8px;\n  flex-wrap: wrap;\n}\n.sd-card__subtitle {\n  margin: 2px 0 0;\n  font-size: 12px;\n  color: var(--az-text-secondary);\n}\n.sd-card__actions {\n  display: flex;\n  align-items: center;\n  gap: 6px;\n}\n.sd-card__body {\n  flex: 1;\n  min-height: 0;\n  min-width: 0;\n  display: flex;\n  flex-direction: column;\n}\n.sd-card__state {\n  flex: 1;\n  min-height: 80px;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  color: var(--az-text-secondary);\n  font-size: 13px;\n}\n.sd-inline {\n  display: inline-flex;\n  align-items: center;\n  gap: 6px;\n  flex-wrap: wrap;\n}\n.sd-inline strong {\n  color: var(--az-text-primary);\n  font-weight: 600;\n}\n\n/* Compact pill tabs + icon button in card headers */\n.sd-card .sd-range .az-btn,\n.sd-card .sd-range button {\n  height: 28px !important;\n  min-width: 0 !important;\n  padding: 0 10px !important;\n  font-size: 12px !important;\n}\n.sd-icon-btn {\n  width: 28px;\n  height: 28px;\n  border-radius: 8px;\n  border: 1px solid var(--az-border-subtle);\n  background: var(--az-white);\n  color: var(--az-text-secondary);\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  cursor: pointer;\n  padding: 0;\n}\n.sd-icon-btn:hover {\n  color: var(--az-text-primary);\n  background: var(--az-gray-50);\n}\n\n/* Charts fill their card */\n.sd-chart {\n  flex: 1;\n  min-height: 0;\n  min-width: 0;\n}\n\n/* Tooltip */\n.sd-tooltip {\n  background: var(--az-white);\n  border: 1px solid var(--az-border-subtle);\n  border-radius: 8px;\n  box-shadow: 0 6px 20px rgba(var(--az-black-rgb, 0, 0, 0), 0.1);\n  padding: 8px 10px;\n  min-width: 130px;\n}\n.sd-tooltip__label {\n  font-size: 12px;\n  color: var(--az-text-secondary);\n  margin-bottom: 4px;\n}\n.sd-tooltip__row {\n  display: flex;\n  align-items: center;\n  gap: 6px;\n}\n.sd-tooltip__key {\n  width: 12px;\n  height: 2px;\n  border-radius: 1px;\n}\n.sd-tooltip__value {\n  font-size: 14px;\n  font-weight: 600;\n  color: var(--az-text-primary);\n}\n.sd-tooltip__name {\n  font-size: 12px;\n  color: var(--az-text-secondary);\n}\n\n/* Donut cards: donut fills the free height, legend below */\n.sd-donut {\n  flex: 1;\n  min-height: 0;\n  display: flex;\n  flex-direction: column;\n  gap: 8px;\n}\n.sd-donut__chart {\n  position: relative;\n  flex: 1;\n  min-height: 110px;\n}\n.sd-donut__center {\n  position: absolute;\n  inset: 0;\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  justify-content: center;\n  pointer-events: none;\n}\n.sd-donut__total {\n  font-size: 17px;\n  font-weight: 600;\n  color: var(--az-text-primary);\n}\n.sd-donut__label {\n  font-size: 11px;\n  color: var(--az-text-secondary);\n}\n\n/* Shared legend */\n.sd-legend {\n  list-style: none;\n  margin: 0;\n  padding: 0;\n  display: flex;\n  flex-direction: column;\n  gap: 4px;\n  flex-shrink: 0;\n}\n.sd-legend li {\n  display: grid;\n  grid-template-columns: 10px minmax(0, 1fr) auto 46px;\n  align-items: center;\n  gap: 8px;\n  font-size: 13px;\n}\n.sd-swatch {\n  width: 10px;\n  height: 10px;\n  border-radius: 3px;\n}\n.sd-legend__name {\n  color: var(--az-text-secondary);\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n.sd-legend__value {\n  font-weight: 600;\n  color: var(--az-text-primary);\n  font-variant-numeric: tabular-nums;\n}\n.sd-legend__pct {\n  text-align: right;\n  color: var(--az-text-secondary);\n  font-variant-numeric: tabular-nums;\n}\n\n/* Revenue table view (scrolls inside the card) */\n.sd-table-wrap {\n  flex: 1;\n  min-height: 0;\n  overflow: auto;\n}\n.sd-table {\n  width: 100%;\n  border-collapse: collapse;\n  font-size: 13px;\n}\n.sd-table th {\n  position: sticky;\n  top: 0;\n  background: var(--az-white);\n  text-align: left;\n  font-size: 12px;\n  font-weight: 500;\n  color: var(--az-text-secondary);\n  padding: 6px 10px;\n  border-bottom: 1px solid var(--az-border-subtle);\n}\n.sd-table td {\n  padding: 7px 10px;\n  border-bottom: 1px solid var(--az-border-subtle);\n  color: var(--az-text-primary);\n}\n.sd-num {\n  text-align: right !important;\n  font-variant-numeric: tabular-nums;\n}\n\n/* Tablet / small laptop: graphs full width, pies side by side below */\n@media (max-width: 991.98px) {\n  .sd-kpis {\n    grid-template-columns: repeat(2, minmax(0, 1fr));\n  }\n  .sd-main {\n    grid-template-columns: minmax(0, 1fr);\n  }\n  .sd-col--graphs .sd-card--revenue {\n    height: 320px;\n    flex: none;\n  }\n  .sd-col--graphs .sd-card--bars {\n    height: 240px;\n    flex: none;\n  }\n  .sd-col--pies {\n    flex-direction: row;\n  }\n  .sd-col--pies .sd-card {\n    height: 300px;\n  }\n}\n\n/* Phones */\n@media (max-width: 575.98px) {\n  .sd-page {\n    padding: 12px;\n  }\n  .sd-head__title {\n    font-size: 18px;\n  }\n  .sd-head__actions {\n    width: 100%;\n  }\n  .sd-head__actions > a {\n    flex: 1;\n  }\n  .sd-head__actions .ant-btn {\n    width: 100%;\n  }\n  .sd-kpi__value {\n    font-size: 20px;\n  }\n  .sd-kpi__spark,\n  .sd-kpi__period {\n    display: none;\n  }\n  .sd-col--pies {\n    flex-direction: column;\n  }\n  .sd-col--graphs .sd-card--revenue {\n    height: 280px;\n  }\n  .sd-col--graphs .sd-card--bars {\n    height: 220px;\n  }\n}\n\n/* ── Image upload field (components/ImageUploadField) ── */\n.az-image-upload {\n  display: flex;\n  align-items: center;\n  gap: var(--az-space-3);\n  flex-wrap: wrap;\n}\n.az-image-upload__preview {\n  width: 96px;\n  height: 96px;\n  flex: none;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  overflow: hidden;\n  border: 1px dashed var(--az-gray-300);\n  border-radius: var(--az-radius-sm);\n  background: var(--az-gray-50);\n}\n.az-image-upload__preview img {\n  width: 100%;\n  height: 100%;\n  object-fit: contain;\n}\n.az-image-upload__placeholder {\n  font-size: 28px;\n  color: var(--az-text-secondary);\n}\n.az-image-upload__actions {\n  display: flex;\n  align-items: center;\n  gap: var(--az-space-2);\n  flex-wrap: wrap;\n  min-width: 0;\n}\n.az-image-upload__name {\n  color: var(--az-text-secondary);\n  font-size: 12px;\n  max-width: 220px;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n/* The error message turns the frame red, like other invalid fields */\n.ant-form-item-has-error .az-image-upload__preview {\n  border-color: var(--az-red-500);\n}\n\n/* ── Add Sales Agent wizard (pages/AffiliateTemplate/CreateAffiliate) ── */\n/* 5 steps: drop the 2-step min width so they fit on one line */\n.onboarding-steps--multi.ant-steps {\n  width: 100%;\n}\n.onboarding-steps--multi.ant-steps .ant-steps-item:not(:last-child) {\n  min-width: 0;\n  flex: 1 1 auto !important;\n}\n.az-wizard-actions {\n  margin-top: var(--az-space-2);\n}\n.az-wizard-loading {\n  display: flex;\n  justify-content: center;\n  padding: 48px 0;\n}\n.az-wizard-verified {\n  display: flex;\n  align-items: center;\n  gap: var(--az-space-2);\n  color: var(--az-text-primary);\n  margin-bottom: var(--az-space-3);\n}\n.az-wizard-verified .anticon {\n  color: var(--az-green-600);\n  font-size: 18px;\n}\n.az-wizard-link {\n  display: inline-block;\n  margin: -12px 0 var(--az-space-3);\n  font-size: 13px;\n}\n.az-wizard-hint {\n  color: var(--az-text-secondary);\n  font-size: 13px;\n  margin: 0 0 var(--az-space-3);\n}\n.az-kyc-grid {\n  display: grid;\n  grid-template-columns: repeat(2, minmax(0, 1fr));\n  gap: var(--az-space-3);\n  margin-bottom: var(--az-space-3);\n}\n.az-kyc-card {\n  border: 1px solid var(--az-border-subtle);\n  border-radius: var(--az-radius-sm);\n  padding: var(--az-space-3) 16px 0;\n  background: var(--az-white);\n  min-width: 0;\n}\n.az-kyc-card__title {\n  font-size: 15px;\n  font-weight: 600;\n  color: var(--az-text-primary);\n  margin: 0 0 var(--az-space-3);\n}\n.az-kyc-card__sides {\n  display: flex;\n  flex-wrap: wrap;\n  column-gap: 24px;\n}\n@media (max-width: 767px) {\n  .az-kyc-grid {\n    grid-template-columns: 1fr;\n  }\n}\n.az-image-upload__pdf {\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  gap: 2px;\n  color: var(--az-red-500);\n  font-size: 28px;\n}\n.az-image-upload__pdf span {\n  font-size: 11px;\n  font-weight: 600;\n}\n\n/* Repeatable sections in registration wizards (licences, markets, warehouses) */\n.az-repeat-card {\n  border: 1px solid var(--az-border-subtle);\n  border-radius: var(--az-radius-sm);\n  padding: var(--az-space-3) 16px 0;\n  margin-bottom: var(--az-space-3);\n  background: var(--az-white);\n}\n.az-repeat-card__head {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  gap: var(--az-space-2);\n}\n.az-repeat-card__head .az-kyc-card__title {\n  margin: 0 0 var(--az-space-3);\n}\n.az-repeat-add {\n  display: flex;\n  margin-bottom: var(--az-space-3);\n}\n.az-wizard-check {\n  display: flex;\n  width: fit-content;\n  margin-bottom: var(--az-space-3);\n}\n.az-progress-cell {\n  display: flex;\n  flex-direction: column;\n  line-height: 1.3;\n}\n.az-progress-cell small {\n  color: var(--az-text-secondary);\n}\n\n/* Sales Agent wizard — app sub-screens (KYC docs, agreements) */\n.az-kyc-substeps.ant-steps {\n  max-width: 720px;\n  margin-bottom: var(--az-space-3);\n  padding: var(--az-space-2) var(--az-space-3);\n  border-radius: var(--az-radius-sm);\n  background: var(--az-gray-50);\n}\n.az-agreement-frame {\n  display: block;\n  width: 100%;\n  height: 420px;\n  border: 1px solid var(--az-border-subtle);\n  border-radius: var(--az-radius-sm);\n  background: var(--az-white);\n  margin-bottom: var(--az-space-3);\n}\n.az-language-options.ant-radio-group {\n  display: flex;\n  flex-direction: column;\n  gap: var(--az-space-2);\n}\n@media (max-width: 767px) {\n  .az-agreement-frame {\n    height: 60vh;\n  }\n}\n\n/* T-Ops product logo (replaces the \"Admin Portal\" wordmark) */\n.az-login__productLogo {\n  display: block;\n  height: 56px;\n  width: auto;\n  max-width: 100%;\n}\n.brandProductLogo {\n  display: block;\n  height: 26px;\n  width: auto;\n}\n@media (max-width: 767px) {\n  .brandProductLogo {\n    height: 22px;\n  }\n}\n\n/* ── Portal name (T-Ops) in the sidebar brand band ── */\n.sb-brand-logo--tops {\n  max-height: 34px;\n  max-width: 100%;\n  object-fit: contain;\n}\n/* Collapsed platform rail: the T mark painted in the Traidster gradient\n   (same as the old \"T\" initial) via a mask of the logo's T */\n.sb-brand-tmark {\n  display: block;\n  width: 36px;\n  height: 32px;\n  background: linear-gradient(\n    135deg,\n    var(--traidster-cobalt) 0%,\n    var(--traidster-midpoint) 50%,\n    var(--traidster-cyan) 100%\n  );\n  -webkit-mask-repeat: no-repeat;\n  mask-repeat: no-repeat;\n  -webkit-mask-position: center;\n  mask-position: center;\n  -webkit-mask-size: contain;\n  mask-size: contain;\n}\n\n/* Portal name in the top bar, before the breadcrumb — client users (platform\n   users see T-Ops in the sidebar brand band) */\n.page-topbar-portal {\n  display: flex;\n  align-items: center;\n  flex-shrink: 0;\n  padding-right: 12px;\n  border-right: 1px solid var(--az-border-subtle);\n}\n.page-topbar-portal img {\n  display: block;\n  height: 20px;\n  width: auto;\n}\n@media (max-width: 767px) {\n  .home-section .page-topbar .page-topbar-portal {\n    display: none;\n  }\n}\n"],"sourceRoot":""}]);
// Exports
___CSS_LOADER_EXPORT___.locals = {};
export default ___CSS_LOADER_EXPORT___;
